import {
  take,
  put,
  call,
  fork,
  select,
  takeEvery,
  takeLeading,
  takeLatest,
  all,
  delay,
  debounce,
} from "redux-saga/effects";
import _, { filter } from "lodash";
import { Auth, Storage } from "aws-amplify";
import * as dateFns from "date-fns";
import moment from "moment";
import html2pdf from "html2pdf.js";

import Constants from "./../helpers/Constants";
import * as actions from "../actions";
import * as reducers from "../reducers";
import {
  isReportV2,
  canSplitSections,
  splitStagesOf,
  SECTION_SPLIT,
} from "../helpers/reportV2";
import API from "../services/API";
// 배송건(receiver) 단위 발번용 mutation 문서 — API.issueDeliveryTracking 시그니처에
// receiverId 가 없어(services/API.js) 지정 호출만 문서를 직접 태운다(2026-08-05).
import { LS_issueDeliveryTracking } from "../graphql/mutations";
import {
  addressToStr,
  hashMd5,
  downloadCSV,
  generateId,
  diffSet,
  numberToCurrency,
  isLegacyInventoryHistories,
  maskingName,
  maskingPhoneNumber,
  generateBarcode,
  numberWithCommas,
  translateChannelError,
} from "../helpers/Utils";
import Authority from "../services/Authority";
import Analytics from "../services/Analytics";
import { buildCopiedProduct } from "../helpers/ProductCopy";
import * as fromMarket from "../reducers/market";
import {
  BasicSample,
  BulkActionInfo,
  BulkActionHeader,
  BulkActionItems,
  TransferHeader,
  TransferInfo,
  TransferItems,
  CountInfo,
  CountItems,
  CountHeader,
  NoteSample,
  TitleSample,
  PurchaseOrderHeader,
  PurchaseOrderInfo,
  PurchaseOrderItems,
  PurchaseOrderCostInfo,
  PurchaseOrderSample,
} from "../helpers/PdfMaker";
import { GetInventoryTransactionReason } from "../components/Elements";
import {
  TABLE_GROUP_HEADERS,
  TABLE_HEADERS,
} from "../components/StockHistoryList";
import {
  getAssetHistoryReason,
  getHistoryFileNameByType,
  getOriginReason,
  REQUEST_APPLY_TRANSITIONS,
} from "../reducers/inventory";
import { ASSET_TABLE_HEADER } from "../pages/StockReport";
import { deleteCookie } from "../helpers/Cookie";
import { getOrderNumber } from "../reducers/order";
import { isCurrentLocationAvailableCoupon } from "../reducers/coupon";
import { SHIPPING_COMPANY_BY_CODE } from "../helpers/GuideData";
import { getPaidAmount } from "../helpers/PaymentInfo";
import { globalAlertRef } from "../components/AlertModalProvider";

const normalizeProvider = (provider) =>
  ["cafe24", "imweb"].includes(provider) ? provider : null;

export function* generateStore({ employee, survey, settings }) {
  try {
    const _settings = {
      ...(settings || {}),
      generateItems: true,
    };
    const store = yield call(API.generateStore, employee, survey, _settings);
    yield put(actions.initStore(store));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "generateStore", {
      employee,
      survey,
      settings,
    });
  }
}

export function* watchGenerateStore() {
  yield takeEvery(actions.GENERATE_STORE, generateStore);
}

function* _setMixPanelInfo(store) {
  const employee = yield select(reducers.getEmployee);
  const hasAdminAccess = yield select(reducers.hasLocationAdminAccess);
  const _store = store.info;
  const _location = yield select(reducers.getCurrentLocation);
  const isMultiLocation = yield select(reducers.isMultiLocation);
  if (isMultiLocation) {
    Analytics.setInfo(
      store.id,
      employee.email,
      hasAdminAccess ? null : _location.id,
      `${_store.name}-${_location.name}`,
      isMultiLocation,
      _store,
      _location.createdAt,
    );
  } else {
    Analytics.setInfo(
      store.id,
      employee.email,
      employee.locationId || store.location.id,
      _store.name,
      false,
      _store,
      _location.createdAt,
    );
  }
}

function* _getStoreContract(storeId) {
  const storeContract = yield call(API.getStoreContract, storeId);
  yield put(actions.receiveStoreContract(storeContract));
}

function* _syncDiscountsByTier(storeId, providerCustomerGroups) {
  const newTierCoupons = [];
  providerCustomerGroups.forEach((group) => {
    if (
      group.discount &&
      group.discount.discountMethod &&
      group.discount.discountValue
    ) {
      newTierCoupons.push({
        tier: group.name,
        discountMethod: group.discount.discountMethod,
        discountValue: group.discount.discountValue,
      });
    }
  });

  const newTierCouponsByTier = newTierCoupons.reduce((obj, coupon) => {
    obj[coupon.tier] = coupon;
    return obj;
  }, {});

  let oldTierCoupons = [];
  let data = {};
  do {
    data = yield call(API.getCoupons, storeId, data.nextToken, 500, {
      conditionType: { eq: "tier" },
    });
    oldTierCoupons = [...oldTierCoupons, ...data.items];
  } while (data.nextToken);

  const oldTierCouponsByTier = oldTierCoupons.reduce((obj, coupon) => {
    obj[coupon.conditionTier] = coupon;
    return obj;
  }, {});

  const addCoupons = newTierCoupons.filter(
    (newCoupon) => !(newCoupon.tier in oldTierCouponsByTier),
  );
  yield all(
    addCoupons.map((coupon) =>
      call(API.createCoupon, {
        id: generateId("cpn"),
        storeId: storeId,
        name: `${coupon.tier} 등급 할인`,
        type: "auto",
        conditionType: "tier",
        conditionTier: coupon.tier,
        discountMethod: coupon.discountMethod,
        discountValue: coupon.discountValue,
        applyScope: "all",
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      }),
    ),
  );

  let deleteCoupons = [];
  let updateCoupons = [];

  for (const oldCoupon of oldTierCoupons) {
    if (!(oldCoupon.conditionTier in newTierCouponsByTier)) {
      deleteCoupons.push(oldCoupon);
    } else if (oldCoupon.conditionTier in newTierCouponsByTier) {
      const _newCoupon = newTierCouponsByTier[oldCoupon.conditionTier];
      const _oldCoupon = oldTierCouponsByTier[oldCoupon.conditionTier];
      if (
        _oldCoupon.discountMethod !== _newCoupon.discountMethod ||
        _oldCoupon.discountValue !== _newCoupon.discountValue
      ) {
        updateCoupons.push(_newCoupon);
      }
    }
  }

  yield all(deleteCoupons.map((coupon) => call(API.deleteCoupon, coupon.id)));
  yield all(
    updateCoupons.map((coupon) =>
      call(API.updateCoupon, {
        ...oldTierCouponsByTier[coupon.tier],
        discountValue: coupon.discountValue,
        discountMethod: coupon.discountMethod,
        updatedAt: new Date().toISOString(),
      }),
    ),
  );
}

export function* watchProviderConnection() {
  yield take(actions.GET_STORE_SUCCESS);
  try {
    const provider = yield select(reducers.getStorePolicyProvider);
    if (!provider.key) return;
    const storeId = yield select(reducers.getStoreId);
    yield call(API.getProviderCustomerCount, storeId, false);
  } catch (error) {
    const DISCONNECTED_MESSAGES = new Set([
      "NotExists",
      "Expired",
      "No usable or refreshable cafe24 token",
      "No refreshable cafe24 token",
      "'access_token'",
      "'client_id'",
      "PROVIDER_NOT_CONNECTED",
    ]);
    const isDisconnected = (error?.errors || []).some((e) =>
      DISCONNECTED_MESSAGES.has(String(e?.message || "").trim()),
    );
    if (isDisconnected && globalAlertRef.showAlert) {
      globalAlertRef.showAlert({
        title: "자사몰 연동 확인",
        description: "토큰이 만료됐거나 연동이 해제되었습니다.",
        buttons: [{ text: "확인" }],
      });
    }
  }
}

export function* getStore({ storeId, withStats }) {
  try {
    let store = yield call(_getStoreWithLocations, storeId);
    if (withStats) {
      const start = new Date();
      const end = new Date();
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 0);
      const report = yield call(
        API.getPeriodReport,
        storeId,
        "location",
        start.toISOString(),
        end.toISOString(),
      );
      const stats = (report.locations || []).reduce((obj, l) => {
        obj[l.id] = l;
        return obj;
      }, {});
      store.locations.items = (store.locations.items || []).map((l) => ({
        ...l,
        stats: stats[l.id] ?? { avg: 0, customer: 0, total: 0 },
      }));
    }

    yield put(actions.initStore(store));
    yield call(_setMixPanelInfo, store);
    yield call(_getStoreContract, storeId);
    yield put(actions.setStoreSuccess());
  } catch (error) {
    console.log("getStores", error);
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "getStore", { storeId });
  }
}

export function* watchGetStore() {
  /*
    takeEvery will fork a new `getAllProducts` task on each GET_ALL_PRODUCTS actions
    i.e. concurrent GET_ALL_PRODUCTS actions are allowed
  */
  yield takeEvery(actions.GET_STORE_REQUEST, getStore);
}

export function* connectProvider({ storeId, mid, provider }) {
  try {
    const providerKey = normalizeProvider(provider);
    if (!providerKey) {
      deleteCookie("mid");
      deleteCookie("provider");
      throw new Error("Invalid provider");
    }
    let store = yield call(_getStoreWithLocations, storeId);
    yield call(API.registerProvider, store.id, mid, providerKey);
    const loyalty = yield call(
      API.getExternalLoyaltyConfig,
      store.id,
      providerKey,
    );
    const locationId = generateId("loc", [storeId, mid]);
    const loc = yield call(API.getStoreLocation, locationId);
    if (!loc) {
      yield call(API.createLocation, {
        id: locationId,
        storeId,
        type: "external",
        name: providerKey === "imweb" ? "아임웹" : "카페24",
        provider: providerKey,
      });
    }
    yield call(API.updateStore, {
      ...store,
      policy: {
        ...(store.policy || {}),
        provider: {
          ...(store.policy?.provider || {}),
          key: providerKey,
          ident: mid,
          customer: true,
          coupon: true,
          locationId,
        },
      },
      loyalty,
    });

    const data = yield call(API.getProviderCustomerGroups, storeId);
    const providerCustomerGroups = data.items || [];
    yield call(_syncDiscountsByTier, store.id, providerCustomerGroups);

    deleteCookie("mid");
    deleteCookie("provider");
    store = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(store));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "connectProvider", { storeId, provider });
  }
}

export function* watchConnectProvider() {
  yield takeEvery(actions.CONNECT_PROVIDER, connectProvider);
}

export function* getBillingCost({ storeId, locationId }) {
  try {
    const cost = yield call(API.getBillingCost, storeId, locationId);
    yield put(actions.receiveBillingCost(cost));
    yield put(actions.setGetBillingCostSucess());
  } catch (error) {
    yield put(actions.setGetBillingCostFailure(error));
    Analytics.sendErrorReport(error, "getBillingCost", { storeId, locationId });
  }
}

export function* watchGetBillingCost() {
  yield takeEvery(actions.GET_BILLING_COST_REQUEST, getBillingCost);
}

export function* updateStore({ updater }) {
  try {
    let store = yield select(reducers.getStore);
    store = yield call(_getStoreWithLocations, store.id);
    const locationId = yield select(reducers.getCurrentLocationId);
    const location = (store.locations.items || []).filter(
      (location) => location.id === locationId,
    )[0];
    let _store = {
      ..._.cloneDeep(store),
      ..._.cloneDeep(updater),
    };
    if (!store.policy?.multiLocation) {
      if (
        _store.info.name !== location.name ||
        _store.info.phone !== location.phone ||
        _store.info.address !== location.address ||
        _store.info.zip !== location.zip
      ) {
        yield call(API.updateLocation, {
          ..._.cloneDeep(location),
          name: _store.info.name,
          phone: _store.info.phone,
          address: _store.info.address,
          zip: _store.info.zip,
        });
      }
    }
    const updatedStore = yield call(API.updateStore, _store);
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateStore", { updater });
  }
}

export function* watchUpdateStore() {
  yield takeEvery(actions.UPDATE_STORE_REQUEST, updateStore);
}

export function* updateMirrorConfig({ config }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const hasMulti = yield select(reducers.isMultiLocation);
    if (hasMulti && Authority.level() !== "store") {
      const locationId = yield select(reducers.getCurrentLocationId);
      const location = yield call(API.getStoreLocation, locationId);
      yield call(API.updateLocation, {
        ..._.cloneDeep(location),
        mirror: _.cloneDeep(config),
      });
      const updatedStoreWithLocation = yield call(
        _getStoreWithLocations,
        storeId,
      );
      yield put(actions.initStore(updatedStoreWithLocation));
    } else {
      const store = yield call(API.getStore, storeId);
      const updatedStore = yield call(_updateStore, {
        ..._.cloneDeep(store),
        mirror: _.cloneDeep(config),
      });
      yield put(actions.initStore(updatedStore));
    }
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateMirrorConfig", config);
  }
}

export function* watchUpdateMirrorConfig() {
  yield takeEvery(actions.UPDATE_MIRROR_CONFIG, updateMirrorConfig);
}

export function* spreadMirrorConfig({}) {
  try {
    const mirrorConfig = yield select(reducers.getMirrorConfig);
    let store = yield select(reducers.getStore);
    store = yield call(_getStoreWithLocations, store.id);
    let _store = _.cloneDeep(store);
    yield all(
      (_store.locations || []).items.map((loc) =>
        call(API.updateLocation, {
          ...loc,
          mirror: mirrorConfig,
        }),
      ),
    );

    const updatedStore = yield call(_getStoreWithLocations, store.id);
    yield put(actions.initStore(updatedStore));

    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "spreadMirrorConfig");
  }
}

export function* watchSpreadMirrorCnfig() {
  yield takeEvery(actions.SPREAD_MIRROR_CONFIG, spreadMirrorConfig);
}

export function* updateDualConfig({ config }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const store = yield call(API.getStore, storeId);
    const oldConfig = yield select(reducers.getDualConfig);
    // delete 로 간주
    if (!config?.video && !_.isEmpty(oldConfig?.video)) {
      yield Storage.remove(oldConfig.video.key, { level: "public" });
    }
    const updatedStore = yield call(_updateStore, {
      ..._.cloneDeep(store),
      mirror: _.cloneDeep(config),
    });
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateDualConfig", config);
  }
}

export function* watchUpdateDualConfig() {
  yield takeEvery(actions.UPDATE_DUAL_CONFIG, updateDualConfig);
}

export function* inviteEmployee({ employee, resend }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const info = yield select(reducers.getStoreInfo);

    const user = yield call(
      API.createUser,
      employee.email,
      employee.firstName,
      employee.lastName,
      employee.phone,
      info.name,
      resend,
    );

    let _employee = _.cloneDeep(employee);
    if (!resend) {
      _employee = yield call(API.createEmployee, {
        id: user.username,
        role: "owner",
        firstName: _employee.firstName,
        lastName: _employee.lastName,
        phone: _employee.phone,
        email: _employee.email,
      });
    } else {
      _employee = yield call(API.updateEmployee, _employee);
    }
    if (resend) {
      const employees = yield select(reducers.getOrganizationsByEmployee);
      for (let emp of employees) {
        if (emp.id === employee.id) {
          yield all(
            (emp.organizations || []).map((org) =>
              call(API.deleteOrganization, org.id),
            ),
          );
        }
      }
    }
    yield all(
      employee.organizations.map((org) =>
        call(API.createOrganization, {
          ...org,
          employeeId: _employee.id,
          storeId: org.storeId || storeId,
          status: "invited",
        }),
      ),
    );
    const organizations = yield call(_getAllOrganizations, storeId);
    yield put(actions.receiveOrganizations(organizations));
    yield put(actions.setEmployeeStatusSuccess());
  } catch (error) {
    if (
      error.errors &&
      error.errors[0].message.includes("UsernameExistsException")
    ) {
      yield put(
        actions.setEmployeeStatusFailure(new Error("UsernameExistsException")),
      );
    } else {
      yield put(actions.setEmployeeStatusFailure(error));
    }

    Analytics.sendErrorReport(error, "inviteEmployee", { employee });
  }
}

export function* watchInviteEmployee() {
  yield takeEvery(actions.INVITE_EMPLOYEE, inviteEmployee);
}

export function* confirmInviteEmployee({ user }) {
  try {
    const employee = yield call(API.getEmployee, user.username);
    console.log(employee);
    for (let org of employee.organizations?.items || []) {
      yield call(API.updateOrganization, {
        ...org,
        status: "confirmed",
      });
    }
    yield put(actions.setEmployeeStatusSuccess());
  } catch (error) {
    yield put(actions.setEmployeeStatusFailure(error));
    Analytics.sendErrorReport(error, "confirmInviteEmployee", { user });
  }
}

export function* watchConfirmInviteEmployee() {
  yield takeEvery(actions.CONFIRM_INVITE_EMPLOYEE, confirmInviteEmployee);
}

function* _getLocations(storeId) {
  let locations = [];
  let data = {};

  do {
    data = yield call(
      API.getStoreLocations,
      storeId,
      data.nextToken,
      //limit 100 or more
      200,
    );
    locations = [...locations, ...data.items];
  } while (data.nextToken);
  return locations;
}

function* _getCustomerTags(storeId, locationId, isOutsourced) {
  let tags = [];
  let data = {};
  if (locationId && !isOutsourced) {
    do {
      data = yield call(
        API.getCustomerTagsByLocation,
        locationId,
        data.nextToken,
        500,
      );
      tags = [...tags, ...data.items];
    } while (data.nextToken);
  } else {
    do {
      data = yield call(
        API.getCustomerTagsByStore,
        storeId,
        data.nextToken,
        500,
      );
      tags = [...tags, ...data.items];
    } while (data.nextToken);
  }
  return tags;
}

function* _updateStore(store) {
  yield call(API.updateStore, store);
  const updatedStore = yield call(_getStoreWithLocations, store.id);
  return updatedStore;
}

function* _getStoreWithLocations(storeId) {
  const [store, locations] = yield all([
    API.getStore(storeId),
    _getLocations(storeId),
  ]);
  return {
    ...store,
    locations: {
      items: locations,
    },
  };
}

function* _getAllTerminals({
  storeId = null,
  locationId = null,
  businessOperatorId = null,
  businessNumber = null,
}) {
  let terminals = [];
  let data = {};
  do {
    if (storeId) {
      data = yield call(
        API.getTerminalsByStore,
        storeId,
        data.nextToken,
        //limit 100 or more
        500,
      );
    } else if (locationId) {
      data = yield call(
        API.getTerminalsByLocation,
        locationId,
        data.nextToken,
        //limit 100 or more
        500,
      );
    } else if (businessOperatorId) {
      data = yield call(
        API.getTerminalsByBusinessOperator,
        businessOperatorId,
        data.nextToken,
        //limit 100 or more
        500,
      );
    } else if (businessNumber) {
      data = yield call(
        API.getTerminalsByBusinessNumber,
        businessNumber,
        data.nextToken,
        //limit 100 or more
        500,
      );
    } else {
      data = yield call(API.getTerminals, data.nextToken, 500);
    }
    terminals = [...terminals, ...(data.items || [])];
  } while (data.nextToken);
  return terminals;
}

function* _getAllRentalContracts({
  businessOperatorId = null,
  storeId = null,
  locationId = null,
}) {
  let rentals = [];
  let data = {};
  do {
    if (businessOperatorId) {
      data = yield call(
        API.getRentalContractsByBusinessOperator,
        businessOperatorId,
        data.nextToken,
        //limit 100 or more
        500,
      );
    } else if (storeId) {
      data = yield call(
        API.getRentalContractsByStore,
        storeId,
        data.nextToken,
        //limit 100 or more
        500,
      );
    } else if (locationId) {
      data = yield call(
        API.getRentalContractsByByLocation,
        locationId,
        data.nextToken,
        //limit 100 or more
        500,
      );
    }
    rentals = [...rentals, ...(data.items || [])];
  } while (data.nextToken);
  return rentals;
}

function* _updateBusinessNumberForOperation(
  businessOperator,
  terminals,
  rentals,
) {
  const newOperatorId = hashMd5(businessOperator.businessNumber);
  let newOperator = yield call(API.createBusinessOperator, {
    id: newOperatorId,
    businessNumber: businessOperator.businessNumber,
    name: businessOperator.name,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
  });
  for (let terminal of terminals) {
    yield call(API.updateTerminal, {
      ...terminal,
      businessOperatorId: newOperatorId,
      updatedAt: new Date().toISOString(),
    });
  }
  // TODO: rental이 한개일때만 처리해둠
  for (let rental of rentals) {
    yield call(API.updateRentalContract, {
      ...rental,
      businessOperatorId: newOperatorId,
      updatedAt: new Date().toISOString(),
    });
  }
  return newOperator;
}

function* _getAllOrganizations(storeId) {
  console.log(storeId);
  let organizations = [];
  let data = {};
  do {
    data = yield call(
      API.getOrganizationsByStore,
      storeId,
      data.nextToken,
      //limit 100 or more
      500,
    );
    organizations = [...organizations, ...data.items];
  } while (data.nextToken);
  return organizations;
}

function* _getAllOrganizationsByEmployee(employeeId) {
  let organizations = [];
  let data = {};
  do {
    data = yield call(
      API.getOrganizationsByEmployee,
      employeeId,
      data.nextToken,
      //limit 100 or more
      500,
    );
    organizations = [...organizations, ...data.items];
  } while (data.nextToken);
  return organizations;
}

function* getRegisterdCustomer(storeCustomers) {
  let stores = [];
  (storeCustomers || []).forEach((customer) => {
    if (customer.status === "registered") {
      stores = [
        ...stores,
        {
          ..._.cloneDeep(customer),
          store: { ..._.cloneDeep(customer.store), location: null },
        },
      ];
      const locations = (customer.store.locations?.items || []).reduce(
        (obj, l) => {
          obj[l.id] = l;
          return obj;
        },
        {},
      );
      (customer.visitedLocationIds || []).forEach((locationId) => {
        if (locationId in locations) {
          stores = [
            ...stores,
            {
              ..._.cloneDeep(customer),
              location: _.cloneDeep(locations[locationId]),
              store: {
                ..._.cloneDeep(customer.store),
                locations: null,
              },
            },
          ];
        }
      });
    }
  });
  return stores;
}

export function* setCurrentOrganization({ organization }) {
  try {
    if (!_.isEmpty(organization)) {
      if (organization.locationId) {
        const location = yield call(
          API.getStoreLocation,
          organization.locationId,
        );
        organization = {
          ...organization,
          location,
        };
      }
      Authority.defineRulesFor(organization);
      yield put(
        actions.getRequestProgressEvents(
          ["admin", "owner"].includes(organization.role)
            ? organization.storeId
            : organization.locationId,
        ),
      );
    }
  } catch (error) {
    console.log(error);
    Analytics.sendErrorReport(error, "setCurrentOrganization", {
      organization,
    });
  }
}

export function* watchSetCurrentOrganization() {
  yield takeEvery(actions.SET_CURRENT_ORGANIZATION, setCurrentOrganization);
}

export function* getOrganizations({ storeId }) {
  try {
    const organizations = yield _getAllOrganizations(storeId);
    yield put(actions.receiveOrganizations(organizations));
    yield put(actions.setEmployeeStatusSuccess());
  } catch (error) {
    yield put(actions.setEmployeeStatusFailure(error));
    Analytics.sendErrorReport(error, "getOrganizations", { storeId });
  }
}

export function* watchGetOrganizations() {
  yield takeEvery(actions.GET_ORGNIZATIONS, getOrganizations);
}

export function* createOrUpdateLocation({ updater }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const locationId = updater.id;
    let location = {};
    if (locationId) {
      location = yield call(API.getStoreLocation, locationId);
    }
    let _location = {
      ..._.cloneDeep(location),
      ..._.cloneDeep(updater),
    };
    if (_location.orderPlanId) {
      let orders = yield select(reducers.getOrderPlans);
      orders = orders.reduce((obj, o) => {
        obj[o.id] = o;
        return obj;
      }, {});
      _location = {
        ..._location,
        settings: {
          ...(_location.settings ?? {}),
          ...(orders[_location.orderPlanId]?.settings ?? {}),
        },
      };
    }
    if (_location.menuPlanId) {
      let menus = yield select(reducers.getMenuPlans);
      console.log(menus);
      menus = menus.reduce((obj, m) => {
        obj[m.id] = m;
        return obj;
      }, {});
      if (_location.menuPlanId in menus) {
        if (menus[_location.menuPlanId].mode === "multi") {
          _location = {
            ..._location,
            menus: menus[_location.menuPlanId].menus,
            settings: {
              ...(_location.settings ?? {}),
              menuGridSize: menus[_location.menuPlanId]?.size,
              menuMode: "multi",
            },
          };
        } else if (menus[_location.menuPlanId].mode === "category") {
          _location = {
            ..._location,
            settings: {
              ...(_location.settings ?? {}),
              menuMode: "category",
            },
          };
        } else if (menus[_location.menuPlanId].mode === "retail") {
          _location = {
            ..._location,
            menu: {
              ...(_location.menu || {}),
              list: {
                ...(_location.menu.list || {}),
                pages: menus[_location.menuPlanId].pages,
              },
            },
          };
        }
      }
    }
    console.log(_location);

    if (!_location.id) {
      _location = yield call(API.createLocation, {
        ..._location,
        storeId,
        id: generateId("loc"),
        state: _location.type !== "warehouse" ? "inactive" : null,
      });
      //TODO: support location settings or product settings for defaults
      yield call(
        API.arrangeProductLocation,
        storeId,
        _location.type === "warehouse"
          ? [
              {
                id: _location.id,
                available: true,
              },
            ]
          : [],
      );
    } else {
      let _store = yield call(_getStoreWithLocations, storeId);
      yield call(API.updateLocation, {
        ..._location,
        // NOTE: reduce dependency
        businessNumber:
          _location.type === "direct" &&
          _location.businessNumber === _store.info.businessNumber
            ? null
            : _location.businessNumber,
        representative:
          _location.type === "direct" &&
          _location.representative === _store.info.representative
            ? null
            : _location.representative,
        updatedAt: new Date().toISOString(),
      });
    }
    let store = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(store));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateLocation", { updater });
  }
}

export function* watchCreateOrUpdateLocation() {
  yield takeEvery(actions.CREATE_OR_UPDATE_LOCATION, createOrUpdateLocation);
}

export function* updateLocation({ location }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const _location = _.cloneDeep(location);
    yield call(API.updateLocation, _location);
    const store = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(store));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
  }
}

export function* watchUpdateLocation() {
  yield takeEvery(actions.UPDATE_LOCATION, updateLocation);
}

function* _createLocationContract(storeId, locationId) {
  const storeContract = yield call(API.getStoreContract, storeId);
  if (!_.isEmpty(storeContract) && storeContract.businessOperatorId) {
    yield call(API.createLocationContract, {
      id: locationId,
      businessOperatorId: storeContract.businessOperatorId,
      menuOrderExemptionQuantity: storeContract.menuOrderExemptionQuantity,
      menuOrderQuantity: storeContract.menuOrderQuantity,
      menuOrderStartedAt: storeContract.menuOrderStartedAt,
      menuOrderRecurringType: storeContract.menuOrderRecurringType,
      taxRefund: storeContract.taxRefund,
      note: storeContract.note,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    });
  }
}

function* _createOrUpdateOrganizations(employee) {
  const _organizations = yield _getAllOrganizations(employee.employeeStoreId);
  if (_.isEmpty(_organizations)) {
    yield call(API.createOrganization, {
      id: generateId("org"),
      role: "owner",
      employeeId: employee.id,
      status: "confirmed",
      enterpriseId: employee.employeeEnterpriseId,
      storeId: employee.employeeStoreId,
      updatedAt: new Date().toISOString(),
      createdAt: new Date().toISOString(),
    });
  } else {
    for (let org of _organizations) {
      if (org.role === "owner") {
        delete org.locationId;
        yield call(API.deleteOrganization, org.id);
        yield call(API.createOrganization, {
          ...org,
          enterpriseId: employee.employeeEnterpriseId,
          updatedAt: new Date().toISOString(),
        });
      } else if (org.role === "manager") {
        yield call(API.updateOrganization, {
          ...org,
          role: "parttimer",
          permissions: org.permissions
            ? [
                JSON.stringify({
                  ...JSON.parse(org.permissions[0]),
                  item: false,
                }),
              ]
            : null,
          updatedAt: new Date().toISOString(),
        });
      }
    }
  }
}

const convertMenuPlan = (menuPlan, location, settings) => {
  if (settings?.mode === "retail") {
    menuPlan = {
      ...menuPlan,
      pages: (location.menu?.list?.pages || []).map((p, index) => ({
        index: index,
        parentId: p.parentId,
        items: p.items || [],
        title: p.title,
      })),
    };
  } else {
    menuPlan = {
      ...menuPlan,
      pages: (location.menu?.board?.pages || []).map((p, index) => ({
        title: p.title,
        index: index,
        items: p.items || [],
        parentId: p.parentId,
      })),
    };
  }
  return menuPlan;
};

export function* lanunchMultiLocation() {
  try {
    let store = yield select(reducers.getStore);
    store = yield call(_getStoreWithLocations, store.id);
    const locationId = yield select(reducers.getCurrentLocationId);
    const location = yield call(API.getStoreLocation, locationId);
    const employee = yield select(reducers.getEmployee);
    yield _createOrUpdateOrganizations(employee);
    const updatedEmployee = yield call(API.getEmployee, employee.id);
    yield put(actions.setCurrentEmployee(updatedEmployee));
    const settings = _.cloneDeep(store.settings);
    const menuPlan = convertMenuPlan(
      {
        id: generateId("mp"),
        name: `${store.info.name} 메뉴판`,
        updatedAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
      },
      location,
      settings,
    );
    yield call(API.updateLocation, {
      ..._.cloneDeep(location),
      type: "direct",
      menus: null,
      menuPlanId: menuPlan.id,
      payments: {
        ...location.payments,
        refund: store.payments?.refund ?? false,
      },
      storeId: store.id,
      settings: settings,
    });
    const updatedStore = yield call(API.updateStore, {
      ..._.cloneDeep(store),
      policy: {
        ...store.policy,
        multiLocation: true,
      },
      plans: {
        menus: [menuPlan],
      },
      settings: settings,
    });
    yield _createLocationContract(store.id, location.id);
    yield call(API.arrangeProductLocation, store.id);
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "lanunchMultiLocation", {});
  }
}

export function* watchLanunchMultiLocation() {
  yield takeEvery(actions.LAUNCH_MULTI_LOCATION, lanunchMultiLocation);
}

export function* createOrUpdateMenuPlan({ plan, isApply }) {
  try {
    let store = yield select(reducers.getStore);
    store = yield call(_getStoreWithLocations, store.id);
    let _store = _.cloneDeep(store);
    const _plan = {
      ..._.cloneDeep(plan),
      id: plan.id || generateId("mp"),
    };
    const updatedStore = yield call(API.updateStore, {
      ..._store,
      plans: {
        ...(_store.plans || {}),
        menus: [
          ...(_store.plans?.menus || []).filter((m) => m.id !== _plan.id),
          _plan,
        ],
      },
    });

    if (_plan.mode === "category") {
      yield all(
        _store.locations.items
          .filter((loc) => loc.menuPlanId === _plan.id)
          .map((loc) =>
            call(API.updateLocation, {
              ...loc,
              menuPlanId: _plan.id,
              settings: {
                ...(loc.settings ?? {}),
                menuMode: "category",
              },
            }),
          ),
      );
    } else {
      if (isApply) {
        if (_plan.mode === "multi") {
          yield all(
            _store.locations.items
              .filter((loc) => loc.menuPlanId === _plan.id)
              .map((loc) =>
                call(API.updateLocation, {
                  ...loc,
                  menuPlanId: _plan.id,
                  menus: _plan.menus,
                  settings: {
                    ...(loc.settings ?? {}),
                    menuGridSize: _plan.size,
                  },
                }),
              ),
          );
        } else if (_plan.mode === "retail") {
          yield all(
            _store.locations.items
              .filter((loc) => loc.menuPlanId === _plan.id)
              .map((loc) =>
                call(API.updateLocation, {
                  ...loc,
                  menu: {
                    ...(loc.menu || {}),
                    list: { ...(loc.menu.list || {}), pages: _plan.pages },
                  },
                }),
              ),
          );
        }
      }
    }
    store = yield call(_getStoreWithLocations, store.id);
    yield put(actions.initStore(store));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateMenuPlan", plan);
  }
}

export function* watchCreateOrUpdateMenuPlan() {
  yield takeEvery(actions.CREATE_OR_UPDATE_MENU_PLAN, createOrUpdateMenuPlan);
}

export function* deleteMenuPlan({ plan }) {
  try {
    let store = yield select(reducers.getStore);
    store = yield call(_getStoreWithLocations, store.id);
    let _store = _.cloneDeep(store);
    const updatedStore = yield call(API.updateStore, {
      ..._store,
      plans: {
        ...(_store.plans || {}),
        menus: (_store.plans?.menus || []).filter((m) => m.id !== plan.id),
      },
    });
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "deleteMenuPlan", plan);
  }
}

export function* watchDeleteMenuPlan() {
  yield takeEvery(actions.DELETE_MENU_PLAN, deleteMenuPlan);
}

export function* createOrUpdateOrderPlan({ plan, isApply, locationIds }) {
  try {
    let store = yield select(reducers.getStore);
    store = yield call(_getStoreWithLocations, store.id);
    let _store = _.cloneDeep(store);
    const _plan = {
      ..._.cloneDeep(plan),
      id: plan.id || generateId("op"),
    };
    const updatedStore = yield call(API.updateStore, {
      ..._store,
      plans: {
        ...(_store.plans || {}),
        orders: [
          ...(_store.plans?.orders || []).filter((o) => o.id !== _plan.id),
          _plan,
        ],
      },
    });
    const locItems = _store.locations?.items || [];
    if (Array.isArray(locationIds)) {
      // 일괄 배포: offline 지점만 대상(!provider && !warehouse). 선택 구독+merge, 해제(기존 구독) 구독 해제.
      const offlineItems = locItems.filter(
        (l) => !l.provider && l.type !== "warehouse",
      );
      const targets = [];
      for (const loc of offlineItems) {
        const selected = locationIds.includes(loc.id);
        const wasSubscribed = loc.orderPlanId === _plan.id;
        if (selected) {
          targets.push(
            call(API.updateLocation, {
              ...loc,
              orderPlanId: _plan.id,
              settings: {
                ...(loc.settings ?? {}),
                ...(plan?.settings ?? {}),
              },
            }),
          );
        } else if (wasSubscribed) {
          targets.push(call(API.updateLocation, { ...loc, orderPlanId: null }));
        }
      }
      if (targets.length) yield all(targets);
      // 지점 변경(구독/해제) 반영 위해 재조회 후 store 갱신.
      const finalStore = yield call(_getStoreWithLocations, _store.id);
      yield put(actions.initStore(finalStore));
    } else {
      if (isApply) {
        yield all(
          locItems
            .filter((loc) => loc.orderPlanId === _plan.id)
            .map((loc) =>
              call(API.updateLocation, {
                ...loc,
                orderPlanId: _plan.id,
                settings: {
                  ...(loc.settings ?? {}),
                  ...(plan?.settings ?? {}),
                },
              }),
            ),
        );
      }
      yield put(actions.initStore(updatedStore));
    }
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateOrderPlan", plan);
  }
}

export function* watchCreateOrUpdateOrderPlan() {
  yield takeEvery(actions.CREATE_OR_UPDATE_ORDER_PLAN, createOrUpdateOrderPlan);
}

export function* deleteOrderPlan({ plan }) {
  try {
    let store = yield select(reducers.getStore);
    store = yield call(_getStoreWithLocations, store.id);
    let _store = _.cloneDeep(store);
    const updatedStore = yield call(API.updateStore, {
      ..._store,
      plans: {
        ...(_store.plans || {}),
        orders: (_store.plans?.orders || []).filter((o) => o.id !== plan.id),
      },
    });
    // 이 plan 을 구독 중인 지점의 orderPlanId 해제
    const subscribers = (_store.locations?.items || []).filter(
      (loc) => loc.orderPlanId === plan.id,
    );
    if (subscribers.length) {
      yield all(
        subscribers.map((loc) =>
          call(API.updateLocation, { ...loc, orderPlanId: null }),
        ),
      );
      // 지점 변경 반영 위해 재조회
      const finalStore = yield call(_getStoreWithLocations, _store.id);
      yield put(actions.initStore(finalStore));
    } else {
      yield put(actions.initStore(updatedStore));
    }
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "deleteOrderPlan", plan);
  }
}

export function* watchDeleteOrderPlan() {
  yield takeEvery(actions.DELETE_ORDER_PLAN, deleteOrderPlan);
}

export function* updateStoreLoyalty({ loyalty }) {
  try {
    let store = yield select(reducers.getStore);
    store = yield call(_getStoreWithLocations, store.id);
    let _loyalty = _.cloneDeep(loyalty);

    _loyalty.conditionItems = (_loyalty.conditionItems || []).filter(
      (item) => item.itemId && item.point && item.parentId,
    );
    _loyalty.rewardItems = (_loyalty.rewardItems || []).filter(
      (item) => item.itemId && item.point && item.parentId,
    );
    _loyalty.rewardRates = (_loyalty.rewardRates || []).filter(
      (item) => item.itemId && item.point && item.rate,
    );
    _loyalty.rewardAmounts = (_loyalty.rewardAmounts || []).filter(
      (item) => item.itemId && item.point && item.amount,
    );

    console.log(_loyalty);
    if (!_.isEmpty(_loyalty.incentives)) {
      if (
        _loyalty.incentives.fulfill &&
        !_loyalty.incentives.fulfillCampaignId
      ) {
        const fCamp = yield call(API.createCampaign, {
          id: generateId("cmp"),
          status: "ready",
          storeId: store.id,
          type: "auto",
          name: "추가 정보 기입시 적립",
          trigger: "fulfill",
          triggerTiming: 0,
          // title:
          // message:
          // method: sms,
        });
        _loyalty.incentives.fulfillCampaignId = fCamp.id;
      }
      if (_loyalty.incentives.birth && !_loyalty.incentives.birthCampaignId) {
        const bCamp = yield call(API.createCampaign, {
          id: generateId("cmp"),
          status: "ready",
          storeId: store.id,
          type: "auto",
          name: "생일 포인트 적립",
          trigger: "birth",
          triggerTiming: 0,
          // title:
          // message:
          // method: sms,
        });
        _loyalty.incentives.birthCampaignId = bCamp.id;
        // TODO: remove (For backward compatibility)
        _loyalty.incentives.birthItem = {
          itemId: "tmp",
          name: `${_loyalty.incentives.birhtPoint}P`,
        };
      }
    }

    let _store = {
      ..._.cloneDeep(store),
      loyalty: _loyalty,
    };
    const updatedStore = yield call(API.updateStore, _store);
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateStoreLoyalty", { loyalty });
  }
}

export function* watchUpdateStoreLoyalty() {
  yield takeEvery(actions.UPDATE_STORE_LOYALTY_REQUEST, updateStoreLoyalty);
}

function* _getBillingHistories(storeId, locationId = null) {
  let billingHistories = [];
  let data = [];
  const isMultiLocation = yield select(reducers.isMultiLocation);
  if (!isMultiLocation) {
    do {
      data = yield call(
        API.getBillingHistoriesByStore,
        storeId,
        null,
        500,
        data.nextToken,
      );
      billingHistories = [...billingHistories, ...data.items];
    } while (data.nextToken);
  } else {
    if (!locationId) {
      do {
        data = yield call(
          API.getBillingHistoriesByStore,
          storeId,
          null,
          500,
          data.nextToken,
        );
        billingHistories = [...billingHistories, ...data.items];
      } while (data.nextToken);
      billingHistories = billingHistories.filter((his) => !his.locationId);
    } else {
      do {
        data = yield call(
          API.getBillingHistoriesByLocation,
          locationId,
          null,
          500,
          data.nextToken,
        );
        billingHistories = [...billingHistories, ...data.items];
      } while (data.nextToken);
    }
  }
  return billingHistories;
}

export function* getBillingHistories({ storeId, locationId = null }) {
  try {
    const billingHistories = yield call(
      _getBillingHistories,
      storeId,
      locationId,
    );
    yield put(actions.initBillingHistories(billingHistories));
    yield put(actions.setGetBillingHistoriesSuccess());
  } catch (error) {
    yield put(actions.setGetBillingHistoriesFailure(error));
    Analytics.sendErrorReport(error, "getBillingHistories", {
      storeId,
      locationId,
    });
  }
}

export function* watchGetBillingHistories() {
  yield takeEvery(actions.GET_BILLING_HISTORIES_REQUEST, getBillingHistories);
}

export function* payFailedBillings({ storeId, locationId = null }) {
  try {
    yield call(API.payBillings, storeId, locationId);
    const billingHistories = yield call(
      _getBillingHistories,
      storeId,
      locationId,
    );
    yield put(actions.initBillingHistories(billingHistories));
    yield put(actions.setGetBillingHistoriesSuccess());
  } catch (error) {
    yield put(actions.setGetBillingHistoriesFailure(error));
    Analytics.sendErrorReport(error, "payFailedBillings", {
      storeId,
      locationId,
    });
  }
}

export function* watchPayFailedBillings() {
  yield takeEvery(actions.PAY_FAILED_BILLINGS, payFailedBillings);
}

export function* getCampaigns({ storeId, page, size, campaignType }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    const hasMulti = yield select(reducers.isMultiLocation);
    const locationId =
      hasMulti &&
      organization.locationId &&
      !["owner", "admin"].includes(organization.role)
        ? organization.locationId
        : null;
    let searchFilters = [
      ...(campaignType
        ? [{ key: "type", operation: "eq", value: campaignType }]
        : []),
      ...(locationId
        ? [{ key: "locationId", operation: "eq", value: locationId }]
        : []),
    ];
    const result = yield call(
      API.getMessageCampaigns,
      storeId,
      page,
      size,
      searchFilters,
    );
    for (let idx in result.items || []) {
      if (
        result.items[idx].status === "requested" ||
        result.items[idx].status === "reserved"
      ) {
        try {
          result.items[idx] = yield call(
            API.getCampaignWithReport,
            result.items[idx].id,
          );
        } catch (err) {
          console.log("Failed to get campainWithReport", err);
        }
      }
    }
    yield put(actions.receiveCampaigns(result.items || [], result.count || 0));
    yield put(actions.setGetCampaignsSuccess());
  } catch (error) {
    yield put(actions.setGetCampaignsFailure(error));
    Analytics.sendErrorReport(error, "getCampaigns", storeId);
  }
}

export function* getCampaign({ campaignId }) {
  try {
    const campaign = yield call(API.getCampaign, campaignId);
    yield put(actions.receiveCampaign(campaign));
    yield put(actions.setGetCampaignSuccess());
  } catch (error) {
    yield put(actions.setGetCampaignFailure(error));
    Analytics.sendErrorReport(error, "getCampaign", {
      campaignId,
    });
  }
}

export function* getCampaignWithReport({ campaignId, start, end }) {
  try {
    const campaign = yield call(
      API.getCampaignWithReport,
      campaignId,
      start,
      end,
    );
    yield put(actions.receiveCampaign(campaign));
    yield put(actions.setGetCampaignSuccess());
  } catch (error) {
    yield put(actions.setGetCampaignFailure(error));
    Analytics.sendErrorReport(error, "getCampaignWithReport", {
      campaignId,
      start,
      end,
    });
  }
}

export function* getCustomersStats({
  storeId,
  page,
  size,
  filters,
  search,
  locationId,
  override,
}) {
  try {
    let { items, count } = yield call(
      API.getCustomerList,
      storeId,
      page,
      size,
      null,
      filters,
      search,
      locationId,
    );
    yield put(actions.receiveCustomers(items, count, override));
    yield put(actions.setGetCustomerStatsSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerStatsFailure(error));
    Analytics.sendErrorReport(error, "getCustomersStats", {
      storeId,
      page,
      size,
      filters,
      search,
      override,
    });
  }
}

export function* watchGetCustomersStats() {
  yield takeEvery(actions.GET_CUSTOMERS_STATS, getCustomersStats);
}

export function* createCampaign({ campaign }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const organization = yield select(reducers.getCurrentOrganization);
    const hasMulti = yield select(reducers.isMultiLocation);
    const locationId =
      hasMulti &&
      organization.locationId &&
      !["owner", "admin"].includes(organization.role)
        ? organization.locationId
        : null;
    let _campaign = _.cloneDeep(campaign);
    if (!_campaign.id) {
      _campaign = {
        ..._campaign,
        id: generateId("cmp"),
        storeId: storeId,
        locationId: locationId,
      };
    }
    if (!_campaign.status) {
      _campaign.status = "draft";
    }
    // updateCampaign 쪽 hasExplicitRecipient 가드가 여기엔 없다 — create 경로에는
    // recipient 가 실릴 수 없기 때문이다. recipient 는 Campaign 의 @connection 출력
    // 필드라(campaignRecipientId 로 서버가 풀어 준다) 조회한 캠페인에만 붙고, 조회한
    // 캠페인에는 id 가 반드시 있는데 createCampaign 을 부르는 화면은 전부 !campaign.id
    // 일 때만 부른다. 신규 초기값(NEW_CAMPAIGN·INIT_CAMPAIGN)에도 recipient 키가 없다.
    if (_campaign.recipient && _campaign.recipient.id) {
      _campaign.campaignRecipientId = _campaign.recipient.id;
    }
    const createdCampaign = yield call(API.createCampaign, _campaign);
    yield put(actions.receiveCampaign(createdCampaign));
    yield put(actions.setCreateOrUpdateCampaignSuccess());
  } catch (error) {
    yield put(actions.setCreateOrUpdateCampaignFailure(error));
    Analytics.sendErrorReport(error, "createCampaign", { campaign });
  }
}

export function* watchCreateCampaign() {
  yield takeEvery(actions.CREATE_CAMPAIGN, createCampaign);
}

export function* updateCampaign({ campaign }) {
  try {
    let _campaign = _.cloneDeep(campaign);
    if (!_campaign.status) {
      _campaign.status = "draft";
    }
    // recipient 는 getCampaign 이 campaignRecipientId 로 풀어 준 연결 객체다(입력 스키마에
    // 없어 API.updateCampaign 이 떼낸다). 에디터는 수신자를 태그/직접찾기로 바꾸면
    // campaignRecipientId 를 null 로 보내 REMOVE 시키는데, cloneDeep 으로 state 에 남은 옛
    // recipient 로 그걸 되살리면 DDB 에 campaignRecipientId 와 campaignRecipientTagId 가
    // 같이 남아 서버가 옛 수신자까지 본다. 현재 선택이 없을 때만 레거시 파생을 남긴다.
    const hasExplicitRecipient =
      !!_campaign.campaignRecipientTagId ||
      (_campaign.recipientList || []).length > 0;
    if (!hasExplicitRecipient && _campaign.recipient && _campaign.recipient.id) {
      _campaign.campaignRecipientId = _campaign.recipient.id;
    }
    const updatedCampaign = yield call(API.updateCampaign, _campaign);
    yield put(actions.receiveCampaign(updatedCampaign));
    yield put(actions.setCreateOrUpdateCampaignSuccess());
  } catch (error) {
    yield put(actions.setCreateOrUpdateCampaignFailure(error));
    Analytics.sendErrorReport(error, "updateCampaign", { campaign });
  }
}

export function* watchUpdateCampaign() {
  yield takeEvery(actions.UPDATE_CAMPAIGN, updateCampaign);
}

export function* launchCampaign({ campaign }) {
  try {
    if (campaign.id) {
      yield call(API.deleteCampaign, campaign.id);
      yield call(API.createCampaign, campaign);
    } else {
      campaign = { ...campaign, id: generateId("cmp") };
      yield call(API.createCampaign, campaign);
    }
    const updatedCampaign = yield call(API.launchCampaign, campaign.id);
    yield put(actions.receiveCampaign(updatedCampaign));
    yield put(actions.setLaunchCampaignSuccess());
  } catch (error) {
    campaign.status = "draft";
    yield call(API.updateCampaign, campaign);
    yield put(actions.setLaunchCampaignFailure(error));
    Analytics.sendErrorReport(error, "launchCampaign", { campaign });
  }
}

export function* watchLanuchCampaign() {
  yield takeEvery(actions.LAUNCH_CAMPAIGN, launchCampaign);
}

export function* cancelCampaign({ campaignId }) {
  try {
    const updatedCampaign = yield call(API.cancelCampaign, campaignId);
    yield put(actions.setCancelCampaignSuccess());
    yield put(actions.receiveCampaign(updatedCampaign));
  } catch (error) {
    console.log(error);
    yield put(actions.setCancelCampaignFailure(error));
    Analytics.sendErrorReport(error, "cancelCampaign", { campaignId });
  }
}

export function* sendBarcodeCouponCampaign({ campaign }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const organization = yield select(reducers.getCurrentOrganization);
    const isOutsourced = yield select(reducers.isOutsourcedCustomer);
    const isMultiLocation = yield select(reducers.isMultiLocation);
    if (!campaign.coupon && campaign.couponId) {
      const coupons = yield select(reducers.getBarcodeCouponsById);
      campaign.coupon = coupons[campaign.couponId];
    }
    let _coupon = _.cloneDeep(campaign.coupon);
    // 쿠폰 소스는 위 getBarcodeCouponsById 조회뿐이다 — 에디터는 쿠폰 레코드를 넘기지
    // 않는다(BM-54). 조회가 미스면 아래 createCoupon 진입부에서 TypeError 로 죽어
    // 사용자에겐 사유 없는 "발송 실패"만 남는다. 연결한 쿠폰이 있는 경우에만 막는다.
    if (campaign.couponId && !_coupon) {
      throw new Error(
        "쿠폰을 찾을 수 없어요. 삭제된 쿠폰일 수 있어요. 쿠폰을 다시 선택해 주세요.",
      );
    }

    let _campaign = {
      ...campaign,
      storeId,
    };

    // Campaign.coupon 은 브랜드 전용 네이티브 오브젝트다. 브랜드 초안을 알림톡으로 바꾼 뒤
    // 저장 없이 바로 발송하면 delete 로는 DDB 의 옛 맵이 남는다 —
    // updateCampaign 은 null 로 온 키만 REMOVE 하고 없는 키는 건드리지 않는다.
    // _coupon 은 이미 cloneDeep 된 뒤라 여기서 지워도 안전하다.
    _campaign.coupon = null;
    const isLocationScoped =
      isMultiLocation &&
      !isOutsourced &&
      organization.locationId &&
      !["owner", "admin"].includes(organization.role);
    if (isLocationScoped) {
      _campaign.locationId = organization.locationId;
    }
    // 쿠폰 없는 알림톡도 이 사가로 온다(CampaignEditor 는 method 로만 분기한다) —
    // 쿠폰이 있을 때만 생성·연결한다. id 없는 쿠폰은 즉석 발급이라 createCoupon 이 필요하다.
    if (_coupon) {
      if (!_coupon.id) {
        _coupon.id = generateId("cpn");
        _coupon.storeId = storeId;
        if (isLocationScoped) {
          _coupon.locationId = organization.locationId;
          _coupon.publishableLocationIds = [organization.locationId];
        }
        _coupon = yield call(API.createCoupon, _coupon);
      }
      _campaign.couponId = _coupon.id;
    }
    if (_campaign.id) {
      _campaign = {
        ..._campaign,
        updatedAt: new Date().toISOString(),
      };
      let updatedCampaign = yield call(API.updateCampaign, _campaign);
      updatedCampaign = yield call(API.launchCampaign, updatedCampaign.id);
      yield put(actions.receiveCampaign(updatedCampaign));
    } else {
      _campaign = {
        ..._campaign,
        id: generateId("cmp"),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      let createdCampaign = yield call(API.createCampaign, _campaign);
      try {
        if (createdCampaign.id) {
          createdCampaign = yield call(API.launchCampaign, createdCampaign.id);
        }
      } catch (error) {
        yield call(API.deleteCampaign, _campaign.id);
        yield put(actions.setLaunchCampaignFailure(error));
        throw error;
      }
      yield put(actions.receiveCampaign(createdCampaign));
    }
    yield put(actions.setLaunchCampaignSuccess());
  } catch (error) {
    console.log("Failed to sendBarcodeCouponCampaign", error);
    // 기존 캠페인은 위에서 status=reserved 로 갱신한 뒤 발송을 시도한다 — 1단계가
    // 거절되면 그 상태로 남아 목록에 '예약됨'으로 보인다(BM-75). launchCampaign 과
    // 같이 draft 로 되돌린다(신규는 위 내부 catch 가 삭제하므로 여기 오지 않는다).
    if (campaign.id) {
      try {
        yield call(API.updateCampaign, { ...campaign, status: "draft" });
      } catch (rollbackError) {
        // 되돌리기 실패는 원인 에러를 덮지 않는다
      }
    }
    yield put(actions.setLaunchCampaignFailure(error));
    Analytics.sendErrorReport(error, "sendBarcodeCouponCampaign", {
      campaign,
    });
  }
}
export function* watchSendBarcodeCouponCampaign() {
  yield takeEvery(
    actions.SEND_BARCODE_COUPON_CAMPAIGN,
    sendBarcodeCouponCampaign,
  );
}

export function* watchGetCampaigns() {
  yield takeEvery(actions.GET_CAMPAIGNS, getCampaigns);
}

export function* watchCancelCampaign() {
  yield takeEvery(actions.CANCEL_CAMPAIGN, cancelCampaign);
}

export function* watchGetCampaignWithReport() {
  yield takeEvery(actions.GET_CAMPAIGN_WITH_REPORT, getCampaignWithReport);
}

export function* watchGetCampaign() {
  yield takeEvery(actions.GET_CAMPAIGN, getCampaign);
}

export function* getKakaoProfile({ storeId }) {
  const _storeId = storeId || (yield select(reducers.getStoreId));
  try {
    const profile = yield call(API.getKakaoProfile, _storeId);
    yield put(actions.receiveKakaoProfile(profile || {}));
  } catch (error) {
    // "kakao channel(daou) is not linked" 는 장애가 아니라 정상 미연동 상태이기도 하다.
    // 전용 status 슬라이스에만 담고 전역 storeStatus 는 건드리지 않는다.
    yield put(actions.receiveKakaoProfileFailure(error));
    Analytics.sendErrorReport(error, "getKakaoProfile", { storeId: _storeId });
  }
}

export function* watchGetKakaoProfile() {
  yield takeEvery(actions.GET_KAKAO_PROFILE, getKakaoProfile);
}

export function* applyBrandMessage({
  storeId,
  unsubscribePhoneNumber,
  unsubscribeAuthNumber,
  evidence,
}) {
  const _storeId = storeId || (yield select(reducers.getStoreId));
  try {
    const result = yield call(
      API.applyBrandMessage,
      _storeId,
      unsubscribePhoneNumber || null,
      unsubscribeAuthNumber || null,
      evidence || null,
    );
    yield put(actions.receiveBrandMessageApply(result || {}));
    // 응답에 재조회된 최신 profile 이 동봉된다(server sales.py:4684) — 별도 조회 불필요.
    if (result && result.profile) {
      yield put(actions.receiveKakaoProfile(result.profile));
    }
  } catch (error) {
    yield put(actions.receiveBrandMessageApplyFailure(error));
    Analytics.sendErrorReport(error, "applyBrandMessage", {
      storeId: _storeId,
    });
  }
}

export function* watchApplyBrandMessage() {
  yield takeEvery(actions.APPLY_BRAND_MESSAGE, applyBrandMessage);
}

export function* updateEmployee({ employee }) {
  try {
    let _employee = _.cloneDeep(employee);
    const updatedEmployee = yield call(API.updateEmployee, _employee);
    yield put(actions.setCurrentEmployee(updatedEmployee));
    yield put(actions.setEmployeeStatusSuccess());
  } catch (error) {
    yield put(actions.setEmployeeStatusFailure(error));
    Analytics.sendErrorReport(error, "updateEmployee", { employee });
  }
}

export function* watchUpdateEmployee() {
  yield takeEvery(actions.UPDATE_EMPLOYEE, updateEmployee);
}

export function* updateOrganizationEmployee({ employee }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    yield call(API.updateEmployee, _.cloneDeep(employee));
    // TODO: yield call(API.updateUserAttribute, _.cloneDeep(employee));

    const employees = yield select(reducers.getOrganizationsByEmployee);
    for (let emp of employees) {
      if (emp.id === employee.id) {
        yield all(
          emp.organizations.map((org) => call(API.deleteOrganization, org.id)),
        );
      }
    }
    yield all(
      employee.organizations.map((org) =>
        call(API.createOrganization, {
          ...org,
          employeeId: employee.id,
          storeId: org.storeId || storeId,
          status: "confirmed",
        }),
      ),
    );
    const organizations = yield call(_getAllOrganizations, storeId);
    yield put(actions.receiveOrganizations(organizations));
    yield put(actions.setEmployeeStatusSuccess());
  } catch (error) {
    yield put(actions.setEmployeeStatusFailure(error));
    Analytics.sendErrorReport(error, "updateOrganizationEmployee", {
      employee,
    });
  }
}

export function* watchUpdateOrganizationEmployee() {
  yield takeEvery(
    actions.UPDATE_ORGANIZATION_EMPLOYEE,
    updateOrganizationEmployee,
  );
}

export function* deleteOrganizationEmployee({ employee }) {
  try {
    yield all(
      (employee.organizations || []).map((org) =>
        call(API.deleteOrganization, org.id),
      ),
    );
    yield call(API.deleteEmployee, employee.id);
    yield call(API.deleteUser, employee.email);
    const storeId = yield select(reducers.getStoreId);
    const organizations = yield call(_getAllOrganizations, storeId);
    yield put(actions.receiveOrganizations(organizations));
    yield put(actions.setEmployeeStatusSuccess());
  } catch (error) {
    yield put(actions.setEmployeeStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteOrganizationEmployee", {
      employee,
    });
  }
}

export function* watchDeleteOrganizationEmployee() {
  yield takeEvery(
    actions.DELETE_ORGANIZATION_EMPLOYEE,
    deleteOrganizationEmployee,
  );
}

function* _getAllProductsByStore(storeId) {
  let products = [];
  let data = {};
  do {
    data = yield call(
      API.getProducts,
      storeId,
      data.nextToken,
      //limit 100 or more
      2000,
    );
    products = [...products, ...data.items];
  } while (data.nextToken);
  return products;
}

function* _getAllCategoriesByStore(storeId) {
  let categories = [];
  let data = {};
  do {
    data = yield call(
      API.getCategories,
      storeId,
      data.nextToken,
      //limit 100 or more
      500,
    );
    categories = [...categories, ...data.items];
  } while (data.nextToken);
  return categories;
}

function* _getAllModifiersByStroe(storeId) {
  let modifiers = [];
  let data = {};
  do {
    data = yield call(
      API.getModifiers,
      storeId,
      data.nextToken,
      //limit 100 or more
      500,
    );
    modifiers = [...modifiers, ...data.items];
  } while (data.nextToken);
  return modifiers;
}

function* _getAllProductCategoriesByStore(storeId) {
  let cc = new Date();
  let productCategories = [];
  let data = {};
  do {
    data = yield call(
      API.getProductCategories,
      storeId,
      data.nextToken,
      //limit 100 or more
      2000,
    );
    productCategories = [...productCategories, ...data.items];
  } while (data.nextToken);
  console.log(
    "PRODUCT_CATEGORIES",
    (new Date().getTime() - cc.getTime()) / 1000,
  );
  return productCategories;
}

function* _getAllProductModifiersByStore(storeId) {
  let cc = new Date();
  let productModifiers = [];
  let data = {};
  do {
    data = yield call(
      API.getProductModifiers,
      storeId,
      data.nextToken,
      //limit 100 or more
      2000,
    );
    productModifiers = [...productModifiers, ...data.items];
  } while (data.nextToken);
  console.log(
    "PRODUCT_MODIFIERS",
    (new Date().getTime() - cc.getTime()) / 1000,
  );
  return productModifiers;
}

// 비품 (Equipment) — LS_getEquipments 는 page/size/filters 패턴 (nextToken 아님)
// 카테고리 패턴과 동일하게 전체 fetch. 매장당 수십~수백개 규모 가정.
function* _getAllEquipmentsByStore(storeId) {
  // isContracted 가 false 면 호출 자체 skip — 계약 없는 store 에서 불필요한 API + 권한 에러 차단
  const contracted = yield select(reducers.isContracted);
  if (!contracted) return [];
  let equipments = [];
  let page = 1;
  const size = 500;
  // count 가 size 보다 작으면 종료. 안전장치로 최대 10 페이지 (5000개).
  while (page <= 10) {
    const data = yield call(API.getEquipments, storeId, page, size, null);
    const items = (data && data.items) || [];
    equipments = [...equipments, ...items];
    if (items.length < size) break;
    page += 1;
  }
  return equipments;
}

function* _getItems(storeId) {
  let a = new Date().getTime();
  let items = {};
  let [
    products,
    categories,
    modifiers,
    productCategories,
    productModifiers,
    equipments,
  ] = yield all([
    _getAllProductsByStore(storeId),
    _getAllCategoriesByStore(storeId),
    _getAllModifiersByStroe(storeId),
    _getAllProductCategoriesByStore(storeId),
    _getAllProductModifiersByStore(storeId),
    _getAllEquipmentsByStore(storeId),
  ]);

  items = {
    products: [],
    categories: [],
    modifiers: [],
    equipments: [],
    categoryProducts: productCategories,
    productModifiers: productModifiers,
  };
  items.equipments = (equipments || []).map((eq) => ({
    ...eq,
    itemId: eq.id,
  }));
  items.products = (products || []).map((prod) => ({
    ...prod,
    itemId: prod.id,
  }));
  items.categories = (categories || []).map((c) => ({
    ...c,
    itemId: c.id,
  }));
  items.modifiers = (modifiers || []).map((m) => ({
    ...m,
    itemId: m.id,
  }));
  console.log("TIME(getItems) TOTAL: " + (new Date().getTime() - a) / 1000);
  return items;
}

export function* getItems({ storeId }) {
  try {
    const items = yield _getItems(storeId);
    yield put(actions.receiveItems(items));
    yield put(actions.setGetItemsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setGetItemsFailure(error));
    Analytics.sendErrorReport(error, "getItems", { storeId });
  }
}

export function* watchGetItems() {
  yield takeEvery(actions.GET_ITEMS, getItems);
}

/** 카테고리만 조회 — 제품 전량을 받지 않는 화면(할인 등)에서 쓴다. */
export function* getCategories({ storeId }) {
  try {
    const categories = yield _getAllCategoriesByStore(storeId);
    yield put(
      actions.receiveCategories(
        (categories || []).map((c) => ({ ...c, itemId: c.id })),
      ),
    );
    yield put(actions.setGetItemsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setGetItemsFailure(error));
    Analytics.sendErrorReport(error, "getCategories", { storeId });
  }
}

export function* watchGetCategories() {
  yield takeEvery(actions.GET_CATEGORIES, getCategories);
}

export function* getItemsWithoutProduct({ storeId }) {
  try {
    let [categories, modifiers, productCategories, productModifiers] =
      yield all([
        _getAllCategoriesByStore(storeId),
        _getAllModifiersByStroe(storeId),
        _getAllProductCategoriesByStore(storeId),
        _getAllProductModifiersByStore(storeId),
      ]);
    let items = {
      products: [],
      categories: [],
      modifiers: [],
      categoryProducts: productCategories,
      productModifiers: productModifiers,
    };
    items.categories = (categories || []).map((c) => ({
      ...c,
      itemId: c.id,
    }));
    items.modifiers = (modifiers || []).map((m) => ({
      ...m,
      itemId: m.id,
    }));
    yield put(actions.receiveItems(items));
    yield put(actions.setGetItemsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setGetItemsFailure(error));
    Analytics.sendErrorReport(error, "getItems", { storeId });
  }
}
export function* watchGetItemsWithoutProduct() {
  yield takeEvery(actions.GET_ITEMS_WITHOUT_PRODUCT, getItemsWithoutProduct);
}

export function* getProduct({ productId }) {
  try {
    const product = yield call(API.getItem, productId);
    yield put(actions.receiveItem(product));
    yield put(actions.setGetItemsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setGetItemsFailure(error));
    Analytics.sendErrorReport(error, "getProduct", { productId });
  }
}

export function* watchGetProduct() {
  yield takeEvery(actions.GET_PRODUCT, getProduct);
}

export function* getLoyaltyReport1({ storeId, locationId, start, end, basis }) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "loyalty",
      start,
      end,
      locationId,
      basis,
    );
    let transactions = [];
    if (locationId) {
      transactions = yield call(
        _getPointTransactionsByLocation,
        locationId,
        null,
        start,
        end,
        basis,
      );
    } else {
      transactions = yield call(
        _getPointTransactionsByStore,
        storeId,
        null,
        start,
        end,
        null,
        basis,
      );
    }

    // NOTE: 비효율적이므로 다른 방법 적용 필요
    for (let t of transactions) {
      const c = yield API.getLocationCustomer(
        t.pointTransactionCustomerId,
        t.locationId,
      );
      t.customer = c;
    }
    console.log(report);
    yield put(
      actions.receiveLoyaltyReport1({
        ...report,
        transactions,
      }),
    );
    yield put(actions.setLoyaltyReport1Success());
  } catch (error) {
    yield put(actions.setLoyaltyReport1Failure(error));
    Analytics.sendErrorReport(error, "getLoyaltyReport1", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetLoyaltyReport1() {
  yield takeEvery(actions.GET_LOYALTY_REPORT1, getLoyaltyReport1);
}

export function* getLoyaltySummary({ storeId }) {
  try {
    const summary = yield call(API.getLoyaltySummary, storeId);
    yield put(actions.setGetLoyaltySummarySuccess());
    yield put(actions.receiveLoyaltySummary(summary));
  } catch (error) {
    yield put(actions.setGetLoyaltySummaryFailure(error));
    Analytics.sendErrorReport(error, "getLoyaltySummary", { storeId });
  }
}

export function* watchGetLoyaltySummary() {
  yield takeEvery(actions.GET_LOYALTY_SUMMARY, getLoyaltySummary);
}

export function* getSalesSummary({ storeId }) {
  try {
    const summary = yield call(API.getSalesSummary, storeId);
    yield put(actions.setGetSalesSummarySuccess());
    yield put(actions.receiveSalesSummary(summary));
  } catch (error) {
    yield put(actions.setGetSalesSummaryFailure(error));
    Analytics.sendErrorReport(error, "getSalesSummary", { storeId });
  }
}

export function* watchGetSalesSummary() {
  yield takeEvery(actions.GET_SALES_SUMMARY, getSalesSummary);
}

export function* getSalesReport({
  storeId,
  item,
  start,
  end,
  locationId,
  locationIds,
  basis,
  brandId,
}) {
  try {
    const report = yield call(
      API.getSalesReport,
      storeId,
      item,
      start,
      end,
      locationId,
      locationIds,
      null,
      null,
      basis,
      brandId,
    );
    // TODO 이미 불러왔으면 안부르게 고치기
    if (item === "products" || item === "categories" || item === "modifiers") {
      const items = yield _getItems(storeId);
      yield put(actions.receiveItems(items));
    }
    yield put(actions.setGetSalesReportSuccess());
    yield put(actions.receiveSalesReport(report));
  } catch (error) {
    yield put(actions.setGetSalesReportFailure(error));
    if (item !== "discount") {
      Analytics.sendMixPanelEvent("error", {
        screen: "ReportItem",
        action: "failed_load",
        detail: error.errors && error.errors[0]?.message,
      });
    }
    Analytics.sendErrorReport(error, "getSalesReport", {
      storeId,
      item,
      start,
      end,
    });
  }
}

export function* watchGetSalesReport() {
  yield takeEvery(actions.GET_SALES_REPORT, getSalesReport);
}

export function* getItemsReport({
  storeId,
  start,
  end,
  locationId,
  locationIds,
  basis,
  brandId,
}) {
  try {
    const report = yield call(
      API.getSalesReport,
      storeId,
      "items",
      start,
      end,
      locationId,
      locationIds,
      null,
      null,
      basis,
      brandId,
    );
    const items = yield _getItems(storeId);
    yield put(actions.receiveItems(items));
    yield put(actions.setItemsReportSuccess());
    yield put(actions.receiveItemsReport(report?.item));
  } catch (error) {
    yield put(actions.setItemsReportFailure(error));
    Analytics.sendErrorReport(error, "getItemsReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetItemsReport() {
  yield takeEvery(actions.GET_ITEMS_REPORT, getItemsReport);
}

/**
 * 매장 메모 조회 — Notice 재활용(type='hq-note').
 *
 * 🔴 조회 전용이다 [우진 확정]. 생성·수정·삭제 mutation 은 배선하지 않는다.
 * 🔴 서버 필터가 없어 storeId 로 전부 받아 클라에서 스캔한다
 *    (sales-rn 과 동일 규약 — sales-rn/src/sagas/index.js:10804).
 *    공개 메모(visibility=true) + 내가 쓴 메모만 보여 준다.
 */
export function* getStoreMemos({ storeId }) {
  try {
    let items = [];
    let nextToken = null;
    for (let page = 0; page < 10; page += 1) {
      const result = yield call(API.getNotices, storeId, null, nextToken, 200);
      items = [...items, ...((result && result.items) || [])];
      nextToken = result && result.nextToken;
      if (!nextToken) break;
    }
    // 🔴 공개 메모만 [우진 판정 2026-08-07] — 내 직원ID 판별이 필요 없다
    const memos = items.filter((n) => n && n.type === "hq-note" && n.visibility === true);
    yield put(actions.receiveStoreMemos(memos));
  } catch (error) {
    // 메모는 리포트 본문이 아니다 — 실패해도 리포트 조회를 막지 않는다
    console.warn("[hq-note] 매장 메모 조회 실패:", error?.message || error);
    yield put(actions.receiveStoreMemos([]));
  }
}

export function* watchGetStoreMemos() {
  yield takeEvery(actions.GET_STORE_MEMOS, getStoreMemos);
}

// 🔴 리포트 조회 순번 [우진 QA 2026-08-14 — 전체지점 화면에 지점 수치가 그려졌다].
//    watcher 가 takeEvery 라 조회가 겹치면 사가가 병렬로 돈다. S-40 분리 로딩으로
//    호출이 1차/2차 2개가 되면서, **이전 조회의 2차가 새 조회의 1차 위에 병합**되어
//    서로 다른 스코프의 값이 한 화면에 섞일 수 있다(응답 도착 순서는 보장되지 않는다).
//    조회마다 순번을 발급해 **최신 순번만** 화면에 반영한다.
let reportSeq = 0;
// item → report 리듀서 슬롯 (명시 섹션 병합 시 기존 봉투 조회용)
const V2_SLOT_BY_ITEM = {
  product: "product",
  discount: "discountV2",
  customer: "customerV2",
};

export function* getOrderReport({
  storeId,
  locationId,
  start,
  end,
  item,
  basis,
  brandId,
  options,
  origin,
}) {
  // S-40 분리 로딩: 무거운 섹션은 1차에서 빼고 부른다. 2차는 아래에서 이어 부른다
  //  — 1차 응답만으로 화면이 먼저 그려진다 [우진 원칙: 가드로 포기하지 않는다]
  const seq = (reportSeq += 1);
  // 🔴 섹션 재시도(S-64 ③)는 **그 섹션만** 다시 부르는 국소 조회다 —
  //    전역 로딩(orderStatus.isFetching → 탭 전체 오버레이)을 켜면 화면 전체가
  //    다시 조회되는 것처럼 보인다 [우진 QA 2026-08-14]. 섹션 자리 스피너는
  //    pendingSections 가 담당하므로 여기서는 전역 상태를 건드리지 않는다.
  const isSectionRetry = !!options?.__sectionRetry;
  // 🔴 호출부가 섹션을 **명시**하면(표 페이지네이션 등) 그대로 1회만 부른다 —
  //    분리 로딩(1차/2차)을 또 얹으면 무거운 섹션을 페이지마다 재계산한다
  //    [우진 QA 2026-08-14 — "페이지 넘길 때마다 5초"]
  const explicitSections = Array.isArray(options?.sections) && options.sections.length
    ? options.sections
    : null;
  const split = explicitSections
    ? null
    : canSplitSections(item)
      ? SECTION_SPLIT[item]
      : null;
  const firstOptions = split
    ? { ...(options || {}), sections: split.first }
    : options;
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      item,
      start,
      end,
      locationId,
      basis,
      origin || null,
      brandId,
      firstOptions,
    );
    const organization = yield select(reducers.getCurrentOrganization);
    // 2차를 기다리는 섹션을 봉투에 표시해 둔다 — 뷰가 그 자리에 로딩을 돌린다
    // (표시가 없으면 「데이터 없음」과 구분되지 않는다)
    if (split) report.pendingSections = split.deferred;
    // 리듀서가 이 순번으로 stale 응답을 걸러 낸다
    if (report && typeof report === "object") report.__seq = seq;
    // 명시 섹션 재조회는 그 섹션만 담겨 온다 — 통째로 갈아끼우면 나머지가 사라진다
    if (explicitSections && isReportV2(item)) {
      const existing = yield select(
        (st) => st.report?.[V2_SLOT_BY_ITEM[item]] || null,
      );
      if (existing && existing.data) {
        yield put(
          actions.mergeReportSections(item, report, explicitSections, false, seq),
        );
        // 🔴 성공 액션을 반드시 보낸다 — 여기서 그냥 return 하면 isFetching 이
        //    true 로 남아 로딩이 끝나지 않는다(표가 갱신되지 않는 것처럼 보인다)
        yield put(actions.setGetOrderReportSuccess());
        return;
      }
    }
    // v2 응답은 공통 봉투({meta,data,warnings})라 v1 의 locations flatten 대상이 아니다.
    // 봉투를 그대로 redux 에 넣고, 언랩(data/meta/warnings)은 파서 진입점에서 한다.
    if (isReportV2(item)) {
      if (item === "taxRefund") {
        const itemReport = yield call(
          API.getPeriodReport,
          storeId,
          "taxRefundItems",
          start,
          end,
          locationId,
          null,
          null,
          brandId,
        );
        yield put(actions.receiveTrsReport(report));
        yield put(actions.receiveTrsItemReport(itemReport));
      } else if (item === "storeList") {
        yield put(actions.receiveStoreListReport(report));
      } else if (item === "product") {
        yield put(actions.receiveProductReport(report));
      } else if (item === "discount") {
        yield put(actions.receiveDiscountV2Report(report));
      } else if (item === "customer") {
        yield put(actions.receiveCustomerV2Report(report));
      } else if (item === "membership") {
        yield put(actions.receiveMembershipV2Report(report));
      } else {
        yield put(actions.receiveOrderReport(report));
      }
      // ── S-40 후속 단계: 무거운 섹션을 **단계별로** 받아 1차 봉투에 병합 ──
      // 🔴 실패해도 1차 화면을 깨지 않는다 — 그 섹션만 실패로 남긴다
      //    (분리 로딩의 목적이 "탭 전체가 함께 죽지 않게" 하는 것이다)
      // 🔴 단계 분할 [S-50]: 고객은 2차=kpi 단독(1.3s, 최상단 카드 조기 표시) →
      //    3차=나머지. 한 덩어리로 부르면 최느린 섹션이 전부를 잡는다.
      //    stages 가 없는 item(product·discount)은 종전대로 1단계다.
      // 🔴 단계 호출은 **전역 try 밖의 사고**가 되면 안 된다 [S-64 ①]. 아래
      //    try/catch 가 이 단계의 모든 예외(GraphQL 실패·타임아웃)를 삼키고
      //    실패 범위를 그 sections 로만 국한한다 — 전역 alert 로 올라가지 않는다.
      for (const stageSections of splitStagesOf(split)) {
        try {
          const deferred = yield call(
            API.getPeriodReport,
            storeId,
            item,
            start,
            end,
            locationId,
            basis,
            origin || null,
            brandId,
            { ...(options || {}), sections: stageSections },
          );
          // 🔴 진단 신호 [S-50]: 요청한 섹션이 응답 data 에도 warnings 에도 없으면
          //    서버가 **무경고 생략**한 것이다(§2-3 위반 후보 — productRank `if pr:`
          //    경로). 화면 표시는 사유를 지어내는 것이라 하지 않는다 — 콘솔로만 남겨
          //    실기기 제보 시 원인을 가를 수 있게 한다.
          {
            const dData = deferred?.data || {};
            const dWarns = deferred?.warnings || [];
            const covered = (k) =>
              dWarns.some((w) => (w?.detail?.sections || []).includes(k));
            const silent = stageSections.filter(
              (k) => !(k in dData) && !covered(k),
            );
            if (silent.length) {
              // eslint-disable-next-line no-console
              console.warn("[report-v2] 요청 섹션이 응답에 없음(무경고)", {
                item,
                silent,
              });
            }
          }
          yield put(
            actions.mergeReportSections(item, deferred, stageSections, false, seq),
          );
        } catch (deferredError) {
          // 🔴 이 단계만 실패로 남기고 **다음 단계는 계속 진행**한다.
          //    전역 실패(setGetOrderReportFailure)로 올리지 않는다 [S-64 ① ·
          //    우진 2026-08-14]: 1차·2차가 성공해 화면 90% 가 그려졌는데
          //    「고객 리포트 조회에 실패하였습니다」 alert 이 뜨면 성공한
          //    섹션까지 못 믿을 값으로 읽힌다. 실패 범위는 이 호출의
          //    sections 뿐이고, 그 카드가 사유+재시도를 보여 준다.
          yield put(
            actions.mergeReportSections(item, null, stageSections, true, seq),
          );
        }
      }
    } else if (locationId && organization?.locationId) {
      if (item === "marketingCustomer") {
        yield put(actions.receiveCustomerReport(report));
      } else if (item === "customer") {
        yield put(
          actions.receiveCustomerReport(
            (report.locations || {})[locationId] || {},
          ),
        );
      } else if (item === "taxRefund") {
        const itemReport = yield call(
          API.getPeriodReport,
          storeId,
          "taxRefundItems",
          start,
          end,
          locationId,
          null,
          null,
          brandId,
        );
        yield put(actions.receiveTrsReport(report));
        yield put(actions.receiveTrsItemReport(itemReport));
      } else {
        // salesSummary 의 subOrigins 는 top-level 에만 내려온다. locationId 조회라
        // top-level 이 이미 해당 지점 범위 집계이므로 지점 노드에 붙여서 저장.
        const locationReport = (report.locations || {})[locationId] || {};
        yield put(
          actions.receiveOrderReport(
            report?.subOrigins
              ? { ...locationReport, subOrigins: report.subOrigins }
              : locationReport,
          ),
        );
      }
    } else {
      if (item === "customer" || item === "marketingCustomer") {
        yield put(actions.receiveCustomerReport(report));
      } else if (item === "taxRefund") {
        const itemReport = yield call(
          API.getPeriodReport,
          storeId,
          "taxRefundItems",
          start,
          end,
          locationId,
          null,
          null,
          brandId,
        );
        yield put(actions.receiveTrsReport(report));
        yield put(actions.receiveTrsItemReport(itemReport));
      } else {
        yield put(actions.receiveOrderReport(report));
      }
    }
    if (!isSectionRetry) yield put(actions.setGetOrderReportSuccess());
  } catch (error) {
    // 섹션 재시도 실패는 전역 실패가 아니다 — 그 섹션만 실패로 남긴다
    if (isSectionRetry) {
      yield put(
        actions.mergeReportSections(item, null, explicitSections || [], true, seq),
      );
      return;
    }
    yield put(actions.setGetOrderReportFailure(error));
    if (item === "salesSummary") {
      Analytics.sendMixPanelEvent("error", {
        screen: "ReportHome",
        action: "failed_load",
        detail: error.errors && error.errors[0]?.message,
      });
    }
    Analytics.sendErrorReport(error, "getOrderReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetOrderReport() {
  yield takeEvery(actions.GET_ORDER_REPORT, getOrderReport);
}

// 고객 리포트 v2 — 지점별 신규/재방문 (item: customerVisit)
export function* getCustomerVisitReport({
  storeId,
  locationId,
  start,
  end,
  basis,
  brandId,
}) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "customerVisit",
      start,
      end,
      locationId,
      basis,
      null,
      brandId,
    );
    yield put(actions.receiveCustomerVisitReport(report));
    yield put(actions.setGetCustomerVisitReportSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerVisitReportFailure(error));
    Analytics.sendErrorReport(error, "getCustomerVisitReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetCustomerVisitReport() {
  yield takeEvery(actions.GET_CUSTOMER_VISIT_REPORT, getCustomerVisitReport);
}

// 고객 데모그래픽별 매출 (item: customerDemographic — 성별/연령/나라, pay 기준 전용)
export function* getCustomerDemographicReport({
  storeId,
  locationId,
  start,
  end,
  basis,
  brandId,
}) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "customerDemographic",
      start,
      end,
      locationId,
      basis,
      null,
      brandId,
    );
    yield put(actions.receiveCustomerDemographicReport(report));
    yield put(actions.setGetCustomerDemographicReportSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerDemographicReportFailure(error));
    Analytics.sendErrorReport(error, "getCustomerDemographicReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetCustomerDemographicReport() {
  yield takeEvery(
    actions.GET_CUSTOMER_DEMOGRAPHIC_REPORT,
    getCustomerDemographicReport,
  );
}

// 고객 데모그래픽별 인기 제품 (item: itemsDemographic — 나라/성별/연령, pay 기준 전용)
export function* getItemsDemographicReport({
  storeId,
  locationId,
  start,
  end,
  basis,
  brandId,
}) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "itemsDemographic",
      start,
      end,
      locationId,
      basis,
      null,
      brandId,
    );
    yield put(actions.receiveItemsDemographicReport(report));
    yield put(actions.setGetItemsDemographicReportSuccess());
  } catch (error) {
    yield put(actions.setGetItemsDemographicReportFailure(error));
    Analytics.sendErrorReport(error, "getItemsDemographicReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetItemsDemographicReport() {
  yield takeEvery(
    actions.GET_ITEMS_DEMOGRAPHIC_REPORT,
    getItemsDemographicReport,
  );
}

// 고객 리포트 v2 — 지점별 세그먼트 매출 (item: customerSales)
export function* getCustomerSalesReport({
  storeId,
  locationId,
  start,
  end,
  basis,
  brandId,
}) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "customerSales",
      start,
      end,
      locationId,
      basis,
      null,
      brandId,
    );
    yield put(actions.receiveCustomerSalesReport(report));
    yield put(actions.setGetCustomerSalesReportSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerSalesReportFailure(error));
    Analytics.sendErrorReport(error, "getCustomerSalesReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetCustomerSalesReport() {
  yield takeEvery(actions.GET_CUSTOMER_SALES_REPORT, getCustomerSalesReport);
}

// 고객 리포트 v2 — 고객 summary (기간 무관, 탭 진입 시 1회)
export function* getCustomerSummaryReport({ storeId, locationId, brandId }) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "customerSummary",
      null,
      null,
      locationId,
      null,
      null,
      brandId,
    );
    yield put(actions.receiveCustomerSummary(report));
    yield put(actions.setGetCustomerSummarySuccess());
  } catch (error) {
    yield put(actions.setGetCustomerSummaryFailure(error));
    Analytics.sendErrorReport(error, "getCustomerSummaryReport", {
      storeId,
      locationId,
    });
  }
}

export function* watchGetCustomerSummaryReport() {
  yield takeEvery(actions.GET_CUSTOMER_SUMMARY, getCustomerSummaryReport);
}

// 할인 리포트 v2 — discount summary/daily/breakdown/discountList
export function* getDiscountReport({
  storeId,
  locationId,
  start,
  end,
  basis,
  origin,
  brandId,
}) {
  try {
    // "offline" 은 서버에서 origin=null 과 동일 (기본값).
    // 실수로 문자열 "offline" 이 넘어오면 null 로 치환.
    const safeOrigin = origin === "offline" ? null : origin || null;
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "discount",
      start,
      end,
      locationId,
      basis,
      safeOrigin,
      brandId,
    );
    yield put(actions.receiveDiscountReport(report));
    yield put(actions.setGetDiscountReportSuccess());
  } catch (error) {
    yield put(actions.setGetDiscountReportFailure(error));
    Analytics.sendErrorReport(error, "getDiscountReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetDiscountReport() {
  yield takeEvery(actions.GET_DISCOUNT_REPORT, getDiscountReport);
}

// 할인 리포트 v2 — 쿠폰 인기제품 TOP5
export function* getDiscountTopProductsReport({
  storeId,
  locationId,
  start,
  end,
  basis,
  origin,
  couponId,
  brandId,
}) {
  try {
    const safeOrigin = origin === "offline" ? null : origin || null;
    // basis 위치에 couponId 만 전달 (basis 값이 섞이지 않도록)
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "discountTopProducts",
      start,
      end,
      locationId,
      couponId || null,
      safeOrigin,
      brandId,
    );
    yield put(actions.receiveDiscountTopProductsReport(report));
    yield put(actions.setGetDiscountTopProductsReportSuccess());
  } catch (error) {
    yield put(actions.setGetDiscountTopProductsReportFailure(error));
    Analytics.sendErrorReport(error, "getDiscountTopProductsReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetDiscountTopProductsReport() {
  yield takeEvery(
    actions.GET_DISCOUNT_TOP_PRODUCTS_REPORT,
    getDiscountTopProductsReport,
  );
}

export function* getChannelOrderReport({
  storeId,
  locationId,
  start,
  end,
  item,
  basis,
  origin,
}) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      item,
      start,
      end,
      null,
      basis,
      origin,
    );
    yield put(actions.receiveChannelOrderReport(report));
    yield put(actions.setChannelOrderReportSuccess());
  } catch (error) {
    yield put(actions.setChannelOrderReportFailure(error));
    Analytics.sendErrorReport(error, "getChannelOrderReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchChannelOrderReport() {
  yield takeEvery(actions.GET_CHANNEL_ORDER_REPORT, getChannelOrderReport);
}

export function* getDeliveryReport({
  storeId,
  locationId,
  start,
  end,
  item,
  basis,
  brandId,
  options,
}) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      item,
      start,
      end,
      locationId,
      basis,
      null,
      brandId,
      options,
    );
    const organization = yield select(reducers.getCurrentOrganization);
    // v2 응답은 공통 봉투라 v1 의 locations flatten 대상이 아니다 (언랩은 파서 진입점).
    if (isReportV2(item)) {
      yield put(actions.receiveDeliveryReport(report));
    } else if (locationId && organization?.locationId) {
      yield put(
        actions.receiveDeliveryReport(report.locations[locationId] || {}),
      );
    } else {
      yield put(actions.receiveDeliveryReport(report));
    }
    yield put(actions.setGetDeliveryReportSuccess());
  } catch (error) {
    yield put(actions.setGetDeliveryReportFailure(error));
    Analytics.sendErrorReport(error, "getDeliveryReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetDeliveryReport() {
  yield takeEvery(actions.GET_DELIVERY_REPORT, getDeliveryReport);
}

export function* getPointReport({
  storeId,
  locationId,
  start,
  end,
  basis,
  brandId,
}) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "point",
      start,
      end,
      locationId,
      basis,
      null,
      brandId,
    );
    const organization = yield select(reducers.getCurrentOrganization);
    if (locationId && organization?.locationId) {
      yield put(actions.receivePointReport(report.locations[locationId] || {}));
    } else {
      yield put(actions.receivePointReport(report));
    }
    yield put(actions.setGetPointReportSuccess());
  } catch (error) {
    yield put(actions.setGetPointReportFailure(error));
    Analytics.sendErrorReport(error, "getPointReport", {
      storeId,
      start,
      end,
    });
  }
}

export function* watchGetPointReport() {
  yield takeEvery(actions.GET_POINT_REPORT, getPointReport);
}

export function* getSubscriptionReport({ storeId, locationId, start, end }) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "subscription",
      start,
      end,
      locationId,
    );
    yield put(actions.receiveSubscriptionReport(report));
    yield put(actions.setGetSubscriptionReportSuccess());
  } catch (error) {
    yield put(actions.setGetSubscriptionReportFailure(error));
    Analytics.sendErrorReport(error, "getSubscriptionReport", {
      storeId,
      start,
      end,
    });
    Analytics.sendMixPanelEvent("error", {
      screen: "Subscription",
      action: "failed_load_report",
      detail: error.errors && error.errors[0]?.message,
    });
  }
}

export function* watchGetSubscriptionReport() {
  yield takeEvery(actions.GET_SUBSCRIPTION_REPORT, getSubscriptionReport);
}

export function* getInventoryItemReport({ storeId, locationId, start, end }) {
  try {
    if (storeId) {
      const items = yield _getItems(storeId);
      yield put(actions.receiveItems(items));
    }
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "inventoryItems",
      start,
      end,
      locationId,
      "pay",
    );
    yield put(actions.receiveInventoryReport(report));
    yield put(actions.setInventoryReportStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryReportStatusFailure(error));
  }
}

export function* watchGetInventoryItemReport() {
  yield takeEvery(actions.GET_INVENTORY_ITEM_REPORT, getInventoryItemReport);
}

export function* getInventoryDateReport({ storeId, locationId, start, end }) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "inventoryDates",
      start,
      end,
      locationId,
      "pay",
    );
    yield put(actions.receiveInventoryReport(report));
    yield put(actions.setInventoryReportStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryReportStatusFailure(error));
  }
}

export function* watchGetInventoryDateReport() {
  yield takeEvery(actions.GET_INVENTORY_DATE_REPORT, getInventoryDateReport);
}

export function* getInventoryTurnoverReport({
  storeId,
  locationId,
  start,
  end,
}) {
  try {
    if (storeId) {
      const items = yield _getItems(storeId);
      yield put(actions.receiveItems(items));
    }
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "inventoryTurnover",
      start,
      end,
      locationId,
      "pay",
    );
    yield put(actions.receiveInventoryReport(report));
    yield put(actions.setInventoryReportStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryReportStatusFailure(error));
  }
}

export function* watchGetInventoryTurnoverReport() {
  yield takeEvery(
    actions.GET_INVENTORY_TURNOVER_REPORT,
    getInventoryTurnoverReport,
  );
}

export function* getInventoryAssetReport({ storeId, locationId, start, end }) {
  try {
    const report = yield call(
      API.getPeriodReport,
      storeId,
      "inventoryAsset",
      start,
      end,
      locationId,
      "pay",
    );
    yield put(actions.receiveInventoryReport(report));
    yield put(actions.setInventoryReportStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryReportStatusFailure(error));
    Analytics.sendErrorReport(error, "getInventoryAssetReport", {
      storeId,
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetInventoryAssetReport() {
  yield takeEvery(actions.GET_INVENTORY_ASSET_REPORT, getInventoryAssetReport);
}

function* _getAllSettlements(storeId, locationId, start, end) {
  let _settlements = [];
  let data = {};
  if (locationId) {
    data = yield call(
      API.getSettlements,
      locationId,
      {
        between: [start, end],
      },
      500,
      "DESC",
      data.nextToken,
    );
    _settlements = [..._settlements, ...data.items];
  } else {
    data = yield call(
      API.getSettlementsByStore,
      storeId,
      {
        between: [start, end],
      },
      500,
      "DESC",
      data.nextToken,
    );
    _settlements = [..._settlements, ...data.items];
  }
  return _settlements;
}

export function* getSettlements({ storeId, locationId, start, end }) {
  try {
    const settlements = yield _getAllSettlements(
      storeId,
      locationId,
      start,
      end,
    );
    yield put(actions.receiveSettlements(settlements));
    yield put(actions.setSettlementsStatusSuccess());
  } catch (error) {
    yield put(actions.setSettlementsStatusFailure(error));
    Analytics.sendErrorReport(error, "getSettlements", { storeId, start, end });
  }
}

export function* watchGetSettlements() {
  yield takeEvery(actions.GET_SETTLEMENTS, getSettlements);
}

export function* getPurchaseOrders({
  storeId,
  locationId,
  page,
  size,
  filters,
  overide,
}) {
  try {
    const { items, count } = yield call(
      API.getPurchaseOrders,
      storeId,
      locationId,
      page,
      size,
      filters,
    );
    yield put(actions.receivePurchaseOrders(items, overide, count));
    yield put(actions.setPurchaseOrderStatusSuccess());
  } catch (error) {
    yield put(actions.setPurchaseOrderStatusFailure(error));
    Analytics.sendErrorReport(error, "getPurchaseOrders");
  }
}

export function* watchGetPurchaseOrders() {
  yield takeEvery(actions.GET_PURCHASE_ORDERS, getPurchaseOrders);
}

export function* getEmployeeByPurchaseOrderId({ purchaseOrderId }) {
  try {
    let purchaseOrdersById = yield select(reducers.getPurchaseOrdersById);
    const purchaseOrder = purchaseOrdersById[purchaseOrderId];
    if (purchaseOrder) {
      if (purchaseOrder.createdByEmployeeId) {
        const employeesById = yield select(reducers.getEmployeesById);
        if (!(purchaseOrder.createdByEmployeeId in employeesById)) {
          let employee = yield call(
            API.getEmployee,
            purchaseOrder.createdByEmployeeId,
          );
          yield put(actions.receiveEmployee(employee));
        } else {
          yield delay(100);
        }
      }
    }
    yield put(actions.setEmployeeStatusSuccess());
  } catch (error) {
    yield put(actions.setEmployeeStatusFailure(error));
    Analytics.sendErrorReport(error, "getEmployeeByPurchaseOrderId");
  }
}

export function* watchGetEmployeeByPurchaseOrderId() {
  yield takeEvery(
    actions.GET_EMPLOYEE_BY_PURCHASE_ORDER_ID,
    getEmployeeByPurchaseOrderId,
  );
}

export function* getPurchaseOrderById({ purchaseOrderId }) {
  try {
    let purchaseOrder = {};
    purchaseOrder = yield call(API.getPurchaseOrder, purchaseOrderId);
    yield put(actions.receivePurchaseOrder(purchaseOrder));
    yield put(actions.setPurchaseOrderStatusSuccess());
  } catch (error) {
    yield put(actions.setPurchaseOrderStatusFailure(error));
    Analytics.sendErrorReport(error, "getPurchaseOrderById");
  }
}

export function* watchGetPurchaseOrderById() {
  yield takeEvery(actions.GET_PURCHASE_ORDER_BY_ID, getPurchaseOrderById);
}

export function* createOrUpdatePurchaseOrder({ purchaseOrder, adjustments }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const locationId = yield select(reducers.getCurrentLocationId);
    const organization = yield select(reducers.getCurrentOrganization);
    let employee = yield select(reducers.getEmployee);
    let _purchaseOrder = _.cloneDeep(purchaseOrder);
    const origin = yield select(reducers.getRequestProgressEventOrigin);
    if (!_purchaseOrder.id) {
      _purchaseOrder.inventories = (_purchaseOrder.inventories || []).map(
        (inv) => ({
          ...inv,
          waitingQuantity: inv.quantity,
        }),
      );
      API.applyPurchaseOrder(
        {
          ..._purchaseOrder,
          locationId: purchaseOrder.locationId || locationId,
          createdLocationId: organization.locationId || null,
          storeId: storeId,
          status: "pending",
          createdByEmployeeId: employee.id,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          expectDate: purchaseOrder.expectDate
            ? new Date(purchaseOrder.expectDate).toISOString()
            : null,
        },
        adjustments,
        origin.name,
        origin.id,
      );
    } else {
      API.applyPurchaseOrder(
        {
          ..._purchaseOrder,
          updatedAt: new Date().toISOString(),
        },
        adjustments,
        origin.name,
        origin.id,
      );
    }
  } catch (error) {
    yield put(actions.setPurchaseOrderStatusFailure(error));
  }
}

export function* watchCreateOrUpdatePurchaseOrder() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_PURCHASE_ORDER,
    createOrUpdatePurchaseOrder,
  );
}

export function* deletePurchaseOrder({ purchaseOrder }) {
  try {
    const origin = yield select(reducers.getRequestProgressEventOrigin);
    API.cancelPurchaseOrder(purchaseOrder, origin.name, origin.id);
  } catch (error) {
    yield put(actions.setPurchaseOrderStatusFailure(error));
  }
}

export function* watchDeletePurchaseOrder() {
  yield takeEvery(actions.DELETE_PURCHASE_ORDER, deletePurchaseOrder);
}

export function* getSuppliers({ storeId, locationId }) {
  try {
    const data = yield call(API.getSuppliers, storeId, locationId);
    const suppliers = data.items;
    yield put(actions.receiveSuppliers(suppliers));
    yield put(actions.setSupplierStatusSuccess());
  } catch (error) {
    yield put(actions.setSupplierStatusFailure(error));
    Analytics.sendErrorReport(error, "getSuppliers");
  }
}

export function* watchGetSuppliers() {
  yield takeEvery(actions.GET_SUPPLIERS, getSuppliers);
}

export function* createOrUpdateSupplier({ supplier }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const locationId = yield select(reducers.getCurrentLocationId);
    const isMultiLocation = yield select(reducers.isMultiLocation);
    const isLocation = isMultiLocation && Authority.level() === "location";
    let _supplier = _.cloneDeep(supplier);

    if (!_supplier.id) {
      _supplier = {
        ..._supplier,
        id: generateId("spl"),
        storeId: storeId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        public: supplier.public || false,
      };
      if (isLocation) {
        _supplier = yield call(API.createSupplier, {
          ..._supplier,
          locationId,
        });
      } else {
        if (isMultiLocation) {
          _supplier = yield call(API.createSupplier, {
            ..._supplier,
          });
        } else {
          _supplier = yield call(API.createSupplier, _supplier);
        }
      }
    } else {
      _supplier = yield call(API.updateSupplier, {
        ...supplier,
        updatedAt: new Date().toISOString(),
      });
    }

    yield put(actions.receiveSupplier(_supplier));
    yield put(actions.setSupplierStatusSuccess());
  } catch (error) {
    yield put(actions.setSupplierStatusFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateSupplier", {
      supplier,
    });
  }
}

export function* watchCreateOrUpdateSupplier() {
  yield takeEvery(actions.CREATE_OR_UPDATE_SUPPLIER, createOrUpdateSupplier);
}

export function* deleteSupplier({ supplier }) {
  try {
    yield call(API.deleteSupplier, supplier.id);
    yield put(actions.receiveDeletedSupplier(supplier));
    yield put(actions.setSupplierStatusSuccess());
  } catch (error) {
    yield put(actions.setSupplierStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteSupplier", {
      supplier,
    });
  }
}

export function* watchDeleteSupplier() {
  yield takeEvery(actions.DELETE_SUPPLIER, deleteSupplier);
}

// ============================================================
// Brand — storeStatus 공용 (setStoreSuccess / setStoreFailure 재사용)
// ============================================================

export function* getBrands({ storeId }) {
  try {
    const data = yield call(API.listBrandsByStore, storeId);
    const brands = (data && data.items) || [];
    yield put(actions.receiveBrands(brands));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "getBrands", { storeId });
  }
}

export function* watchGetBrands() {
  yield takeEvery(actions.GET_BRANDS, getBrands);
}

export function* createOrUpdateBrand({ brand }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    let _brand = _.cloneDeep(brand);
    if (!_brand.id) {
      _brand = {
        ..._brand,
        id: generateId("brd"),
        storeId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      _brand = yield call(API.createBrand, _brand);
    } else {
      _brand = yield call(API.updateBrand, {
        ..._brand,
        updatedAt: new Date().toISOString(),
      });
    }
    yield put(actions.receiveBrand(_brand));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateBrand", { brand });
  }
}

export function* watchCreateOrUpdateBrand() {
  yield takeEvery(actions.CREATE_OR_UPDATE_BRAND, createOrUpdateBrand);
}

export function* deleteBrand({ brand }) {
  try {
    yield call(API.deleteBrand, brand.id);
    yield put(actions.receiveDeletedBrand(brand));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "deleteBrand", { brand });
  }
}

export function* watchDeleteBrand() {
  yield takeEvery(actions.DELETE_BRAND, deleteBrand);
}

export function* getSalesHistories({ storeId, locationId, start, end }) {
  try {
    let histories = [];
    if (locationId) {
      histories = yield call(
        API.getSalesHistoriesByLocation,
        locationId,
        start,
        end,
      );
    } else {
      histories = yield call(API.getSalesHistoriesByStore, storeId, start, end);
    }
    yield put(actions.setGetSalesHistoriesSuccess());
    yield put(actions.receiveSalesHistories(histories));
  } catch (error) {
    yield put(actions.setGetSalesHistoriesFailure(error));
    Analytics.sendErrorReport(error, "getSalesHistories", {
      storeId,
      start,
      end,
    });
  }
}

export function* watchGetSalesHistories() {
  yield takeEvery(actions.GET_SALES_HISTORIES, getSalesHistories);
}

export function* updateProducts({ products }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const locationId = yield select(reducers.getCurrentLocationId);
    const level = "public";
    yield call(
      Storage.put,
      `${storeId}/excel/products.json`,
      JSON.stringify(products),
      {
        level: level,
        contentType: "application/json",
      },
    );
    yield call(
      API.parseItemTemplate,
      storeId,
      `${level}/${storeId}/excel/products.json`,
      locationId,
      "json",
      "general",
    );
    yield call(
      API.syncItemTemplate,
      storeId,
      `${level}/${storeId}/excel/products.json`,
      "general",
    );
    const updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    console.log(error);
    if (
      !error?.errors ||
      error.errors[0].errorType !== "Lambda:ExecutionTimeoutException"
    ) {
      yield put(actions.syncItemTemplateFailure(error));
    }
    Analytics.sendErrorReport(error, "updateProducts", { products });
    Analytics.sendMixPanelEvent("error", {
      screen: "Product",
      action: "failed_save_in_excel_mode",
      detail: error.errors && error.errors[0]?.message,
    });
  }
}

export function* watchUpdateProducts() {
  yield takeEvery(actions.UPDATE_PRODUCTS, updateProducts);
}

export function* updateSkuLocations({ locations }) {
  try {
    for (let loc of locations) {
      yield call(API.updateSkuLocation, loc);
    }
    const storeId = yield select(reducers.getStoreId);
    const updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems, true));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "updateSkuLocations", { locations });
  }
}

export function* watchUpdateSkuLocations() {
  yield takeEvery(actions.UPDATE_SKU_LOCATIONS, updateSkuLocations);
}

function* _createOrUpdateProduct(product, transactions, modifierOrdering) {
  const storeId = yield select(reducers.getStoreId);
  let _product = _.cloneDeep({ ...product, type: "product" });
  // 서버 model.update 가 payload 의 updatedAt 을 우선 사용 — 클라이언트 기존값
  // 그대로 보내면 갱신 안 됨. strip 해서 server-side util.now() 적용되게 함.
  delete _product.updatedAt;
  delete _product.createdAt;
  let _skus = [];
  for (let sku of _product.skus || []) {
    _skus.push({
      ...sku,
      cost: _.isNil(sku.cost) || sku.cost === "" ? null : Number(sku.cost) || 0,
      stockAlertQty:
        _.isNil(sku.stockAlertQty) || sku.stockAlertQty === ""
          ? null
          : Number(sku.stockAlertQty) || 0,
    });
  }
  _product.skus = _skus;
  const options = modifierOrdering ? { modifierOrdering } : null;
  console.log(transactions);
  _product.storeId = storeId;

  if (!product.itemId) {
    _product = {
      ..._product,
      id: generateId("prod"),
    };
    _product = yield call(API.createItem, _product, options);
  } else {
    _product = yield call(API.updateItem, _product, options);
  }
  if (product.attributes) {
    let store = yield select(reducers.getStore);
    const attributesByName = (store.item?.attributes || []).reduce(
      (obj, attr) => {
        obj[attr.name] = attr;
        return obj;
      },
      {},
    );
    let isNeedSave = false;
    for (let attr of product.attributes) {
      if (!(attr.name in attributesByName)) {
        if (_.isEmpty(store.item)) {
          store.item = {};
        }
        if (_.isEmpty(store.item.attributes)) {
          store.item.attributes = [];
        }
        store.item.attributes = [
          ...store.item.attributes,
          { name: attr.name, type: attr.type },
        ];
        isNeedSave = true;
      }
    }
    if (isNeedSave) {
      const updatedStore = yield call(API.updateStore, store);
      yield put(actions.initStore(updatedStore));
    }
  }
  return _product;
}

function* _updateCatMenus(location, prodCategoryIds, prodId) {
  const _menusByCateId = (location.catMenus || []).reduce((obj, m) => {
    obj[m.parentId] = _.cloneDeep(m);
    return obj;
  }, {});

  (prodCategoryIds || []).forEach((cateId) => {
    if (cateId in _menusByCateId) {
      if (!(_menusByCateId[cateId].ids || []).includes(prodId)) {
        _menusByCateId[cateId].ids = [..._menusByCateId[cateId].ids, prodId];
      }
    }
  });

  Object.values(_menusByCateId).forEach((catMenu) => {
    if ((catMenu.ids || []).includes(prodId)) {
      if (!(prodCategoryIds || []).includes(catMenu.parentId)) {
        _menusByCateId[catMenu.parentId].ids = (
          _menusByCateId[catMenu.parentId].ids || []
        ).filter((id) => id !== prodId);
      }
    }
  });
  const catMenus = Object.values(_menusByCateId);
  const _location = { ...location, catMenus: catMenus };
  if (!_.isEqual(location, _location)) {
    yield call(API.updateLocation, _location);
  }
}

function* _updateMenuBoardConfigFromProduct(location, prodCategoryIds, prodId) {
  let menuBoard = _.cloneDeep(location.menu?.board);
  if ((menuBoard?.pages || []).length > 0) {
    let pagesByParentId = menuBoard.pages.reduce((obj, p) => {
      obj[p.parentId] = p;
      return obj;
    }, {});
    (prodCategoryIds || []).forEach((cateId) => {
      if (cateId in pagesByParentId) {
        let prodIdsInPage = (pagesByParentId[cateId].items || [])
          .filter((item) => item.type === "product")
          .map((item) => item.parentId);
        if (!(prodIdsInPage || []).includes(prodId)) {
          let itemsByPosition = (pagesByParentId[cateId]?.items || []).reduce(
            (obj, i) => {
              obj[i.position] = i;
              return obj;
            },
            {},
          );
          let nextPosition = 0;
          while (nextPosition in itemsByPosition) {
            nextPosition += 1;
          }
          let updatedMenuItem = {
            parentId: prodId,
            type: "product",
            position: nextPosition,
          };
          itemsByPosition[nextPosition] = updatedMenuItem;
          pagesByParentId[cateId] = {
            ...pagesByParentId[cateId],
            items: [...(pagesByParentId[cateId].items || []), updatedMenuItem],
          };
        }
      }
    });
    menuBoard.pages.forEach((page) => {
      let itemsByParentId = (page.items || [])
        .filter((item) => item.type === "product")
        .reduce((obj, i) => {
          obj[i.parentId] = i;
          return obj;
        }, {});
      if (prodId in itemsByParentId) {
        if (!(prodCategoryIds || []).includes(page.parentId)) {
          pagesByParentId[page.parentId] = {
            ...pagesByParentId[page.parentId],
            items: (pagesByParentId[page.parentId].items || []).filter(
              (item) => item.parentId !== prodId,
            ),
          };
          // pagesByParentId[page.parentId].items = (
          //   pagesByParentId[page.parentId].items || []
          // ).filter((item) => item.parentId !== prodId);
        }
      }
    });
    // prod 삭제시 favorite에서 제외
    if (!prodCategoryIds && prodId) {
      let favoriteItemsByParentId = (menuBoard.favorite || []).reduce(
        (obj, f) => {
          obj[f.parentId] = f;
          return obj;
        },
        {},
      );
      if (prodId in favoriteItemsByParentId) {
        menuBoard = {
          ...menuBoard,
          favorite: menuBoard.favorite.filter((fav) => fav.parentId !== prodId),
        };
      }
    }
    menuBoard = { ...menuBoard, pages: Object.values(pagesByParentId) };
    const _location = {
      ...location,
      menu: { ...(location.menu || {}), board: menuBoard || {} },
    };
    if (!_.isEqual(location, _location)) {
      yield call(API.updateLocation, _location);
    }
  }
}

export function* updateStoreMenuPlan({ menuPlanId }) {
  try {
    let store = yield select(reducers.getStore);
    const categoriesById = yield select(reducers.getCategoriesById);
    const productsById = yield select(reducers.getProductsById);
    const items = yield select(reducers.getItems);
    let menusByPlanId = (store.plans?.menus || []).reduce((obj, m) => {
      obj[m.id] = m;
      return obj;
    }, {});
    for (let x = 0; x < (menusByPlanId[menuPlanId]?.pages || []).length; x++) {
      // 카테고리가 삭제된 경우
      if (!(menusByPlanId[menuPlanId].pages[x].parentId in categoriesById)) {
        menusByPlanId[menuPlanId].pages = menusByPlanId[
          menuPlanId
        ].pages.filter(
          (page) =>
            page.parentId !== menusByPlanId[menuPlanId].pages[x].parentId,
        );
      } else {
        const category =
          categoriesById[menusByPlanId[menuPlanId].pages[x].parentId];
        const categoryProductIds = (items.categoryProducts || [])
          .filter((cp) => cp.categoryId === category.id)
          .map((item) => item.productId);
        const itemsByProductId = (
          menusByPlanId[menuPlanId].pages[x].items || []
        ).reduce((obj, i) => {
          obj[i.parentId] = i;
          return obj;
        }, {});
        // 카테고리 내에서 제품이 추가된 경우
        for (let prodId of categoryProductIds || []) {
          if (!(prodId in itemsByProductId)) {
            const itemsByPosition = (
              menusByPlanId[menuPlanId].pages[x].items || []
            ).reduce((obj, i) => {
              obj[i.position] = i;
              return obj;
            }, {});
            let nextPosition = 0;
            while (nextPosition in itemsByPosition) {
              nextPosition += 1;
            }
            let updatedMenuItem = {
              parentId: prodId,
              type: "product",
              position: nextPosition,
            };
            menusByPlanId[menuPlanId].pages[x] = {
              ...menusByPlanId[menuPlanId].pages[x],
              items: [
                ...(menusByPlanId[menuPlanId].pages[x].items || []),
                updatedMenuItem,
              ],
            };
          }
        }
        for (
          let y = 0;
          y < (menusByPlanId[menuPlanId].pages[x].items || []).length;
          y++
        ) {
          if (menusByPlanId[menuPlanId].pages[x].items[y].type === "product") {
            // 카테고리 내에서 제품이 삭제된 경우
            // 제품이 아예 삭제된 경우
            if (
              !(categoryProductIds || []).includes(
                menusByPlanId[menuPlanId].pages[x].items[y].parentId,
              ) ||
              !(
                menusByPlanId[menuPlanId].pages[x].items[y].parentId in
                productsById
              )
            ) {
              menusByPlanId[menuPlanId].pages[x] = {
                ...menusByPlanId[menuPlanId].pages[x],
                items: menusByPlanId[menuPlanId].pages[x].items
                  .filter(
                    (item) =>
                      item.parentId !==
                      menusByPlanId[menuPlanId].pages[x].items[y].parentId,
                  )
                  .map((i, index) => ({ ...i, position: index })),
              };
            }
          }
        }
      }
    }
    const updatedStore = yield call(API.updateStore, {
      ...store,
      plans: {
        ...(store.plans || {}),
        menus: Object.values(menusByPlanId),
      },
    });
    store = yield call(_getStoreWithLocations, store.id);
    yield put(actions.initStore(store));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateStoreMenuPlan", menuPlanId);
  }
}

export function* watchUpdateStoreMenuPlan() {
  yield takeEvery(actions.UPDATE_STORE_MENUPLAN, updateStoreMenuPlan);
}

export function* createOrUpdateProduct({
  product,
  histories,
  modifierOrdering,
}) {
  try {
    const isLegacy = yield select(reducers.isLegacyItems);
    const isMultiLocation = yield select(reducers.isMultiLocation);
    const storeId = yield select(reducers.getStoreId);
    const locations = yield call(_getLocations, storeId);
    if (!isLegacy) {
      let _product = _.cloneDeep(product);
      _product = yield _createOrUpdateProduct(
        product,
        histories,
        modifierOrdering,
      );
      if (isMultiLocation) {
        for (let i = 0; i < locations.length; i++) {
          const _location = _.cloneDeep(locations[i]);
          yield _updateCatMenus(_location, _product.categoryIds, _product.id);
          yield _updateMenuBoardConfigFromProduct(
            _location,
            _product.categoryIds,
            _product.id,
          );
        }
        const updatedStore = yield call(_getStoreWithLocations, storeId);
        yield put(actions.initStore(updatedStore));
      } else {
        for (let i = 0; i < locations.length; i++) {
          const _location = _.cloneDeep(locations[i]);
          yield _updateCatMenus(_location, _product.categoryIds, _product.id);
          yield _updateMenuBoardConfigFromProduct(
            _location,
            _product.categoryIds,
            _product.id,
          );
        }
        const updatedStore = yield call(_getStoreWithLocations, storeId);
        yield put(actions.initStore(updatedStore));
      }
      let updatedItems = yield _getItems(storeId);
      yield put(actions.receiveItems(updatedItems));
      yield put(actions.setUpdateItemsSuccess());
    } else {
      const items = yield select(reducers.getItems);
      let _items = _.cloneDeep(items);
      let _product = _.cloneDeep(product);

      let products = yield select(reducers.getProductsById);

      if (!_product.itemId) {
        _product.itemId = generateId("prod");
      }
      for (let idx in _product.skus) {
        if (!_product.skus[idx].itemId) {
          _product.skus[idx].itemId = generateId("sku");
        }
        if ((histories || []).length > 0) {
          for (let i = 0; i < histories.length; i++) {
            if (histories[i] && histories[i].id === _product.skus[idx].itemId) {
              if (
                products[_product.itemId] &&
                products[_product.itemId].skusById[histories[i].id]
              ) {
                let stock =
                  products[_product.itemId].skusById[histories[i].id].stock ||
                  0;
                _product.skus[idx].stock = stock + histories[i].quantity;
              } else {
                _product.skus[idx].stock = histories[i].quantity;
              }
            }
          }
        }
      }

      _items.productModifiers = (items.productModifiers || []).filter(
        (item) => item.productId !== _product.itemId,
      );
      _items.categoryProducts = (items.categoryProducts || []).filter(
        (item) => item.productId !== _product.itemId,
      );

      for (let categoryId of _product.categoryIds || []) {
        _items.categoryProducts = [
          ..._items.categoryProducts,
          { categoryId: categoryId, productId: _product.itemId },
        ];
      }
      delete _product.categoryIds;
      for (let idx = 0; idx < (_product.modifierIds || []).length; idx++) {
        const modifierId = _product.modifierIds[idx];
        const productModifier = {
          modifierId: modifierId,
          productId: _product.itemId,
        };
        if (modifierOrdering) {
          productModifier.position = idx + 1;
        }
        _items.productModifiers = [
          ..._items.productModifiers,
          productModifier,
        ];
      }
      delete _product.modifierIds;

      let _products = {};
      for (let prod of _items.products || []) {
        _products[prod.itemId] = prod;
      }
      _products[_product.itemId] = _product;
      _items.products = Object.values(_products);
      _items.updatedAt = new Date().toISOString();
      const updatedItems = yield call(API.updateItems, _items);
      if ((histories || []).length > 0) {
        console.log(histories);
        yield all(
          histories.map((hs) =>
            call(API.createInventoryHistory, {
              ...hs,
              createdAt: hs.createdAt || new Date().toISOString(),
            }),
          ),
        );
      }
      yield put(actions.receiveItems(updatedItems));
      yield put(actions.setUpdateItemsSuccess());
    }
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateProduct", {
      product,
      histories,
    });
  }
}

export function* watchCreateOrUpdateProduct() {
  yield takeEvery(actions.CREATE_OR_UPDATE_PRODUCT, createOrUpdateProduct);
}

/**
 * 제품 다건 복사 (figma 1498:51019).
 *
 * 서버에 복사/벌크생성 mutation 이 없어 createItem 을 순차 호출한다. 동시 실행하면
 * _createOrUpdateProduct 안의 store.item.attributes 병합(:3469-3498)이 서로 덮어쓰므로
 * 반드시 순차여야 한다. 1회 상한은 Constants.COPY_ITEMS_MAX.
 *
 * 원본은 리스트 응답이 아니라 LS_getProductsByIds 로 다시 읽는다 — 리스트 쿼리에는
 * 사이드메뉴/커스텀필드/지점 override 가 없어 그대로 복사하면 유실된다.
 *
 * 개별 실패는 건너뛰고 계속 진행한 뒤 성공/실패 개수를 리포트한다 (부분 성공 허용).
 *
 * createOrUpdateProduct 와 달리 isLegacyItems(구 items blob) 분기를 두지 않는다.
 * getItems 쿼리가 legacy 필드를 select 하지 않고 서버도 내려주지 않아 그 플래그는 항상 falsy —
 * 레거시 경로는 도달 불가능한 잔재다. (같은 화면의 일괄 삭제 deleteItems 도 분기가 없다.)
 */
export function* copyProducts({ productIds, options }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const ids = (productIds || []).filter(Boolean);
    if (ids.length === 0) {
      yield put(actions.setCopyProductsResult({ successCount: 0, failCount: 0 }));
      yield put(actions.setUpdateItemsSuccess());
      return;
    }
    const sources = yield call(API.getProductsByIds, storeId, ids);
    const sourceById = (sources || []).reduce((obj, p) => {
      if (p?.id) obj[p.id] = p;
      return obj;
    }, {});

    let successCount = 0;
    let failCount = 0;

    // 선택 순서를 유지해 복사본 생성 순서가 사용자가 고른 순서와 같게 한다.
    for (const id of ids) {
      const source = sourceById[id];
      if (!source) {
        failCount += 1;
        continue;
      }
      let created = null;
      try {
        const payload = buildCopiedProduct(source, {
          bulk: true,
          keepAvailable: options?.keepAvailable !== false,
          showSkuMgmt: options?.showSkuMgmt !== false,
          showBarcodeMgmt: options?.showBarcodeMgmt !== false,
        });
        // modifierOrdering=true — ProductEditor 저장 경로와 동일하게 사이드메뉴 순서를 보존한다.
        created = yield _createOrUpdateProduct(payload, null, true);
      } catch (error) {
        failCount += 1;
        Analytics.sendErrorReport(error, "copyProducts", { productId: id });
        continue;
      }
      // 제품 자체는 이미 생성됐다. 이후 메뉴판 반영이 실패해도 "복사 실패"로 세면
      // 사용자가 재시도해 중복 생성되므로(다건은 바코드가 공란이라 서버 중복 거부도 안 걸린다)
      // 성공은 생성 시점에 확정한다.
      successCount += 1;
      try {
        // 카테고리를 승계했으므로 POS 메뉴판에도 반영해야 한다 (단건 저장 경로와 동일 처리).
        // locations 는 반드시 매 제품마다 다시 읽는다 — _updateCatMenus/_updateMenuBoardConfigFromProduct
        // 는 location 의 catMenus/menu 를 통째로 덮어쓰므로(API.updateLocation), 루프 밖 스냅샷을
        // 재사용하면 직전 제품이 방금 추가된 항목을 다음 반복이 지운다.
        const locations = yield call(_getLocations, storeId);
        for (let i = 0; i < locations.length; i++) {
          const _location = _.cloneDeep(locations[i]);
          yield _updateCatMenus(_location, created.categoryIds, created.id);
          yield _updateMenuBoardConfigFromProduct(
            _location,
            created.categoryIds,
            created.id,
          );
        }
      } catch (error) {
        // 메뉴판 반영 실패는 제품 생성을 되돌리지 않는다. 로그만 남기고 계속한다.
        Analytics.sendErrorReport(error, "copyProducts.menu", { productId: id });
      }
    }

    const updatedStore = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(updatedStore));
    const updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setCopyProductsResult({ successCount, failCount }));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "copyProducts", { productIds });
    // 결과를 발행하지 않으면 화면이 아무 피드백 없이 멈춘다 (완료 팝업·선택해제·refetch 가 전부 결과 구독).
    yield put(
      actions.setCopyProductsResult({
        successCount: 0,
        failCount: (productIds || []).length,
      }),
    );
  }
}

export function* watchCopyProducts() {
  yield takeEvery(actions.COPY_PRODUCTS, copyProducts);
}

export function* deleteProduct({ id }) {
  try {
    const isMultiLocation = yield select(reducers.isMultiLocation);
    const storeId = yield select(reducers.getStoreId);
    const locations = yield call(_getLocations, storeId);
    const isLegacy = yield select(reducers.isLegacyItems);
    if (!isLegacy) {
      yield call(API.deleteItem, { id, type: "product" });

      if (isMultiLocation) {
        for (let i = 0; i < locations.length; i++) {
          const _location = _.cloneDeep(locations[i]);
          yield _updateCatMenus(_location, null, id);
          yield _updateMenuBoardConfigFromProduct(_location, null, id);
        }
        const updatedStore = yield call(_getStoreWithLocations, storeId);
        yield put(actions.initStore(updatedStore));
      } else {
        for (let i = 0; i < locations.length; i++) {
          let _location = _.cloneDeep(locations[i]);
          yield _updateCatMenus(_location, null, id);
          yield _updateMenuBoardConfigFromProduct(_location, null, id);
        }
        const updatedStore = yield call(_getStoreWithLocations, storeId);
        yield put(actions.initStore(updatedStore));
      }
      let updatedItems = yield _getItems(storeId);
      yield put(actions.setUpdateItemsSuccess());
      yield put(actions.receiveItems(updatedItems));
    } else {
      const items = yield select(reducers.getItems);
      let _items = _.cloneDeep(items);

      _items.productModifiers = (_items.productModifiers || []).filter(
        (item) => item.productId !== id,
      );
      _items.categoryProducts = (_items.categoryProducts || []).filter(
        (item) => item.productId !== id,
      );

      let _products = {};
      for (let prod of _items.products || []) {
        _products[prod.itemId] = prod;
      }
      delete _products[id];
      _items.products = Object.values(_products);
      _items.updatedAt = new Date().toISOString();
      const updatedItems = yield call(API.updateItems, _items);
      yield put(actions.setUpdateItemsSuccess());
      yield put(actions.receiveItems(updatedItems));
    }
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "deleteProduct", { id });
  }
}

export function* watchDeleteProduct() {
  yield takeEvery(actions.DELETE_PRODUCT, deleteProduct);
}

export function* adjustInventories({ adjustments }) {
  try {
    const origin = yield select(reducers.getRequestProgressEventOrigin);
    API.applyInventoryAdjustments(adjustments, origin.name, origin.id);
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
  }
}

export function* watchAdjustInventories() {
  yield takeEvery(actions.ADJUST_INVENTORIES, adjustInventories);
}

export function* createSkuInventory({ productId, skuId, inventory }) {
  try {
    const storeId = yield select(reducers.getStoreId);

    console.log(inventory);

    yield call(API.createInventory, {
      storeId,
      productId,
      skuId,
      inventory,
    });
    let updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "createSkuInventory", {
      productId,
      skuId,
      inventory,
    });
  }
}

export function* watchCreateSkuInventory() {
  yield takeEvery(actions.CREATE_SKU_INVENTORY, createSkuInventory);
}

export function* updateSkuInventory({ inventory }) {
  try {
    console.log(inventory);
    yield call(API.updateInventory, inventory);
    const storeId = yield select(reducers.getStoreId);
    let updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "updateSkuInventory", {
      inventory,
    });
  }
}

export function* watchUpdateSkuInventory() {
  yield takeEvery(actions.UPDATE_SKU_INVENTORY, updateSkuInventory);
}

export function* deleteSkuInventory({ inventory }) {
  try {
    console.log(inventory);
    yield call(API.deleteInventory, inventory);
    const storeId = yield select(reducers.getStoreId);
    let updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "deleteSkuInventory", {
      inventory,
    });
  }
}

export function* watchDeleteSkuInventory() {
  yield takeEvery(actions.DELETE_SKU_INVENTORY, deleteSkuInventory);
}

export function* updateSyncMenuBoard({ menu, locationInfo }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const categories = yield select(reducers.getCategoriesById);
    const products = yield select(reducers.getProductsById);
    const _locationInfo = yield call(API.getStoreLocation, locationInfo.id);
    const availableProductIds = new Set();
    for (const productInfo of Object.values(products)) {
      for (const sku of productInfo.skus) {
        for (const location of sku.locations) {
          if (location.id === locationInfo.id && location.available) {
            availableProductIds.add(productInfo.id);
            break;
          }
        }
      }
    }
    let _pages = [];

    menu.pages.forEach((page, idx) => {
      if (categories[page.parentId]) {
        _pages.push({
          ...page,
          index: _pages.length,
          title: categories[page.parentId].name,
        });
      }
    });
    const availableMenu = _pages.map((page) => ({
      ...page,
      items: page.items
        .filter((item) => availableProductIds.has(item.parentId))
        .map((item, idx) => ({
          ...item,
          position: idx,
        })),
    }));
    const _location = {
      ..._locationInfo,
      menuPlanId: menu.id,
      menu: {
        ..._locationInfo.menu,
        board: {
          ...(_locationInfo.menu?.board || {}),
          ...(_locationInfo.menu?.board?.favorite
            ? {
                favorite: _locationInfo.menu.board.favorite.filter((item) => {
                  if (item.type === "product") {
                    return availableProductIds.has(item.parentId);
                  }
                  return true;
                }),
              }
            : {}),
          pages: availableMenu,
        },
      },
      settings: {
        ..._locationInfo.settings,
        menuMode: "category",
      },
    };
    yield call(API.updateLocation, _location);
    const updatedStore = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(updatedStore));
    yield put(actions.syncMenuBoardSuccess());
  } catch (error) {
    yield put(actions.syncMenuBoardFailure(error));
    Analytics.sendErrorReport(error, "updateSyncMenuBoard", {
      menu,
      locationInfo,
    });
  }
}

export function* watchUpdateSyncMenuBoard() {
  yield takeEvery(actions.SYNC_MENU_BOARD, updateSyncMenuBoard);
}

function* _updateMenuPlanFromCategory(category, categoryId, isDelete) {
  const store = yield select(reducers.getStore);
  const _store = _.cloneDeep(store);
  const menus = yield select(reducers.getMenuPlans);
  let _menus = _.cloneDeep(menus);
  const _categoryId = categoryId || category.id;
  for (let x in _menus || []) {
    if (_menus[x].mode === "category") {
      for (let y in _menus[x].pages || []) {
        // 카테고리 삭제할때, menuPlan 삭제해주기
        if (isDelete) {
          _menus[x].pages = _menus[x].pages.filter(
            (page) => page.parentId !== _categoryId,
          );
        }
      }
    }
  }
  const updatedStore = yield call(API.updateStore, {
    ..._store,
    plans: {
      ...(_store.plans || {}),
      menus: _menus,
    },
  });
  yield put(actions.initStore(updatedStore));
}

function* _updateCatMenuPageFromCategory(category, forwardedLocation) {
  const location = yield select(reducers.getCurrentLocation);
  let _location = _.cloneDeep(forwardedLocation || location);
  let _cateMenus = (_location.catMenus || []).reduce((obj, m) => {
    obj[m.parentId] = m;
    return obj;
  }, {});
  if (category.id in _cateMenus) {
    // 카테고리의 product에서 추가된게 있는지 체크
    for (let prodId of category.productIds) {
      if (!(_cateMenus[category.id].ids || []).includes(prodId)) {
        _cateMenus[category.id].ids = [..._cateMenus[category.id].ids, prodId];
      }
    }
    // 카테고리에서 product 삭제된게 있는지 체크
    for (let id of _cateMenus[category.id].ids || []) {
      if (!category.productIds.includes(id)) {
        _cateMenus[category.id].ids = _cateMenus[category.id].ids.filter(
          (originalId) => id && originalId !== id,
        );
      }
    }
    _cateMenus[category.id] = {
      ..._cateMenus[category.id],
      title: category.name,
    };
    _location.catMenus = Object.values(_cateMenus);

    const updatedLocation = yield call(API.updateLocation, _location);
    const storeId = yield select(reducers.getStoreId);
    const updatedStore = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(updatedStore));
  }
}

function* _updateMenuBoardConfigFromCategory(category, forwardedLocation) {
  const location = yield select(reducers.getCurrentLocation);
  let _location = _.cloneDeep(forwardedLocation || location);
  let menuBoard = _.cloneDeep(_location.menu?.board);

  if ((menuBoard?.pages || []).length > 0) {
    let pagesByParentId = menuBoard.pages.reduce((obj, p) => {
      obj[p.parentId] = p;
      return obj;
    }, {});
    if (category.id in pagesByParentId) {
      for (let prodId of category.productIds) {
        let itemsByProductId = (pagesByParentId[category.id]?.items || [])
          .filter((item) => item.type === "product")
          .reduce((obj, i) => {
            obj[i.parentId] = i;
            return obj;
          }, {});
        let itemsByPosition = (
          pagesByParentId[category.id]?.items || []
        ).reduce((obj, i) => {
          obj[i.position] = i;
          return obj;
        }, {});
        if (!(prodId in itemsByProductId)) {
          let nextPosition = 0;
          while (nextPosition in itemsByPosition) {
            nextPosition += 1;
          }
          let updatedMenuItem = {
            parentId: prodId,
            type: "product",
            position: nextPosition,
          };
          itemsByPosition[nextPosition] = updatedMenuItem;
          pagesByParentId[category.id] = {
            ...pagesByParentId[category.id],
            items: [
              ...(pagesByParentId[category.id].items || []),
              updatedMenuItem,
            ],
          };
        }
      }
      for (let prodItem of (pagesByParentId[category.id]?.items || []).filter(
        (item) => item.type === "product",
      )) {
        if (!category.productIds.includes(prodItem.parentId)) {
          pagesByParentId[category.id].items = pagesByParentId[
            category.id
          ]?.items.filter((_item) => _item.parentId !== prodItem.parentId);
        }
      }
      pagesByParentId[category.id] = {
        ...pagesByParentId[category.id],
        title: category.name,
      };
    }
    menuBoard = { ...menuBoard, pages: Object.values(pagesByParentId) };
    _location = {
      ..._location,
      menu: { ...(_location.menu || {}), board: menuBoard || {} },
    };
    const updatedLocation = yield call(API.updateLocation, _location);
    const storeId = yield select(reducers.getStoreId);
    const updatedStore = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(updatedStore));
  }
}

export function* createOrUpdateCategory({ category }) {
  try {
    const isLegacy = yield select(reducers.isLegacyItems);
    const isMultiLocation = yield select(reducers.isMultiLocation);
    const storeId = yield select(reducers.getStoreId);
    const locations = yield call(_getLocations, storeId);
    if (!isLegacy) {
      const storeId = yield select(reducers.getStoreId);
      let _category = _.cloneDeep({
        ...category,
        type: "category",
        storeId,
        itemId: category.itemId || generateId("ct"),
      });
      if (!_category.id) {
        _category.id = _category.itemId;
      }
      // 서버 Category.update 가 payload 의 updatedAt 우선 사용 → strip
      delete _category.updatedAt;
      delete _category.createdAt;
      if (!category.itemId) {
        yield call(API.createItem, _category);
      } else {
        yield call(API.updateItem, _category);
      }
      if (isMultiLocation) {
        for (let i = 0; i < locations.length; i++) {
          const _location = _.cloneDeep(locations[i]);
          yield _updateCatMenuPageFromCategory(category, _location);
          yield _updateMenuBoardConfigFromCategory(category, _location);
        }
        yield _updateMenuPlanFromCategory(category);
      } else {
        for (let i = 0; i < locations.length; i++) {
          const _location = _.cloneDeep(locations[i]);
          yield _updateCatMenuPageFromCategory(category, _location);
          yield _updateMenuBoardConfigFromCategory(category, _location);
        }
      }

      const updatedItems = yield _getItems(storeId);
      yield put(actions.receiveItems(updatedItems));
      yield put(actions.setUpdateItemsSuccess());
    } else {
      const items = yield select(reducers.getItems);
      let _items = _.cloneDeep(items);
      let _category = _.cloneDeep(category);

      if (!_category.itemId) {
        _category.itemId = generateId("ct");
      }

      _items.categoryProducts = (_items.categoryProducts || []).filter(
        (item) => item.categoryId !== _category.itemId,
      );

      for (let productId of _category.productIds || []) {
        _items.categoryProducts = [
          ..._items.categoryProducts,
          { categoryId: _category.itemId, productId: productId },
        ];
      }
      delete _category.productIds;

      let _categories = {};
      for (let cate of _items.categories || []) {
        _categories[cate.itemId] = _.cloneDeep(cate);
      }
      _categories[_category.itemId] = _category;
      _items.categories = Object.values(_categories);
      _items.updatedAt = new Date().toISOString();
      const updatedItems = yield call(API.updateItems, _items);
      yield put(actions.receiveItems(updatedItems));
      yield put(actions.setUpdateItemsSuccess());
    }
  } catch (error) {
    console.log(error);
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateCategory", { category });
    if (!category.itemId) {
      Analytics.sendMixPanelEvent("error", {
        screen: "Category",
        action: "failed_create_category",
        detail: error.errors && error.errors[0]?.message,
      });
    } else {
      Analytics.sendMixPanelEvent("error", {
        screen: "Category",
        action: "failed_update_category",
        detail: error.errors && error.errors[0]?.message,
      });
    }
  }
}

export function* watchCreateOrUpdateCategory() {
  yield takeEvery(actions.CREATE_OR_UPDATE_CATEGORY, createOrUpdateCategory);
}

function* _deleteCatMenuPageFromCategory(catId, forwardedLocation) {
  const location = yield select(reducers.getCurrentLocation);
  let _location = _.cloneDeep(forwardedLocation || location);
  let _cateMenus = (_location.catMenus || []).reduce((obj, m) => {
    obj[m.parentId] = m;
    return obj;
  }, {});
  if (catId in _cateMenus) {
    _location.catMenus = _location.catMenus.filter(
      (page) => page.parentId !== catId,
    );
    _location.catMenus = _location.catMenus.map((menu, index) => ({
      ...menu,
      index,
    }));
    const updatedLocation = yield call(API.updateLocation, _location);
    const storeId = yield select(reducers.getStoreId);
    const updatedStore = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(updatedStore));
  }
}

function* _deleteMenuBoardConfigFromCategory(cateId, forwardedLocation) {
  const location = yield select(reducers.getCurrentLocation);
  let _location = _.cloneDeep(forwardedLocation || location);
  let menuBoard = _.cloneDeep(_location.menu?.board);

  if ((menuBoard?.pages || []).length > 0) {
    let pagesByParentId = menuBoard.pages.reduce((obj, p) => {
      obj[p.parentId] = p;
      return obj;
    }, {});
    if (cateId in pagesByParentId) {
      menuBoard.pages = menuBoard.pages.filter(
        (page) => page.parentId !== cateId,
      );
      menuBoard.pages = menuBoard.pages.map((page, _index) => ({
        ...page,
        index: _index,
      }));
      _location = {
        ..._location,
        menu: { ...(_location.menu || {}), board: menuBoard || {} },
      };
      const updatedLocation = yield call(API.updateLocation, _location);
      const storeId = yield select(reducers.getStoreId);
      const updatedStore = yield call(_getStoreWithLocations, storeId);
      yield put(actions.initStore(updatedStore));
    }
  }
}

export function* deleteCategory({ id }) {
  try {
    const isLegacy = yield select(reducers.isLegacyItems);
    const storeId = yield select(reducers.getStoreId);
    if (!isLegacy) {
      const isMultiLocation = yield select(reducers.isMultiLocation);
      const locations = yield call(_getLocations, storeId);

      yield call(API.deleteItem, { id, type: "category" });

      if (isMultiLocation) {
        for (let i = 0; i < locations.length; i++) {
          yield _deleteCatMenuPageFromCategory(id, locations[i]);
          yield _deleteMenuBoardConfigFromCategory(id, locations[i]);
        }
        let checkUpdateCatMenuPlan = yield _updateMenuPlanFromCategory(
          null,
          id,
          true,
        );
      } else {
        for (let i = 0; i < locations.length; i++) {
          yield _deleteCatMenuPageFromCategory(id, locations[i]);
          yield _deleteMenuBoardConfigFromCategory(id, locations[i]);
        }
      }
      const updatedItems = yield _getItems(storeId);
      yield put(actions.receiveItems(updatedItems));
      yield put(actions.setUpdateItemsSuccess());
    } else {
      const items = yield select(reducers.getItems);
      let _items = _.cloneDeep(items);

      _items.categoryProducts = (_items.categoryProducts || []).filter(
        (item) => item.categoryId !== id,
      );

      let _categories = {};
      for (let cate of _items.categories || []) {
        _categories[cate.itemId] = cate;
      }
      delete _categories[id];
      _items.categories = Object.values(_categories);
      _items.updatedAt = new Date().toISOString();
      const updatedItems = yield call(API.updateItems, _items);
      yield put(actions.setUpdateItemsSuccess());
      yield put(actions.receiveItems(updatedItems));
    }
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "deleteCategory", { id });
  }
}

export function* watchDeleteCategory() {
  yield takeEvery(actions.DELETE_CATEGORY, deleteCategory);
}

// ---- Equipment (비품) sagas ----
// 카테고리/모디파이어와 동일 패턴: mutation 호출 → _getItems 재호출하여 redux 전체 갱신.
export function* createOrUpdateEquipment({ equipment }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const payload = { ...equipment, storeId };
    // updateEquipment 는 id 필요. id 없으면 createEquipment.
    // inventories 는 서버가 무시하므로 input 에서 제거 (불필요 payload).
    delete payload.inventories;
    delete payload.itemId;
    delete payload.createdAt;
    delete payload.updatedAt;
    yield call(API.createOrUpdateEquipment, payload);
    const updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateEquipment", { equipment });
  }
}

export function* watchCreateOrUpdateEquipment() {
  yield takeEvery(actions.CREATE_OR_UPDATE_EQUIPMENT, createOrUpdateEquipment);
}

export function* deleteEquipment({ id }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    yield call(API.deleteEquipment, id);
    const updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "deleteEquipment", { id });
  }
}

export function* watchDeleteEquipment() {
  yield takeEvery(actions.DELETE_EQUIPMENT, deleteEquipment);
}

// ---- 비품 입출고 (EQUIPMENT_INOUT_SPEC §5/§6) ----
// 입고/사용처리/실사 — adjustEquipments. 조정 후 _getItems 재호출로 inventories 갱신.
// 저장 상태는 기존 itemStatus(setUpdateItems*) 재사용 → 모달이 itemStatus 전환 감지하여 close.
export function* adjustEquipments({ adjustments }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    yield call(API.adjustEquipments, adjustments);
    const updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "adjustEquipments", { adjustments });
  }
}

export function* watchAdjustEquipments() {
  yield takeEvery(actions.ADJUST_EQUIPMENTS, adjustEquipments);
}

// 이동·불출 — 비품은 1-step 즉시 처리. status="complete" 강제.
// suspect: 백엔드 applyEquipmentTransfer 가 "신규 생성 + complete" 1콜을 지원한다고 가정
//          (기존 설계는 생성=pending, pending→complete 2콜). 서버 완료분 동작 확인 필요.
export function* applyEquipmentTransfer({ history }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    // storeId 는 EquipmentTransferHistoryInput 에서 ID!(필수) — modal payload 에 없으므로 여기서 주입.
    const payload = { ...history, storeId, status: "complete" };
    yield call(API.applyEquipmentTransfer, payload);
    const updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "applyEquipmentTransfer", { history });
  }
}

export function* watchApplyEquipmentTransfer() {
  yield takeEvery(actions.APPLY_EQUIPMENT_TRANSFER, applyEquipmentTransfer);
}

// 비품 히스토리 조회.
// suspect: LS_getEquipmentHistories 응답 shape — { items, count } 가정 (ModelEquipmentHistoryConnection).
export function* getEquipmentHistories({ params }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const { page = 1, size = 30, filters = null } = params || {};
    const data = yield call(API.getEquipmentHistories, storeId, page, size, filters);
    const items = data?.items || data?.histories || [];
    const count = data?.count != null ? data.count : items.length;
    yield put(actions.receiveEquipmentHistories(items, count));
  } catch (error) {
    console.log(error);
    yield put(actions.setEquipmentHistoriesFailure(error));
    Analytics.sendErrorReport(error, "getEquipmentHistories", { params });
  }
}

export function* watchGetEquipmentHistories() {
  yield takeEvery(actions.GET_EQUIPMENT_HISTORIES, getEquipmentHistories);
}

// ---- 품목코드 체계 (ProductCodeRule) sagas ----
// createOrUpdateEquipment 패턴 동일: mutation 호출 → 목록 재조회로 redux 갱신.
// (equipment 는 _getItems 전체 재조회, 여기서는 전용 listProductCodeRulesByStore 재조회)
export function* getProductCodeRules() {
  try {
    const storeId = yield select(reducers.getStoreId);
    const data = yield call(API.getProductCodeRules, storeId);
    const items = data?.items || [];
    yield put(actions.receiveProductCodeRules(items, items.length));
  } catch (error) {
    console.log(error);
    // 조회 실패 — 빈 목록으로 처리해 설정 페이지가 깨지지 않게 함.
    yield put(actions.receiveProductCodeRules([], 0));
    Analytics.sendErrorReport(error, "getProductCodeRules", {});
  }
}

export function* watchGetProductCodeRules() {
  yield takeEvery(actions.GET_PRODUCT_CODE_RULES, getProductCodeRules);
}

export function* createOrUpdateProductCodeRule({ rule }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    // createProductCodeRule 은 storeId! 필수. updateProductCodeRule 도 storeId 허용.
    const payload = { ...rule, storeId };
    delete payload.createdAt;
    delete payload.updatedAt;
    yield call(API.createOrUpdateProductCodeRule, payload);
    // 저장 후 목록 재조회 → productCode.status isFetching true→false 전환으로 모달 close 감지.
    const data = yield call(API.getProductCodeRules, storeId);
    const items = data?.items || [];
    yield put(actions.receiveProductCodeRules(items, items.length));
  } catch (error) {
    console.log(error);
    yield put(actions.setProductCodeRulesFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateProductCodeRule", { rule });
  }
}

export function* watchCreateOrUpdateProductCodeRule() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_PRODUCT_CODE_RULE,
    createOrUpdateProductCodeRule,
  );
}

export function* deleteProductCodeRule({ ruleId }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    yield call(API.deleteProductCodeRule, ruleId);
    const data = yield call(API.getProductCodeRules, storeId);
    const items = data?.items || [];
    yield put(actions.receiveProductCodeRules(items, items.length));
  } catch (error) {
    console.log(error);
    yield put(actions.setProductCodeRulesFailure(error));
    Analytics.sendErrorReport(error, "deleteProductCodeRule", { ruleId });
  }
}

export function* watchDeleteProductCodeRule() {
  yield takeEvery(actions.DELETE_PRODUCT_CODE_RULE, deleteProductCodeRule);
}

// 제품/카테고리/사이드메뉴/비품 일괄 삭제 (비동기).
// API 호출만 — 서버가 RequestProgressEvent(func="DELETE_ITEMS") 를 발행하고,
// 진행/완료(progress=100)는 subscription → redux → ProgressComp 배너가 처리.
// 리스트 갱신은 각 리스트 페이지가 DELETE_ITEMS 이벤트 종료를 감지해 refetch.
export function* deleteItems({ itemType, itemIds, storeId }) {
  try {
    const sid = storeId || (yield select(reducers.getStoreId));
    yield call(API.deleteItems, itemType, itemIds, sid);
  } catch (error) {
    console.log(error);
    Analytics.sendErrorReport(error, "deleteItems", { itemType });
  }
}

export function* watchDeleteItems() {
  yield takeEvery(actions.DELETE_ITEMS, deleteItems);
}

function* _deleteModifierSoldoutIds(id) {
  const modifiersById = yield select(reducers.getModifiersById);
  const currentModifierSkuIds = modifiersById[id].skus.map((sku) => sku.id);
  const currentLocationId = yield select(reducers.getCurrentLocationId);
  const currentLocation = yield call(API.getStoreLocation, currentLocationId);
  const prevSoldoutIds = currentLocation.modifierSoldoutIds || [];
  const shouldUpdateLocation = currentModifierSkuIds.filter((skuId) =>
    prevSoldoutIds.includes(skuId),
  ).length;

  if (shouldUpdateLocation) {
    const updatedModifierSoldoutIds = diffSet(
      prevSoldoutIds,
      currentModifierSkuIds,
    );
    currentLocation.modifierSoldoutIds = updatedModifierSoldoutIds;
    yield put(actions.updateLocation(currentLocation));
  }
}

function* _updateModifierSoldoutIds(id, currentSoldoutIds) {
  const currentLocationId = yield select(reducers.getCurrentLocationId);
  const currentLocation = yield call(API.getStoreLocation, currentLocationId);
  const totalSoldoutIds = currentLocation.modifierSoldoutIds || [];
  const modifiersById = yield select(reducers.getModifiersById);
  const prevSkuIds = modifiersById[id]?.skus.map((sku) => sku.id);
  const prevSoldoutIds = prevSkuIds.filter((skuId) =>
    totalSoldoutIds.includes(skuId),
  );
  const shouldUpdateLocation = !_.isEqual(prevSoldoutIds, currentSoldoutIds);
  if (shouldUpdateLocation) {
    const prevSoldoutIdsWithoutPrevSku = diffSet(
      totalSoldoutIds,
      prevSoldoutIds,
    );
    const updatedModifierSoldoutIds = [
      ...prevSoldoutIdsWithoutPrevSku,
      ...currentSoldoutIds,
    ];
    currentLocation.modifierSoldoutIds = updatedModifierSoldoutIds;
    yield put(actions.updateLocation(currentLocation));
  }
}

export function* createOrUpdateModifier({ modifier, soldoutIds, soldoutOnly }) {
  try {
    const isLegacy = yield select(reducers.isLegacyItems);
    if (!isLegacy) {
      const storeId = yield select(reducers.getStoreId);
      // 외지점 readonly — soldoutIds 만 currentLocation 에 적용. API.updateItem skip.
      if (soldoutOnly) {
        yield call(_updateModifierSoldoutIds, modifier.id, soldoutIds || []);
        const updatedItems = yield _getItems(storeId);
        yield put(actions.setUpdateItemsSuccess());
        yield put(actions.receiveItems(updatedItems));
        return;
      }
      let _modifier = _.cloneDeep({
        ...modifier,
        type: "modifier",
        storeId: storeId,
        itemId: modifier.itemId || generateId("mod"),
      });
      if (!_modifier.id) {
        _modifier.id = _modifier.itemId;
      }
      // 서버 Modifier.update 가 payload 의 updatedAt 우선 사용 → strip
      delete _modifier.updatedAt;
      delete _modifier.createdAt;
      if (!modifier.itemId) {
        yield call(API.createItem, _modifier);
      } else {
        yield call(API.updateItem, _modifier);
        yield call(_updateModifierSoldoutIds, modifier.id, soldoutIds);
      }
      const updatedItems = yield _getItems(storeId);
      yield put(actions.setUpdateItemsSuccess());
      yield put(actions.receiveItems(updatedItems));
    } else {
      const items = yield select(reducers.getItems);
      let _items = _.cloneDeep(items);
      let _modifier = _.cloneDeep(modifier);

      if (!_modifier.itemId) {
        _modifier.itemId = generateId("mod");
      }
      for (let idx in _modifier.skus) {
        if (!_modifier.skus[idx].itemId) {
          _modifier.skus[idx].itemId = generateId("sku");
        }
      }

      _items.productModifiers = (_items.productModifiers || []).filter(
        (item) => item.modifierId !== _modifier.itemId,
      );

      for (let productId of _modifier.productIds || []) {
        _items.productModifiers = [
          ..._items.productModifiers,
          { modifierId: _modifier.itemId, productId: productId },
        ];
      }
      delete _modifier.productIds;

      let _modifiers = {};
      for (let mod of _items.modifiers || []) {
        _modifiers[mod.itemId] = mod;
      }
      _modifiers[_modifier.itemId] = _modifier;
      _items.modifiers = Object.values(_modifiers);
      _items.updatedAt = new Date().toISOString();
      const updatedItems = yield call(API.updateItems, _items);
      yield put(actions.setUpdateItemsSuccess());
      yield put(actions.receiveItems(updatedItems));
    }
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateModifier", { modifier });
    if (!modifier.itemId) {
      Analytics.sendMixPanelEvent("error", {
        screen: "Modifier",
        action: "failed_create_modifier",
        detail: error.errors && error.errors[0]?.message,
      });
    } else {
      Analytics.sendMixPanelEvent("error", {
        screen: "Modifier",
        action: "failed_update_modifier",
        detail: error.errors && error.errors[0]?.message,
      });
    }
  }
}

export function* watchCreateOrUpdateModifier() {
  yield takeEvery(actions.CREATE_OR_UPDATE_MODIFIER, createOrUpdateModifier);
}

export function* deleteModifier({ id }) {
  try {
    const isLegacy = yield select(reducers.isLegacyItems);
    if (!isLegacy) {
      yield call(API.deleteItem, { id, type: "modifier" });
      yield call(_deleteModifierSoldoutIds, id);
      const storeId = yield select(reducers.getStoreId);
      const updatedItems = yield _getItems(storeId);
      yield put(actions.setUpdateItemsSuccess());
      yield put(actions.receiveItems(updatedItems));
    } else {
      const items = yield select(reducers.getItems);
      let _items = _.cloneDeep(items);

      _items.productModifiers = (_items.productModifiers || []).filter(
        (item) => item.productId !== id,
      );

      let _modifiers = {};
      for (let mod of _items.modifiers || []) {
        _modifiers[mod.itemId] = mod;
      }
      delete _modifiers[id];
      _items.modifiers = Object.values(_modifiers);
      _items.updatedAt = new Date().toISOString();
      const updatedItems = yield call(API.updateItems, _items);
      yield put(actions.setUpdateItemsSuccess());
      yield put(actions.receiveItems(updatedItems));
    }
  } catch (error) {
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendErrorReport(error, "deleteModifier", { id });
  }
}

export function* watchDeleteModifier() {
  yield takeEvery(actions.DELETE_MODIFIER, deleteModifier);
}

export function* getOrderWithTransactions({ orderId }) {
  try {
    const order = yield call(API.getOrder, orderId);
    yield put(actions.receiveOrder(order));
    yield put(actions.setGetOrderSuccess());
  } catch (error) {
    yield put(actions.setGetOrderFailure(error));
    Analytics.sendErrorReport(error, "getOrderWithTransactions", { orderId });
  }
}

export function* watchGetOrderWithTransactions() {
  yield takeEvery(
    actions.GET_ORDER_WITH_TRANSACTIONS,
    getOrderWithTransactions,
  );
}

export function* getOrder({ orderId, withStore }) {
  try {
    let a = new Date().getTime();
    const order = yield call(API.getOrder, orderId);
    yield put(actions.receiveOrder(order));
    if (withStore) {
      const customerId = order.customerId || order.receiptCustomerId;
      if (customerId) {
        let [items, storeCustomer] = yield all([
          _getItems(order.storeId),
          _getStoreCustomerWithTransaction(order.storeId, customerId),
        ]);
        yield put(actions.receiveItems(items));
        yield put(actions.receiveStore(storeCustomer));
      }
    }
    console.log("TIME(getOrder) TOTAL: " + (new Date().getTime() - a) / 1000);
    yield put(actions.setGetOrderSuccess());
  } catch (error) {
    yield put(actions.setGetOrderFailure(error));
    Analytics.sendErrorReport(error, "getOrder", { orderId, withStore });
  }
}

export function* watchGetOrder() {
  yield takeEvery(actions.GET_ORDER, getOrder);
}

export function* getOrdersByStore({ storeId, start, end, nextToken }) {
  try {
    // TODO: Change from locationId to StoreId
    // query(with GSI) is can be pagination, scan(without GSI) is cannot
    const locationId = yield select(reducers.getCurrentLocationId);
    let data = yield call(
      API.getOrdersByLocation,
      locationId,
      { between: [start, end] },
      nextToken,
      15,
    );

    if (nextToken) {
      const orders = yield select(reducers.getOrdersById);
      data = {
        ...data,
        items: [...data.items, ...Object.values(orders)],
      };
    }
    yield put(actions.receiveOrders(data));
    yield put(actions.setGetOrdersSuccess);
  } catch (error) {
    yield put(actions.setGetOrderFailure(error));
    Analytics.sendErrorReport(error, "getOrdersByStore", {
      storeId,
      start,
      end,
      nextToken,
    });
  }
}

export function* watchGetOrdersByStore() {
  yield takeEvery(actions.GET_ORDERS_BY_STORE, getOrdersByStore);
}

export function* getOrdersByCustomer({ customerId, limit, nextToken }) {
  try {
    let byCustomer = yield call(
      API.getOrdersByCustomer,
      customerId,
      nextToken,
      limit,
    );
    let byReceiptCustomer = {};
    let receiptCustomerData = [];
    do {
      byReceiptCustomer = yield call(
        API.getOrdersByReciptCustomer,
        customerId,
        limit,
        byReceiptCustomer.nextToken,
      );
      receiptCustomerData = [
        ...receiptCustomerData,
        ...byReceiptCustomer.items,
      ];
    } while (byReceiptCustomer.nextToken);
    let data = {
      items: [...byCustomer.items, ...receiptCustomerData],
      nextToken: byCustomer.nextToken,
    };

    if (nextToken) {
      const orders = yield select(reducers.getOrdersById);
      data = {
        ...data,
        items: [...data.items, ...Object.values(orders)],
      };
    }
    yield put(actions.receiveOrders(data));
    yield put(actions.setGetOrdersSuccess());
  } catch (error) {
    yield put(actions.setGetOrderFailure(error));
    Analytics.sendErrorReport(error, "getOrdersByCustomer", {
      customerId,
      limit,
      nextToken,
    });
  }
}

export function* watchGetOrdersByCustomer() {
  yield takeEvery(actions.GET_ORDERS_BY_CUSTOMER, getOrdersByCustomer);
}

export function* getShippingOrders({ locationId, start, end, filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    let orders = [];
    let data = {};
    let shippingData = {};
    if (locationId) {
      do {
        data = yield call(
          API.getOrdersByLocation,
          locationId,
          { between: [start, end] },
          data.nextToken,
          100,
          {
            retailOption: { eq: "shipping" },
            state: { ne: "returned" },
            // 외부 채널(cafe24·imweb·app)은 발주/CS조회 화면이 담당 — 구 배송주문 관리에서 제외
            and: [{ origin: { ne: "cafe24" } }, { origin: { ne: "imweb" } }, { origin: { ne: "app" } }],
          },
        );
        orders = [...orders, ...(data.items || [])];
      } while (data.nextToken);
      do {
        shippingData = yield call(
          API.getShippingOrdersByShippingLocation,
          locationId,
          { between: [start, end] },
          shippingData.nextToken,
          100,
          {
            retailOption: { eq: "shipping" },
            state: { ne: "returned" },
            // 외부 채널(cafe24·imweb·app)은 발주/CS조회 화면이 담당 — 구 배송주문 관리에서 제외
            and: [{ origin: { ne: "cafe24" } }, { origin: { ne: "imweb" } }, { origin: { ne: "app" } }],
          },
        );
        orders = [...orders, ...(shippingData.items || [])];
      } while (shippingData.nextToken);
    } else {
      do {
        data = yield call(
          API.getOrdersByStore,
          storeId,
          { between: [start, end] },
          data.nextToken,
          100,
          {
            retailOption: { eq: "shipping" },
            state: { ne: "returned" },
            // 외부 채널(cafe24·imweb·app)은 발주/CS조회 화면이 담당 — 구 배송주문 관리에서 제외
            and: [{ origin: { ne: "cafe24" } }, { origin: { ne: "imweb" } }, { origin: { ne: "app" } }],
          },
        );
        orders = [...orders, ...(data.items || [])];
      } while (data.nextToken);
    }
    yield put(actions.receiveOrders({ items: orders }));
    yield put(actions.setGetOrdersSuccess);
  } catch (error) {
    yield put(actions.setGetOrderFailure(error));
    Analytics.sendErrorReport(error, "getShippingOrders", {
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetShippingOrders() {
  yield takeEvery(actions.GET_SHIPPING_ORDERS, getShippingOrders);
}

function* _loadOrderHistoriesData(storeId, page, size, filters) {
  try {
    let data = yield call(API.getOrders, storeId, page, size, filters);
    yield put(actions.receiveOrderHistories(data));
    yield put(actions.setGetOrderHistoriesSuccess());
  } catch (error) {
    yield put(actions.setGetOrderHistoriesFailure(error));
    Analytics.sendErrorReport(error, "loadOrderHistoriesData", {
      storeId,
      page,
      size,
      filters,
    });
  }
}

function* _loadOrderHistoriesStats(storeId, filters) {
  try {
    yield put(actions.requestOrderHistoriesStats());
    let stats = yield call(API.getOrdersStats, storeId, filters);
    yield put(actions.receiveOrderHistoriesStats(stats));
  } catch (error) {
    yield put(actions.failOrderHistoriesStats(error));
    Analytics.sendErrorReport(error, "loadOrderHistoriesStats", {
      storeId,
      filters,
    });
  }
}

export function* getAllOrderHistories({ storeId, page, size, filters }) {
  // 페이지 데이터와 stats 를 병렬 fork — 페이지 먼저 보이고 stats 는 나중 채워짐.
  let _filters = _.cloneDeep(filters || []);
  yield fork(_loadOrderHistoriesData, storeId, page, size, _filters);
  yield fork(_loadOrderHistoriesStats, storeId, _filters);
}

export function* watchGetAllOrderHistories() {
  yield takeEvery(actions.GET_ALL_ORDER_HISTORIES, getAllOrderHistories);
}

export function* _getLegacyInventoryHistories(filtersByKey) {
  const start = filtersByKey["createdAt"].value[0];
  const end = filtersByKey["createdAt"].value[1];
  const _search = filtersByKey["search"]?.value;
  const reason = filtersByKey["reason"]?.value;
  let locationFilter = filtersByKey["locationId"]?.value;
  if ((locationFilter || []).length === 1) {
    locationFilter = [locationFilter];
  }

  const limitEnd = Constants.STOCK_HISTORY_UPDATE_DATE;
  const _end = moment(limitEnd).isAfter(moment(end)) ? end : limitEnd;

  let data = {};
  let histories = [];

  const isMulti = yield select(reducers.isMultiLocation);
  const storeId = yield select(reducers.getStoreId);
  const locationId = yield select(reducers.getCurrentLocationId);
  const skusById = yield select(reducers.getSkusById);
  const prodsById = yield select(reducers.getProductsById);
  if (isMulti && Authority.level() === "location") {
    do {
      data = yield call(
        API.getInventoryHistoriesByLocation,
        locationId,
        { between: [start, _end] },
        data.nextToken,
        300,
      );
      histories = [...histories, ...data.items];
    } while (data.nextToken);
  } else {
    do {
      data = yield call(
        API.getInventoryHistoriesByStore,
        storeId,
        { between: [start, _end] },
        data.nextToken,
        300,
      );
      histories = [...histories, ...data.items];
    } while (data.nextToken);

    if ((locationFilter || []).length > 0) {
      histories = histories.filter((his) =>
        locationFilter.includes(his.locationId),
      );
    }
  }
  if (_search) {
    histories = histories.filter((his) => {
      let mixedIds = his.id.split("sku_");
      const skuId = "sku_" + mixedIds[1];
      const sku = skusById[skuId];
      const prodName = prodsById[sku?.parentId]?.name || "삭제된 제품";

      return (
        (prodName || "").includes(_search) ||
        (sku?.unit || "").includes(_search) ||
        (sku?.model || "").includes(_search)
      );
    });
  }
  if (reason) {
    histories = histories.filter((his) => reason.includes(his.reason));
  }

  return histories;
}

export function* getInventoryHistoriesByStore({
  page,
  size,
  historyType,
  filters,
  override,
}) {
  try {
    const storeId = yield select(reducers.getStoreId);

    const filtersByKey = filters.reduce((obj, filter) => {
      obj[filter.key] = filter;
      return obj;
    }, {});
    const start = filtersByKey["createdAt"].value[0];
    const isLegacy = isLegacyInventoryHistories(start, historyType);
    let legacyHistories = [];
    let totalItems = [];
    let totalCount = 0;
    if (isLegacy) {
      legacyHistories = yield call(_getLegacyInventoryHistories, filtersByKey);
    }

    let { items, count } = yield call(
      API.getInventoryHistories,
      storeId,
      page,
      size,
      historyType === "group" ? "group" : "single",
      filters,
    );

    const employeesById = yield select(reducers.getEmployeesById);
    let employeeIds = new Set();
    for (let item of items) {
      if (item.employeeId) {
        employeeIds.add(item.employeeId);
      }
    }
    employeeIds = Array.from(employeeIds).filter(
      (id) => !(id in employeesById),
    );
    let employees = yield all(
      employeeIds.map((id) => call(API.getEmployee, id)),
    );
    employees = employees.filter((emp) => emp);
    if (employees.length > 0) {
      yield put(actions.receiveEmployees(employees));
    }

    totalItems = [
      ...items,
      ...legacyHistories
        .slice(0, page * size - count)
        .map((his) => ({ ...his, isLegacy: true })),
    ];
    totalCount = count + legacyHistories.length;
    yield put(
      actions.receiveInventoryHistories(totalItems, totalCount, override),
    );
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "getInventoryHistoriesByStore", {
      page,
      size,
      historyType,
      filters,
      override,
    });
  }
}

export function* watchGetInventoryHistoriesByStore() {
  yield takeEvery(
    actions.GET_INVENTORY_HISTORIES_BY_STORE,
    getInventoryHistoriesByStore,
  );
}

export function* getInventoryHistoriesByGroupId({
  history,
  page,
  size,
  filters,
}) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const { items, count } = yield call(
      API.getInventoryHistories,
      storeId,
      page,
      size,
      "single",
      filters,
    );
    let _history = {
      ...history,
      histories: items || [],
    };
    yield put(actions.receiveInventoryHistory(_history, count));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "getInventoryHistoriesByGroupId", {
      history,
      page,
      size,
      filters,
    });
  }
}

export function* watchGetInventoryHistoriesByGroupId() {
  yield takeEvery(
    actions.GET_INVENTORY_HISTORIES_BY_GROUP_ID,
    getInventoryHistoriesByGroupId,
  );
}

export function* getInventoryHistoriesByLocation({ locationId, start, end }) {
  try {
    let data = {};
    let histories = [];
    do {
      data = yield call(
        API.getInventoryHistoriesByLocation,
        locationId,
        { between: [start, end] },
        data.nextToken,
        300,
      );
      histories = [...histories, ...data.items];
    } while (data.nextToken);
    yield put(actions.receiveInventoryHistories(histories));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "getInventoryHistoriesByLocation", {
      locationId,
      start,
      end,
    });
  }
}

export function* watchGetInventoryHistoriesByLocation() {
  yield takeEvery(
    actions.GET_INVENTORY_HISTORIES_BY_LOCATION,
    getInventoryHistoriesByLocation,
  );
}

export function* getInventoryTransferHistories({
  page,
  size,
  filters,
  override,
}) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const { items, count } = yield call(
      API.getInventoryTransferHistories,
      storeId,
      page,
      size,
      filters,
    );
    const employeesById = yield select(reducers.getEmployeesById);
    let employeeIds = new Set();
    for (let item of items) {
      if (item.employeeId) {
        employeeIds.add(item.employeeId);
      }
    }
    employeeIds = Array.from(employeeIds).filter(
      (id) => !(id in employeesById),
    );
    let employees = yield all(
      employeeIds.map((id) => call(API.getEmployee, id)),
    );
    employees = employees.filter((emp) => emp);
    if (employees.length > 0) {
      yield put(actions.receiveEmployees(employees));
    }
    yield put(
      actions.receiveInventoryTransferHistories(items, count, override),
    );
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "getInventoryTransferHistories", {
      page,
      size,
      filters,
      override,
    });
  }
}

export function* watchGetInventoryTransferHistories() {
  yield takeEvery(
    actions.GET_INVENTORY_TRANSFER_HISTORIES,
    getInventoryTransferHistories,
  );
}

export function* getInventoryNewRequests({ page, size, filters, override }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const { items, count } = yield call(
      API.getInventoryRequestHistories,
      storeId,
      page,
      size,
      filters,
    );
    const employeesById = yield select(reducers.getEmployeesById);
    let employeeIds = new Set();
    for (const item of items) {
      [
        item.requestEmployeeId,
        item.confirmEmployeeId,
        item.shipEmployeeId,
        item.completeEmployeeId,
      ].forEach((id) => {
        if (id) employeeIds.add(id);
      });
    }
    employeeIds = Array.from(employeeIds).filter(
      (id) => !(id in employeesById),
    );
    let employees = yield all(
      employeeIds.map((id) => call(API.getEmployee, id)),
    );
    employees = employees.filter((emp) => emp);
    if (employees.length > 0) {
      yield put(actions.receiveEmployees(employees));
    }
    yield put(actions.receiveNewRequests(items, count, override));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendMixPanelEvent("error", {
      screen: "NewStockRequest",
      action: "failed_load",
      error: error,
    });
    Analytics.sendErrorReport(error, "getInventoryNewRequests", {
      page,
      size,
      filters,
      override,
    });
  }
}

export function* watchGetInventoryNewRequests() {
  yield takeEvery(actions.GET_NEW_REQUESTS, getInventoryNewRequests);
}

export function* getSkuInventoryHistories({
  startDate,
  endDate,
  skuId,
  filters,
}) {
  try {
    const storeId = yield select(reducers.getStoreId);
    let { items, prevQuantity } = yield call(
      API.getSkuInventoryHistories,
      startDate,
      endDate,
      skuId,
      storeId,
      filters,
    );

    let list = items;
    let prev = prevQuantity;
    yield put(actions.receiveSkuInventoryHistories(list, prev));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "getSkuInventoryHistories", {
      startDate,
      endDate,
      skuId,
      filters,
    });
  }
}

export function* watchGetSkuInventoryHistories() {
  yield takeEvery(
    actions.GET_SKU_INVENTORY_HISTORIES,
    getSkuInventoryHistories,
  );
}

export function* getInventoryHistorySummary({ startDate, endDate, filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    let list = yield call(
      API.getInventoryHistorySummary,
      storeId,
      startDate,
      endDate,
      filters,
    );
    yield put(actions.receiveInventoryHistorySummary(list));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "getInventoryHistorySummary", {
      startDate,
      endDate,
      filters,
    });
  }
}

export function* watchGetInventoryHistorySummary() {
  yield takeEvery(
    actions.GET_INVENTORY_HISTORY_SUMMARY,
    getInventoryHistorySummary,
  );
}

export function* completeInventoryTransferHistory({ history }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const employee = yield select(reducers.getEmployee);
    let _history = _.cloneDeep(history);

    _history = {
      ..._history,
      storeId,
      employeeId: _history.employeeId || employee.id,
    };
    const origin = yield select(reducers.getRequestProgressEventOrigin);
    API.applyInventoryTransferHistory(_history, origin.name, origin.id);
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
  }
}

export function* watchCompleteInventoryTransferHistory() {
  yield takeEvery(
    actions.COMPLETE_INVENTORY_TRANSFER_HISTORY,
    completeInventoryTransferHistory,
  );
}

export function* deleteInventoryTransferHistory({ historyId }) {
  try {
    const inventoryTransferHistoriesById = yield select(
      reducers.getInventoryTransferHistoriesById,
    );
    const transferHistory = inventoryTransferHistoriesById[historyId];
    const origin = yield select(reducers.getRequestProgressEventOrigin);
    API.cancelInventoryTransferHistory(transferHistory, origin.name, origin.id);
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
  }
}

export function* watchDeleteInventoryTransferHistory() {
  yield takeEvery(
    actions.DELETE_INVENTORY_TRANSFER_HISTORY,
    deleteInventoryTransferHistory,
  );
}

export function* getCountHistories({ page, size, filters, override }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const { items, count } = yield call(
      API.getInventoryCountHistories,
      storeId,
      page,
      size,
      filters,
    );
    const employeesById = yield select(reducers.getEmployeesById);
    let employeeIds = new Set();
    for (let item of items) {
      if (item.employeeId) {
        employeeIds.add(item.employeeId);
      }
    }
    employeeIds = Array.from(employeeIds).filter(
      (id) => !(id in employeesById),
    );
    let employees = yield all(
      employeeIds.map((id) => call(API.getEmployee, id)),
    );
    employees = employees.filter((emp) => emp);
    if (employees.length > 0) {
      yield put(actions.receiveEmployees(employees));
    }
    yield put(actions.receiveCountHistories(items, count, override));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "getCountHistories", {
      page,
      size,
      filters,
    });
  }
}

export function* watchGetCountHistories() {
  yield takeEvery(actions.GET_INVENTORY_COUNT_HISTORIES, getCountHistories);
}

export function* getPointTransactions({ storeId, start, end, nextToken }) {
  try {
    let data = yield call(API.getPointTransactions, nextToken, 1000, {
      storeId: { eq: storeId },
      createdAt: { between: [start, end] },
    });
    if (nextToken) {
      const trans = yield select(reducers.getPointTransactions);
      data = {
        ...data,
        items: [...data.items, ...trans.items],
      };
    }
    yield put(actions.receivePointTransactions(data));
    yield put(actions.setGetPointTransactionsSuccess());
  } catch (error) {
    yield put(actions.setGetPointTransactionsFailure(error));
    Analytics.sendErrorReport(error, "getPointTransactions", {
      storeId,
      start,
      end,
      nextToken,
    });
  }
}

export function* watchGetPointTransactions() {
  yield takeEvery(actions.GET_POINT_TRANSACTIONS, getPointTransactions);
}

function* _getCorporateCustomerByStore(storeId, page, size, filters, search) {
  const { items, count } = yield call(
    API.getCustomersWithCount,
    storeId,
    page,
    size,
    null,
    filters,
    search,
  );
  let customers = [];
  for (let customer of items) {
    customers = [
      ...customers,
      {
        ...customer,
        ...customer.stats,
      },
    ];
  }
  for (let idx in customers) {
    let [orders, points, credits] = yield all([
      _getOrdersByStore(storeId, customers[idx].id),
      _getPointTransactionsByStore(storeId, customers[idx].id),
      _getCreditTransactionsByStore(storeId, customers[idx].id),
    ]);
    customers[idx] = {
      ...customers[idx],
      credits: { items: credits },
      points: { items: points },
      orders: { items: orders },
    };
  }
  return {
    list: customers,
    count: count,
  };
}

export function* getProviderCustomer({
  storeId,
  locationId,
  filter,
  withOrders,
}) {
  try {
    let _list = [];
    const customers = yield call(API.getProviderCustomers, storeId, null, [
      filter,
    ]);
    _list = customers;
    // provider가 'custom'인 경우 customerId 없음
    if (_list.length > 0 && _list[0].customerId) {
      const registerdCustomer = yield call(
        API.getCustomers,
        _list[0].customerId,
        storeId,
      );
      if ((registerdCustomer || []).length > 0) {
        _list[0] = {
          ..._list[0],
          registeredCustomer: registerdCustomer[0],
        };
      }
    }
    if (_list.length > 0 && withOrders) {
      const storeProvider = yield select(reducers.getStorePolicyProvider);
      const providerKey = _list[0].provider || storeProvider?.key;
      let orders = [];
      let data = {};
      do {
        data = yield call(
          API.getOrdersByStoreCustomer,
          _list[0].id,
          {
            providerCustomerId: {
              eq: _list[0].providerCustomerId,
            },
            ...(providerKey
              ? {
                  customerProvider: {
                    eq: providerKey,
                  },
                  origin: { ne: providerKey },
                }
              : {}),
          },
          50,
          data.nextToken,
        );
        orders = [...orders, ...data.items];
      } while (data.nextToken);
      orders = orders.filter(
        (order) => !locationId || order.orderLocationId === locationId,
      );
      _list[0] = {
        ..._list[0],
        orders: orders,
      };
    }
    yield put(actions.receiveRetrievedCustomer(_list[0]));
    yield put(actions.setGetCustomerSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setGetCustomersFailure(error));
    Analytics.sendErrorReport(error, "getCustomers", { storeId });
  }
}

export function* watchGetProviderCustomer() {
  yield takeEvery(actions.GET_PROVIDER_CUSTOMER, getProviderCustomer);
}

export function* getCustomers({
  page,
  size,
  sort,
  filters,
  search,
  filterLocationId,
  override,
}) {
  try {
    const storeId = yield select(reducers.getStoreId);
    let { items, count } = yield call(
      API.getCustomerList,
      storeId,
      page,
      size,
      sort,
      filters,
      search,
      filterLocationId,
    );
    items = items.filter((i) => i.id);
    let list = items;
    yield put(actions.receiveCustomers(list, count, override));

    yield put(actions.setGetCustomerSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setGetCustomersFailure(error));
    Analytics.sendErrorReport(error, "getCustomers", {
      page,
      size,
      filters,
      search,
      override,
    });
  }
}

export function* watchGetCustomers() {
  yield takeEvery(actions.GET_CUSTOMERS, getCustomers);
}

export function* searchProviderCustomer({ searchText }) {
  const isOutsourced = yield select(reducers.isOutsourcedCustomer);
  const storeId = yield select(reducers.getStoreId);
  try {
    if (isOutsourced) {
      const locationId = yield select(reducers.getCurrentLocationId);
      const customers = yield call(
        API.getProviderCustomers,
        storeId,
        locationId,
        [
          {
            key: "phone",
            value: searchText,
            operation: "eq",
          },
        ],
      );
      yield put(actions.receiveCustomers(customers, customers.length, true));
      yield put(actions.setGetCustomerSuccess());
    }
  } catch (error) {
    console.log("Failed to search provider customer", error);
    yield put(actions.setGetCustomersFailure(error));
    Analytics.sendErrorReport(error, "searchProviderCustomer", {
      searchText,
      storeId,
    });
  }
}

export function* watchSearchProviderCustomer() {
  yield takeEvery(actions.SEARCH_PROVIDER_CUSTOMER, searchProviderCustomer);
}

export function* getCustomersByTag({
  storeId,
  page,
  size,
  sort,
  filters,
  search,
  override,
}) {
  try {
    const { items, count } = yield call(
      API.getCustomerList,
      storeId,
      page,
      size,
      sort,
      filters,
      search,
    );
    yield put(actions.receiveCustomersByTag(items, count, override));
    yield put(actions.setCustomerTagStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCustomerTagStatusFailure(error));
    Analytics.sendErrorReport(error, "getCustomersByTag", { storeId });
  }
}

export function* watchGetCustomersByTag() {
  yield takeEvery(actions.GET_CUSTOMERS_BY_TAG, getCustomersByTag);
}

export function* getCorporateCustomers({
  storeId,
  page,
  size,
  filters,
  search,
}) {
  try {
    const { list, count } = yield _getCorporateCustomerByStore(
      storeId,
      page,
      size,
      filters,
      search,
    );
    yield put(actions.receiveCustomers(list, count));
    yield put(actions.setGetCustomerSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setGetCustomersFailure(error));
    Analytics.sendErrorReport(error, "getCorporateCustomers", { storeId });
  }
}

export function* watchGetCorporateCustomers() {
  yield takeEvery(actions.GET_CORPORATE_CUSTOMERS, getCorporateCustomers);
}

function* _getCustomer(id, locationId, withStat) {
  let customer = yield call(API.getCustomer, id);
  let customerWithPoint = null;
  let stats = {};
  let pointData = {};
  let pointTransactions = [];
  let orderData = {};
  let orders = [];
  let creditData = {};
  let creditTransactions = [];
  if (!customer.provider) {
    customerWithPoint = yield call(
      API.getLocationCustomer,
      id,
      customer.locationId,
    );
    const customerWithPointByLocationId = (
      customerWithPoint.locations || []
    ).reduce((obj, loc) => {
      obj[loc.id] = loc;
      return obj;
    }, {});
    customer.locations = customer.locations.map((loc) => ({
      ...loc,
      point: customerWithPointByLocationId[loc.id]?.point || {},
      credit: customerWithPointByLocationId[loc.id]?.credit || {},
    }));
  } else {
    customerWithPoint = {
      point: customer.point,
      credit: null,
    };
  }
  if (withStat) {
    do {
      orderData = yield call(
        API.getOrdersByStoreCustomer,
        id,
        locationId ? { orderLocationId: { eq: locationId } } : null,
        50,
        orderData.nextToken,
      );
      orders = [...orders, ...orderData.items];
    } while (orderData.nextToken);

    // 연동 고객(카페24·아임웹)의 포인트 원장은 자사몰이다 — DDB PointTransaction 에는
    // 지연 적립 예약(pending)·상쇄 취소(canceled)까지 섞여 있어 그대로 보이면 안 된다.
    // 포인트 탭이 자사몰 원장(LS_getProviderPointTransactions)으로 채운 목록을 유지하고,
    // 자체 등록(custom)·비연동 고객만 DDB 를 읽는다(재동기화·정보 수정 저장 경로)
    const isOutsourced = yield select(reducers.isOutsourcedCustomer);
    if (isOutsourced && customer.provider && customer.provider !== "custom") {
      const prev = yield select(reducers.getRetrievedCustomer);
      pointTransactions = prev?.id === customer.id ? prev.points || [] : [];
    } else {
      do {
        pointData = yield call(
          API.getPointTransactionByStoreCustomer,
          id,
          locationId
            ? { locationId: { eq: locationId }, point: { gt: 0 } }
            : null,
          50,
          pointData.nextToken,
        );
        pointTransactions = [...pointTransactions, ...pointData.items];
      } while (pointData.nextToken);
    }

    do {
      creditData = yield call(
        API.getCreditTransactionsByStoreCustomer,
        id,
        creditData.nextToken,
        {
          ...(locationId ? { locationId: { eq: locationId } } : {}),
        },
        500,
      );
      creditTransactions = [...creditTransactions, ...creditData.items];
    } while (creditData.nextToken);

    let customerLocation = (customer.locations || []).filter(
      (loc) => loc.id === locationId,
    )[0];
    stats = locationId ? customerLocation.stats : customer.stats;
  }
  const _customer = {
    ...customer,
    ...stats,
    credits: _.reverse(_.sortBy(creditTransactions, "createdAt")).filter(
      (p) => !locationId || p.locationId === locationId,
    ),
    points: _.reverse(_.sortBy(pointTransactions, "createdAt")).filter(
      (p) => !locationId || p.locationId === locationId,
    ),

    orders: _.reverse(_.sortBy(orders, "createdAt")).filter(
      (o) => !locationId || o.orderLocationId === locationId,
    ),
    point: customerWithPoint?.point,
    credit: customerWithPoint?.credit,
  };
  return _customer;
}

export function* getCustomer({ id, locationId }) {
  try {
    const customer = yield _getCustomer(id, locationId);
    yield put(actions.receiveCustomer(customer));
    yield put(actions.setGetCustomerSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerFailure(error));
    Analytics.sendErrorReport(error, "getCustomer", { id });
  }
}

export function* watchGetCustomer() {
  yield takeEvery(actions.GET_CUSTOMER, getCustomer);
}

export function* retrieveCustomer({ id, withStat }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    let customer = null;
    customer = yield _getCustomer(id, organization.locationId, withStat);
    yield put(actions.receiveRetrievedCustomer(customer));
    yield put(actions.setGetCustomerSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerFailure(error));
    Analytics.sendErrorReport(error, "retrieveCustomer", { id });
  }
}

export function* watchRetrieveCustomer() {
  yield takeEvery(actions.RETRIEVE_CUSTOMER, retrieveCustomer);
}

export function* retrieveCustomerWithPoint({ start, end }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    let customer = yield select(reducers.getRetrievedCustomer);
    const storeId = yield select(reducers.getStoreId);
    const isOutsourced = yield select(reducers.isOutsourcedCustomer);
    let pointData = {};
    let pointTransactions = [];
    if (isOutsourced && customer.provider !== "custom") {
      pointData = yield call(
        API.getProviderCustomerPointTransactions,
        storeId,
        customer.providerCustomerId,
        start,
        end,
      );
      pointTransactions = [...pointTransactions, ...pointData.items];
    } else {
      do {
        pointData = yield call(
          API.getPointTransactionByStoreCustomer,
          customer.id,
          organization.locationId
            ? {
                locationId: { eq: organization.locationId },
                point: { gt: 0 },
              }
            : null,
          50,
          pointData.nextToken,
          start,
          end,
        );
        pointTransactions = [...pointTransactions, ...pointData.items];
      } while (pointData.nextToken);
    }
    customer = {
      ...customer,
      points: _.reverse(_.sortBy(pointTransactions, "createdAt")).filter(
        (p) =>
          !organization.locationId || p.locationId === organization.locationId,
      ),
    };
    yield put(actions.receiveRetrievedCustomer(customer));
    yield put(actions.setGetCustomerSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerFailure(error));
    Analytics.sendErrorReport(error, "retrieveCustomerWithPoint");
  }
}

export function* watchRetrieveCustomerWithPoint() {
  yield takeEvery(
    actions.RETRIEVE_CUSTOMER_WITH_POINT,
    retrieveCustomerWithPoint,
  );
}

export function* retrieveCustomerWithOrder({ start, end }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    let customer = yield select(reducers.getRetrievedCustomer);
    let orderData = {};
    let orders = [];
    do {
      orderData = yield call(
        API.getOrdersByStoreCustomer,
        customer.id,
        null,
        50,
        orderData.nextToken,
        start,
        end,
      );
      orders = [...orders, ...orderData.items];
    } while (orderData.nextToken);
    customer = {
      ...customer,
      orders: _.reverse(_.sortBy(orders, "createdAt")).filter(
        (o) =>
          !organization.locationId ||
          o.orderLocationId === organization.locationId,
      ),
    };
    yield put(actions.receiveRetrievedCustomer(customer));
    yield put(actions.setGetCustomerSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerFailure(error));
    Analytics.sendErrorReport(error, "retrieveCustomerWithOrder");
  }
}

export function* watchRetrieveCustomerWithOrder() {
  yield takeEvery(
    actions.RETRIEVE_CUSTOMER_WITH_ORDER,
    retrieveCustomerWithOrder,
  );
}

export function* retrieveCustomerWithCredit({ start, end }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    let customer = yield select(reducers.getRetrievedCustomer);
    let creditData = {};
    let creditTransactions = [];
    if (customer.customerId) {
      do {
        creditData = yield call(
          API.getCreditTransactionsByStoreCustomer,
          customer.id,
          creditData.nextToken,
          {
            ...(organization.locationId
              ? { locationId: { eq: organization.locationId } }
              : {}),
          },
          500,
          start,
          end,
        );
        creditTransactions = [...creditTransactions, ...creditData.items];
      } while (creditData.nextToken);
    }
    customer = {
      ...customer,
      credits: _.reverse(_.sortBy(creditTransactions, "createdAt")).filter(
        (p) =>
          !organization.locationId || p.locationId === organization.locationId,
      ),
    };
    yield put(actions.receiveRetrievedCustomer(customer));
    yield put(actions.setGetCustomerSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerFailure(error));
    Analytics.sendErrorReport(error, "retrieveCustomerWithCredit");
  }
}

export function* watchRetrieveCustomerWithCredit() {
  yield takeEvery(
    actions.RETRIEVE_CUSTOMER_WITH_CREDIT,
    retrieveCustomerWithCredit,
  );
}

export function* retrieveCustomerWithCoupon({}) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    let customer = yield select(reducers.getRetrievedCustomer);
    const isOutsourced = yield select(reducers.isOutsourcedCustomer);
    const providerCustomerId = customer.providerCustomerId;
    let externalTransactions = [];
    let locationCouponTransactions = [];
    let locationCouponData = {};
    if (isOutsourced) {
      const globalCustomerId = customer.customerId;
      let storeCustomer = null;
      externalTransactions = yield call(
        API.getProviderCouponTransactions,
        organization.storeId,
        providerCustomerId,
      );
      externalTransactions = externalTransactions.filter(
        (tran) => !dateFns.isBefore(new Date(tran.expiredDate), new Date()),
      );
      if (globalCustomerId) {
        // NOTE: storeCustomer의 바코드쿠폰 transaction 조회
        const data = yield call(
          API.getLocationCustomersByOriginId,
          globalCustomerId,
          { eq: organization.storeId },
          null,
          null,
          {
            _delta: {
              ne: "DELETE",
            },
            status: {
              eq: "registered",
            },
          },
        );
        if (data.items.length > 0) {
          storeCustomer = data.items[0];
        }
        if (!_.isEmpty(storeCustomer)) {
          do {
            locationCouponData = yield call(
              API.getCouponTransactionsByStoreCustomer,
              storeCustomer.id,
              "barcode",
              {
                state: {
                  ne: "used",
                },
              },
              100,
              locationCouponData.nextToken,
            );
            locationCouponTransactions = [
              ...locationCouponTransactions,
              ...locationCouponData.items,
            ];
          } while (locationCouponData.nextToken);
          locationCouponTransactions = locationCouponTransactions.filter(
            (tran) =>
              ((tran.publishableLocationIds || []).length === 0 ||
                tran.publishableLocationIds.includes(
                  organization.locationId,
                )) &&
              !dateFns.isBefore(new Date(tran.expiredDate), new Date()),
          );
        }
      }
    } else {
      do {
        locationCouponData = yield call(
          API.getCouponTransactionsByStoreCustomer,
          customer.id,
          "barcode",
          {
            state: {
              ne: "used",
            },
          },
          100,
          locationCouponData.nextToken,
        );
        locationCouponTransactions = [
          ...locationCouponTransactions,
          ...locationCouponData.items,
        ];
      } while (locationCouponData.nextToken);
      locationCouponTransactions = locationCouponTransactions.filter(
        (tran) =>
          ((tran.publishableLocationIds || []).length === 0 ||
            tran.publishableLocationIds.includes(organization.locationId)) &&
          !dateFns.isBefore(new Date(tran.expiredDate), new Date()),
      );
    }
    customer = {
      ...customer,
      couponTransactions: [
        ...(locationCouponTransactions || []),
        ...(externalTransactions || []),
      ],
    };
    yield put(actions.receiveRetrievedCustomer(customer));
    yield put(actions.setGetCustomerSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerFailure(error));
    Analytics.sendErrorReport(error, "retrieveCustomerWithCoupon");
  }
}

export function* watchRetrieveCustomerWithCoupon() {
  yield takeEvery(
    actions.RETRIEVE_CUSTOMER_WITH_COUPON,
    retrieveCustomerWithCoupon,
  );
}

export function* updateOrder({ order }) {
  try {
    let _order = {
      ..._.cloneDeep(order),
      orderCustomerId: order.customer?.id || null,
    };
    if (_order.hasOwnProperty("_note")) {
      if (_order.note !== _order._note) {
        _order.note = _order._note;
      }
      delete _order._note;
      if (!_order.note) {
        _order.note = null;
      }
    }
    for (let i in _order.charges || []) {
      for (let j in _order.charges[i].refunds || []) {
        delete _order.charges[i].refunds[j].returnItems;
      }
    }
    delete _order.customer;
    const updatedOrder = yield call(API.updateOrder, _order);
    yield put(actions.receiveOrder(updatedOrder));
    yield put(actions.setUpdateOrderSuccess());
  } catch (error) {
    yield put(actions.setUpdateOrderFailure(error));
    Analytics.sendErrorReport(error, "updateOrder", { order });
  }
}

export function* watchUpdateOrder() {
  yield takeEvery(actions.UPDATE_ORDER, updateOrder);
}

export function* updateOrders({ orders }) {
  try {
    let _orders = (orders || []).map((o) => ({
      ...o,
      orderCustomerId: o.customer?.id || null,
    }));

    let updatedOrders = yield all(
      _orders.map((order) => call(API.updateOrder, order)),
    );
    let existOrdersById = yield select(reducers.getOrdersById);
    let itemsById = (updatedOrders || []).reduce((obj, o) => {
      obj[o.id] = o;
      return obj;
    }, existOrdersById);

    yield put(actions.receiveOrders({ items: Object.values(itemsById) }));
    yield put(actions.setUpdateOrderSuccess());
  } catch (error) {
    yield put(actions.setUpdateOrderFailure(error));
    Analytics.sendErrorReport(error, "updateOrders");
  }
}

export function* watchUpdateOrders() {
  yield takeEvery(actions.UPDATE_ORDERS, updateOrders);
}

export function* createOrUpdateCustomerGroup({ group }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    if (!group.id) {
      yield call(API.createCustomerGroup, {
        ...group,
        id: generateId("cg"),
        storeGroupsId: storeId,
      });
    } else {
      yield call(API.updateCustomerGroup, {
        ...group,
        updatedAt: new Date().toISOString(),
      });
    }
    const groups = yield call(
      _getCustomerGroup,
      storeId,
      group.locationGroupsId,
    );
    yield put(actions.receiveCustomerGroups(groups));
    yield put(actions.setGetCustomerGroupsSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setGetCustomerGroupsFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateCustomerGroup", { group });
  }
}

export function* watchCreateOrUpdateCustomerGroup() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_CUSTOMER_GROUP,
    createOrUpdateCustomerGroup,
  );
}

export function* deleteCustomerGroup({ groupId }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    yield call(API.deleteCustomerGroup, groupId);
    const storeId = yield select(reducers.getStoreId);
    const groups = yield call(
      _getCustomerGroup,
      storeId,
      organization.locationId,
    );
    yield put(actions.receiveCustomerGroups(groups));
    yield put(actions.setGetCustomerGroupsSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerGroupsFailure(error));
    Analytics.sendErrorReport(error, "deleteCustomerGroup", { groupId });
  }
}

export function* watchDeleteCustomerGroup() {
  yield takeEvery(actions.DELETE_CUSTOMER_GROUP, deleteCustomerGroup);
}

export function* getCustomerTags({ storeId, locationId, override }) {
  try {
    const isOutsourced = yield select(reducers.isOutsourcedCustomer);
    const tags = yield call(
      _getCustomerTags,
      storeId,
      locationId,
      isOutsourced,
    );
    yield put(actions.receiveCustomerTags(tags, override));
    yield put(actions.setCustomerTagStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCustomerTagStatusFailure(error));
    Analytics.sendErrorReport(error, "getCustomerTags", {
      storeId,
      locationId,
    });
  }
}

export function* watchGetCustomerTags() {
  yield takeEvery(actions.GET_CUSTOMER_TAGS, getCustomerTags);
}

export function* getCustomerTagsWithCount({ storeId, locationId }) {
  try {
    const tags = yield call(API.getCustomerTagsWithCount, storeId, locationId);
    yield put(actions.receiveCustomerTags(tags, true));
    yield put(actions.setCustomerTagStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCustomerTagStatusFailure(error));
    Analytics.sendErrorReport(error, "getCustomerTagsWithCount", {
      storeId,
      locationId,
    });
  }
}

export function* watchGetCustomerTagsCount() {
  yield takeEvery(
    actions.GET_CUSTOMER_TAGS_WITH_COUNT,
    getCustomerTagsWithCount,
  );
}

export function* createOrUpdateCustomerTag({ tag }) {
  try {
    const current = new Date().toISOString();
    if (!tag.id) {
      const organization = yield select(reducers.getCurrentOrganization);
      const isMultiLocation = yield select(reducers.isMultiLocation);
      const isOutsourced = yield select(reducers.isOutsourcedCustomer);
      let _tag = {
        ...tag,
        id: generateId("ctg"),
        type: tag.type || "custom",
        storeId: yield select(reducers.getStoreId),
        updatedAt: current,
        createdAt: current,
      };
      if (!isOutsourced && isMultiLocation && organization.locationId) {
        _tag = {
          ..._tag,
          locationId: yield select(reducers.getCurrentLocationId),
        };
      }
      let createdTag = yield call(API.createCustomerTag, _tag);
      if (
        createdTag.type !== "auto" &&
        (createdTag.conditions || []).length > 0
      ) {
        yield delay(200);
        yield call(API.applyCustomerTag, createdTag);
      }
      yield put(actions.receiveCustomerTag(createdTag));
    } else {
      let updatedTag = yield call(API.updateCustomerTag, {
        ...tag,
        updatedAt: current,
      });
      if (updatedTag.type !== "auto") {
        updatedTag = yield call(API.applyCustomerTag, updatedTag);
      }
      yield put(actions.receiveCustomerTag(updatedTag));
    }
    yield put(actions.setCustomerTagStatusSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setCustomerTagStatusFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateCustomerTag", { tag });
  }
}

export function* watchCreateOrUpdateCustomerTag() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_CUSTOMER_TAG,
    createOrUpdateCustomerTag,
  );
}

export function* updateCustomerTags({ tags }) {
  try {
    const updatedTags = yield all(
      tags.map((tag) =>
        call(API.updateCustomerTag, {
          ...tag,
          updatedAt: new Date().toISOString(),
        }),
      ),
    );
    yield put(actions.receiveCustomerTags(updatedTags));
    yield put(actions.setCustomerTagStatusSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setCustomerTagStatusFailure(error));
    Analytics.sendErrorReport(error, "updateCustomerTags", { tags });
  }
}

export function* watchUpdateCustomerTags() {
  yield takeEvery(actions.UPDATE_CUSTOMER_TAGS, updateCustomerTags);
}

export function* deleteCustomerTag({ tagId }) {
  try {
    yield call(API.deleteCustomerTag, tagId);
    yield put(actions.receiveDeletedCustomerTag(tagId));
    yield put(actions.setCustomerTagStatusSuccess());
  } catch (error) {
    yield put(actions.setCustomerTagStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteCustomerTag", { tagId });
  }
}

export function* watchDeleteCustomerTag() {
  yield takeEvery(actions.DELETE_CUSTOMER_TAG, deleteCustomerTag);
}

export function* createOrUpdateStoreAttributes({ attributes }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const store = yield call(API.getStore, storeId);
    const updatedStore = yield call(_updateStore, {
      ...store,
      customer: { ...store.customer, attributes: attributes },
    });
    // yield put(actions.receiveStore(updatedStore));
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateStoreAttributes", {
      attributes,
    });
  }
}

export function* watchCreateOrUpdateStoreAtrributes() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_STORE_ATTRIBUTES,
    createOrUpdateStoreAttributes,
  );
}

function* _buildBaseCustomer(customer, meta = null) {
  /**
   * meta = {
   *    ident: string,
   *    method(=identType): string,
   * }
   */
  const ident = meta?.ident ? meta.ident : customer.phone;
  const customerId = hashMd5(ident);
  const isMulti = yield select(reducers.isMultiLocation);
  const isHeadOffice = isMulti && Authority.level() === "store";
  const base = {
    customerId: customerId,
    ident: ident,
    identType: meta?.method || "phone",
    name: meta?.method === "card" ? "미등록 고객" : customer.name,
    phone: customer.phone,
    type: customer.type,
    ...(isHeadOffice
      ? {}
      : { locationId: yield select(reducers.getCurrentLocationId) }),
    storeId: yield select(reducers.getStoreId),
    email: customer.email,
    ageGroup: customer.ageGroup,
    birth: customer.birth,
    gender: customer.gender,
  };
  return base;
}

function* _buildBaseOutsourcedCustomer(customer, storeProvider) {
  const ident = `${storeProvider.key}|${storeProvider.ident}|${customer.providerCustomerId}`;
  const customerId = hashMd5(ident);
  const isMulti = yield select(reducers.isMultiLocation);
  const isHeadOffice = isMulti && Authority.level() === "store";
  const base = {
    customerId: customerId,
    ident: ident,
    identType: "user",
    name: customer.name || "",
    phone: customer.phone,
    type: "individual",
    ...(isHeadOffice
      ? {}
      : { locationId: yield select(reducers.getCurrentLocationId) }),
    storeId: yield select(reducers.getStoreId),
    email: customer.email,
    birth: customer.birth,
    gender: customer.gender,
    tier: customer.tier,
    provider: storeProvider.key,
    providerCustomerId: customer.providerCustomerId,
  };
  return base;
}

export function* createCustomer({ customer }) {
  try {
    const _customer = _.cloneDeep(customer);
    let createdCustomer = yield call(API.createCustomer, {
      ...(yield _buildBaseCustomer(_customer)),
      status: "registered",
      id: generateId("cst"),
      registeredAt: new Date().toISOString(),
      note: _customer.note,
    });
    yield put(actions.receiveCustomer(createdCustomer));
    yield put(actions.setCreateCustomerSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setCreateCustomerFailure(error));
    Analytics.sendErrorReport(error, "createCustomer", { customer });
  }
}

export function* watchCreateCustomer() {
  yield takeEvery(actions.CREATE_CUSTOMER, createCustomer);
}

function* _getCustomerGroup(storeId, locationId) {
  const count = yield call(API.getCustomerCount, storeId, locationId);
  const size = 5000;
  let pages = _.range(Math.ceil(count / size));
  let data = [];
  const chunkSize = 8;
  for (let i = 0; i < pages.length; i += chunkSize) {
    const chunk = pages.slice(i, i + chunkSize);
    const _data = yield all(
      chunk.map((i) =>
        call(API.getCustomerGroups, storeId, locationId, i + 1, size),
      ),
    );
    data = [...data, ..._data];
  }
  let groups = {};
  let list = [];
  for (let d of data) {
    console.log(d);
    for (let g of d.items) {
      if (g.key in groups) {
        groups[g.key].count += g.count;
        groups[g.key].list = [...groups[g.key].list, ...g.list];
      } else {
        groups[g.key] = g;
      }
    }
    list = [...list, ...d.list];
  }
  return {
    items: Object.values(groups),
    list,
  };
}

export function* getCustomerGroups({ storeId, locationId }) {
  try {
    const groups = yield call(_getCustomerGroup, storeId, locationId);
    yield put(actions.receiveCustomerGroups(groups));
    yield put(actions.setGetCustomerGroupsSuccess());
  } catch (error) {
    yield put(actions.setGetCustomerGroupsFailure(error));
    Analytics.sendErrorReport(error, "getCustomerGroups", { storeId });
  }
}

export function* watchGetCustomerGorups() {
  yield takeEvery(actions.GET_CUSTOMER_GROUPS, getCustomerGroups);
}

export function* getCustomerGroupsWithCount({ storeId, locationId }) {
  try {
    const countByStatus = yield call(
      API.getCustomerCountsByStatus,
      storeId,
      locationId,
    );
    yield put(
      actions.receiveCustomerGroups({
        items: countByStatus,
        list: [],
      }),
    );
    yield put(actions.setGetCustomerGroupsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setGetCustomerGroupsFailure(error));
    Analytics.sendErrorReport(error, "getCustomerGroupsWithCount", { storeId });
  }
}

export function* watchGetCustomerGroupsWithCount() {
  yield takeEvery(
    actions.GET_CUSTOMER_GROUPS_WITH_COUNT,
    getCustomerGroupsWithCount,
  );
}

export function* deleteCustomer({ customer }) {
  try {
    let tags = [];
    if ((customer.tags || []).length > 0) {
      const tagIds = customer.tags.map((tag) => tag.tagId);
      tags = yield all(tagIds.map((tagId) => call(API.getCustomerTag, tagId)));
    } else {
      let data = {};
      data = yield call(
        API.getCustomerTagsByStore,
        customer.storeId,
        data.nextToken,
        500,
      );
      tags = [...tags, ...data.items];
    }
    yield all(
      tags.map((tag) => {
        let _tag = {};
        if (tag.customerIds && tag.customerIds.includes(customer.id)) {
          let _customerIds = tag.customerIds.filter((id) => id !== customer.id);
          _tag = {
            ...tag,
            customerIds: _customerIds,
            updatedAt: new Date().toISOString(),
          };
          return call(API.updateCustomerTag, _tag);
        }
      }),
    );

    yield call(API.deleteCustomer, customer.id);
    yield put(actions.receiveDeletedCustomer(customer.id));
    yield put(actions.setDeleteCustomerSuccess());
  } catch (error) {
    yield put(actions.setDeleteCustomerFailure(error));
    Analytics.sendErrorReport(error, "deleteCustomer", { customer });
  }
}

export function* watchDeleteCustomer() {
  yield takeEvery(actions.DELETE_CUSTOMER, deleteCustomer);
}

export function* updateCustomer({ customer }) {
  try {
    let _customer = _.cloneDeep(customer);
    const organization = yield select(reducers.getCurrentOrganization);
    // TODO: if changed origin, just change customerId of pointtransaction
    let updatedCustomer = yield call(API.updateCustomer, {
      ..._customer,
      ...(customer.identType !== "user"
        ? yield _buildBaseCustomer(_customer)
        : {}),
    });
    let _updatedCustomerWithStats = yield _getCustomer(
      customer.id,
      organization.locationId,
      true,
      updatedCustomer,
    );
    let retrieved = yield select(reducers.getRetrievedCustomer);
    if (!_.isEmpty(retrieved)) {
      yield put(actions.receiveRetrievedCustomer(_updatedCustomerWithStats));
    }
    yield put(actions.receiveCustomer(_updatedCustomerWithStats));
    yield put(actions.setUpdateCustomerSuccess());
  } catch (error) {
    yield put(actions.setUpdateCustomerFailure(error));
    Analytics.sendErrorReport(error, "updateCustomer", { customer });
  }
}

export function* watchUpdateCustomer() {
  yield takeEvery(actions.UPDATE_CUSTOMER, updateCustomer);
}

function* _getMarketCustomer(id) {
  let _customer = { stores: {} };
  let _stats = {};
  let _payments = {};
  let _subscriptions = {};
  let _stores = {};
  const customers = yield call(
    API.getCustomers,
    id,
    null,
    null,
    "registered",
    true,
  );
  for (let c of customers) {
    _customer = { ...c, id: c.customerId, stores: {} };
    (c.payments || []).forEach((payment) => (_payments[payment.id] = payment));
    (c.subscriptions || []).forEach(
      (subscription) =>
        (_subscriptions[subscription.id] = {
          ...subscription,
          locationId: c.locationId,
          storeId: c.storeId,
        }),
    );
    if (c.storeId !== "unknown" && c.storeId) {
      _stats[c.storeId] = {
        use: c.point?.used || 0,
        save: c.point?.saved || 0,
        lastVisitedAt: c.stats?.lastVisitedAt,
      };
      _stores[c.storeId] = {
        ...c,
        store: {
          ...c.store,
          location: c.location,
          locations: [
            ...(_stores[c.storeId]?.store?.locations || []),
            c.location,
          ],
        },
      };
    }
  }
  _customer.stores.items = Object.values(_stores);
  _customer.payments = Object.values(_payments);
  _customer.subscriptions = Object.values(_subscriptions);
  return { customer: _customer, stats: _stats };
}

export function* getUser({ id }) {
  try {
    // TODO: test when case of not signed in
    console.log("get user id: ", id);
    let b = new Date().getTime();
    try {
      const cognito = yield Auth.currentAuthenticatedUser();
      if (cognito) {
        let [_data, cart, ordersByCustomerId] = yield all([
          _getMarketCustomer(id),
          API.getCart(id),
          API.getOrdersByCustomer(id, null, 10),
        ]);
        let ordersByReceiptCustomer = {};
        let data = [];
        do {
          ordersByReceiptCustomer = yield call(
            API.getOrdersByReciptCustomer,
            id,
            10,
            ordersByReceiptCustomer.nextToken,
          );
          data = [...data, ...ordersByReceiptCustomer.items];
        } while (ordersByReceiptCustomer.nextToken);
        let orders = {
          items: [...ordersByCustomerId.items, ...data],
          nextToken: ordersByCustomerId.nextToken,
        };
        yield put(actions.receivePointStats(_data.stats));
        yield put(actions.receiveOrders(orders));
        yield put(actions.receiveUser(_data.customer));
        // NOTE: duplicate with getCart
        if (cart) {
          if (cart.storeId) {
            cart.store = yield call(_getStoreWithLocations, cart.storeId);
            cart.store.location = cart.store.locations.items.filter(
              (l) => l.id === cart.orderLocationId,
            )[0];
          }
          yield put(actions.receiveCart(cart));
        }
      } else {
        throw new Error("no authenticated user");
      }
    } catch (err) {
      let { customer, stats } = yield call(_getMarketCustomer, id);
      yield put(actions.receivePointStats(stats));
      yield put(actions.receiveUser(customer));
    }
    console.log("TIME TOTAL: " + (new Date().getTime() - b) / 1000);
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "getUser", { id });
  }
}

export function* watchGetUser() {
  yield takeEvery(actions.GET_USER, getUser);
}

export function* updateUser({ user, incentiveCustomerId }) {
  try {
    yield call(
      API.updateCustomer,
      {
        ...user,
        customerId: user.id,
      },
      true,
    );
    if (incentiveCustomerId) {
      for (let customer of user.stores.items) {
        if (customer.id === incentiveCustomerId) {
          yield call(API.createPointTransaction, {
            id: generateId("pt"),
            locationId: customer.locationId,
            storeId: customer.storeId,
            point: customer.store.loyalty.incentives.fulfillPoint,
            name: "정보 기입 감사 포인트",
            type: "save",
            note: "fulfill",
            customerId: customer.customerId,
            pointTransactionCustomerId: customer.id,
            expiredAt: fromMarket.getExpiredAt(
              customer.store.loyalty.condition?.expirationPeriod,
            ),
          });
          let stats = yield select(reducers.getPointStatsByStoreId);
          stats[customer.storeId].save +=
            customer.store.loyalty.incentives.fulfillPoint;
          yield put(actions.receivePointStats(stats));
        }
      }
    }
    const { customer } = yield call(_getMarketCustomer, user.id);
    yield put(actions.receiveUser(customer));
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "updateUser", { user });
  }
}

export function* watchUpdateUser() {
  yield takeEvery(actions.UPDATE_USER, updateUser);
}

export function* signUpMarket({ user, orderId }) {
  try {
    yield Auth.signOut();
    yield put(actions.setMarketAuthenticated(false));
    yield put(actions.clearUser());
    yield Auth.signUp({
      username: hashMd5(user.phone) + "@thegrowing.co",
      password: user.password,
      attributes: {
        name: user.name,
        birthdate: user.birth,
        gender: user.gender,
        "custom:phone_number": user.phone,
        "custom:marketing_agree": user.agreements.marketing ? "1" : "0",
        "custom:skip_verification": "1",
      },
    });
    yield Auth.signIn(hashMd5(user.phone) + "@thegrowing.co", user.password);
    const origin = yield _getMarketCustomer(hashMd5(user.phone));

    if (!origin.customer.id) {
      const createGlobalCustomer = {
        id: "unknown",
        storeId: "unknown",
        locationId: "unknown",
        customerId: hashMd5(user.phone),
        ident: user.phone,
        identType: "phone",
        name: user.name,
        phone: user.phone,
        birth: user.birth,
        gender: user.gender,
        agreements: {
          privacy: user.agreements.privacy,
          provider: null,
          //todo 마케팅 정보 메일
        },
      };
      yield call(API.createCustomer, createGlobalCustomer, true);
    } else {
      const updateStoreCustomer = {
        ...origin.customer,
        name: user.name,
        phone: user.phone,
        birth: user.birth,
        gender: user.gender,
        agreements: {
          ...(origin.customer.agreements ?? {}),
          privacy: user.agreements.privacy,
          //todo 마케팅 정보 메일
        },
      };
      yield call(API.updateCustomer, updateStoreCustomer, true);
    }

    yield call(
      _updateOrderFromReceipt,
      orderId,
      hashMd5(user.phone),
      origin.customer,
    );
    yield put(actions.getUser(hashMd5(user.phone)));
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "signUpMarket", { user });
  }
}

export function* watchSignUpMarket() {
  yield takeEvery(actions.SIGNUP_MARKET, signUpMarket);
}

export function* signInMarket({ id, password, orderId }) {
  try {
    yield Auth.signIn(hashMd5(id) + "@thegrowing.co", password);
    let { customer } = yield call(_getMarketCustomer, hashMd5(id));
    yield put(actions.getCart(customer.id));
    yield put(actions.receiveUser(customer));
    yield call(_updateOrderFromReceipt, orderId, hashMd5(id), customer);
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "signInMarket", { id, password });
  }
}

export function* watchSignInMarket() {
  yield takeEvery(actions.SIGNIN_MARKET, signInMarket);
}

export function* setStoreFavorite({ storeCustomer }) {
  try {
    // TODO: remove for optimizing, take a long time
    yield call(API.updateCustomer, storeCustomer);
    let { customer } = yield call(_getMarketCustomer, storeCustomer.customerId);
    yield put(
      actions.receiveUser({ ...customer, updatedAt: new Date().toISOString() }),
    );
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "setStoreFavorite", { storeCustomer });
  }
}

export function* watchSetStoreFavorite() {
  yield takeEvery(actions.SET_STORE_FAVORITE, setStoreFavorite);
}

function* _getStoreCustomerWithTransaction(storeId, customerId) {
  // TODO: stores is changed properties, cannot use stores[storeId] directly
  console.log(storeId);
  let storeCustomer = {};
  let storeCustomers = yield call(
    API.getCustomers,
    customerId,
    null,
    null,
    "registered",
    true,
    null,
    100,
  );
  storeCustomers = (storeCustomers || []).filter(
    (customer) => customer.storeId === storeId,
  );

  if (storeCustomers.length > 0) {
    storeCustomer = storeCustomers[0];
    if (!storeCustomer.points) {
      storeCustomer.points = {};
    }
    let pointTransactions = [];
    let pointData = {};
    do {
      pointData = yield call(
        API.getPointTransactionByStoreCustomer,
        storeCustomer.id,
        null,
        500,
        pointData.nextToken,
      );
      pointTransactions = [...pointTransactions, ...pointData.items];
    } while (pointData.nextToken);
    storeCustomer.points.items = pointTransactions;

    if (!storeCustomer.credits) {
      storeCustomer.credits = {};
    }
    let creditTransactions = [];
    let creditData = {};
    do {
      creditData = yield call(
        API.getCreditTransactionsByStoreCustomer,
        storeCustomer.id,
        creditData.nextToken,
        null,
        500,
      );
      creditTransactions = [...creditTransactions, ...creditData.items];
    } while (creditData.nextToken);
    storeCustomer.credits.items = creditTransactions;
  }
  return storeCustomer;
}

function* _handleCreateStoreCustomer(storeId, locationId, user) {
  const data = yield call(
    API.getLocationCustomersByOriginId,
    user.id,
    { eq: storeId },
    null,
    null,
    {
      _delta: {
        ne: "DELETE",
      },
      status: {
        eq: "registered",
      },
    },
  );
  const customers = data.items;
  if (customers.length === 0) {
    const location = yield call(API.getStoreLocation, locationId);
    const salesDate = location.state === "opened" && location.sales?.startedAt;
    yield call(API.createCustomer, {
      id: generateId("cst"),
      customerId: user.id,
      storeId: storeId,
      locationId: locationId,
      status: "registered",
      registeredAt: new Date().toISOString(),
      salesDate: salesDate ?? null,
    });
  }
}

export function* loadStore({ storeId, locationId }) {
  try {
    const user = yield select(reducers.getUser);
    const keys = localStorage.getItem(Constants.STORAGE_KEYS.STORE);
    if (keys && storeId === keys.split("|")[0]) {
      const [storeId, locationId] = keys.split("|");
      yield _handleCreateStoreCustomer(storeId, locationId, user);
      localStorage.removeItem(Constants.QUERY_KEYS.STORE_LOCATION);
      localStorage.removeItem(Constants.STORAGE_KEYS.STORE);
    }
    let b = new Date().getTime();
    let [storeCustomer, stats, items] = yield all([
      _getStoreCustomerWithTransaction(storeId, user.id),
      API.getStoreStats(storeId),
      _getItems(storeId),
    ]);
    storeCustomer = {
      ...storeCustomer,
      store: {
        ...storeCustomer.store,
        stats,
      },
      location: storeCustomer.locations.filter((l) => l.id === locationId)[0],
    };
    console.log(storeCustomer);
    yield put(actions.receiveStore(storeCustomer));
    yield put(actions.receiveItems(items));
    console.log("TIME(loadStore) TOTAL: " + (new Date().getTime() - b) / 1000);
    yield put(actions.setMarketStoreStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStoreStatusFailure(error));
    Analytics.sendErrorReport(error, "loadStore", { storeId });
  }
}

export function* watchLoadStore() {
  yield takeEvery(actions.LOAD_STORE, loadStore);
}

function* _addItemToCart(item) {
  // TODO: storeId, customerId to ID, ...
  let cart = yield select(reducers.getCart);
  const user = yield select(reducers.getUser);

  console.log(cart);
  console.log(item);
  let items = _.cloneDeep(cart.items);
  let newItem = item;
  if (newItem.type === "discount") {
    items = items.filter((i) => i.type !== "discount");
  }
  for (var i = 0; i < items.length; i++) {
    // Update quantity of Item
    if (
      newItem.type !== "custom" &&
      fromMarket.isEqualItem(items[i], newItem)
    ) {
      if (items[i].discountMethod !== "rate") {
        items[i] = fromMarket.addItem(items[i], newItem);
        items[i].referId = newItem.referId;
        items[i].point += newItem.point || 0;
      }
      cart = {
        ...cart,
        ...fromMarket.brushupItems(items),
      };

      if (!cart.id) {
        cart.id = user.id;
        cart = yield call(API.createCart, cart);
      } else {
        cart = yield call(API.updateCart, cart);
      }
      return cart;
    }
  }
  // Add Item
  const storeId = yield select(reducers.getMarketStoreId);
  const stores = yield select(reducers.getMarketStores);
  console.log(stores);
  items = [
    ...items,
    { ...newItem, index: (_.maxBy(items, "index")?.index || 0) + 1 },
  ];
  cart = {
    ...cart,
    ...fromMarket.brushupItems(items),
    storeId: cart.storeId || storeId,
    // TODO: 등록고객이 아닌 경우 생성
    orderCustomerId: stores[storeId]?.customer.id,
    orderLocationId: stores[storeId]?.location.id,
    createdAt: cart.createdAt ? cart.createdAt : new Date().toISOString(),
  };
  console.log(cart);
  if (!cart.id) {
    cart.id = user.id;
    cart = yield call(API.createCart, cart);
  } else {
    cart = yield call(API.updateCart, cart);
  }
  return cart;
}

export function* addItemsToCart({ items }) {
  try {
    let cart = null;
    for (let item of items) {
      cart = yield _addItemToCart(item);
      console.log(cart);
      yield put(actions.receiveCart(cart));
    }
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "addItemsToCart", { items });
  }
}

export function* watchAddItemsToCart() {
  yield takeEvery(actions.ADD_ITEMS_TO_CART, addItemsToCart);
}

export function* addItemToCart({ item }) {
  try {
    const cart = yield _addItemToCart(item);
    yield put(actions.receiveCart({ ...cart }));
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "addItemToCart", { item });
  }
}

export function* watchAddItemToCart() {
  yield takeEvery(actions.ADD_ITEM_TO_CART, addItemToCart);
}

const getDeliveryFee = (orderAmount, market) => {
  let fees = market?.deliveryFee || [];
  let deliveryPrice = 0;
  if (fees.length > 0) {
    for (let i = 0; i < fees.length; i++) {
      if (orderAmount >= fees[i].start && orderAmount <= fees[i].end) {
        deliveryPrice = fees[i].fee;
      }
    }
  }
  console.log("delivery amount", deliveryPrice);
  return deliveryPrice;
};

export function* setOrderType({ orderType }) {
  try {
    const market = yield select(reducers.getCartStoreMarketConfig);
    console.log(market);
    let cart = _.cloneDeep(yield select(reducers.getCart));

    if (market.deliveryFee) {
      const deliveries = cart.items.filter((item) => item.type === "delivery");
      if (orderType === "delivery") {
        let amount = getDeliveryFee(cart.amount, market);
        if (deliveries.length === 0 && amount > 0) {
          yield call(addItemToCart, {
            item: {
              type: "delivery",
              name: "배송비",
              price: amount,
              amount: amount,
              quantity: 1,
              modifiers: [],
            },
          });
        }
      } else {
        if (deliveries.length > 0) {
          console.log("aaa");
          yield call(removeItemFromCart, { index: deliveries[0].index });
        }
      }
    }

    cart = _.cloneDeep(yield select(reducers.getCart));
    if (market.takeoutDiscount) {
      const discounts = cart.items.filter((item) => item.referId === "takeout");
      if (orderType === "takeout") {
        if (discounts.length === 0) {
          yield call(addItemToCart, {
            item: {
              type: "discount",
              name: `테이크아웃 할인(${market.takeoutDiscount}%)`,
              quantity: 1,
              referId: "takeout",
              discountValue: market.takeoutDiscount,
              discountMethod: "rate",
              modifiers: [],
            },
          });
        }
      } else {
        if (discounts.length > 0) {
          console.log("bbb");
          yield call(removeItemFromCart, { index: discounts[0].index });
        }
      }
    }
    // yield put(actions.receiveCart(cart));
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "setOrderType", { orderType });
  }
}

export function* watchSetOrderType() {
  yield takeEvery(actions.SET_ORDER_TYPE, setOrderType);
}

export function* duplicateOrderToCart({ order }) {
  try {
    let cart = {
      ...fromMarket.brushupItems(
        order.items.filter((item) => item.type === "sku"),
      ),
      id: order.customerId,
      storeId: order.storeId,
      orderLocationId: order.orderLocationId,
      orderCustomerId: order.orderCustomerId,
      createdAt: new Date().toISOString(),
    };
    cart = yield call(API.createCart, cart);
    yield put(actions.receiveCart(cart));
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "duplicateOrderToCart", { order });
  }
}

export function* watchDuplicateOrderToCart() {
  yield takeEvery(actions.DUPLICATE_ORDER_TO_CART, duplicateOrderToCart);
}

export function* removeItemFromCart({ index }) {
  try {
    let cart = yield select(reducers.getCart);
    const removeItems = cart.items.filter((item) => item.index === index);
    if (
      removeItems.length > 0 &&
      removeItems[0].referId &&
      (removeItems[0].type === "sku" ||
        removeItems[0].type === "discount" ||
        removeItems[0].type === "credit")
    ) {
      cart.items = cart.items.filter(
        (item) => item.referId !== removeItems[0].referId,
      );
    } else {
      cart.items = cart.items.filter((item) => item.index !== index);
    }
    cart = {
      ...cart,
      ...fromMarket.brushupItems(cart.items),
    };
    console.log(cart);
    if (cart.items.length === 0) {
      yield call(API.deleteCart, cart.id);
      yield put(actions.clearCart());
    } else {
      cart = yield call(API.updateCart, cart);
      yield put(actions.receiveCart(cart));
    }

    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "removeItemFromCart", { index });
  }
}

export function* watchRemoveItemFromCart() {
  yield takeEvery(actions.REMOVE_ITEM_FROM_CART, removeItemFromCart);
}

export function* deleteCart({ cart }) {
  try {
    yield call(API.deleteCart, cart.id);
    yield put(actions.clearCart());
    yield put(actions.clearStore());
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteCart", { cart });
  }
}

export function* watchDeleteCart() {
  yield takeEvery(actions.DELETE_CART, deleteCart);
}

export function* getCart({ cartId, withStore }) {
  try {
    console.log("getCart.", cartId);
    let cart = yield call(API.getCart, cartId);
    console.log("getCart.", cart);
    if (cart) {
      if (cart.storeId) {
        cart.store = yield call(_getStoreWithLocations, cart.storeId);
        cart.store.location = cart.store.locations.items.filter(
          (l) => l.id === cart.orderLocationId,
        )[0];
      }
      yield put(actions.receiveCart(cart));
    }
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "getCart", { cartId, withStore });
  }
}

export function* watchGetCart() {
  yield takeEvery(actions.GET_CART, getCart);
}

export function* _handlePaidOrder(order) {
  const transaction = yield select(reducers.getCartUsePoint);
  console.log(transaction);
  if (transaction) {
    let _transaction = {
      ...transaction,
      id: generateId("pt"),
      storeId: order.storeId,
      locationId: order.orderLocationId,
      type: "use",
      origin: "market",
      currency: order.currency,
      orderId: order.id,
      orderNumber: order.number,
      customerId: order.customerId,
      pointTransactionCustomerId: order.orderCustomerId,
      expiredAt: null,
      salesDate: order.salesDate,
    };
    const createdTransaction = yield call(
      API.createPointTransaction,
      _transaction,
    );
    console.log("createdUsePoint", createdTransaction);
  }

  // Credit
  for (let idx in order.items) {
    if (order.items[idx].type === "credit") {
      let item = order.items[idx];
      let _deposit = yield call(API.createCreditTransaction, {
        id: generateId("ct"),
        locationId: order.orderLocationId,
        storeId: order.storeId,
        name: item.name || null,
        origin: "market",
        type: "deposit",
        amount: item.amount * (item.quantity || 1),
        currency: "KRW",
        customerId: order.customerId,
        creditTransactionCustomerId: order.orderCustomerId,
        salesDate: order.salesDate,
      });
      order.items[idx].parentId = _deposit.id;
      console.log("createdCredit", _deposit);
    }
  }

  return order;
}

export function* payOrder({ cart, method }) {
  try {
    if (method === "credit") {
      const customer = yield _getStoreCustomerWithTransaction(
        cart.storeId,
        cart.id,
      );
      yield put(actions.receiveStore(customer));
      const salesDate = yield select(reducers.getSalesStartedAtForMarket);
      const point = yield select(reducers.getMarketStoreCredit);
      if (point < cart.amount) {
        throw new Error("balanceInvalid");
      }
      const items = yield _getItems(cart.storeId);
      yield put(actions.receiveItems(items));

      const date = new Date().toISOString();
      let order = {
        id: generateId("ord"),
        origin: "market",
        storeId: cart.storeId,
        amount: cart.amount,
        orderLocationId: cart.orderLocationId,
        orderCustomerId: cart.orderCustomerId,
        quantity: cart.quantity,
        diningOption: cart.diningOption,
        address: addressToStr(cart.address),
        reserveDate: cart.reserveDate,
        pickupDate: cart.pickupDate,
        shippingDate: cart.shippingDate,
        request: cart.request,
        tax: 0,
        customerId: cart.id,
        state: "paid",
        diningState: "created",
        receiptId: cart.receiptId,
        items: cart.items,
        charges: [],
        paidAt: date,
        updatedAt: date,
        createdAt: date,
        salesDate,
      };

      if (order.amount > 0) {
        let charge = {
          id: generateId("ch"),
          amount: order.amount,
          method: method,
          tax: 0,
          createdAt: date,
        };

        const withdrawTransaction = yield call(API.createCreditTransaction, {
          id: generateId("ct"),
          locationId: order.orderLocationId,
          storeId: order.storeId,
          type: "withdraw",
          amount: order.amount,
          currency: "KRW",
          customerId: order.customerId,
          creditTransactionCustomerId: order.orderCustomerId,
          salesDate,
        });
        charge = {
          ...charge,
          creditTransactionId: withdrawTransaction.id,
        };

        order.charges = [charge];
      }

      order = yield _handlePaidOrder(order);
      console.log(order);
      const createdOrder = yield call(API.createOrder, order);
      yield put(actions.receiveOrder(createdOrder));
      yield call(API.deleteCart, cart.id);

      yield put(actions.clearCart());
      yield put(actions.clearStore());
      yield put(actions.receiveCheckout(order));
      yield put(actions.setMarketStatusSuccess());
    } else {
      yield put(
        actions.setMarketStatusFailure(new Error("Invalid payment method")),
      );
    }
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "payOrder", { cart, method });
  }
}

export function* watchPayOrder() {
  yield takeEvery(actions.PAY_ORDER, payOrder);
}

export function* receivePaidOrder({ ident }) {
  try {
    // TEST: http://localhost:3000/order-completed?id=20201004141408830
    const orders = yield select(reducers.getOrdersByReceiptId);
    if (ident in orders) {
      yield put(actions.receiveCheckout(orders[ident]));
      yield put(actions.setMarketOrderStatusSuccess());
      return;
    }

    const charge = yield call(API.getSalesOrder, ident);
    const order = yield call(API.getOrder, charge.orderId);
    if (charge.state === "paid" && order.state === "paid") {
      let [storeCustomer, stats] = yield all([
        _getStoreCustomerWithTransaction(order.storeId, order.customerId),
        API.getStoreStats(order.storeId),
      ]);
      storeCustomer = {
        ...storeCustomer,
        store: {
          ...storeCustomer.store,
          stats,
        },
        location: storeCustomer.locations.filter(
          (location) => location.id === order.orderLocationId,
        )[0],
      };
      yield put(actions.receiveStore(storeCustomer));
      yield put(actions.receiveOrder(order));
      yield put(actions.clearCart());
      yield put(actions.receiveCheckout(order));
      yield put(actions.setMarketOrderStatusSuccess());
    } else {
      yield put(
        actions.setMarketOrderStatusFailure(new Error("Failed to pay")),
      );
    }
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketOrderStatusFailure(error));
    Analytics.sendErrorReport(error, "receivePaidOrder", { ident });
  }
}

export function* watchRecievePaidOrder() {
  yield takeEvery(actions.RECEIVE_PAID_ORDER, receivePaidOrder);
}

export function* registerOrUpdateAccount({ account }) {
  try {
    let updatedStore = {};
    let removeAccount = {};
    let store = yield select(reducers.getStore);
    const locationId = yield select(reducers.getCurrentLocationId);
    const location = yield call(API.getStoreLocation, locationId);
    const isMultiLocation = yield select(reducers.isMultiLocation);
    store = yield call(_getStoreWithLocations, store.id);

    const { ident, holder, bankCode, number, businessNumber } = yield call(
      API.registerAccount,
      {
        ...account,
        ident: generateId(null),
        name: store.info.name || "",
      },
    );
    if (isMultiLocation && location.type === "indirect") {
      removeAccount = location.market?.account || {};
      let _location = {
        ..._.cloneDeep(location),
        market: {
          ..._.cloneDeep(location.market),
          account: {
            ident,
            holder,
            bankCode,
            bank: account.bank,
            number,
            businessNumber,
          },
        },
        subscription: {
          ..._.cloneDeep(location.subscription),
          account: {
            ident,
            holder,
            bankCode,
            bank: account.bank,
            number,
            businessNumber,
          },
        },
      };
      yield call(API.updateLocation, _location);
    } else {
      removeAccount = store.market?.account || {};
      let _store = {
        ..._.cloneDeep(store),
        market: {
          ..._.cloneDeep(store.market),
          account: {
            ident,
            holder,
            bankCode,
            bank: account.bank,
            number,
            businessNumber,
          },
        },
        subscription: {
          ..._.cloneDeep(store.subscription),
          account: {
            ident,
            holder,
            bankCode,
            bank: account.bank,
            number,
            businessNumber,
          },
        },
      };
      yield call(API.updateStore, _store);
    }
    try {
      if (removeAccount?.ident) {
        //remove
        yield call(API.updateAccount, {
          ...removeAccount,
          name: `removed(${store.id})`,
        });
      }
    } catch (err) {
      console.debug("Failed to update old account");
    }
    updatedStore = yield call(_getStoreWithLocations, store.id);
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "registerOrUpdateAccount", { account });
  }
}

export function* watchRegisterOrUpdateAccount() {
  yield takeEvery(actions.REGISTER_OR_UPDATE_ACCOUNT, registerOrUpdateAccount);
}

export function* sendReceiptLink({ recipient, order }) {
  try {
    yield call(API.sendReceiptLink, recipient, order);
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "sendReceiptLink", { recipient, order });
  }
}

export function* watchSendReceiptLink() {
  yield takeEvery(actions.SEND_RECEIPT_LINK, sendReceiptLink);
}

export function* cancelOrder({ order }) {
  try {
    let a = new Date().getTime();
    for (let idx in order.charges || []) {
      if (
        order.charges[idx].pg === "nicepay" &&
        order.charges[idx].method === "card"
      ) {
        const refundId = generateId("rf");
        const result = yield call(
          API.cancelCardPay,
          refundId,
          order.charges[idx].transactionId,
          order.charges[idx].amount,
        );
        order.charges[idx].refunds = [
          {
            id: refundId,
            amount: order.charges[idx].amount,
            supplyAmount: order.charges[idx].supplyAmount,
            taxFreeAmount: order.charges[idx].taxFreeAmount,
            tax: order.charges[idx].tax,
            approvalNumber: result.approvalNumber,
            approvalDate: result.approvalDate,
            transactionId: result.transactionId,
            createdAt: new Date().toISOString(),
          },
        ];
      }
      if (order.charges[idx].method === "credit") {
        const refundTransaction = yield call(API.createCreditTransaction, {
          id: generateId("ct"),
          locationId: order.orderLocationId,
          storeId: order.storeId,
          type: "deposit",
          amount: order.amount,
          currency: "KRW",
          customerId: order.customerId,
          description: "returned",
          creditTransactionCustomerId: order.orderCustomerId,
        });

        let refund = {
          id: generateId("rf"),
          amount: order.charges[idx].amount,
          supplyAmount: order.charges[idx].supplyAmount,
          taxFreeAmount: order.charges[idx].taxFreeAmount,
          tax: order.charges[idx].tax,
          creditTransactionId: refundTransaction.id,
          createdAt: refundTransaction.createdAt,
        };
        if (
          order.charges[idx].pg === "nicepay" &&
          order.charges[idx].transactionId
        ) {
          const refundId = generateId("rf");
          const result = yield call(
            API.cancelCashReceipt,
            refundId,
            order.charges[idx].transactionId,
            order.charges[idx].amount,
            order.charges[idx].supplyAmount,
            order.charges[idx].tax,
            order.charges[idx].taxFreeAmount,
          );
          refund = {
            ...refund,
            transactionId: result.transactionId,
            approvalNumber: result.approvalNumber,
            approvalDate: result.approvalDate,
          };
        }
        order.charges[idx].refunds = [refund];
      }
    }

    let creditItems = order.items.filter((item) => item.type === "credit");
    console.log("creditAmount", creditItems);
    const creditAmount = _.sumBy(creditItems, "amount");
    if (creditAmount > 0) {
      yield call(API.createCreditTransaction, {
        id: generateId("ct"),
        locationId: order.orderLocationId,
        storeId: order.storeId,
        type: "withdraw",
        origin: "market",
        amount: creditAmount,
        currency: "KRW",
        customerId: order.customerId,
        description: "returned",
        creditTransactionCustomerId: order.orderCustomerId,
      });
    }

    const loyalty = yield select(reducers.getMarketStoreLoyalty);
    let _usePoint = fromMarket.getLoyaltyUsePointByOrder(order, loyalty);
    if (_usePoint && _usePoint.point > 0) {
      let transaction = {
        ..._usePoint,
        id: generateId("pt"),
        storeId: order.storeId,
        locationId: order.orderLocationId,
        type: "save",
        currency: order.currency,
        orderId: order.id,
        orderNumber: order.number,
        origin: "market",
        note: "returned",
        customerId: order.customerId,
        pointTransactionCustomerId: order.orderCustomerId,
        expiredAt: fromMarket.getExpiredAt(loyalty.condition.expirationPeriod),
      };
      yield call(API.createPointTransaction, transaction);
    }

    order.amountReturned = order.amount;
    order.state = "returned";
    order.diningState = "cancelled";
    order.returnedAt = new Date().toISOString();
    order.returns = [
      {
        amount: order.amount,
        items: order.items.filter((item) => item.type === "sku"),
        refundId: order.charges[0]?.refunds[0].id,
        createdAt: new Date().toISOString(),
      },
    ];
    order = yield call(API.updateOrder, order);
    console.log("TIME(cancel) TOTAL: " + (new Date().getTime() - a) / 1000);
    yield put(actions.receiveOrder(order));
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "cancelOrder", { order });
  }
}

export function* watchCancelOrder() {
  yield takeEvery(actions.CANCEL_ORDER, cancelOrder);
}

export function* resetMarketPassword({ ident, password }) {
  try {
    yield call(API.resetPassword, ident, password);
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
    Analytics.sendErrorReport(error, "resetMarketPassword", {
      ident,
      password,
    });
  }
}

export function* watchResetMarketPassword() {
  yield takeEvery(actions.RESET_MARKET_PASSWORD, resetMarketPassword);
}

// Coupon
function* _getAllCoupons(storeId, locationId, isPublish) {
  let coupons = [];
  let data = {};
  do {
    data = yield call(
      API.getCoupons,
      storeId,
      data.nextToken,
      //limit 100 or more
      500,
      {
        conditionType: {
          ne: "tier",
        },
      },
    );
    coupons = [...coupons, ...data.items];
  } while (data.nextToken);
  coupons = coupons.filter((coupon) =>
    isCurrentLocationAvailableCoupon(coupon, locationId, isPublish),
  );
  return coupons;
}

function* _getPointTransactionsByStore(
  storeId,
  customerId = null,
  start = null,
  end = null,
  locationId = null,
  basis = null,
) {
  let b = new Date();
  let transactions = [];
  let data = {};
  let filter = {};
  if (customerId) {
    filter.pointTransactionCustomerId = { eq: customerId };
  }
  if (locationId) {
    filter.locationId = { eq: locationId };
  }

  if (basis === "sales") {
    do {
      data = yield call(
        API.getPointTransactionsByStoreSalesDate,
        storeId,
        start && end ? { between: [start, end] } : null,
        data.nextToken,
        //limit 100 or more
        500,
        !_.isEmpty(filter) ? filter : null,
      );
      transactions = [...transactions, ...data.items];
    } while (data.nextToken);
    transactions = transactions.sort((a, b) => {
      if (moment(b.createdAt).isAfter(moment(a.createdAt))) {
        return 1;
      } else if (moment(a.createdAt).isAfter(moment(b.createdAt))) {
        return -1;
      }
    });
  } else {
    do {
      data = yield call(
        API.getPointTransactionsByStore,
        storeId,
        start && end ? { between: [start, end] } : null,
        data.nextToken,
        //limit 100 or more
        500,
        !_.isEmpty(filter) ? filter : null,
      );
      transactions = [...transactions, ...data.items];
    } while (data.nextToken);
  }
  console.log("POINT", (new Date().getTime() - b.getTime()) / 1000);
  return transactions;
}

function* _getPointTransactionsByLocation(
  locationId,
  customerId = null,
  start = null,
  end = null,
  basis = null,
) {
  let b = new Date();
  let transactions = [];
  let data = {};
  if (basis === "sales") {
    do {
      data = yield call(
        API.getPointTransactionsByLocationSalesDate,
        locationId,
        start && end ? { between: [start, end] } : null,
        data.nextToken,
        500,
        customerId ? { pointTransactionCustomerId: { eq: customerId } } : null,
      );
      transactions = [...transactions, ...data.items];
    } while (data.nextToken);
    transactions = transactions.sort((a, b) => {
      if (moment(b.createdAt).isAfter(moment(a.createdAt))) {
        return 1;
      } else if (moment(a.createdAt).isAfter(moment(b.createdAt))) {
        return -1;
      }
    });
  } else {
    do {
      data = yield call(
        API.getPointTransactionsByLocation,
        locationId,
        start && end ? { between: [start, end] } : null,
        data.nextToken,
        500,
        customerId ? { pointTransactionCustomerId: { eq: customerId } } : null,
      );
      transactions = [...transactions, ...data.items];
    } while (data.nextToken);
  }
  console.log("POINT", (new Date().getTime() - b.getTime()) / 1000);
  return transactions;
}

function* _getCreditTransactionsByStore(
  storeId,
  customerId = null,
  locationId = null,
) {
  let b = new Date();

  let transactions = [];
  let data = {};
  let filter = {};

  if (customerId) {
    filter.creditTransactionCustomerId = { eq: customerId };
  }
  if (locationId) {
    filter.locationId = { eq: locationId };
  }
  do {
    data = yield call(
      API.getCreditTransactionsByStore,
      storeId,
      null,
      data.nextToken,
      //limit 100 or more
      500,
      !_.isEmpty(filter) ? filter : null,
    );
    transactions = [...transactions, ...data.items];
  } while (data.nextToken);
  console.log("CREDIT", (new Date().getTime() - b.getTime()) / 1000);
  return transactions;
}

function* _getOrdersByStore(storeId, customerId = null, locationId = null) {
  let b = new Date();
  let orders = [];
  let data = {};
  let filter = {};
  if (customerId) {
    filter.orderCustomerId = { eq: customerId };
  }
  if (locationId) {
    filter.orderLocationId = { eq: locationId };
  }
  do {
    data = yield call(
      API.getOrdersOnlyByStore,
      storeId,
      null,
      data.nextToken,
      500,
      !_.isEmpty(filter) ? filter : null,
    );
    orders = [...orders, ...data.items];
  } while (data.nextToken);
  console.log("ORDERS", (new Date().getTime() - b.getTime()) / 1000);
  return orders;
}

export function* getProviderOrders({ filters, page, size }) {
  const storeId = yield select(reducers.getStoreId);
  try {
    let data = yield call(API.getProviderOrders, storeId, page, size, filters);
    yield put(
      actions.receiveProviderOrders({ items: data.items, count: data.count }),
    );
    yield put(actions.setGetOrderHistoriesSuccess());
  } catch (error) {
    yield put(actions.setGetOrderHistoriesFailure(error));
    Analytics.sendErrorReport(error, "getProviderOrders", {
      storeId,
    });
  }
}

export function* watchGetProviderOrders() {
  yield takeEvery(actions.GET_PROVIDER_ORDERS, getProviderOrders);
}

export function* getCoupon({ couponId }) {
  try {
    const coupon = yield call(API.getCoupon, couponId);
    yield put(actions.receiveCoupon(coupon));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "getCoupon", { couponId });
  }
}

export function* watchGetCoupon() {
  yield takeEvery(actions.GET_COUPON, getCoupon);
}

export function* getCoupons({ storeId }) {
  try {
    const org = yield select(reducers.getCurrentOrganization);
    const coupons = yield call(_getAllCoupons, storeId, org?.locationId);
    yield put(actions.receiveCoupons(coupons));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "getCoupons", { storeId });
  }
}

export function* watchGetCoupons() {
  yield takeEvery(actions.GET_COUPONS, getCoupons);
}

export function* getCouponsForPublish({ storeId }) {
  try {
    const org = yield select(reducers.getCurrentOrganization);
    const coupons = yield call(_getAllCoupons, storeId, org?.locationId, true);
    yield put(actions.receiveCoupons(coupons));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "getCouponsForPublish", { storeId });
  }
}

export function* watchGetCouponsForPublish() {
  yield takeEvery(actions.GET_COUPONS_FOR_PUBLISH, getCouponsForPublish);
}

export function* createCoupon({ coupon }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const organization = yield select(reducers.getCurrentOrganization);
    const isMultiLocation = yield select(reducers.isMultiLocation);
    let _coupon = {
      ...coupon,
      id: generateId("cpn"),
      storeId,
    };
    if (isMultiLocation && organization.locationId) {
      _coupon.locationId = organization.locationId;
    }
    const createdCoupon = yield call(API.createCoupon, _coupon);
    yield put(actions.receiveCoupon(createdCoupon));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "createCoupon", { coupon });
  }
}

export function* watchCreateCoupon() {
  yield takeEvery(actions.CREATE_COUPON, createCoupon);
}

export function* updateCoupon({ coupon }) {
  try {
    const updatedCoupon = yield call(API.updateCoupon, coupon);
    yield put(actions.receiveCoupon(updatedCoupon));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "updateCoupon", { coupon });
  }
}

export function* watchUpdateCoupon() {
  yield takeEvery(actions.UPDATE_COUPON, updateCoupon);
}

function* _updateMenuBoardConfigFromDiscountDelete(couponId) {
  const storeId = yield select(reducers.getStoreId);
  const locations = yield call(_getLocations, storeId);
  let needUpdates = [];
  for (let location of locations) {
    let _location = _.cloneDeep(location);
    let menuBoard = _.cloneDeep(_location.menu?.board);
    if ((menuBoard?.pages || []).length > 0) {
      let pagesByParentId = menuBoard.pages.reduce((obj, p) => {
        obj[p.parentId] = p;
        return obj;
      }, {});
      for (let page of menuBoard.pages) {
        let itemsByParentId = (page.items || []).reduce((obj, i) => {
          obj[i.parentId] = i;
          return obj;
        }, {});
        if (couponId in itemsByParentId) {
          pagesByParentId[page.parentId] = {
            ...pagesByParentId[page.parentId],
            items: pagesByParentId[page.parentId].items.filter(
              (item) => item.parentId !== couponId,
            ),
          };
        }
      }
      let favoriteItemsByParentId = (menuBoard.favorite || []).reduce(
        (obj, f) => {
          obj[f.parentId] = f;
          return obj;
        },
        {},
      );
      if (couponId in favoriteItemsByParentId) {
        menuBoard = {
          ...menuBoard,
          favorite: menuBoard.favorite.filter(
            (fav) => fav.parentId !== couponId,
          ),
        };
      }
      menuBoard = { ...menuBoard, pages: Object.values(pagesByParentId) };
      _location = {
        ..._location,
        menu: { ...(_location.menu || {}), board: menuBoard || {} },
      };
      if (!_.isEqual(location, _location)) {
        needUpdates.push(_location);
      }
    }
  }
  yield all(needUpdates.map((loc) => call(API.updateLocation, loc)));
}

export function* deleteCoupon({ coupon }) {
  try {
    yield call(API.deleteCoupon, coupon.id);
    yield put(actions.receiveDeletedCoupon(coupon.id));
    yield _updateMenuBoardConfigFromDiscountDelete(coupon.id);
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteCoupon", { coupon });
  }
}

export function* watchDeleteCoupon() {
  yield takeEvery(actions.DELETE_COUPON, deleteCoupon);
}

export function* deleteCoupons({ coupons }) {
  try {
    for (let coupon of coupons) {
      yield call(API.deleteCoupon, coupon.id);
      yield put(actions.receiveDeletedCoupon(coupon.id));
      yield _updateMenuBoardConfigFromDiscountDelete(coupon.id);
    }
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteCoupons", { coupons });
  }
}

export function* watchDeleteCoupons() {
  yield takeEvery(actions.DELETE_COUPONS, deleteCoupons);
}

export function* uploadItemTemplate({
  storeId,
  key,
  locationId,
  excelType,
  templateSessionId,
}) {
  // 클라이언트에서 업로드 해준 파일의 키를 가지고 서버에서 다운로드 - product부분에서 해주자
  // 파싱을 다하고 나서 넘겨주는 값을 가지고 다시 api.syncdb 호출
  try {
    const items = yield call(
      API.parseItemTemplate,
      storeId,
      key,
      locationId,
      "excel",
      excelType,
      templateSessionId,
    );
    if (items["errors"].length > 0) {
      yield put(actions.parseItemTemplateFailure(items["errors"]));
    } else {
      yield put(actions.parseItemTemplateSuccess(items["data"]));
    }
  } catch (error) {
    yield put(actions.parseItemTemplateFailure(error));
  }
}

export function* watchUploadItemTemplate() {
  yield takeEvery(actions.UPLOAD_ITEM_TEMPLATE, uploadItemTemplate);
}

export function* syncItemTemplate({
  storeId,
  key,
  excelType,
  templateSessionId,
}) {
  // 클라이언트에서 업로드 해준 파일의 키를 가지고 서버에서 다운로드 - product부분에서 해주자
  // 파싱을 다하고 나서 넘겨주는 값을 가지고 다시 api.syncdb 호출
  try {
    yield call(API.syncItemTemplate, storeId, key, excelType, templateSessionId);
    let updateStore = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(updateStore));
    // NOTE: handle syncItemTemplateSuccess in receiveStoreStatus
  } catch (error) {
    console.log(error);
    if (
      !error?.errors ||
      error.errors[0].errorType !== "Lambda:ExecutionTimeoutException"
    ) {
      yield put(actions.syncItemTemplateFailure(error));
    }
  }
}

export function* watchSyncItemTemplate() {
  yield takeEvery(actions.SYNC_ITEM_TEMPLATE, syncItemTemplate);
}

export function* uploadInventoryTemplate({
  storeId,
  employeeId,
  key,
  standardType,
  reason,
  options,
  templateSessionId,
}) {
  try {
    const items = yield call(
      API.parseInventoryTemplate,
      storeId,
      employeeId,
      key,
      reason,
      standardType,
      options,
      templateSessionId,
    );
    if (items["errors"].length > 0) {
      yield put(actions.parseItemTemplateFailure(items["errors"]));
    } else {
      yield put(actions.parseItemTemplateSuccess(items["data"]));
    }
  } catch (error) {
    yield put(actions.parseItemTemplateFailure(error));
  }
}

export function* watchUploadInventoryTemplate() {
  yield takeEvery(actions.UPLOAD_INVENTORY_TEMPLATE, uploadInventoryTemplate);
}

export function* generateInventoryTemplate({
  storeId,
  locationId,
  standardType,
  reason,
}) {
  try {
    yield call(
      API.generateInventoryTemplate,
      storeId,
      locationId,
      reason,
      standardType,
    );
  } catch (error) {
    if (
      !error?.errors ||
      error.errors[0].errorType !== "Lambda:ExecutionTimeoutException"
    ) {
      yield put(actions.generateItemTemplateFailure(error));
    }
  }
}

export function* watchGenerateInventoryTemplate() {
  yield takeEvery(
    actions.GENERATE_INVENTORY_TEMPLATE,
    generateInventoryTemplate,
  );
}

export function* syncInventoryTemplate({
  storeId,
  key,
  standardType,
  reason,
  templateSessionId,
}) {
  try {
    yield call(
      API.syncInventoryTemplate,
      storeId,
      key,
      reason,
      standardType,
      templateSessionId,
    );
    // NOTE: handle syncItemTemplateSuccess in receiveStoreStatus
  } catch (error) {
    console.log(error);
    if (
      !error?.errors ||
      error.errors[0].errorType !== "Lambda:ExecutionTimeoutException"
    ) {
      yield put(actions.syncItemTemplateFailure(error));
    }
  }
}

export function* watchSyncInventoryTemplate() {
  yield takeEvery(actions.SYNC_INVENTORY_TEMPLATE, syncInventoryTemplate);
}

export function* receiveStoreStatus({ status }) {
  try {
    if (status.item) {
      switch (status.item.func) {
        case "syncItemTemplate":
          if (status.item.errorMessage) {
            yield put(
              actions.syncItemTemplateFailure(
                new Error(status.item.errorMessage),
              ),
            );
          } else if (status.item.progress === 100) {
            yield put(actions.syncItemTemplateSuccess());
          }
          break;
        case "launchCampaign":
          const campaigns = yield select(reducers.getCampaigns);
          for (let c of campaigns) {
            if (c.status === "requesting") {
              const campaign = yield call(API.getCampaignWithReport, c.id);
              yield put(actions.receiveCampaign(campaign));
            }
          }
          break;
        case "syncInventoryTemplate":
          if (status.item.errorMessage) {
            yield put(
              actions.syncItemTemplateFailure(
                new Error(status.item.errorMessage),
              ),
            );
          } else if (status.item.progress === 100) {
            yield put(actions.syncItemTemplateSuccess());
          }
          break;
        case "generateInventoryTemplate":
          if (status.item.errorMessage) {
            yield put(
              actions.generateItemTemplateFailure(
                new Error(status.item.errorMessage),
              ),
            );
          } else if (status.item.progress === 100) {
            yield put(actions.generateItemTemplateSuccess());
          } else if (status.item.progress === 0) {
            yield put(actions.initGenerateInventoryTemplate());
          }
          break;
        default:
          break;
      }
    }
  } catch (error) {
    console.log(error);
    Analytics.sendErrorReport(error, "receiveStoreStatus", { status });
  }
}

export function* watchReceiveStoreStatus() {
  yield takeEvery(actions.RECEIVE_STORE_STATUS, receiveStoreStatus);
}

export function* updateActiveChangedProducts({ products }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    yield all(products.map((product) => call(API.updateItem, product)));
    const updatedItems = yield _getItems(storeId);
    yield put(actions.receiveItems(updatedItems));
    yield put(actions.setUpdateItemsSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setUpdateItemsFailure(error));
    Analytics.sendMixPanelEvent("error", {
      screen: "Product",
      action: "save_soldout",
      detail: error.errors && error.errors[0]?.message,
    });
  }
}

export function* watchUpdateActiveChangedProducts() {
  yield takeEvery(
    actions.UPDATE_ACTIVE_CHANGED_PRODUCTS,
    updateActiveChangedProducts,
  );
}

export function* getOrderByApiKey({ orderId, withStore }) {
  try {
    const order = yield call(API.getOrderByApiKey, orderId);
    yield put(actions.receiveOrder(order));
    if (withStore) {
      const [store, location] = yield all([
        API.getStoreLoyaltyByApiKey(order.storeId),
        API.getLocationByApiKey(order.orderLocationId),
      ]);
      yield put(actions.receiveReceiptStore(store, location));
    }
    yield put(actions.setGetOrderSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setGetOrderFailure(error));
  }
}

export function* watchGetOrderByApiKey() {
  yield takeEvery(actions.GET_ORDER_BY_APIKEY, getOrderByApiKey);
}

function* _updateOrderFromReceipt(orderId, phone, origin) {
  if (orderId) {
    const order = yield call(API.getOrderByApiKey, orderId);
    if (order.receiptCustomerId === phone) {
      let _order = _.cloneDeep(order);
      if (!order.orderCustomerId) {
        let storeCustomers = origin.stores.items;
        storeCustomers = yield getRegisterdCustomer(storeCustomers);
        storeCustomers = (storeCustomers || []).reduce((obj, customer) => {
          obj[customer.storeId] = customer;
          return obj;
        }, {});
        let storeCustomer = storeCustomers[order.storeId] || {};
        if (_.isEmpty(storeCustomer)) {
          const location = yield call(
            API.getStoreLocation,
            order.orderLocationId,
          );
          const salesDate =
            location.state === "opened" && location.sales?.startedAt;
          storeCustomer = yield call(API.createCustomer, {
            id: generateId("cst"),
            customerId: origin.id,
            storeId: order.storeId,
            locationId: order.orderLocationId,
            status: "registered",
            registeredAt: new Date().toISOString(),
            salesDate,
          });
        }
        _order = {
          ..._order,
          customerId: origin.id,
          orderCustomerId: storeCustomer.id,
          receiptCustomerId: null,
        };
        yield call(API.updateOrder, _order);
      } else {
        let _storeCustomer = yield call(
          API.getLocationCustomer,
          order.orderCustomerId,
          order.orderLocationId,
        );
        if (_storeCustomer.status === "anonymous") {
          let updatedCustomers = yield getRegisterdCustomer(
            origin.stores.items,
          );
          updatedCustomers = (updatedCustomers || []).reduce(
            (obj, customer) => {
              obj[customer.storeId] = customer;
              return obj;
            },
            {},
          );
          if (!_.isEmpty(updatedCustomers[order.storeId] || {})) {
            const orders = yield API.getOrdersByLocation(order.orderLocationId);
            const ordersByStoreCustomerId = orders.items.reduce(
              (obj, order) => {
                obj[order.orderCustomerId] = [
                  ...(obj[order.orderCustomerId] || []),
                  order,
                ];
                return obj;
              },
              {},
            );
            yield all(
              ordersByStoreCustomerId[_storeCustomer.id].map((ord) =>
                call(API.updateOrder, {
                  ...ord,
                  customerId: origin.id,
                  orderCustomerId: updatedCustomers[order.storeId].id,
                  updatedAt: new Date().toISOString(),
                  receiptCustomerId: null,
                }),
              ),
            );
            yield call(API.deleteCustomer, _storeCustomer.id);
            _storeCustomer = updatedCustomers[order.storeId];
          } else {
            const location = yield call(
              API.getStoreLocation,
              order.orderLocationId,
            );
            const salesDate =
              location.state === "opened" && location.sales?.startedAt;
            const currentTime = new Date().toISOString();
            _storeCustomer = yield call(API.updateCustomer, {
              ..._storeCustomer,
              customerId: origin.id,
              status: "registered",
              registeredAt: currentTime,
              updatedAt: currentTime,
              salesDate,
            });
            _order = {
              ..._order,
              customerId: origin.id,
              orderCustomerId: _storeCustomer.id,
              receiptCustomerId: null,
            };
            yield call(API.updateOrder, _order);
          }
        }
      }
    }
  }
}

export function* updateOrderFromReceipt({ orderId, user }) {
  try {
    let origin = yield call(
      _getMarketCustomer,
      user.attributes.email.split("@")[0],
    );
    yield call(
      _updateOrderFromReceipt,
      orderId,
      user.attributes.email.split("@")[0],
      origin,
    );
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketOrderStatusFailure(error));
  }
}

export function* watchUpdateOrderFromReceipt() {
  yield takeEvery(actions.UPDATE_ORDER_FROM_RECEIPT, updateOrderFromReceipt);
}

export function* getSubscriptionPlans({ storeId }) {
  try {
    const items = yield _getItems(storeId);
    yield put(actions.receiveItems(items));
    let plans = [];
    let data = {};
    do {
      data = yield call(
        API.getSubscriptionPlans,
        storeId,
        data.nextToken,
        //limit 100 or more
        500,
      );
      plans = [...plans, ...data.items];
    } while (data.nextToken);
    yield put(actions.receiveSubscriptionPlans(plans));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    console.log(error);
    yield put(actions.setSubscriptionStatusFailure(error));
    Analytics.sendErrorReport(error, "getSubscriptionPlans", { storeId });
  }
}

export function* watchGetSubscriptionPlans() {
  yield takeEvery(actions.GET_SUBSCRIPTION_PLANS, getSubscriptionPlans);
}

export function* getSubscriptionPlan({ planId, locationId }) {
  try {
    let plan = yield call(API.getSubscriptionPlan, planId);
    const store = yield call(API.getStore, plan.storeId);
    plan = {
      ...plan,
      storeInfo: {
        ...store.info,
        policy: store.policy,
      },
    };
    if (locationId) {
      const location = yield call(API.getStoreLocation, locationId);
      plan = {
        ...plan,
        location: location,
      };
    }
    yield put(actions.receiveSubscriptionPlan(plan));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    yield put(actions.setSubscriptionStatusFailure(error));
    Analytics.sendErrorReport(error, "getSubscriptionPlan", { planId });
  }
}

export function* watchGetSubscriptionPlan() {
  yield takeEvery(actions.GET_SUBSCRIPTION_PLAN, getSubscriptionPlan);
}

export function* createSubscriptionPlan({ plan }) {
  try {
    let _plan = _.cloneDeep(plan);
    if (!_plan.id) {
      _plan.id = generateId("sp");
    }
    _plan = {
      ..._plan,
      storeId: yield select(reducers.getStoreId),
    };
    const createdPlan = yield call(API.createSubscriptionPlan, _plan);
    yield put(actions.receiveSubscriptionPlan(createdPlan));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    yield put(actions.setSubscriptionStatusFailure(error));
    Analytics.sendErrorReport(error, "createSubscriptionPlan", { plan });
  }
}

export function* watchCreateSubscriptionPlan() {
  yield takeEvery(actions.CREATE_SUBSCRIPTION_PLAN, createSubscriptionPlan);
}

export function* updateSubscriptionPlan({ plan }) {
  try {
    let _plan = _.cloneDeep(plan);
    const updatedPlan = yield call(API.updateSubscriptionPlan, _plan);
    yield put(actions.receiveSubscriptionPlan(updatedPlan));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    yield put(actions.setSubscriptionStatusFailure(error));
    Analytics.sendErrorReport(error, "updateSubscriptionPlan", { plan });
  }
}

export function* watchUpdateSubscriptionPlan() {
  yield takeEvery(actions.UPDATE_SUBSCRIPTION_PLAN, updateSubscriptionPlan);
}

export function* deleteSubscriptionPlan({ plan }) {
  try {
    yield call(API.deleteSubscriptionPlan, plan.id);
    yield put(
      actions.receiveSubscriptionPlan({
        ...plan,
        _delta: "DELETE",
      }),
    );
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    yield put(actions.setSubscriptionStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteSubscriptionPlan", { plan });
  }
}

export function* watchDeleteSubscriptionPlan() {
  yield takeEvery(actions.DELETE_SUBSCRIPTION_PLAN, deleteSubscriptionPlan);
}

export function* createCustomerPayment({ customer, payment }) {
  try {
    const updatedPayment = yield call(API.createCustomerPayment, {
      ...payment,
      id: generateId("cp"),
      method: "card",
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      customerId: customer.id,
    });
    const updatedCustomer = yield call(_getMarketCustomer, customer.id);
    yield put(actions.receiveUser(updatedCustomer.customer));
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    const errorMessage = error.errors
      ? error.errors[0].message
        ? error.errors[0].message
        : "카드 추가에 실패했습니다."
      : error;
    yield put(actions.setMarketStatusFailure(errorMessage));
  }
}

export function* watchCreateCustomerPayment() {
  yield takeEvery(actions.CREATE_CUSTOMER_PAYMENT, createCustomerPayment);
}

export function* deleteCustomerPayment({ id }) {
  try {
    const customer = yield select(reducers.getUser);
    const deletedPayment = yield call(API.deleteCustomerPayment, id);
    const updatedCustomer = yield call(_getMarketCustomer, customer.id);
    yield put(actions.receiveUser(updatedCustomer.customer));
    yield put(actions.setMarketStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setMarketStatusFailure(error));
  }
}

export function* watchDeleteCustomerPayment() {
  yield takeEvery(actions.DELETE_CUSTOMER_PAYMENT, deleteCustomerPayment);
}

export function* subscribeSubscriptionPlan({
  customerId,
  planId,
  locationId,
  storeId,
}) {
  try {
    const data = yield call(
      API.getLocationCustomersByOriginId,
      customerId,
      {
        eq: storeId,
      },
      null,
      null,
      {
        _delta: {
          ne: "DELETE",
        },
        status: {
          eq: "registered",
        },
      },
    );
    const locationCustomers = data.items;
    const location = yield call(API.getStoreLocation, locationId);
    const salesDate = location.state === "opened" && location.sales?.startedAt;
    if (locationCustomers.length > 0) {
      const locationsById = (locationCustomers[0].locations || []).reduce(
        (obj, loc) => {
          obj[loc.id] = loc;
          return obj;
        },
        {},
      );
      if (!(locationId in locationsById)) {
        locationsById[locationId] = {
          id: locationId,
          isFavorite: false,
          status: "registered",
          point: {
            used: 0,
            saved: 0,
            extra: 0,
            lastSavedAt: null,
            lastTransactedAt: null,
          },
          credit: {
            deposit: 0,
            withdraw: 0,
            balance: 0,
            lastTransactedAt: null,
          },
          registeredAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
          salesDate,
        };
        yield call(API.updateCustomer, {
          ...locationCustomers[0],
          locations: Object.values(locationsById),
        });
      }
    } else {
      const customer = yield select(reducers.getUser);
      yield call(API.createCustomer, {
        ...(yield _buildBaseCustomer(customer)),
        id: generateId("cst"),
        storeId: storeId,
        locationId: locationId,
        status: "registered",
        registeredAt: new Date().toISOString(),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        salesDate,
      });
    }
    const subscription = yield call(
      API.subscribeSubscriptionPlan,
      customerId,
      planId,
      locationId,
    );
    const updatedCustomer = yield call(_getMarketCustomer, customerId);
    yield put(actions.receiveUser(updatedCustomer.customer));
    yield put(actions.setSubscriptionEditing(subscription));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    console.log(error);
    yield put(actions.setSubscriptionStatusFailure(error));
  }
}

export function* watchSubscribeSubscriptionPlan() {
  yield takeEvery(
    actions.SUBSCRIBE_SUBSCRIPTION_PLAN,
    subscribeSubscriptionPlan,
  );
}

export function* stopSubscription({ subscriptionId }) {
  try {
    const subscription = yield call(API.stopSubscription, subscriptionId);
    let _customer = yield select(reducers.getUser);
    const { customer } = yield call(_getMarketCustomer, _customer.id);
    yield put(actions.receiveUser(customer));
    yield put(actions.setSubscriptionEditing(subscription));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    console.log(error);
    yield put(actions.setSubscriptionStatusFailure(error));
  }
}

export function* watchStopSubscription() {
  yield takeEvery(actions.STOP_SUBSCRIPTION, stopSubscription);
}

export function* restoreSubscription({ subscriptionId }) {
  try {
    const subscription = yield call(API.restoreSubscription, subscriptionId);
    let _customer = yield select(reducers.getUser);
    const { customer } = yield call(_getMarketCustomer, _customer.id);
    yield put(actions.receiveUser(customer));
    yield put(actions.setSubscriptionEditing(subscription));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    console.log(error);
    yield put(actions.setSubscriptionStatusFailure(error));
  }
}

export function* watchRestoreSubscription() {
  yield takeEvery(actions.RESTORE_SUBSCRIPTION, restoreSubscription);
}

export function* cancelSubscription({ subscriptionId }) {
  try {
    const subscription = yield call(API.cancelSubscription, subscriptionId);
    let _customer = yield select(reducers.getUser);
    const { customer } = yield call(_getMarketCustomer, _customer.id);
    yield put(actions.receiveUser(customer));
    yield put(actions.setSubscriptionEditing(subscription));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    console.log(error);
    yield put(actions.setSubscriptionStatusFailure(error));
  }
}

export function* watchCancelSubscription() {
  yield takeEvery(actions.CANCEL_SUBSCRIPTION, cancelSubscription);
}

export function* getSubscriptionEvents({ customerId }) {
  try {
    let eventsData = {};
    let data = [];
    do {
      eventsData = yield call(
        API.getSubscriptionEvents,
        customerId,
        20,
        eventsData.nextToken,
      );
      data = [...data, ...eventsData.items];
    } while (eventsData.nextToken);
    yield put(actions.receiveSubscriptionEvents(data));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    console.log(error);
    yield put(actions.setSubscriptionStatusFailure(error));
  }
}

export function* watchGetSubscriptionEvents() {
  yield takeEvery(actions.GET_SUBSCRIPTION_EVENTS, getSubscriptionEvents);
}

export function* getSubscriptionPlansByIds({ ids }) {
  try {
    const plans = yield all(ids.map((id) => call(API.getSubscriptionPlan, id)));
    yield put(actions.receiveSubscriptionPlans(plans));
    yield put(actions.setSubscriptionStatusSucess());
  } catch (error) {
    console.log(error);
    yield put(actions.setSubscriptionStatusFailure(error));
  }
}

export function* watchGetSubscriptionPlansByIds() {
  yield takeEvery(
    actions.GET_SUBSCRIPTION_PLANS_BY_IDS,
    getSubscriptionPlansByIds,
  );
}

export function* createOrUpdateTerminal({ terminal }) {
  try {
    let _terminal = _.cloneDeep(terminal);
    let currentTerminal = yield call(API.getTerminal, terminal.id);
    if (!currentTerminal) {
      _terminal = yield call(API.createTerminal, _terminal);
    } else {
      _terminal = yield call(API.updateTerminal, {
        ..._terminal,
        updatedAt: new Date().toISOString(),
      });
    }
    yield put(actions.receiveContractTerminal(_terminal));
    yield put(actions.receiveTerminals([_terminal]));
    yield put(actions.setTerminalStatusSuccesss());
  } catch (error) {
    console.log(error);
    yield put(actions.setTerminalStatusFailure(error));
  }
}

export function* watchCreateOrUpdateTerminal() {
  yield takeEvery(actions.CREATE_OR_UPDATE_TERMINAL, createOrUpdateTerminal);
}

export function* deleteTerminal({ terminalId }) {
  try {
    yield call(API.deleteTerminal, terminalId);
    yield put(actions.receiveDeletedTerminal(terminalId));
    yield put(actions.receiveDeletedContractTerminalId(terminalId));
    yield put(actions.setTerminalStatusSuccesss());
  } catch (error) {
    yield put(actions.setTerminalStatusFailure(error));
  }
}

export function* watchDeleteTerminal() {
  yield takeEvery(actions.DELETE_TERMINAL, deleteTerminal);
}

export function* getTerminals() {
  try {
    const terminals = yield call(_getAllTerminals, {});
    yield put(actions.receiveTerminals(terminals));
    yield put(actions.setTerminalStatusSuccesss());
  } catch (error) {
    yield put(actions.setTerminalStatusFailure(error));
  }
}

export function* watchGetTerminals() {
  yield takeEvery(actions.GET_TERMINALS, getTerminals);
}

export function* getExpectedRecipientNumber({
  storeId,
  locationId,
  locationIds,
  tagId,
  includes,
  filter,
}) {
  try {
    let _filter = [
      ...(locationId
        ? [
            {
              key: "locationId",
              value: locationId || null,
            },
          ]
        : []),
      {
        key: "status",
        value: "registered",
      },
      ...(tagId ? [{ key: "tagId", value: tagId }] : []),
      ...(!_.isEmpty(filter)
        ? [
            {
              key: "registeredAt",
              operation: filter.operation,
              value: filter.value,
            },
          ]
        : []),
    ];
    const count = yield call(
      API.getCustomerCount,
      storeId,
      locationId,
      "registered",
      _filter,
      includes,
      locationIds,
    );
    yield put(actions.setExpectedRecipientNumber(count));
    yield put(actions.setGetCampaignSuccess());
  } catch (error) {
    yield put(actions.setGetCampaignFailure(error));
  }
}

export function* watchGetRecipientCount() {
  yield takeEvery(
    actions.GET_EXPECTED_RECIPIENT_NUMBER,
    getExpectedRecipientNumber,
  );
}

export function* getRegisteredCustomerCount({ storeId, locationId }) {
  try {
    const count = yield call(
      API.getCustomerCount,
      storeId,
      locationId,
      "registered",
      [
        ...(locationId
          ? [
              {
                key: "locationId",
                value: locationId,
              },
            ]
          : []),
        {
          key: "status",
          value: "registered",
        },
      ],
    );
    yield put(actions.receiveCustomers([], count, false));
    yield put(actions.setGetRegisteredCustomerCountSuccess());
  } catch (error) {
    yield put(actions.setGetRegisteredCustomerCountFailure(error));
  }
}

export function* watchGetRegisteredCustomerCount() {
  yield takeEvery(
    actions.GET_REGISTERED_CUSTOMER_COUNT,
    getRegisteredCustomerCount,
  );
}

export function* updateReceiptConfig({ config }) {
  try {
    const isMultiLocation = yield select(reducers.isMultiLocation);
    const storeId = yield select(reducers.getStoreId);
    if (isMultiLocation) {
      const locationId = yield select(reducers.getCurrentLocationId);
      let location = yield call(API.getStoreLocation, locationId);
      yield call(API.updateLocation, {
        ...location,
        receipt: config,
      });
    } else {
      let store = yield call(API.getStore, storeId);
      yield call(API.updateStore, {
        ...store,
        receipt: config,
      });
    }
    let updatedStore = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateReceiptConfig", {
      config,
    });
  }
}

export function* watchUpdateReceiptConfig() {
  yield takeEvery(actions.UPDATE_RECEIPT_CONFIG, updateReceiptConfig);
}

export function* updateViewConfig({ config }) {
  try {
    const isMultiLocation = yield select(reducers.isMultiLocation);
    const storeId = yield select(reducers.getStoreId);
    if (isMultiLocation && Authority.level() === "location") {
      const locationId = yield select(reducers.getCurrentLocationId);
      let location = yield call(API.getStoreLocation, locationId);
      yield call(API.updateLocation, {
        ...location,
        view: config,
      });
    } else {
      let store = yield call(API.getStore, storeId);
      yield call(API.updateStore, {
        ...store,
        view: config,
      });
    }
    let updatedStore = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateViewConfig", {
      config,
    });
  }
}

export function* watchUpdateViewConfig() {
  yield takeEvery(actions.UPDATE_VIEW_CONFIG, updateViewConfig);
}

export function* findEmployeeByEmail({ email }) {
  try {
    yield put(actions.receiveSearchedMgmtStores([]));
    const employee = yield call(API.getEmployeeByEmail, email);
    if (employee) {
      if (employee.employeeStoreId) {
        const store = yield call(API.getStore, employee.employeeStoreId);
        yield put(actions.receiveSearchedMgmtStores([{ ...employee, store }]));
      } else {
        const organizations = yield call(
          _getAllOrganizationsByEmployee,
          employee.id,
        );
        let searched = [];
        for (let org of organizations) {
          const store = yield call(API.getStore, org.storeId);
          if (org.locationId) {
            const location = yield call(API.getStoreLocation, org.locationId);
            searched = [
              ...searched,
              {
                ...employee,
                store: { ...store, location: location },
              },
            ];
          } else {
            searched = [...searched, { ...employee, store }];
          }
        }
        yield put(actions.receiveSearchedMgmtStores(searched));
      }
    }
    yield put(actions.setMgmtStatusSuccess());
  } catch (error) {
    yield put(actions.setMgmtStatusFailure(error));
    Analytics.sendErrorReport(error, "findEmployeeByEmail", email);
  }
}

export function* watchFindEmployeeByEmail() {
  yield takeEvery(actions.FIND_EMPLOYEE_BY_EMAIL, findEmployeeByEmail);
}

export function* getMgmtContract({ employee }) {
  try {
    let data = {
      store: employee.store,
      operator: {},
      contract: {},
      terminals: [],
      rentals: [],
    };

    // Owner of store
    if (employee.employeeStoreId) {
      const storeContract = yield call(API.getStoreContract, employee.store.id);
      data.contract = storeContract || {};
      if (data.contract.businessOperatorId) {
        const [operator, terminals, rentals] = yield all([
          API.getBusinessOperator(storeContract.businessOperatorId),
          _getAllTerminals({
            storeId: employee.store.id,
          }),
          _getAllRentalContracts({ storeId: employee.store.id }),
        ]);
        data.operator = operator || {};
        data.terminals = terminals;
        data.rentals = rentals;
      }
      if (employee.store.policy?.multiLocation) {
        const locations = yield call(_getLocations, employee.employeeStoreId);
        yield put(actions.receiveMgmtLocations(locations));
      }
    } else {
      const locationContract = yield call(
        API.getLocationContract,
        employee.store.location.id,
      );
      data.contract = locationContract || {};
      if (data.contract.businessOperatorId) {
        const [operator, terminals, rentals] = yield all([
          API.getBusinessOperator(locationContract.businessOperatorId),
          _getAllTerminals({
            locationId: employee.store.location.id,
          }),
          _getAllRentalContracts({ locationId: employee.store.location.id }),
        ]);
        data.operator = operator || {};
        data.terminals = terminals;
        data.rentals = rentals;
      }
    }

    if (data.operator.businessNumber) {
      let _terminals = yield call(_getAllTerminals, {
        businessNumber: data.operator.businessNumber,
      });
      data.terminals = Object.values(
        [...data.terminals, ..._terminals].reduce((obj, t) => {
          obj[t.id] = t;
          return obj;
        }, {}),
      );
    }

    console.log("RECEIVED MGMT CONTRACT", data);
    yield put(actions.receiveMgmtContract(data));
    yield put(actions.setMgmtStatusSuccess());
  } catch (error) {
    yield put(actions.setMgmtStatusFailure(error));
    Analytics.sendErrorReport(error, "getMgmtContract", employee);
  }
}

export function* watchGetMgmtContract() {
  yield takeEvery(actions.GET_MGMT_CONTRACT, getMgmtContract);
}

export function* clearAllContract() {
  try {
    yield put(actions.clearOperator());
    yield put(actions.clearContract());
    yield put(actions.clearMgmtLocations());
    yield put(actions.setMgmtStatusSuccess());
  } catch (error) {
    yield put(actions.setMgmtStatusFailure(error));
  }
}

export function* watchClearAllContract() {
  yield takeEvery(actions.CLEAR_ALL_CONTRACT, clearAllContract);
}

export function* createOrUpdateContract({
  contract,
  operator,
  employee,
  isFranchise,
}) {
  try {
    // TODO: businessNumber 바뀌었을때
    let updatedContract = null;
    let updatedOperator = _.cloneDeep(operator);
    if (!isFranchise) {
      if (!operator.id) {
        updatedOperator = yield call(API.createBusinessOperator, {
          id: hashMd5(operator.businessNumber),
          businessNumber: operator.businessNumber,
          name: operator.name,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      } else {
        let originalOperator = yield call(API.getBusinessOperator, operator.id);
        if (originalOperator.businessNumber !== operator.businessNumber) {
          let terminals = yield call(_getAllTerminals, {
            businessOperatorId: originalOperator.id,
          });
          let rentals = yield call(_getAllRentalContracts, {
            businessOperatorId: originalOperator.id,
          });

          updatedOperator = yield call(
            _updateBusinessNumberForOperation,
            operator,
            terminals,
            rentals,
          );
        } else {
          updatedOperator = yield call(API.updateBusinessOperator, {
            ...operator,
            updatedAt: new Date().toISOString(),
          });
        }
      }
    }
    contract = { ...contract, businessOperatorId: updatedOperator.id };
    if (employee.employeeStoreId) {
      const storeContract = yield call(API.getStoreContract, employee.store.id);
      if (!storeContract) {
        updatedContract = yield call(API.createStoreContract, {
          ...contract,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      } else {
        updatedContract = yield call(API.updateStoreContract, {
          ...contract,
          updatedAt: new Date().toISOString(),
        });
      }
    } else {
      const locationContract = yield call(
        API.getLocationContract,
        employee.store.location.id,
      );
      if (!locationContract) {
        updatedContract = yield call(API.createLocationContract, contract);
      } else {
        updatedContract = yield call(API.updateLocationContract, contract);
      }
    }
    if (updatedContract) {
      yield put(actions.receiveUpdatedContract(updatedContract));
    }
    yield put(actions.receiveOperator(updatedOperator));
    yield put(actions.setMgmtStatusSuccess());
  } catch (error) {
    yield put(actions.setMgmtStatusFailure(error));
  }
}

export function* watchCreateOrUpdateContract() {
  yield takeEvery(actions.CREATE_OR_UPDATE_CONTRACT, createOrUpdateContract);
}

export function* updateMgmtContract({ contract, employee }) {
  try {
    let updatedContract = null;
    if (employee.employeeStoreId) {
      const storeContract = yield call(API.getStoreContract, employee.store.id);
      if (!storeContract) {
        updatedContract = yield call(API.createStoreContract, {
          ...contract,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      } else {
        updatedContract = yield call(API.updateStoreContract, {
          ...contract,
          updatedAt: new Date().toISOString(),
        });
      }
    } else {
      const locationContract = yield call(
        API.getLocationContract,
        employee.store.location.id,
      );
      if (!locationContract) {
        updatedContract = yield call(API.createLocationContract, contract);
      } else {
        updatedContract = yield call(API.updateLocationContract, contract);
      }
    }
    if (updatedContract) {
      yield put(actions.receiveUpdatedContract(updatedContract));
    }
    yield put(actions.setMgmtStatusSuccess());
  } catch (error) {
    yield put(actions.setMgmtStatusFailure(error));
  }
}

export function* watchUpdateMgmtContract() {
  yield takeEvery(actions.UPDATE_MGMT_CONTRACT, updateMgmtContract);
}

export function* createOrUpdateRentalContract({ rentalContract }) {
  try {
    let updatedContract = _.cloneDeep(rentalContract);
    if (!rentalContract.id) {
      updatedContract = yield call(API.createRentalContract, {
        ...rentalContract,
        id: generateId("rc"),
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
    } else {
      updatedContract = yield call(API.updateRentalContract, {
        ...rentalContract,
        updatedAt: new Date().toISOString(),
      });
    }
    yield put(actions.receiveRentals([updatedContract]));
    yield put(actions.setMgmtStatusSuccess());
  } catch (error) {
    yield put(actions.setMgmtStatusFailure(error));
  }
}

export function* watchCreateOrUpdateRentalContract() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_RENTAL_CONTRACT,
    createOrUpdateRentalContract,
  );
}

export function* findStoresByOperatorId({ businessOperatorId }) {
  try {
    let stores = [];
    const storeContracts = yield call(
      API.getStoreContractsByBusinessOperator,
      businessOperatorId,
      null,
      300,
    );
    for (let c of storeContracts.items) {
      const store = yield call(API.getStore, c.id);
      stores = [...stores, { employeeStoreId: store.id, store: store }];
    }
    const locationContracts = yield call(
      API.getLocationContractsByBusinessOperator,
      businessOperatorId,
      null,
      300,
    );
    for (let c of locationContracts.items) {
      const location = yield call(API.getStoreLocation, c.id);
      const store = yield call(API.getStore, location.storeId);
      stores = [
        ...stores,
        { employeeStoreId: null, store: { ...store, location: location } },
      ];
    }
    yield put(actions.receiveSearchedStoresByOperatorId(stores));
    yield put(actions.setMgmtStatusSuccess());
  } catch (error) {
    yield put(actions.setMgmtStatusFailure(error));
  }
}

export function* watchFindStoresByOperatorId() {
  yield takeEvery(actions.FIND_STORES_BY_OPERATOR_ID, findStoresByOperatorId);
}

export function* createOrUpdateLabelTemplate({ template }) {
  try {
    if (template.id) {
      const updatedTemplate = yield call(API.updateLabelTemplate, {
        ...template,
        updatedAt: new Date().toISOString(),
      });
      yield put(actions.receiveLabelTemplate(updatedTemplate));
    } else {
      const storeId = yield select(reducers.getStoreId);
      const organization = yield select(reducers.getCurrentOrganization);
      const createdTemplate = yield call(API.createLabelTemplate, {
        ...template,
        id: generateId("lbt"),
        storeId: storeId,
        locationId: organization.locationId,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      yield put(actions.receiveLabelTemplate(createdTemplate));
    }
    yield put(actions.setTemplateStatusSuccess());
  } catch (error) {
    yield put(actions.setTemplateStatusFailure(error));
  }
}

export function* watchCreateOrUpdateLabelTemplate() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_LABEL_TEMPLATE,
    createOrUpdateLabelTemplate,
  );
}

export function* deleteLabelTemplate({ id }) {
  try {
    yield call(API.deleteLabelTemplate, id);
    yield put(actions.receiveDeletedLabelTemplate(id));
    yield put(actions.setTemplateStatusSuccess());
  } catch (error) {
    yield put(actions.setTemplateStatusFailure(error));
  }
}

export function* watchDeleteLabelTemplate() {
  yield takeEvery(actions.DELETE_LABEL_TEMPLATE, deleteLabelTemplate);
}

export function* getLabelTemplates({ storeId }) {
  try {
    const locationId = yield select(reducers.getCurrentLocationId);
    let data = yield call(API.getLabelTemplatesByStore, storeId);
    let templates = data.items;
    templates = templates.filter(
      (template) => template.locationId === locationId || !template.locationId,
    );
    yield put(actions.receiveLabelTemplates(templates));
    yield put(actions.setTemplateStatusSuccess());
  } catch (error) {
    yield put(actions.setTemplateStatusFailure(error));
  }
}

export function* watchGetLabelTemplates() {
  yield takeEvery(actions.GET_LABEL_TEMPLATES, getLabelTemplates);
}

export function* updateSettings({ settings }) {
  try {
    const isMultiLocation = yield select(reducers.isMultiLocation);
    const storeId = yield select(reducers.getStoreId);
    if (isMultiLocation && Authority.level() !== "store") {
      const locationId = yield select(reducers.getCurrentLocationId);
      const location = yield call(API.getStoreLocation, locationId);
      yield call(API.updateLocation, {
        ..._.cloneDeep(location),
        settings: _.cloneDeep(settings),
      });
      const updatedStore = yield call(_getStoreWithLocations, storeId);
      yield put(actions.initStore(updatedStore));
    } else {
      const store = yield call(API.getStore, storeId);
      const updatedStore = yield call(_updateStore, {
        ..._.cloneDeep(store),
        settings: _.cloneDeep(settings),
      });
      yield put(actions.initStore(updatedStore));
    }
    yield put(actions.setStoreSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateSettings", { settings });
  }
}

export function* watchUpdateSettings() {
  yield takeEvery(actions.UPDATE_SETTINGS, updateSettings);
}

export function* convertPosMode({ newMode }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const store = yield call(API.getStore, storeId);
    // 소매 → 외식(general) 일괄 전환 시 지점 메뉴판 타입을 카테고리 메뉴판으로 맞춘다.
    // POS(sales-rn MainScreen)는 settings.menuMode 가 비어 있으면 'multi'(멀티 메뉴판)로 처리하므로,
    // 명시해 주지 않으면 전환 직후 전 지점이 멀티 메뉴판으로 뜬다.
    // POS 가 있는 오프라인 지점만 대상 (창고·자사몰 pseudo 지점 제외 — getOfflineLocations 기준과 동일).
    // ★ id + settings 만 보낸다. updateLocation 은 partial update 라 안 보낸 필드는 건드리지 않으므로,
    //   menu/kiosk/receipt 같은 다른 nested 객체를 되돌려 쓰다가 잘려 나갈 위험이 없다.
    //   settings 는 nested 객체 전체가 교체되므로 기존 값을 spread 해서 통째로 넘긴다.
    // ★ _updateStore 가 끝에서 store+locations 를 다시 읽어오므로, 지점 갱신을 그 앞에 둬야
    //   initStore 에 반영된다.
    if (newMode === "general") {
      const locations = yield call(_getLocations, storeId);
      yield all(
        (locations || [])
          .filter((loc) => !loc.provider && loc.type !== "warehouse")
          .map((loc) =>
            call(API.updateLocation, {
              id: loc.id,
              settings: {
                ...(loc.settings ?? {}),
                menuMode: "category",
              },
            }),
          ),
      );
    }
    const updatedStore = yield call(_updateStore, {
      ..._.cloneDeep(store),
      ...(newMode === "retail" && store.market?.status === "active"
        ? { market: { ...store.market, status: "inactive" } }
        : {}),
      settings: {
        ...store.settings,
        mode: newMode,
        // 단일지점 매장은 POS 가 지점 settings 가 아니라 store settings 를 읽는다
        // (sales-rn getOrderSettings: isMultiLocation 이면 location.settings, 아니면 store.settings.
        //  schema 의 Store.settings 주석도 "multiLocation don't use"). 위 지점 루프만으로는
        //  단일지점 매장에 적용되지 않으므로 store 쪽에도 같이 넣는다.
        ...(newMode === "general" ? { menuMode: "category" } : {}),
      },
    });
    yield put(actions.initStore(updatedStore));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "convertPosMode", { newMode });
  }
}

export function* watchConvertPosMode() {
  yield takeEvery(actions.CONVERT_POS_MODE, convertPosMode);
}

export function* createInventoryTransferHistory({
  history,
  templateType,
  templateSessionId,
}) {
  try {
    let _history = _.cloneDeep(history);
    const storeId = yield select(reducers.getStoreId);
    const employee = yield select(reducers.getEmployee);
    _history = {
      ..._history,
      storeId,
      employeeId: _history.employeeId || employee.id,
    };
    const origin = yield select(reducers.getRequestProgressEventOrigin);
    API.applyInventoryTransferHistory(
      _history,
      origin.name,
      origin.id,
      templateType,
      templateSessionId,
    );
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
  }
}

export function* watchCreateInventoryTransferHistory() {
  yield takeEvery(
    actions.CREATE_INVENTORY_TRANSFER_HISTORY,
    createInventoryTransferHistory,
  );
}

export function* sharePurchaseOrderPdf({ purchaseOrder }) {
  try {
    const skus = yield select(reducers.getSkusById);
    const prodsById = yield select(reducers.getProductsById);

    let header = PurchaseOrderHeader;
    let info = PurchaseOrderInfo;
    let items = PurchaseOrderItems;
    let costInfo = PurchaseOrderCostInfo;
    let _title = "발주서";

    let _html = _.cloneDeep(PurchaseOrderSample);

    let _infoString = _.cloneDeep(info);
    if (_infoString.includes("{{createdAt}}")) {
      _infoString = _infoString.replace(
        "{{createdAt}}",
        purchaseOrder.createdAt,
      );
    }
    if (_infoString.includes("{{supplier}}")) {
      _infoString = _infoString.replace("{{supplier}}", purchaseOrder.supplier);
    }
    if (_infoString.includes("{{location}}")) {
      _infoString = _infoString.replace("{{location}}", purchaseOrder.location);
    }
    if (_infoString.includes("{{expectDate}}")) {
      _infoString = _infoString.replace(
        "{{expectDate}}",
        purchaseOrder.expectDate,
      );
    }
    if (_infoString.includes("{{receivedAt}}")) {
      _infoString = _infoString.replace(
        "{{receivedAt}}",
        purchaseOrder.receivedAt,
      );
    }
    if (_infoString.includes("{{employee}}")) {
      _infoString = _infoString.replace("{{employee}}", purchaseOrder.employee);
    }
    if (_infoString.includes("{{status}}")) {
      _infoString = _infoString.replace("{{status}}", purchaseOrder.status);
    }
    if (_infoString.includes("{{totalCost}}")) {
      _infoString = _infoString.replace(
        "{{totalCost}}",
        numberToCurrency(purchaseOrder.totalCost),
      );
    }

    let _costInfoString = _.cloneDeep(costInfo);
    if (_costInfoString.includes("{{supplyCost}}")) {
      _costInfoString = _costInfoString.replace(
        "{{supplyCost}}",
        numberToCurrency(purchaseOrder.supplyCost),
      );
    }
    if (_costInfoString.includes("{{vat}}")) {
      _costInfoString = _costInfoString.replace(
        "{{vat}}",
        numberToCurrency(purchaseOrder.vat),
      );
    }
    if (_costInfoString.includes("{{totalCost}}")) {
      _costInfoString = _costInfoString.replace(
        "{{totalCost}}",
        numberToCurrency(purchaseOrder.totalCost),
      );
    }

    // NOTE
    let _noteString = _.cloneDeep(NoteSample);
    if (_noteString.includes("{{note}}")) {
      const escapedNote = String(purchaseOrder.note || "")
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/\n/g, "<br/>");
      _noteString = _noteString.replace("{{note}}", escapedNote);
    }

    // TITLE
    let _titleString = _.cloneDeep(TitleSample);
    _titleString = _titleString.replace("{{title}}", _title);

    _html = _html.replace("{{info}}", _infoString);
    _html = _html.replace("{{costInfo}}", _costInfoString);
    _html = _html.replace("{{header}}", header);
    _html = _html.replace("{{noteSample}}", _noteString);
    _html = _html.replace("{{titleSample}}", _titleString);

    let _skusString = "";
    for (let item of purchaseOrder.inventories || []) {
      let statementItem = _.cloneDeep(items);
      const sku = skus[item.skuId];
      const product = prodsById[sku?.parentId];
      if (statementItem.includes("{{prodName}}")) {
        statementItem = statementItem.replace(
          "{{prodName}}",
          product ? product.name : "삭제된 제품",
        );
      }
      if (statementItem.includes("{{skuName}}")) {
        statementItem = statementItem.replace("{{skuName}}", sku?.name || "");
      }
      if (statementItem.includes("{{unit}}")) {
        statementItem = statementItem.replace("{{unit}}", sku?.unit || "");
      }
      if (statementItem.includes("{{model}}")) {
        statementItem = statementItem.replace("{{model}}", sku?.model || "");
      }
      if (statementItem.includes("{{cost}}")) {
        statementItem = statementItem.replace(
          "{{cost}}",
          numberToCurrency(item?.cost || ""),
        );
      }
      if (statementItem.includes("{{quantity}}")) {
        statementItem = statementItem.replace(
          "{{quantity}}",
          item?.quantity || "",
        );
      }
      if (statementItem.includes("{{waitingQuantity}}")) {
        statementItem = statementItem.replace(
          "{{waitingQuantity}}",
          item?.waitingQuantity || "",
        );
      }
      if (statementItem.includes("{{receivedQuantity}}")) {
        statementItem = statementItem.replace(
          "{{receivedQuantity}}",
          item?.receivedQuantity || "",
        );
      }
      if (statementItem.includes("{{totalCost}}")) {
        statementItem = statementItem.replace(
          "{{totalCost}}",
          numberToCurrency(item?.receivedQuantity * item?.cost),
        );
      }

      _skusString += statementItem;
    }
    _html = _html.replace("{{skus}}", _skusString);
    const options = {
      filename: "발주서",
      pagebreak: {
        avoid: "tr",
        mode: "css",
      },
      margin: [5, 0, 7, 0],
    };
    yield html2pdf()
      .set(options)
      .from(_html)
      .toPdf()
      .get("pdf")
      .then(function (pdf) {
        var totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(0);
          pdf.text(
            i + " / " + totalPages,
            pdf.internal.pageSize.getWidth() / 2.1,
            pdf.internal.pageSize.getHeight() - 2,
          );
        }
      })
      .save();
    yield put(actions.setSharePdfSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setSharePdfFailure(error));
    Analytics.sendErrorReport(error, "sharePurchaseOrderPdf", {
      purchaseOrder,
    });
  }
}

export function* watchSharePurchaseOrderPdf() {
  yield takeEvery(actions.SHARE_PURCHASE_ORDER_PDF, sharePurchaseOrderPdf);
}

export function* shareHistoryPdf({ history, _type = "bulk" }) {
  try {
    const skus = yield select(reducers.getSkusById);
    const prodsById = yield select(reducers.getProductsById);

    let header = null;
    let info = null;
    let items = null;
    let _title = "";
    if (_type === "transfer") {
      header = TransferHeader;
      info = TransferInfo;
      items = TransferItems;
      _title = "지점이동 내역서";
    } else if (_type === "count") {
      header = CountHeader;
      info = CountInfo;
      items = CountItems;
      _title = "재고실사 내역서";
    } else if (_type === "bulk") {
      header = BulkActionHeader;
      info = BulkActionInfo;
      items = BulkActionItems;
      _title = "재고조정 내역서";
    }

    let _html = _.cloneDeep(BasicSample);

    let _infoString = _.cloneDeep(info);
    if (_infoString.includes("{{createdAt}}")) {
      _infoString = _infoString.replace("{{createdAt}}", history.createdAt);
    }
    if (_infoString.includes("{{location}}")) {
      _infoString = _infoString.replace("{{location}}", history.location || "");
    }
    if (_infoString.includes("{{employee}}")) {
      _infoString = _infoString.replace("{{employee}}", history.employee || "");
    }
    if (_infoString.includes("{{fromLocation}}")) {
      _infoString = _infoString.replace(
        "{{fromLocation}}",
        history.fromLocation || "",
      );
    }
    if (_infoString.includes("{{toLocation}}")) {
      _infoString = _infoString.replace(
        "{{toLocation}}",
        history.toLocation || "",
      );
    }
    if (_infoString.includes("{{state}}")) {
      _infoString = _infoString.replace("{{state}}", history.state || "");
    }
    if (_infoString.includes("{{totalCost}}")) {
      _infoString = _infoString.replace(
        "{{totalCost}}",
        history.totalCost || "",
      );
    }
    // NOTE
    let _noteString = _.cloneDeep(NoteSample);
    if (_noteString.includes("{{note}}")) {
      _noteString = _noteString.replace("{{note}}", history.note || "");
    }

    // TITLE
    let _titleString = _.cloneDeep(TitleSample);
    _titleString = _titleString.replace("{{title}}", _title);

    _html = _html.replace("{{info}}", _infoString);
    _html = _html.replace("{{header}}", header);
    _html = _html.replace("{{noteSample}}", _noteString);
    _html = _html.replace("{{titleSample}}", _titleString);

    let _skusString = "";
    for (let item of history.origin.items || []) {
      let statementItem = _.cloneDeep(items);
      const sku = skus[item.skuId];
      const product = prodsById[sku.parentId];
      if (statementItem.includes("{{prodName}}")) {
        statementItem = statementItem.replace(
          "{{prodName}}",
          product?.name || "",
        );
      }
      if (statementItem.includes("{{skuName}}")) {
        statementItem = statementItem.replace("{{skuName}}", sku?.name || "");
      }
      if (statementItem.includes("{{unit}}")) {
        statementItem = statementItem.replace("{{unit}}", sku?.unit || "");
      }
      if (statementItem.includes("{{model}}")) {
        statementItem = statementItem.replace("{{model}}", sku?.model || "");
      }
      if (statementItem.includes("{{quantity}}")) {
        statementItem = statementItem.replace("{{quantity}}", item.quantity);
      }
      if (statementItem.includes("{{reason}}")) {
        statementItem = statementItem.replace(
          "{{reason}}",
          GetInventoryTransactionReason(item.reason),
        );
      }
      if (statementItem.includes("{{cost}}")) {
        statementItem = statementItem.replace(
          "{{cost}}",
          numberToCurrency(item.cost),
        );
      }
      if (statementItem.includes("{{receivedQuantity}}")) {
        statementItem = statementItem.replace(
          "{{receivedQuantity}}",
          item.receivedQuantity || "",
        );
      }
      if (statementItem.includes("{{diff}}")) {
        statementItem = statementItem.replace(
          "{{diff}}",
          item.receivedQuantity - item.quantity,
        );
      }
      if (statementItem.includes("{{countDiff}}")) {
        statementItem = statementItem.replace(
          "{{countDiff}}",
          item.currQuantity - item.prevQuantity,
        );
      }
      if (statementItem.includes("{{costDiff}}")) {
        statementItem = statementItem.replace(
          "{{costDiff}}",
          numberToCurrency(item.cost),
        );
      }
      if (statementItem.includes("{{prevQuantity}}")) {
        statementItem = statementItem.replace(
          "{{prevQuantity}}",
          item.prevQuantity || "",
        );
      }
      if (statementItem.includes("{{currQuantity}}")) {
        statementItem = statementItem.replace(
          "{{currQuantity}}",
          item.currQuantity || "",
        );
      }
      if (statementItem.includes("{{totalCost}}")) {
        statementItem = statementItem.replace(
          "{{totalCost}}",
          numberToCurrency(item.totalCost),
        );
      }
      _skusString += statementItem;
    }
    _html = _html.replace("{{skus}}", _skusString);
    const options = {
      filename: _title,
      pagebreak: {
        avoid: "tr",
        mode: "css",
      },
      margin: [5, 0, 7, 0],
    };
    yield html2pdf()
      .set(options)
      .from(_html)
      .toPdf()
      .get("pdf")
      .then(function (pdf) {
        var totalPages = pdf.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
          pdf.setPage(i);
          pdf.setFontSize(10);
          pdf.setTextColor(0);
          pdf.text(
            i + " / " + totalPages,
            pdf.internal.pageSize.getWidth() / 2.1,
            pdf.internal.pageSize.getHeight() - 2,
          );
        }
      })
      .save();

    yield put(actions.setSharePdfSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setSharePdfFailure(error));
    Analytics.sendErrorReport(error, "shareHistoryPdf", {
      history,
    });
  }
}

export function* watchShareHistoryPdf() {
  yield takeEvery(actions.SHARE_HISTORY_PDF, shareHistoryPdf);
}

export function* shareGroupHistoryPdf({ history, _type }) {
  try {
    const currentLocationId = yield select(reducers.getCurrentLocationId);
    const storeId = yield select(reducers.getStoreId);
    let histories = [];
    let filters = [
      {
        key: "groupId",
        value: history.origin.groupId,
        operation: "eq",
      },
    ];
    if (Authority.level() === "location") {
      filters.push({
        key: "locationId",
        value: currentLocationId,
        operation: "eq",
      });
    }
    let page = 1;
    let _count = 0;
    do {
      const { items, count } = yield call(
        API.getInventoryHistories,
        storeId,
        page,
        300,
        "single",
        filters,
      );
      histories = [...histories, ...items];
      _count = count;
      page++;
    } while (_count > histories.length);

    let _history = [];
    _history = {
      ...history,
      origin: {
        ...history.origin,
        items: histories || [],
        histories: histories || [],
      },
    };

    yield put(actions.shareHistoryPdf(_history, _type));
  } catch (error) {
    console.log(error);
    Analytics.sendErrorReport(error, "shareGroupHistoryPdf", {
      history,
      _type,
    });
  }
}

export function* watchShareGroupHistoryPdf() {
  yield takeEvery(actions.SHARE_GROUP_HISTORY_PDF, shareGroupHistoryPdf);
}

export function* updateInventoryHistory({
  historyId,
  quantity,
  cost,
  expiryDate,
}) {
  try {
    const updatedHistory = yield call(
      API.updateInventoryHistory,
      historyId,
      quantity,
      cost,
      expiryDate,
    );
    yield put(actions.receiveInventoryHistory(updatedHistory));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "updateInventoryHistory", {
      historyId,
      quantity,
      cost,
      expiryDate,
    });
  }
}

export function* watchUpdateInventoryHistory() {
  yield takeEvery(actions.UPDATE_INVENTORY_HISTORY, updateInventoryHistory);
}

export function* updateInventoryHistoryNote({ historyId, note }) {
  try {
    const updatedHistory = yield call(
      API.updateInventoryHistoryMeta,
      historyId,
      note,
    );
    yield put(actions.receiveInventoryHistory(updatedHistory));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "updateInventoryHistoryNote", {
      historyId,
      note,
    });
  }
}

export function* watchUpdateInventoryHistoryNote() {
  yield takeEvery(
    actions.UPDATE_INVENTORY_HISTORY_NOTE,
    updateInventoryHistoryNote,
  );
}

export function* refreshInventoryHistory({ page, size, historyType, filters }) {
  try {
    let histories = [];
    let _page = 1;
    let _count = 0;
    const storeId = yield select(reducers.getStoreId);
    do {
      const { items, count } = yield call(
        API.getInventoryHistories,
        storeId,
        _page,
        size,
        historyType,
        filters,
      );
      histories = [...histories, ...items];
      _count = count;
      _page += 1;
    } while (_page <= page);
    yield put(actions.receiveInventoryHistories(histories, _count, true));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "refreshInventoryHistory", {
      page,
      size,
      historyType,
      filters,
    });
  }
}

export function* watchRefreshInventoryHistory() {
  yield takeEvery(actions.REFRESH_INVENTORY_HISTORY, refreshInventoryHistory);
}

export function* downloadInventoryHistories({ historyType, filters }) {
  try {
    let headers = [];
    let data = [];
    let histories = [];
    let _page = 1;
    let _count = 0;
    let fileName = getHistoryFileNameByType(historyType);

    const locationsById = yield select(reducers.getStoreLocationsById);
    const skusById = yield select(reducers.getSkusById);
    const productsById = yield select(reducers.getProductsById);
    const storeId = yield select(reducers.getStoreId);

    do {
      const { items, count } = yield call(
        API.getInventoryHistories,
        storeId,
        _page,
        100,
        historyType === "group" ? "group" : "single",
        filters,
      );
      histories = [...histories, ...items];
      _count = count;
      _page++;
    } while (_count > histories.length);

    let legacyHistories = [];
    const filtersByKey = filters.reduce((obj, filter) => {
      obj[filter.key] = filter;
      return obj;
    }, {});
    const start = filtersByKey["createdAt"].value[0];
    const isLegacy = isLegacyInventoryHistories(start, historyType);
    if (isLegacy) {
      legacyHistories = yield call(_getLegacyInventoryHistories, filtersByKey);
    }
    if (legacyHistories.length) {
      histories = [
        ...histories,
        ...legacyHistories.map((his) => ({ ...his, isLegacy: true })),
      ];
    }
    if (historyType === "group") {
      headers = TABLE_GROUP_HEADERS.map((header) => header.label);
      data = histories.map((history) => [
        moment(history.createdAt).format("YYYY/MM/DD HH:mm"),
        locationsById[history.locationId]?.name,
        GetInventoryTransactionReason(history.reason),
        history.groupHistoryCount,
        history.totalCost,
        history.note,
      ]);
    } else if (historyType === "single") {
      headers = TABLE_HEADERS.map((header) => header.label);
      data = histories
        .map((history) => {
          let skuId = history.skuId;
          // NOTE: for legacy
          if (!skuId && history.isLegacy) {
            let mixedIds = history.id.split("sku_");
            skuId = "sku_" + mixedIds[1];
          }
          let sku = skusById[skuId] || {};
          const prod = productsById[sku?.parentId] || {};
          return [
            moment(history.createdAt).format("YYYY/MM/DD HH:mm"),
            locationsById[history.locationId]?.name,
            prod.name ?? "삭제된 제품",
            sku.name ?? "삭제된 제품",
            sku.model,
            GetInventoryTransactionReason(history.reason),
            Number(history.quantity),
            history.totalCost,
            history.note,
          ];
        })
        .filter((his) => his);
    } else if (historyType === "asset") {
      headers = ASSET_TABLE_HEADER.map((column) => column.label);
      data = histories
        .map((history) => [
          getAssetHistoryReason(history),
          moment(history.createdAt).format(Constants.MOMENT.DATETIME_FORMAT),
          history.totalCost || 0,
          getOriginReason(history.reason),
        ])
        .filter((his) => his);
    }
    downloadCSV([headers, ...data], fileName);
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendErrorReport(error, "downloadInventoryHistories", {
      historyType,
      filters,
    });
  }
}

export function* watchDownloadInventoryHistories() {
  yield takeEvery(
    actions.DOWNLOAD_INVENTORY_HISTORIES,
    downloadInventoryHistories,
  );
}

function* _createCouponTrasnactions(
  coupon,
  transactions,
  locationId,
  settings,
) {
  const locations = yield select(reducers.getStoreLocations);
  let expiredDate = new Date();
  if (coupon.validDateType === "publish") {
    expiredDate.setDate(new Date().getDate() + Number(coupon.validValue));
  } else if (coupon.validDateType === "endOfMonth") {
    expiredDate = new Date(
      expiredDate.getFullYear(),
      expiredDate.getMonth() + 1,
      0,
    );
  } else {
    expiredDate = new Date(coupon.validValue);
  }
  expiredDate.setHours(23, 59, 59, 999);
  let _transactions = transactions.map((tran) => ({
    id: generateId("cpt"),
    couponTransactionsId: coupon.id,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    state: settings?.state || "generated",
    barcode: tran,
    expiredDate: expiredDate.toISOString(),
    generateLocationId: locationId || null,
    generatedAt: new Date().toISOString(),
    storeId: coupon.storeId,
    locationId: coupon.locationId,
    customerId: settings?.customerId || null,
    providerCustomerId: settings?.providerCustomerId || null,
    couponTransactionCustomerId: settings?.couponTransactionCustomerId || null,
    name: coupon.name,
    code: coupon.code || null,
    type: coupon.type || null,
    method: coupon.method || null,
    conditionType: coupon.conditionType,
    conditionAmount: coupon.conditionAmount,
    conditionItems: coupon.conditionItems,
    conditionTimes: coupon.conditionTimes,
    discountMethod: coupon.discountMethod,
    discountValue: coupon.discountValue,
    campaignId: settings?.campaignId,
    publishableLocationIds:
      coupon.publishableLocationIds || locations.map((loc) => loc.id),
    applyScope: coupon.applyScope,
    applyItemId: coupon.applyItemId,
    startedAt: coupon.startedAt,
    endedAt: coupon.endedAt,
  }));
  let updatedTransactions = [];
  const CHUNK_SIZE = 3000;
  for (let i = 0; i < _transactions.length; i += CHUNK_SIZE) {
    let chunk = _transactions.slice(i, i + CHUNK_SIZE);
    const _updatedTransactions = yield call(
      API.createBarcodeCouponTransactions,
      chunk,
    );
    updatedTransactions = [...updatedTransactions, ..._updatedTransactions];
  }
  return updatedTransactions;
}

export function* createCouponTransactions({ coupon, transactions }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    const updatedCouponTransactions = yield _createCouponTrasnactions(
      coupon,
      transactions,
      organization.locationId,
    );
    const updatedCoupon = yield call(API.updateCoupon, {
      ...coupon,
      generateCount:
        (coupon.publishCount || 0) + updatedCouponTransactions.length,
    });
    yield put(actions.receiveCoupon(updatedCoupon));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "createCouponTransactions", {
      coupon,
      transactions,
    });
  }
}

export function* watchCreateCouponTransactions() {
  yield takeEvery(actions.CREATE_COUPON_TRANSACTIONS, createCouponTransactions);
}

// 복수 고객에게 쿠폰 일괄 발급
function* issueCouponToCustomers({ coupon, customerIds }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    const storeId = organization.storeId;
    let successCount = 0;

    if (coupon.provider) {
      // provider(자사몰) 쿠폰 발급 — 고객별 providerCustomerId로 cafe24 쿠폰 발행
      const customersById = yield select(reducers.getCustomersById);
      const couponNumber = coupon.providerCouponId;

      for (const customerId of customerIds) {
        try {
          const customer = customersById[customerId];
          const memberId = customer?.providerCustomerId;
          if (!memberId) {
            console.warn(
              `Customer ${customerId} has no providerCustomerId, skipping`,
            );
            continue;
          }
          yield call(
            API.createProviderCouponTransaction,
            storeId,
            memberId,
            couponNumber,
          );
          successCount++;
        } catch (err) {
          console.error(
            `Failed to issue provider coupon to customer ${customerId}`,
            err,
          );
        }
      }
    } else {
      // 바코드 쿠폰 발급
      for (const customerId of customerIds) {
        try {
          // 바코드 생성 (중복되지 않는 바코드를 찾을 때까지 반복)
          let issuedBarcodes = [];
          let tempBarcode = null;
          do {
            tempBarcode = generateBarcode();
            issuedBarcodes = yield call(
              API.getCouponTransactionsByStoreBarcode,
              storeId,
              tempBarcode,
            );
          } while (issuedBarcodes.length === 0);

          yield _createCouponTrasnactions(coupon, [tempBarcode], null, {
            state: "published",
            couponTransactionCustomerId: customerId,
          });
          successCount++;
        } catch (err) {
          console.error(
            `Failed to issue coupon to customer ${customerId}`,
            err,
          );
        }
      }

      // generateCount 업데이트
      yield call(API.updateCoupon, {
        ...coupon,
        generateCount: (coupon.publishCount || 0) + successCount,
      });
    }

    yield put(actions.completeCouponIssue(successCount));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "issueCouponToCustomers", {
      coupon,
      customerIds,
    });
  }
}

function* issueCouponByFilter({ coupon, recipientFilters }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    yield call(
      API.issueCouponByFilter,
      organization.storeId,
      coupon.provider ? coupon.providerCouponId : coupon.id,
      coupon.provider || null,
      recipientFilters,
    );
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setCouponStatusFailure(error));
  }
}

export function* watchIssueCouponByFilter() {
  yield takeEvery(actions.ISSUE_COUPON_BY_FILTER, issueCouponByFilter);
}

export function* watchIssueCouponToCustomers() {
  yield takeEvery(actions.ISSUE_COUPON_TO_CUSTOMERS, issueCouponToCustomers);
}

// 쿠폰 발행내역 조회 (바코드 / provider 분기)
function* searchCouponTransactions({ params }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    const storeId = organization.storeId;
    const {
      start,
      end,
      couponId,
      customerId,
      isProvider,
      providerCustomerId,
      page,
      size,
    } = params;

    let result;
    if (isProvider && couponId) {
      // provider(자사몰) 쿠폰 발행내역 — cafe24는 YYYY-MM-DD 형식 필요
      result = yield call(API.getProviderCouponIssues, storeId, couponId, {
        start: start,
        end: end,
        providerCustomerId: providerCustomerId || undefined,
      });
    } else {
      // 바코드 쿠폰 발행내역
      result = yield call(API.getCouponTransactions, storeId, start, end, {
        couponId: couponId || undefined,
        customerId: customerId || undefined,
        page: page || 1,
        size: size || 30,
      });
    }

    yield put(actions.receiveCouponTransactionsSearch(result));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "searchCouponTransactions", { params });
  }
}

export function* watchSearchCouponTransactions() {
  yield takeEvery(actions.SEARCH_COUPON_TRANSACTIONS, searchCouponTransactions);
}

export function* downloadCsvForTransactions({ coupon }) {
  try {
    if (coupon.publishCount > 1000) {
      yield delay(200);
      yield put(actions.setCouponStatusFailure(new Error("TooManyData")));
      return;
    } else {
      let transactions = [];
      transactions = yield call(API.getBarcodeCouponTransactions, coupon.id);
      let headers = [
        "쿠폰 이름",
        "사용 지점",
        "사용일시",
        "바코드",
        "고객 이름",
        "고객 전화번호",
        "사용 여부",
        "할인된 금액",
      ];
      let csvData = [];
      for (let transaction of transactions || []) {
        let row = [
          transaction.couponName,
          transaction.locationName,
          transaction.usedAt
            ? moment(transaction.usedAt).format("YYYY/MM/DD HH:mm")
            : "",
          transaction.barcode,
        ];
        row.push(
          ...[
            maskingName(transaction.customerName),
            maskingPhoneNumber(transaction.phone),
          ],
        );
        row.push(transaction.state === "used" ? "사용" : "미사용");
        row.push(
          transaction.amount ? `${numberToCurrency(transaction.amount)}` : "",
        );
        csvData.push(row);
      }
      downloadCSV([headers, ...csvData], "사용 데이터");
    }
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "downloadCsvForTransactions", {
      coupon,
    });
  }
}

export function* watchDownloadCsvForTransactions() {
  yield takeEvery(
    actions.DOWNLOAD_CSV_FOR_TRANSACTIONS,
    downloadCsvForTransactions,
  );
}

function* _getBarcodeCoupon(couponId) {
  let coupon = yield call(API.getCouponByApiKey, couponId);
  if (coupon.storeId) {
    const store = yield call(API.getStoreByApiKey, coupon.storeId);
    let locations = [];
    let data = {};
    do {
      data = yield call(
        API.getStoreLocationsByApiKey,
        store.id,
        data.nextToken,
        200,
      );
      locations = [...locations, ...data.items];
    } while (data.nextToken);
    yield put(
      actions.initStore({
        ...store,
        locations: {
          items: locations,
        },
      }),
    );
  }
  yield put(actions.receiveCoupon(coupon));
  if (coupon.referId) {
    const product = yield call(API.getItemByApiKey, coupon.referId);
    yield put(
      actions.receiveItem({
        ...product,
        type: "product",
      }),
    );
  }
  return coupon;
}

export function* getBarcodeCoupon({ couponId, locationId }) {
  try {
    yield _getBarcodeCoupon(couponId, locationId);
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "getBarcodeCoupon", {
      couponId,
      locationId,
    });
  }
}

export function* watchGetBarcodeCoupon() {
  yield takeEvery(actions.GET_BARCODE_COUPON, getBarcodeCoupon);
}

export function* getBarcodeCouponTransaction({ transactionId }) {
  try {
    const transaction = yield call(
      API.getCouponTransactionByApiKey,
      transactionId,
    );
    yield put(actions.receiveBarcodeCouponTransaction(transaction));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "getCouponTransaction", {
      transactionId,
    });
  }
}

export function* watchGetBarcodeCouponTransaction() {
  yield takeEvery(
    actions.GET_BARCODE_COUPON_TRANSACTION,
    getBarcodeCouponTransaction,
  );
}

export function* getBarcodeCoupons({ storeId, override }) {
  try {
    const organization = yield select(reducers.getCurrentOrganization);
    const coupons = yield call(API.getCouponsWithStats, storeId, [
      { key: "method", value: "barcode", operation: "eq" },
    ]);
    const filteredCoupons = coupons.filter((coupon) =>
      isCurrentLocationAvailableCoupon(coupon, organization.locationId),
    );
    yield put(actions.receiveBarcodeCoupons(filteredCoupons, override));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "getBarcodeCoupons", {
      storeId,
    });
  }
}

export function* watchGetBarcodeCoupons() {
  yield takeEvery(actions.GET_BARCODE_COUPONS, getBarcodeCoupons);
}

export function* getBarcodeCouponTransactionByPhone({
  storeId,
  locationId,
  couponId,
  phone,
}) {
  try {
    const barcodeCouponsById = yield select(reducers.getBarcodeCouponsById);
    const originalCoupon = barcodeCouponsById[couponId];
    const globalCustomerId = hashMd5(phone);
    const customers = yield call(
      API.getCustomers,
      globalCustomerId,
      null,
      null,
      null,
      null,
      1,
      1,
    );
    let customer = {};
    const storeCustomers = (customers || []).filter(
      (c) => c.storeId === storeId,
    );

    let couponTransaction = null;
    let _customer = null;

    if (
      storeCustomers.filter((c) => c.status === "registered").length > 0 ||
      storeCustomers.filter(
        (c) =>
          c.status === "anonymous" &&
          c.locationId === originalCoupon.locationId,
      ).length > 0
    ) {
      if (
        storeCustomers.filter((c) => c.status === "registered").length === 0 &&
        storeCustomers.filter(
          (c) =>
            c.status === "anonymous" &&
            c.locationId === originalCoupon.locationId,
        ).length > 0
      ) {
        const _anonymousCustomer = storeCustomers.filter(
          (c) =>
            c.status === "anonymous" &&
            c.locationId === originalCoupon.locationId,
        )[0];
        _customer = yield call(API.updateCustomer, {
          ..._anonymousCustomer,
          customerId: globalCustomerId,
          status: "registered",
          registeredAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
      }
      const data = yield call(
        API.getCouponTransactionsByStoreCustomerByApi,
        couponId,
        null,
        { customerId: { eq: globalCustomerId } },
        500,
      );
      const transactions = data.items || [];
      const publishedTransactions =
        transactions.filter((item) => item.state === "published") || [];
      const usedTransactions =
        transactions.filter((item) => item.state === "used") || [];
      const refundedTransactions =
        transactions.filter((item) => item.state === "refunded") || [];
      if (publishedTransactions.length > 0) {
        couponTransaction = _.sortBy(publishedTransactions, "expiredDate")[0];
      } else if (refundedTransactions.length > 0) {
        couponTransaction = _.sortBy(refundedTransactions, "expiredDate")[0];
      } else if (
        usedTransactions.length > 0 &&
        !originalCoupon.duplicatePublish
      ) {
        couponTransaction = _.sortBy(usedTransactions, "expiredDate")[0];
      } else {
        let issuedBarcodes = [];
        let tempBarcode = null;
        do {
          tempBarcode = generateBarcode();
          issuedBarcodes = yield call(
            API.getCouponTransactionsByStoreBarcode,
            storeId,
            tempBarcode,
          );
        } while (issuedBarcodes.length === 0);
        let createdTransactions = [];
        const registeredCustomer =
          storeCustomers.filter((c) => c.status === "registered")[0] ||
          _customer;
        createdTransactions = yield _createCouponTrasnactions(
          originalCoupon,
          [tempBarcode],
          null,
          {
            state: "published",
            customerId: globalCustomerId,
            couponTransactionCustomerId: registeredCustomer.id,
          },
        );
        couponTransaction = createdTransactions[0];
      }
    } else {
      _customer = yield call(API.createCustomer, {
        ...(yield _buildBaseCustomer({ ...customer, phone })),
        status: "registered",
        id: generateId("cst"),
        registeredAt: new Date().toISOString(),
        ...(originalCoupon.locationId
          ? { locationId: originalCoupon.locationId }
          : {}),
        storeId,
      });
      let issuedBarcodes = [];
      let tempBarcode = null;
      do {
        tempBarcode = generateBarcode();
        issuedBarcodes = yield call(
          API.getCouponTransactionsByStoreBarcode,
          storeId,
          tempBarcode,
        );
      } while (issuedBarcodes.length === 0);
      let createdTransactions = [];
      createdTransactions = yield _createCouponTrasnactions(
        originalCoupon,
        [tempBarcode],
        null,
        {
          state: "published",
          customerId: globalCustomerId,
          couponTransactionCustomerId: _customer.id,
        },
      );
      couponTransaction = createdTransactions[0];
    }

    yield put(actions.receiveBarcodeCouponTransaction(couponTransaction));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "getBarcodeCoupons", {
      storeId,
    });
  }
}

export function* watchGetBarcodeCouponTransactionByPhone() {
  yield takeEvery(
    actions.GET_BARCODE_COUPOON_TRANSACTION_BY_PHONE,
    getBarcodeCouponTransactionByPhone,
  );
}

export function* issueBarcodeCouponForProviderCustomer({
  customer,
  phone,
  couponId,
}) {
  try {
    const barcodeCouponsById = yield select(reducers.getBarcodeCouponsById);
    const originalCoupon = barcodeCouponsById[couponId];
    const storeId = yield select(reducers.getStoreId);
    let couponTransaction = null;
    if (!_.isEmpty(customer) && customer.id) {
      const data = yield call(
        API.getCouponTransactionsByStoreCustomerByApi,
        couponId,
        customer.id,
        null,
        500,
      );
      const transactions = data.items || [];
      const publishedTransactions =
        transactions.filter((item) => item.state === "published") || [];
      const usedTransactions =
        transactions.filter((item) => item.state === "used") || [];
      const refundedTransactions =
        transactions.filter((item) => item.state === "refunded") || [];
      if (publishedTransactions.length > 0) {
        couponTransaction = _.sortBy(publishedTransactions, "expiredDate")[0];
      } else if (refundedTransactions.length > 0) {
        couponTransaction = _.sortBy(refundedTransactions, "expiredDate")[0];
      } else if (
        usedTransactions.length > 0 &&
        !originalCoupon.duplicatePublish
      ) {
        couponTransaction = _.sortBy(usedTransactions, "expiredDate")[0];
      } else {
        let issuedBarcodes = [];
        let tempBarcode = null;
        do {
          tempBarcode = generateBarcode();
          issuedBarcodes = yield call(
            API.getCouponTransactionsByStoreBarcode,
            storeId,
            tempBarcode,
          );
        } while (issuedBarcodes.length === 0);
        let createdTransactions = [];
        createdTransactions = yield _createCouponTrasnactions(
          originalCoupon,
          [tempBarcode],
          null,
          {
            state: "published",
            customerId: customer.customerId,
            couponTransactionCustomerId: customer.id,
            providerCustomerId: customer.providerCustomerId,
          },
        );
        couponTransaction = createdTransactions[0];
      }
    }

    yield put(actions.receiveBarcodeCouponTransaction(couponTransaction));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "issueBarcodeCouponForProviderCustomer", {
      phone,
    });
  }
}

export function* watchIssueBarcodeCouponForProviderCustomer() {
  yield takeEvery(
    actions.ISSUE_BARCODE_COUPON_FOR_PROVIDER_CUSTOMER,
    issueBarcodeCouponForProviderCustomer,
  );
}

export function* issueBarcodeCouponWithStoreCustomerId({
  storeCustomerId,
  couponId,
  locationId,
  campaignId,
}) {
  try {
    const barcodeCouponsById = yield select(reducers.getBarcodeCouponsById);
    let originalCoupon = null;
    if (!_.isEmpty(barcodeCouponsById)) {
      originalCoupon = barcodeCouponsById[couponId];
    } else {
      originalCoupon = yield _getBarcodeCoupon(couponId);
    }
    const storeCustomer = yield call(API.getCustomer, storeCustomerId);
    let couponTransaction = null;
    if (!_.isEmpty(storeCustomer)) {
      const data = yield call(
        API.getCouponTransactionsByStoreCustomerByApi,
        couponId,
        storeCustomer.id,
        null,
        500,
      );
      const transactions = data.items || [];
      const publishedTransactions =
        transactions.filter((item) => item.state === "published") || [];
      const usedTransactions =
        transactions.filter((item) => item.state === "used") || [];
      const refundedTransactions =
        transactions.filter((item) => item.state === "refunded") || [];
      if (publishedTransactions.length > 0) {
        couponTransaction = _.sortBy(publishedTransactions, "expiredDate")[0];
      } else if (refundedTransactions.length > 0) {
        couponTransaction = _.sortBy(refundedTransactions, "expiredDate")[0];
      } else if (
        usedTransactions.length > 0 &&
        !originalCoupon.duplicatePublish
      ) {
        couponTransaction = _.sortBy(usedTransactions, "expiredDate")[0];
      } else {
        let issuedBarcodes = [];
        let tempBarcode = null;
        do {
          tempBarcode = generateBarcode();
          issuedBarcodes = yield call(
            API.getCouponTransactionsByStoreBarcode,
            storeCustomer.storeId,
            tempBarcode,
          );
        } while (issuedBarcodes.length === 0);
        let createdTransactions = [];
        createdTransactions = yield _createCouponTrasnactions(
          originalCoupon,
          [tempBarcode],
          null,
          {
            state: "published",
            customerId: storeCustomer.customerId,
            couponTransactionCustomerId: storeCustomer.id,
            providerCustomerId:
              storeCustomer.identType === "user" && storeCustomer.ident
                ? storeCustomer.ident.split("|")[2]
                : null,
            campaignId: campaignId,
          },
        );
        couponTransaction = createdTransactions[0];
      }
      yield put(actions.receiveBarcodeCouponTransaction(couponTransaction));
      yield put(actions.setCouponStatusSuccess());
    } else {
      throw Error("Customer not exists");
    }
  } catch (error) {
    console.log(error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "issueBarcodeCouponWithStoreCustomerId", {
      storeCustomerId,
      couponId,
      locationId,
    });
  }
}

export function* watchIssueBarcodeCouponWithStoreCustomerId() {
  yield takeEvery(
    actions.ISSUE_BARCODE_COUPON_WITH_STORE_CUSTOMER_ID,
    issueBarcodeCouponWithStoreCustomerId,
  );
}

export function* dropoutAccount({}) {
  try {
    const user = yield Auth.currentAuthenticatedUser();
    yield call(API.deleteUser, user.attributes.sub);
    Auth.signOut();
    yield put(actions.setDropoutSuccess());
  } catch (error) {
    console.log("dropoutAccount error: ", error);
    yield put(actions.setDropoutFailure(error));
    Analytics.sendErrorReport(error, "dropoutAccount", {});
  }
}

export function* watchDropoutAccount() {
  yield takeEvery(actions.DROPOUT_ACCOUNT, dropoutAccount);
}

export function* syncExternalLoyalty({ storeId }) {
  let provider;
  try {
    let store = yield call(API.getStore, storeId);
    provider = store?.policy?.provider?.key;
    if (!provider) {
      throw new Error("Missing store provider");
    }
    const loyalty = yield call(API.getExternalLoyaltyConfig, storeId, provider);
    yield call(API.updateStore, {
      ...store,
      policy: {
        ...(store.policy || {}),
        provider: {
          ...(store.policy?.provider || {}),
        },
      },
      loyalty,
    });
    const data = yield call(API.getProviderCustomerGroups, storeId);
    const updatedGroups = data.items || [];
    const config = yield call(API.getProviderConfig, storeId);
    const originalOrder = (config.autoTier?.tiers || []).map((t) => t.tierId);
    let existingTiersByTierId = _.cloneDeep(
      config.autoTier?.tiers || [],
    ).reduce((obj, t) => {
      obj[t.tierId] = t;
      return obj;
    }, {});
    for (let g of updatedGroups) {
      if (existingTiersByTierId[g.id]) {
        existingTiersByTierId[g.id] = {
          ...existingTiersByTierId[g.id],
          name: g.name,
        };
      }
    }
    const updatedIds = updatedGroups.map((g) => g.id);
    for (let tier of Object.values(existingTiersByTierId)) {
      if (!updatedIds.includes(tier.tierId)) {
        delete existingTiersByTierId[tier.tierId];
      }
    }

    const updatedTiers = originalOrder
      .filter((id) => existingTiersByTierId[id]) // 남아있는 것만
      .map((id) => existingTiersByTierId[id]);

    const updatedConfig = yield call(API.updateProviderConfig, {
      ...config,
      autoTier: {
        ...(config?.autoTier || {}),
        active: config?.autoTier?.active || false,
        interval: config?.autoTier?.interval || "month",
        intervalValue: config?.autoTier?.intervalValue || 1,
        orderPeriod: config?.autoTier?.orderPeriod || 12,
        tiers: updatedTiers,
      },
      updatedAt: new Date().toISOString(),
    });
    yield call(_syncDiscountsByTier, storeId, updatedGroups);
    yield put(actions.receiveProviderCustomerGroups(updatedGroups));
    yield put(actions.receiveProviderConfig(updatedConfig));
    store = yield call(_getStoreWithLocations, storeId);
    yield put(actions.initStore(store));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    console.log("sync External Loyalty: ", error);
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "syncExternalLoyalty", { storeId, provider });
  }
}

export function* watchSyncExternalLoyalty() {
  yield takeEvery(actions.SYNC_EXTERNAL_LOYALTY, syncExternalLoyalty);
}

export function* getProviderCoupons({ storeId }) {
  try {
    const coupons = yield call(API.getProviderCoupons, storeId);
    const config = yield call(API.getProviderConfig, storeId);
    let _usable = _.cloneDeep(config.usableCouponIds || []);
    let liveCouponIds = (coupons || []).map((c) => c.providerCouponId);
    _usable = _usable.filter((id) => liveCouponIds.includes(id));
    const updatedConfig = yield call(API.updateProviderConfig, {
      ...config,
      usableCouponIds: _usable,
      updatedAt: new Date().toISOString(),
    });
    yield put(actions.receiveProviderConfig(updatedConfig));
    yield put(actions.receiveProviderCoupons(coupons));
    yield put(actions.setCouponStatusSuccess());
  } catch (error) {
    console.log("get provider coupons: ", error);
    yield put(actions.setCouponStatusFailure(error));
    Analytics.sendErrorReport(error, "getProviderCoupons", { storeId });
  }
}

export function* watchGetProviderCoupons() {
  yield takeEvery(actions.GET_PROVIDER_COUPONS, getProviderCoupons);
}

export function* updateProviderConfig({ updater }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const config = yield call(API.getProviderConfig, storeId);
    const updatedConfig = yield call(API.updateProviderConfig, {
      ...config,
      ...updater,
      updatedAt: new Date().toISOString(),
    });
    const isActivated =
      updater.autoTier && updater.autoTier.active && !config.autoTier?.active;
    const isOrderPeriodUpdated =
      updater.autoTier &&
      updater.autoTier.active &&
      config.autoTier?.active &&
      updater.autoTier.orderPeriod > (config.autoTier?.orderPeriod || 0);
    const needSyncOrders = isActivated || isOrderPeriodUpdated;
    if (needSyncOrders) {
      const launchDate = updater.autoTier.launchDate;
      const startDate = new Date(
        new Date(launchDate).setMonth(
          new Date(launchDate).getMonth() - updater.autoTier.orderPeriod,
        ),
      );
      startDate.setHours(0, 0, 0, 0);
      API.syncProviderOrders(storeId, startDate.toISOString());
    }
    yield put(actions.receiveProviderConfig(updatedConfig));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "updateProviderConfig", { updater });
  }
}

export function* watchUpdateProviderConfig() {
  yield takeEvery(actions.UPDATE_PROVIDER_CONFIG, updateProviderConfig);
}

export function* getProviderConfig({ storeId }) {
  try {
    const config = yield call(API.getProviderConfig, storeId);
    yield put(actions.receiveProviderConfig(config || {}));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getProviderConfig", { storeId });
  }
}

export function* watchGetProviderConfig() {
  yield takeEvery(actions.GET_PROVIDER_CONFIG, getProviderConfig);
}

export function* updateStoreProvider({ provider }) {
  const storeId = yield select(reducers.getStoreId);
  try {
    if (provider) {
      let store = yield call(API.getStore, storeId);
      const updatedStore = yield call(_updateStore, {
        ..._.cloneDeep(store),
        policy: {
          ...(store.policy || {}),
          provider: provider,
        },
      });
      yield put(actions.initStore(updatedStore));
    }
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateStoreProvider", { storeId });
  }
}

export function* watchUpdateStoreProvider() {
  yield takeEvery(actions.UPDATE_STORE_PROVIDER, updateStoreProvider);
}

export function* getProviderCustomerGroups({ storeId }) {
  try {
    const data = yield call(API.getProviderCustomerGroups, storeId);
    // 백엔드가 [items], { items: [...] }, { id1: {...}, id2: {...} } 등 다양한
    // 응답 형태를 돌려줄 수 있어 항상 배열로 정규화해서 dispatch 한다.
    let groups = [];
    if (Array.isArray(data)) {
      groups = data;
    } else if (Array.isArray(data?.items)) {
      groups = data.items;
    } else if (data && typeof data === "object") {
      groups = Object.values(data);
    }
    yield put(actions.receiveProviderCustomerGroups(groups));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getProviderCustomerGroups", { storeId });
  }
}

export function* watchGetProviderCustomerGroups() {
  yield takeEvery(
    actions.GET_PROVIDER_CUSTOMER_GROUPS,
    getProviderCustomerGroups,
  );
}

// LS_listAvailableShops 응답 정규화. 서버(provider.listAvailableShops)는 배열을 내리고
// 각 원소는 { shopNo, name, originName, countryCode, languageCode, baseDomain, active, default }.
// 결과 shape: { shopNo(String), name, countryCode, languageCode, isMain }.
// isMain = 카페24 기본몰(= Cafe24Auth.shopNumber). 화면은 이 값으로만 메인을 걸러낸다.
// ⚠ countryCode 는 shop 국가가 아니라 사업자 국가(business_country_code)라 서브몰이 달라도 전부 같다.
//   서브몰 구분에는 languageCode 를 쓴다.
const normalizeAvailableShops = (data) => {
  const list = Array.isArray(data)
    ? data
    : Array.isArray(data?.shops)
      ? data.shops
      : Array.isArray(data?.items)
        ? data.items
        : [];
  return list
    .map((shop) => ({
      shopNo: String(shop.shopNo ?? shop.shop_no ?? ""),
      name: shop.name || shop.shop_name || "",
      countryCode: shop.countryCode || shop.business_country_code || "",
      languageCode: shop.languageCode || shop.language_code || "",
      // 몰별 결제 통화. 서버가 listAvailableShops 응답에 추가하는 중이라 아직 안 올 수 있다
      // (미도착 시 "" → 화면은 통화 미확인으로 표기, 저장은 기존 값 보존).
      // currencyName 은 표시용 transient — ShopMap 스키마에 필드가 없어 저장하지 않는다.
      currencyCode: shop.currencyCode || shop.currency_code || "",
      currencyName: shop.currencyName || shop.currency_name || "",
      isMain: shop.default === true || shop.default === "T",
    }))
    .filter((shop) => shop.shopNo);
};

export function* getAvailableShops({ storeId }) {
  try {
    const data = yield call(API.listAvailableShops, storeId);
    yield put(actions.receiveAvailableShops(normalizeAvailableShops(data)));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.receiveAvailableShopsFailure(error));
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getAvailableShops", { storeId });
  }
}

export function* watchGetAvailableShops() {
  yield takeEvery(actions.GET_AVAILABLE_SHOPS, getAvailableShops);
}

export function* getTierUpdateHistories({ storeId }) {
  try {
    let data = [];
    let histories = [];
    do {
      data = yield call(
        API.getTierUpdateHistories,
        storeId,
        null,
        data.nextToken,
        100,
      );
      histories = [...histories, ...data.items];
    } while (data.nextToken);
    yield put(actions.receiveTierUpdateHistories(histories));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getTierUpdateHistories", { storeId });
  }
}

export function* watchGetTierUpdateHistories() {
  yield takeEvery(actions.GET_TIER_UPDATE_HISTORIES, getTierUpdateHistories);
}

export function* linkProviderProducts({
  options,
  excludeKeys,
  pairs,
  condition,
}) {
  const storeId = yield select(reducers.getStoreId);
  try {
    if (options) {
      delete options.method;
      if (options.start) {
        options.start = new Date(options.start).toISOString();
      }
    }
    const employee = yield select(reducers.getEmployee);
    API.linkProviderProducts(
      storeId,
      options,
      pairs,
      excludeKeys,
      employee.id,
      condition,
    );
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
  }
}

export function* watchLinkProviderProducts() {
  yield takeEvery(actions.LINK_PROVIDER_PRODUCTS, linkProviderProducts);
}

export function* unlinkProviderProducts({ skuIds }) {
  const storeId = yield select(reducers.getStoreId);
  try {
    yield call(API.unlinkProviderProducts, storeId, skuIds);
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "unlinkProviderProducts", {
      storeId,
      skuIds,
    });
  }
}

export function* watchUnlinkProviderProducts() {
  yield takeEvery(actions.UNLINK_PROVIDER_PRODUCTS, unlinkProviderProducts);
}

export function* getProviderProducts({ search, searchType, isLoadMore }) {
  const storeId = yield select(reducers.getStoreId);
  try {
    let data = [];
    const size = 100;
    const providerProducts = yield select(reducers.getProductsFromProvider);
    let since = null;
    if (isLoadMore && providerProducts.length > 0) {
      // since = 마지막 product 의 referId (서버가 그 product 까지 잘라낸다)
      since = providerProducts[providerProducts.length - 1].referId;
    }
    let filters = [
      {
        key: "linked",
        operation: "eq",
        value: false,
      },
      {
        key: "available",
        operation: "eq",
        value: true,
      },
    ];
    data = yield call(
      API.getProviderProducts,
      storeId,
      size,
      since,
      search,
      searchType,
      filters,
    );
    yield put(
      actions.receiveProviderProducts(
        isLoadMore
          ? [...(providerProducts || []), ...(data.items || [])]
          : data.items || [],
      ),
    );
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getProviderProducts", { storeId });
  }
}

export function* watchGetProviderProducts() {
  yield takeEvery(actions.GET_PROVIDER_PRODUCTS, getProviderProducts);
}

export function* syncProviderInventories({ skuIds, condition }) {
  const storeId = yield select(reducers.getStoreId);
  try {
    const employee = yield select(reducers.getEmployee);
    API.syncProviderInventories(
      storeId,
      employee.id,
      skuIds,
      condition,
      // NOTE: VERSION
      "2026-03-03",
    );
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
  }
}

export function* watchSyncProviderInventories() {
  yield takeEvery(actions.SYNC_PROVIDER_INVENTORIES, syncProviderInventories);
}

export function* getInventorySyncHistories({ filters }) {
  const storeId = yield select(reducers.getStoreId);
  try {
    const size = 20;
    let histories = [];
    let data = {};
    let page = 1;
    do {
      data = yield call(
        API.getInventorySyncHistories,
        storeId,
        page++,
        size,
        filters,
      );
      histories = [...histories, ...data.items];
    } while (data.count > histories.length);
    yield put(actions.receiveInventorySyncHistories(histories));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getInventorySyncHistories", { storeId });
  }
}

export function* watchGetInventorySyncHistories() {
  yield takeEvery(
    actions.GET_INVENTORY_SYNC_HISTORIES,
    getInventorySyncHistories,
  );
}

export function* getProviderProductsLinkHistories({ start, end }) {
  const storeId = yield select(reducers.getStoreId);
  try {
    const size = 20;
    let histories = [];
    let data = {};
    do {
      data = yield call(
        API.getProviderProductsLinkHistories,
        storeId,
        { between: [start, end] },
        null,
        size,
        data.nextToken,
      );
      histories = [...histories, ...data.items];
    } while (data.nextToken);
    yield put(actions.receiveLinkHistories(histories));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getProviderProductsLinkHistories", {
      storeId,
    });
  }
}

export function* watchGetProviderProductsLinkHistories() {
  yield takeEvery(
    actions.GET_PROVIDER_PRODUCTS_LINK_HISTORIES,
    getProviderProductsLinkHistories,
  );
}

export function* syncProviderProducts({ skuIds, excludeKeys, condition }) {
  const storeId = yield select(reducers.getStoreId);
  try {
    const employee = yield select(reducers.getEmployee);
    yield call(
      API.syncProviderProducts,
      storeId,
      skuIds,
      excludeKeys,
      employee.id,
      condition,
    );
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "syncProviderProducts", {
      storeId,
      excludeKeys,
    });
  }
}

export function* watchSyncProviderProducts() {
  yield takeEvery(actions.SYNC_PROVIDER_PRODUCTS, syncProviderProducts);
}

export function* getWaiting({ waitingId }) {
  try {
    const waiting = yield call(API.getWaitingDelta, waitingId);
    if (waiting.storeId && waiting.locationId) {
      const store = yield call(API.getStoreByApiKey, waiting.storeId);
      const location = yield call(API.getLocationByApiKey, waiting.locationId);
      const info = yield call(
        API.getWaitingInfo,
        waiting.storeId,
        waiting.locationId,
        location.state === "opened" ? location.sales?.startedAt : null,
        waitingId,
      );
      yield put(
        actions.initStore({
          ...store,
          locations: {
            items: [
              store.policy?.multiLocation && waiting.locationId ? location : {},
            ],
          },
        }),
      );
      yield put(actions.receiveWaitingInfo(info));
      yield put(actions.receiveWaiting(waiting));
      yield put(actions.setWaitingStatusSuccess());
    }
  } catch (error) {
    yield put(actions.setWaitingStatusFailure(error));
    Analytics.sendErrorReport(error, "getWaitingDelta", { waitingId });
  }
}

export function* watchGetWaiting() {
  yield takeEvery(actions.GET_WAITING, getWaiting);
}

export function* getWaitingInfo({ storeId, locationId, waitingId }) {
  try {
    if (storeId && locationId) {
      const location = yield call(API.getLocationByApiKey, locationId);
      const info = yield call(
        API.getWaitingInfo,
        storeId,
        locationId,
        (location.state === "opened" && location.sales?.startedAt) ?? null,
        waitingId,
      );
      yield put(actions.receiveWaitingInfo(info));
      yield put(actions.setWaitingStatusSuccess());
    } else {
      yield delay(100);
      yield put(actions.setWaitingStatusFailure("no storeId"));
    }
  } catch (error) {
    yield put(actions.setWaitingStatusFailure(error));
    Analytics.sendErrorReport(error, "getWaitingInfo", { storeId, locationId });
  }
}

export function* watchGetWaitingInfo() {
  yield takeEvery(actions.GET_WAITING_INFO, getWaitingInfo);
}

export function* cancelWaiting({ waiting }) {
  try {
    if (waiting.id) {
      yield call(API.cancelWaiting, {
        ...waiting,
        updatedAt: new Date().toISOString(),
      });
      yield put(actions.clearWaiting());
      yield put(actions.setWaitingStatusSuccess());
    }
  } catch (error) {
    yield put(actions.setWaitingStatusFailure(error));
    Analytics.sendErrorReport(error, "cancelWaiting", { waiting });
  }
}

export function* watchCancelWaiting() {
  yield takeEvery(actions.CANCEL_WAITING, cancelWaiting);
}

export function* updateCreditConfig({ config }) {
  try {
    const isMultiLocation = yield select(reducers.isMultiLocation);
    const storeId = yield select(reducers.getStoreId);
    if (isMultiLocation && Authority.level() !== "store") {
      const locationId = yield select(reducers.getCurrentLocationId);
      const location = yield call(API.getStoreLocation, locationId);
      yield call(API.updateLocation, {
        ..._.cloneDeep(location),
        credit: _.cloneDeep(config),
      });
      const updatedStore = yield call(_getStoreWithLocations, storeId);
      yield put(actions.initStore(updatedStore));
    } else {
      const store = yield call(API.getStore, storeId);
      const updatedStore = yield call(_updateStore, {
        ..._.cloneDeep(store),
        credit: _.cloneDeep(config),
      });
      yield put(actions.initStore(updatedStore));
    }
    yield put(actions.setStoreSuccess());
  } catch (error) {
    console.error(error);
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "updateSettings", { config });
  }
}

export function* watchUpdateCreditConfig() {
  yield takeEvery(actions.UPDATE_CREDIT_CONFIG, updateCreditConfig);
}

export function* getCreditStatus({}) {
  const storeId = yield select(reducers.getStoreId);
  const hasMulti = yield select(reducers.isMultiLocation);
  const locationId = yield select(reducers.getCurrentLocationId);
  const hasLocationId =
    hasMulti && Authority.level() !== "store" ? locationId : null;
  try {
    const _receivedCreditStatus = yield call(
      API.getCreditStatus,
      storeId,
      hasLocationId,
    );
    yield put(actions.receiveCreditStatus(_receivedCreditStatus.balance));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "getCreditStatus", {
      storeId,
      locationId,
    });
  }
}

export function* watchGetCreditStatus() {
  yield takeEvery(actions.GET_CREDIT_STATUS, getCreditStatus);
}

export function* getCreditHistories({ page, size, filters, override }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const hasMulti = yield select(reducers.isMultiLocation);
    const locationId = yield select(reducers.getCurrentLocationId);
    const hasLocationId =
      hasMulti && Authority.level() !== "store" ? locationId : null;
    let totalItems = [];
    let totalCount = 0;

    let { items, count } = yield call(
      API.getCreditTransactionHistories,
      storeId,
      page,
      size,
      hasLocationId,
      filters,
    );

    totalItems = items;
    totalCount = count;
    yield put(actions.receiveCreditHistories(totalItems, totalCount, override));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "getCreditHistories", {
      page,
      filters,
      override,
    });
  }
}

export function* watchGetCreditHistories() {
  yield takeEvery(actions.GET_CREDIT_HISTORIES, getCreditHistories);
}

export function* chargeCredit({ amount, quantity }) {
  const storeId = yield select(reducers.getStoreId);
  const hasMulti = yield select(reducers.isMultiLocation);
  const locationId = yield select(reducers.getCurrentLocationId);
  const hasLocationId =
    hasMulti && Authority.level() !== "store" ? locationId : null;
  const currentCredit = yield select(reducers.getStoreCreditStatus);
  try {
    const history = yield call(
      API.chargeCredit,
      storeId,
      amount,
      quantity,
      hasLocationId,
    );
    yield put(actions.receiveCreditStatus(Math.ceil(currentCredit + quantity)));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
    Analytics.sendErrorReport(error, "chargeCredit", {
      storeId,
      amount,
      locationId,
    });
  }
}

export function* watchChargeCredit() {
  yield takeEvery(actions.CHARGE_CREDIT, chargeCredit);
}

export function* getRequestProgressEvents({ originId }) {
  try {
    console.log("getRequestProgressEvents", originId);
    const date = new Date();
    date.setMinutes(date.getMinutes() - 60);
    let data = {};
    let items = [];
    do {
      data = yield call(
        API.getRequestProgressEventsByOrigin,
        originId,
        { gt: date.toISOString() },
        { origin: { eq: "dashboard" }, status: { eq: "ongoing" } },
        data.nextToken,
      );
      items = [...items, ...data.items];
    } while (data.nextToken);
    if (items.length > 0) {
      yield put(actions.receiveRequestProgressEvents(items));
    }
  } catch (error) {
    Analytics.sendErrorReport(error, "getRequestProgressEvents", {
      originId,
    });
  }
}

export function* watchGetRequestProgressEvents() {
  yield takeEvery(
    actions.GET_REQUEST_PROGRESS_EVENTS,
    getRequestProgressEvents,
  );
}

export function* receiveRequestProgressEvent({ event }) {
  try {
    switch (event.func) {
      case Constants.ASYNC_PROGRESS_FUNC.APPLY_INVENTORY_TRANSFER_HISTORY:
      case Constants.ASYNC_PROGRESS_FUNC.CANCEL_INVENTORY_TRANSFER_HISTORY:
      case Constants.ASYNC_PROGRESS_FUNC.APPLY_INVENTORY_COUNT_HISTORY:
        if (event.errorMessage) {
          yield put(
            actions.setInventoryStatusFailure(Error(event.errorMessage)),
          );
        } else if (event.progress === 0) {
          yield put(actions.setInventoryStatusSuccess());
        }
        break;
      case Constants.ASYNC_PROGRESS_FUNC.CANCEL_PURCHASE_ORDER:
      case Constants.ASYNC_PROGRESS_FUNC.APPLY_PURCHASE_ORDER:
        if (event.errorMessage) {
          yield put(
            actions.setPurchaseOrderStatusFailure(Error(event.errorMessage)),
          );
        } else if (event.progress === 0) {
          yield put(actions.setPurchaseOrderStatusSuccess());
        }
        break;
      case Constants.ASYNC_PROGRESS_FUNC.APPLY_INVENTORY_ADJUSTMENTS:
        if (event.errorMessage) {
          yield put(actions.setUpdateItemsFailure(Error(event.errorMessage)));
        } else if (event.progress === 0) {
          yield put(actions.setUpdateItemsSuccess());
        }
        break;
      case Constants.ASYNC_PROGRESS_FUNC.SYNC_PRODUCTS:
      case Constants.ASYNC_PROGRESS_FUNC.SYNC_INVENTORIES:
        if (event.errorMessage) {
          yield put(
            actions.setProviderStatusFailure(Error(event.errorMessage)),
          );
        } else if (event.progress === 0) {
          yield put(actions.setProviderStatusSuccess());
        }
        break;
      // 배송주문 발주 — delivery.py 는 완료를 progress=100 으로 emit (0 이 아님).
      // 응답(뮤테이션 반환)과 별개로, Lambda 타임아웃 등으로 응답을 못 받아도
      // 진행률 이벤트만으로 목록이 갱신되도록 여기서도 재조회를 건다.
      case Constants.ASYNC_PROGRESS_FUNC.CONFIRM_DELIVERY_ORDERS:
        if (event.errorMessage) {
          yield put(
            actions.setDeliveryConfirmStatusFailure(Error(event.errorMessage)),
          );
        } else if (event.progress === 100) {
          yield put(actions.setDeliveryConfirmStatusSuccess());
          yield put(actions.getPreConfirmOrders());
          yield put(actions.getDeliveryStageOrders());
        }
        break;
      case Constants.ASYNC_PROGRESS_FUNC.AUTO_MATCH_DELIVERY_ORDERS:
        if (event.errorMessage) {
          yield put(
            actions.setDeliveryMatchStatusFailure(Error(event.errorMessage)),
          );
        } else if (event.progress === 100) {
          yield put(actions.setDeliveryMatchStatusSuccess());
          yield put(actions.getDeliveryStageOrders());
        }
        break;
      // 동기화/수집 — 서버 7/23 emit 시작. confirm 과 동일하게 응답 유실 대비
      // progress=100 에서도 목록 재조회(정상 응답 시 중복 조회는 허용).
      case Constants.ASYNC_PROGRESS_FUNC.RELOAD_DELIVERY_ORDERS:
        if (event.errorMessage) {
          yield put(actions.setDeliveryStatusFailure(Error(event.errorMessage)));
        } else if (event.progress === 100) {
          yield put(actions.setDeliveryStatusSuccess());
          yield put(actions.getPreConfirmOrders());
          yield put(actions.getDeliveryStageOrders());
        }
        break;
      case Constants.ASYNC_PROGRESS_FUNC.IMPORT_CHANNEL_ORDERS:
        if (event.errorMessage) {
          yield put(
            actions.setDeliveryImportStatusFailure(Error(event.errorMessage)),
          );
        } else if (event.progress === 100) {
          yield put(actions.setDeliveryImportStatusSuccess());
          yield put(actions.getPreConfirmOrders());
          yield put(actions.getDeliveryStageOrders());
        }
        break;
      case Constants.ASYNC_PROGRESS_FUNC.ISSUE_COUPONS:
        if (event.progress === 100) {
          const storeId = yield select(reducers.getStoreId);
          const isOutsourced = yield select(reducers.isOutsourcedCustomer);
          yield put(actions.getBarcodeCoupons(storeId, true));
          if (isOutsourced) {
            yield put(actions.getProviderCoupons(storeId));
          }
        }
        break;
      default:
        break;
    }
  } catch (error) {
    Analytics.sendErrorReport(error, "receiveRequestProgressEvent", {
      event,
    });
  }
}

export function* watchReceiveRequestProgressEvent() {
  yield takeEvery(
    actions.RECEIVE_REQUEST_PROGRESS_EVENT,
    receiveRequestProgressEvent,
  );
}

export function* getDraftOnoffConfig({ storeId }) {
  try {
    const config = yield call(API.getOnoffConfig, storeId, "draft");
    yield put(actions.receiveDraftOnoffConfig(config));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getDraftOnoffConfig", {
      storeId,
    });
  }
}

export function* watchGetDraftOnoffConfig() {
  yield takeEvery(actions.GET_DRAFT_ONOFF_CONFIG, getDraftOnoffConfig);
}

export function* getLiveOnoffConfig({ storeId }) {
  try {
    const config = yield call(API.getOnoffConfig, storeId, "live");
    yield put(actions.receiveLiveOnoffConfig(config));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getLiveOnoffConfig", {
      storeId,
    });
  }
}

export function* watchGetLiveOnoffConfig() {
  yield takeEvery(actions.GET_LIVE_ONOFF_CONFIG, getLiveOnoffConfig);
}

export function* createOrUpdateOnoffConfig({ config }) {
  try {
    let updatedConfig = null;
    const _config = _.cloneDeep(config);
    const storeId = yield select(reducers.getStoreId);
    if (_config.stage === "live") {
      const liveConfig = yield call(API.getOnoffConfig, storeId, "live");
      if (_.isEmpty(liveConfig)) {
        updatedConfig = yield call(API.createOnoffConfig, {
          ..._config,
          storeId: storeId,
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        });
      } else {
        // 다음에 새로운 로고 업로드시 기존 로고 지워주기
        updatedConfig = yield call(API.updateOnoffConfig, {
          ..._config,
          updatedAt: new Date().toISOString(),
        });
      }
      yield call(API.deleteOnoffConfig, storeId, "draft");
      yield put(actions.clearDraftOnoffConfig());
      yield put(actions.receiveLiveOnoffConfig(updatedConfig));
    } else {
      const draftConfig = yield call(API.getOnoffConfig, storeId, "draft");
      if (_.isEmpty(draftConfig)) {
        updatedConfig = yield call(API.createOnoffConfig, {
          ..._config,
          storeId: storeId,
          updatedAt: new Date().toISOString(),
          createdAt: new Date().toISOString(),
        });
      } else {
        updatedConfig = yield call(API.updateOnoffConfig, {
          ..._config,
          updatedAt: new Date().toISOString(),
        });
      }
      yield put(actions.receiveDraftOnoffConfig(updatedConfig));
    }
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "createOrUpdateOnoffConfig", {
      config,
    });
  }
}

export function* watchCreateOrUpdateOnoffConfig() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_ONOFF_CONFIG,
    createOrUpdateOnoffConfig,
  );
}

export function* deleteDraftOnoffConfig({}) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const updatedConfig = yield call(API.deleteOnoffConfig, storeId, "draft");
    yield put(actions.clearDraftOnoffConfig());
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteDraftOnoffConfig", {});
  }
}

export function* watchDeleteDraftOnoffConfig() {
  yield takeEvery(actions.DELETE_DRAFT_ONOFF_CONFIG, deleteDraftOnoffConfig);
}

export function* getCherryPickers({ storeId }) {
  try {
    const customers = yield call(API.getCherryPickers, storeId);
    yield put(
      actions.receivePickers((customers || []).length > 0 ? customers : []),
    );
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getCherryPickers", { storeId });
  }
}

export function* watchGetCherryPickers() {
  yield takeEvery(actions.GET_CHERRY_PICKERS, getCherryPickers);
}

export function* getBlackListCustomers({ storeId }) {
  try {
    const customers = yield call(API.getBlackList, storeId);
    const customersById = (customers || []).reduce((obj, c) => {
      obj[c.id] = c;
      return obj;
    }, {});
    yield put(actions.receiveBlackListCustomers(customersById));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getBlackListCustomers", { storeId });
  }
}

export function* watchGetBlackListCustomers() {
  yield takeEvery(actions.GET_BLACKLIST_CUSTOMERS, getBlackListCustomers);
}

export function* getCustomerTierHistories({ storeId, customerId, start, end }) {
  try {
    let histories = yield call(API.getCustomerTierHistories, storeId, [
      {
        key: "customerId",
        value: customerId,
        operation: "eq",
      },
      {
        key: "createdAt",
        value: [start, end],
        operation: "between",
      },
    ]);
    histories = _.reverse(_.sortBy(histories || [], "createdAt"));
    yield put(actions.receiveCustomerTierHistories(histories || []));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "getCustomerTierHistories", { storeId });
  }
}

export function* watchGetCustomerTierHistories() {
  yield takeEvery(
    actions.GET_CUSTOMER_TIER_HISTORIES,
    getCustomerTierHistories,
  );
}

export function* updateCustomerTier({ storeId, customerId, tierId }) {
  try {
    let data = yield call(API.updateCustomerTier, storeId, customerId, tierId);
    yield put(actions.receiveRetrievedCustomer(data));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "updateCustomerTier", { storeId });
  }
}

export function* watchUpdateCustomerTier() {
  yield takeEvery(actions.UPDATE_CUSTOMER_TIER, updateCustomerTier);
}

export function* updateCustomerBlackList({ storeId, customerId, active }) {
  try {
    let data = yield call(
      API.updateCustomerBlackList,
      storeId,
      customerId,
      active,
    );
    // yield put(actions.receiveCustomerTierHistories(histories || []));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    console.log(error);
    yield put(actions.setProviderStatusFailure(error));
    Analytics.sendErrorReport(error, "updateCustomerBlackList", {
      storeId,
      customerId,
    });
  }
}

export function* watchUpdateCustomerBlackList() {
  yield takeEvery(actions.UPDATE_CUSTOMER_BLACKLIST, updateCustomerBlackList);
}

export function* getStoreDevices({ storeId, timestamp = null }) {
  try {
    let devices = [];
    let data = {};
    do {
      data = yield call(
        API.getLocationDevicesByStore,
        storeId,
        timestamp ? { gt: timestamp } : null,
        data.nextToken,
        1500,
        !timestamp ? { _delta: { ne: "DELETE" } } : null,
      );
      devices = [...devices, ...data.items];
    } while (data.nextToken);
    yield put(actions.receiveStoreDevices(devices));
    yield put(actions.setStoreSuccess());
  } catch (error) {
    yield put(actions.setStoreFailure(error));
  }
}

export function* watchGetStoreDevices() {
  yield takeEvery(actions.GET_STORE_DEVICES, getStoreDevices);
}

export function* sendShippingNotice({ order, receiver, option }) {
  try {
    const store = yield select(reducers.getStoreInfo);
    const storeId = yield select(reducers.getStoreId);
    const locationId = yield select(reducers.getCurrentLocationId);
    const locationsById = yield select(reducers.getStoreLocationsById);
    const location = locationsById[order.orderLocationId];
    const hasMilt = yield select(reducers.isMultiLocation);
    let csPhone = hasMilt ? location.phone || store.phone : store.phone;
    if (!_.isEmpty(receiver)) {
      const address = `${receiver.address1 || ""} ${receiver.address2 || ""} ${
        receiver.zip
      }`;
      if (option === "accept") {
        yield call(
          API.sendNotice,
          { phone: receiver.phone },
          "shippingaccept",
          {
            name: receiver.name || "고객",
            storeName: store.name,
            orderNumber: getOrderNumber(
              order.orderId,
              order.number,
              order.numberPrefix,
              order.originId,
            ),
            orderItems: order.items
              .filter((item) => item.type === "sku" || item.type === "custom")
              .map((item) => ` • ${item.name} x ${item.quantity || 1}`)
              .join("\n"),
            paidAmount: `${numberWithCommas(getPaidAmount(order) || 0)}원`,
            shippingAddress: address,
            shippingNote: receiver.note || "",
            csPhone: csPhone,
          },
          order.storeId || storeId,
          order.orderLocationId || locationId,
        );
      } else {
        let shippingCompany = `배송사: ${
          SHIPPING_COMPANY_BY_CODE[receiver?.shippingCompanyCode] || ""
        }`;
        yield call(
          API.sendNotice,
          { phone: receiver.phone },
          "shippingstart",
          {
            name: receiver.name || "고객",
            storeName: store.name,
            orderNumber: getOrderNumber(
              order.orderId,
              order.number,
              order.numberPrefix,
              order.originId,
            ),
            orderItems: order.items
              .filter((item) => item.type === "sku" || item.type === "custom")
              .map((item) => ` • ${item.name} x ${item.quantity || 1}`)
              .join("\n"),
            carrierName: shippingCompany,
            trackingNumber: receiver.trackingNumber || "",
            csPhone: csPhone,
          },
          order.storeId || storeId,
          order.orderLocationId || locationId,
        );
      }

      const updatedOrder = yield call(
        API.updateOrder,
        {
          ...order,
          receivers: (order.receivers || []).map((_receiver) => {
            if (
              _receiver.phone === receiver.phone &&
              _receiver.zip === receiver.zip
            ) {
              return {
                ..._receiver,
                shippingAcceptCount:
                  option === "accept"
                    ? (_receiver.shippingAcceptCount || 0) + 1
                    : _receiver.shippingAcceptCount || 0,
                shippingStartCount:
                  option === "start"
                    ? (_receiver.shippingStartCount || 0) + 1
                    : _receiver.shippingStartCount || 0,
              };
            } else {
              return _receiver;
            }
          }),
        },
        false,
      );
      yield put(actions.receiveOrder(updatedOrder));
      yield put(actions.setUpdateOrderSuccess());
    }
  } catch (error) {
    console.log(error);
    yield put(actions.setUpdateOrderFailure(error));
    Analytics.sendErrorReport(error, "sendShippingNotice", {
      order,
      option,
    });
  }
}

export function* watchSendShippingNotice() {
  yield takeEvery(actions.SEND_SHIPPING_NOTICE, sendShippingNotice);
}

export function* getProductInventories({ filters, page, size }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const data = yield call(
      API.getProductInventories,
      storeId,
      page,
      size,
      filters,
    );
    yield put(actions.receiveProductInventories(data.count, data.list));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
  }
}

export function* watchGetProductInventories() {
  yield takeEvery(actions.GET_PRODUCT_INVENTORIES, getProductInventories);
}

export function* getSkuInventoryReport({ skuId, start, end, locationId }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const data = yield call(
      API.getSkuInventoryReport,
      storeId,
      skuId,
      start,
      end,
      locationId,
    );
    yield put(actions.receiveSkuInventoryReport(data));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryReportStatusFailure(error));
  }
}

export function* watchGetSkuInventoryReport() {
  yield takeEvery(actions.GET_SKU_INVENTORY_REPORT, getSkuInventoryReport);
}

export function* getProductList({ filters, page, size }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const data = yield call(API.getProductList, storeId, page, size, filters);
    yield put(actions.receiveProductList(data.count, data.list));
    yield put(actions.setGetItemsSuccess());
  } catch (error) {
    yield put(actions.setGetItemsFailure(error));
  }
}

export function* watchGetProductList() {
  yield takeEvery(actions.GET_PRODUCT_LIST, getProductList);
}

export function* getProductsByIds({ productIds }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const products = [];
    for (let ids of _.chunk(productIds, 200)) {
      const data = yield call(API.getProductsByIds, storeId, ids || []);
      products.push(...data);
    }
    yield put(actions.receiveProductList(products.length, products));
    yield put(actions.setGetItemsSuccess());
  } catch (error) {
    yield put(actions.setGetItemsFailure(error));
  }
}

export function* watchGetProductsByIds() {
  yield takeEvery(actions.GET_PRODUCTS_BY_IDS, getProductsByIds);
}

export function* getAllProductInventoriesForExport({ filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    let list = [];
    let data = {};
    let page = 1;
    do {
      data = yield call(API.getProductInventories, storeId, page, 50, filters);
      list = [...list, ...(data.list || [])];
      page += 1;
    } while (data.count > list.length);
    yield put(actions.receiveAllProductInventoriesForExport(list));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
  }
}

export function* watchGetAllProductInventoriesForExport() {
  yield takeEvery(
    actions.GET_ALL_PRODUCT_INVENTORIES_FOR_EXPORT,
    getAllProductInventoriesForExport,
  );
}

export function* getContractSubscriptionPlans() {
  try {
    let planData = yield call(API.getContractSubscriptionPlans);
    yield put(actions.receiveContractPlans(planData.items));
    yield put(actions.setContractStatusSuccess());
  } catch (error) {
    yield put(actions.setContractStatusFailed(error));
  }
}

export function* watchGetContractSubscriptionPlans() {
  yield takeEvery(actions.GET_CONTRACT_PLANS, getContractSubscriptionPlans);
}

export function* paySubscriptionPlan({ planId, billingCycle }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const contract = yield call(
      API.subscribePaidPlan,
      storeId,
      planId,
      billingCycle,
    );
    yield call(_getStoreContract, storeId);
    yield put;
    yield put(actions.setContractStatusSuccess());
  } catch (error) {
    yield put(actions.setContractStatusFailed(error));
  }
}

export function* watchPaySubscriptionPlan() {
  yield takeEvery(actions.PAY_SUBSCRIPTION_PLAN, paySubscriptionPlan);
}

export function* cancelSubscriptionPlan({ planId }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const contract = yield call(API.cancelPaidPlan, storeId, planId);
    yield call(_getStoreContract, storeId);
    yield put(actions.setContractStatusSuccess());
  } catch (error) {
    yield put(actions.setContractStatusFailed(error));
  }
}

export function* watchCancelSubscriptionPlan() {
  yield takeEvery(actions.CANCEL_SUBSCRIPTION_PLAN, cancelSubscriptionPlan);
}

export function* updateContractSubscriptionPlan({ plan, servedStoreId }) {
  try {
    const storeId = servedStoreId
      ? servedStoreId
      : yield select(reducers.getStoreId);
    const mgmtContract = yield select(reducers.getMgmtContract);
    let originalContract = servedStoreId
      ? mgmtContract.contract
      : yield select(reducers.getStoreContract);
    originalContract = {
      ...originalContract,
      subscriptions: originalContract.subscriptions.map((sub) => {
        if (sub.planId === plan.planId) {
          return plan;
        } else {
          return sub;
        }
      }),
    };
    const updatedStoreContract = yield call(
      API.updateStoreContract,
      originalContract,
    );
    yield call(_getStoreContract, storeId);
    yield put(
      actions.receiveMgmtContract({
        ...mgmtContract,
        contract: updatedStoreContract,
      }),
    );
    yield put(actions.setContractStatusSuccess());
  } catch (error) {
    yield put(actions.setContractStatusFailed(error));
  }
}

export function* watchUpdateContractSubscriptionPlan() {
  yield takeEvery(
    actions.UPDATE_CONTRACT_SUBSCRIPTION_PLAN,
    updateContractSubscriptionPlan,
  );
}

export function* getLocationStats() {
  try {
    const currentLocationId = yield select(reducers.getCurrentLocationId);
    const stats = yield call(API.getLocationStats, currentLocationId);
    yield put(actions.receiveLocationStats(stats));
  } catch (error) {
    console.log("getLocationStats", error);
  }
}

export function* watchGetLocationStats() {
  yield takeEvery(actions.GET_LOCATION_STATS, getLocationStats);
}

export function* getCustomerEvents() {
  try {
    const storeId = yield select(reducers.getStoreId);
    const eventData = yield call(
      API.getCustomerEventsByStore,
      storeId,
      30,
      null,
    );
    const eventsById = (eventData?.items || []).reduce((obj, e) => {
      obj[e.id] = e;
      return obj;
    }, {});
    yield put(actions.receiveCustomerEvents(eventsById));
    yield put(actions.setCustomerEventStatusSuccess());
  } catch (error) {
    yield put(actions.setCustomerEventStatusFailure(error));
  }
}

export function* watchGetCustomerEvents() {
  yield takeEvery(actions.GET_CUSTOMER_EVENTS, getCustomerEvents);
}

export function* createOrUpdateCustomerEvent({ customerEvent }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    let employee = yield select(reducers.getEmployee);
    let event = _.cloneDeep(customerEvent);
    if (!event.id) {
      event = {
        ...event,
        id: generateId("ce"),
        storeId: storeId,
        employeeId: employee.id,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      };
      const createdEvent = yield call(API.createCustomerEvent, event);
      yield put(actions.receiveCustomerEvent(createdEvent));
    } else {
      const updatedEvent = yield call(API.updateCustomerEvent, {
        ...event,
        employeeId: employee.id,
        updatedAt: new Date().toISOString(),
      });
      yield put(actions.receiveCustomerEvent(updatedEvent));
    }
    yield put(actions.setCustomerEventStatusSuccess());
  } catch (error) {
    yield put(actions.setCustomerEventStatusFailure(error));
  }
}

export function* watchCreateOrUpdateCustomerEvent() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_CUSTOMER_EVENT,
    createOrUpdateCustomerEvent,
  );
}

export function* deleteCustomerEvent({ customerEvent }) {
  try {
    const deleted = yield call(API.deleteCustomerEvent, customerEvent.id);
    yield put(actions.setCustomerEventStatusSuccess());
  } catch (error) {
    yield put(actions.setCustomerEventStatusFailure(error));
  }
}

export function* watchDeleteCustomerEvent() {
  yield takeEvery(actions.DELETE_CUSTOMER_EVENT, deleteCustomerEvent);
}

export function* getCustomerEventReport({ eventId, start, end }) {
  try {
    const _start = new Date(new Date(start).setHours(0, 0, 0, 0));
    const _end = new Date(new Date(end).setHours(23, 59, 59, 999));
    const campaignReport = yield call(
      API.getCustomerEventReport,
      eventId,
      _start.toISOString(),
      _end.toISOString(),
    );
    yield put(actions.receiveCustomerEventReport(campaignReport));
    yield put(actions.setCustomerEventStatusSuccess());
  } catch (error) {
    console.log("Failed to getCustomerEventReport", error);
    yield put(actions.setCustomerEventStatusFailure(error));
  }
}

export function* watchGetCustomerEventReport() {
  yield takeEvery(actions.GET_CUSTOMER_EVENT_REPORT, getCustomerEventReport);
}

export function* getMessageCampaignReport({
  start,
  end,
  locationId = null,
  campaignId = null,
}) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const _end = new Date(new Date(end).setHours(23, 59, 59, 999));
    const _start = new Date(new Date(start).setHours(0, 0, 0, 0));
    let filters = [];
    if (locationId) {
      filters = [
        ...filters,
        { key: "locationId", operation: "eq", value: locationId },
      ];
    }
    if (campaignId) {
      filters = [
        ...filters,
        { key: "campaignId", operation: "eq", value: campaignId },
      ];
    }
    const campaignReport = yield call(
      API.getMessageCampaignReport,
      storeId,
      _start.toISOString(),
      _end.toISOString(),
      filters,
    );
    yield put(actions.receiveMessageCampaignReport(campaignReport));
    yield put(actions.setMessageCampaignReportSuccess());
  } catch (error) {
    console.log("Failed to getCustomerEventReport", error);
    yield put(actions.setMessageCampaignReportFailure(error));
  }
}

export function* watchGetMessageCmapignReport() {
  yield takeEvery(
    actions.GET_MESSAGE_CAMPAIGN_REPORT,
    getMessageCampaignReport,
  );
}

export function* getCustomerEventUserList({ eventId, item, page, size }) {
  try {
    const listData = yield call(
      API.getCustomerEventList,
      eventId,
      item,
      page,
      size,
    );
    if (item === "customer") {
      yield put(actions.receiveCustomerEventUserList(listData));
    } else {
      yield put(actions.receiveCustomerStoryList(listData));
    }
    yield put(actions.setCustomerEventStatusSuccess());
  } catch (error) {
    yield put(actions.setCustomerEventStatusFailure(error));
  }
}

export function* watchGetCustomerEventUserList() {
  yield takeEvery(
    actions.GET_CUSTOMER_EVENT_USER_LIST,
    getCustomerEventUserList,
  );
}

export function* getProviderCustomerCount() {
  try {
    const storeId = yield select(reducers.getStoreId);
    const count = yield call(API.getProviderCustomerCount, storeId);
    yield put(actions.receiverProviderCustomerCount(count));
    yield put(actions.setProviderStatusSuccess());
  } catch (error) {
    yield put(actions.setProviderStatusFailure(error));
  }
}

export function* watchGetProviderCustomerCount() {
  yield takeEvery(
    actions.GET_PROVIDER_CUSTOMER_COUNT,
    getProviderCustomerCount,
  );
}

export function* createInventoryNewRequest({ history }) {
  try {
    let _history = _.cloneDeep(history);
    const storeId = yield select(reducers.getStoreId);
    _history = {
      ..._history,
      storeId,
      createdAt: _history.createdAt || new Date().toISOString(),
      updatedAt: _history.updatedAt || new Date().toISOString(),
    };
    const created = yield call(API.createInventoryRequestHistory, _history);
    yield put(actions.receiveNewRequest(created));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendMixPanelEvent("error", {
      screen: "NewStockRequest",
      action: "failed_create",
      error: error,
    });
  }
}

export function* watchCreateInventoryNewRequest() {
  yield takeEvery(actions.CREATE_NEW_REQUEST, createInventoryNewRequest);
}

export function* updateInventoryNewRequest({ history }) {
  try {
    const requestsById = yield select(reducers.getInventoryRequestsById);
    const original = requestsById[history.id];
    let _history = _.cloneDeep(history);
    _history = {
      ..._history,
      updatedAt: new Date().toISOString(),
    };
    const prevStatus = original?.status;
    const nextStatus = _history.status;
    const shouldApply = REQUEST_APPLY_TRANSITIONS.some(
      ([from, to]) => prevStatus === from && nextStatus === to,
    );
    if (shouldApply) {
      const origin = yield select(reducers.getRequestProgressEventOrigin);
      try {
        yield call(
          API.applyInventoryRequestHistory,
          _history,
          origin.name,
          history.id,
        );
      } catch (err) {
        console.error("[applyInventoryRequestHistory error]", err);
      }
    } else {
      const updated = yield call(API.updateInventoryRequestHistory, _history);
      yield put(actions.receiveNewRequest(updated));
    }
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendMixPanelEvent("error", {
      screen: "NewStockRequest",
      action: "failed_update",
      error: error,
    });
  }
}

export function* watchUpdateInventoryNewRequest() {
  yield takeEvery(actions.UPDATE_NEW_REQUEST, updateInventoryNewRequest);
}

export function* createInventoryNewRequests({ histories, onlyCreate }) {
  try {
    // NOTE: 본사에서 바로 confirmed로 만드는 케이스임
    let _histories = _.cloneDeep(histories);
    const storeId = yield select(reducers.getStoreId);
    _histories = _histories.map((his) => ({
      ...his,
      storeId,
      status: onlyCreate ? "requested" : "created",
      createdAt: his.createdAt || new Date().toISOString(),
      updatedAt: his.updatedAt || new Date().toISOString(),
    }));
    const created = yield call(API.createInventoryRequestHistories, _histories);
    yield put(actions.receiveNewRequests(created));
    if (!onlyCreate && created.length) {
      let updating = _.cloneDeep(created);
      updating = updating.map((u) => ({ ...u, status: "confirmed" }));
      const origin = yield select(reducers.getRequestProgressEventOrigin);
      yield all(
        updating.map((his) =>
          call(API.applyInventoryRequestHistory, his, origin.name, his.id),
        ),
      );
    }
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendMixPanelEvent("error", {
      screen: "NewStockRequest",
      action: "failed_create_bulk",
      error: error,
    });
  }
}

export function* watchCreateInventoryNewRequests() {
  yield takeEvery(actions.CREATE_NEW_REQUESTS, createInventoryNewRequests);
}

export function* updateInventoryNewRequests({ histories }) {
  try {
    const requestsById = yield select(reducers.getInventoryRequestsById);
    let _histories = _.cloneDeep(histories);
    _histories = _histories.map((his) => ({
      ...his,
      updatedAt: new Date().toISOString(),
    }));
    const applyTargets = [];
    const directUpdateTargets = [];
    for (const updated of _histories) {
      const original = requestsById?.[updated.id];
      const prevStatus = original?.status;
      const nextStatus = updated.status;

      const shouldApply = REQUEST_APPLY_TRANSITIONS.some(
        ([from, to]) => prevStatus === from && nextStatus === to,
      );
      if (shouldApply) {
        applyTargets.push(updated);
      } else {
        directUpdateTargets.push(updated);
      }
    }
    if (applyTargets.length > 0) {
      const origin = yield select(reducers.getRequestProgressEventOrigin);
      yield all(
        applyTargets.map((his) =>
          call(API.applyInventoryRequestHistory, his, origin.name, his.id),
        ),
      );
    }
    if (directUpdateTargets.length > 0) {
      const updatedList = yield call(
        API.updateInventoryRequestHistories,
        directUpdateTargets,
      );
      yield put(actions.receiveNewRequests(updatedList));
    }
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendMixPanelEvent("error", {
      screen: "NewStockRequest",
      action: "failed_update_bulk",
      error: error,
    });
  }
}

export function* watchUpdateInventoryNewRequests() {
  yield takeEvery(actions.UPDATE_NEW_REQUESTS, updateInventoryNewRequests);
}

export function* deleteInventoryNewRequest({ historyId }) {
  try {
    const deleted = yield call(API.deleteInventoryRequestHistory, historyId);
    yield put(actions.receiveDeletedNewRequest(deleted));
    yield put(actions.setInventoryStatusSuccess());
  } catch (error) {
    yield put(actions.setInventoryStatusFailure(error));
    Analytics.sendMixPanelEvent("error", {
      screen: "NewStockRequest",
      action: "failed_delete",
      error: error,
    });
  }
}

export function* watchDeleteInventoryNewRequest() {
  yield takeEvery(actions.DELETE_NEW_REQUEST, deleteInventoryNewRequest);
}

export function* getMembershipReport({ start, end, interval }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const report = yield call(
      API.getMembershipReport,
      storeId,
      start,
      end,
      interval,
    );
    yield put(actions.receiveMembershipReport(report));
    yield put(actions.setMembershipReportSuccess());
  } catch (error) {
    yield put(actions.setMembershipReportFailure(error));
    Analytics.sendMixPanelEvent("error", {
      screen: "membershipReport",
      action: "failed_load",
      error: error,
    });
  }
}

export function* watchGetmembershipReport() {
  yield takeEvery(actions.GET_MEMBERSHIP_REPORT, getMembershipReport);
}

export function* createOrUpdateCampaignTemplate({ template }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const organization = yield select(reducers.getCurrentOrganization);
    const hasMulti = yield select(reducers.isMultiLocation);
    const locationId =
      hasMulti && organization.locationId ? organization.locationId : null;
    if (!template.id) {
      const createdTemplate = yield call(API.createCampaignTemplate, {
        ...template,
        id: generateId("ctp"),
        storeId: storeId,
        ...(locationId ? { locationId } : {}),
        ads: true,
        updatedAt: new Date().toISOString(),
      });
      yield put(actions.receiveCampaignTemplate(createdTemplate));
    } else {
      const updatedTemplate = yield call(API.updateCampaignTemplate, {
        ...template,
        updatedAt: new Date().toISOString(),
      });
      yield put(actions.receiveCampaignTemplate(updatedTemplate));
    }
    yield put(actions.setGetCampaignSuccess());
  } catch (error) {
    yield put(actions.setGetCampaignFailure(error));
  }
}

export function* watchCreateOrUpdateCampaignTemplate() {
  yield takeEvery(
    actions.CREATE_OR_UPDATE_CAMPAIGN_TEMPLATE,
    createOrUpdateCampaignTemplate,
  );
}

export function* deleteCampaignTemplate({ templateId }) {
  try {
    yield call(API.deleteCampaignTemplate, templateId);
    yield put(actions.receiveDeletedTemplate(templateId));
    yield put(actions.setGetCampaignSuccess());
  } catch (error) {
    yield put(actions.setGetCampaignFailure(error));
  }
}

export function* watchDeleteCampaignTemplate() {
  yield takeEvery(actions.DELETE_CAMPAIGN_TEMPLATE, deleteCampaignTemplate);
}

export function* getCampaignTemplates() {
  try {
    const storeId = yield select(reducers.getStoreId);
    const organization = yield select(reducers.getCurrentOrganization);
    const hasMulti = yield select(reducers.isMultiLocation);
    const locationId =
      hasMulti && organization.locationId ? organization.locationId : null;
    let templateData = {};
    let templates = [];
    do {
      templateData = yield call(
        API.getCampaignTemplates,
        {
          ...(locationId
            ? {
                locationId: {
                  eq: locationId,
                },
              }
            : {}),
          storeId: {
            eq: storeId,
          },
        },
        50,
        templateData.nextToken,
      );
      templates = [...templates, ...templateData.items];
      // gotcha: 커서는 응답(templateData)에 있다. 누적 배열(templates)에는 nextToken 이
      // 없어 항상 undefined → 첫 페이지(50건)에서 끊겼다. 저장 템플릿 50건 초과 매장이 영향.
    } while (templateData.nextToken);
    yield put(actions.receiveCampaignTemplates(templates));
    yield put(actions.setGetCampaignSuccess());
  } catch (error) {
    yield put(actions.setGetCampaignFailure(error));
  }
}

export function* watchGetCampaignTemplates() {
  yield takeEvery(actions.GET_CAMPAIGN_TEMPLATES, getCampaignTemplates);
}

// ===== 배송주문 관리 — 발주(주문 가져오기/발주확인/매칭/합포) =====
// storeId/employeeId/locationId 는 store 에서 select. 서버 스키마 배포 전이므로
// 호출은 배포 후 동작(계약 = sales-server erp-scheme FIELD_MAP).

function* selectDeliveryContext() {
  const storeId = yield select(reducers.getStoreId);
  const employee = yield select(reducers.getEmployee);
  const organization = yield select(reducers.getCurrentOrganization);
  return {
    storeId,
    employeeId: employee ? employee.id : null,
    locationId: organization ? organization.locationId : null,
  };
}

export function* getPreConfirmOrders({ filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const orders = yield call(API.listPreConfirmOrders, storeId, filters);
    yield put(actions.receivePreConfirmOrders(orders || []));
    yield put(actions.setDeliveryStatusSuccess());
  } catch (error) {
    yield put(actions.setDeliveryStatusFailure(error));
    Analytics.sendErrorReport(error, "getPreConfirmOrders");
  }
}

export function* watchGetPreConfirmOrders() {
  yield takeEvery(actions.GET_PRE_CONFIRM_ORDERS, getPreConfirmOrders);
}

export function* getDeliveryStageOrders() {
  try {
    const storeId = yield select(reducers.getStoreId);
    const stageOrders = yield call(API.listDeliveryStageOrders, storeId);
    yield put(actions.receiveDeliveryStageOrders(stageOrders));
    yield put(actions.setDeliveryStatusSuccess());
  } catch (error) {
    yield put(actions.setDeliveryStatusFailure(error));
    Analytics.sendErrorReport(error, "getDeliveryStageOrders");
  }
}

export function* watchGetDeliveryStageOrders() {
  yield takeEvery(actions.GET_DELIVERY_STAGE_ORDERS, getDeliveryStageOrders);
}

export function* getConfirmBatches({ filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const batches = yield call(API.listConfirmBatches, storeId, filters);
    yield put(actions.receiveConfirmBatches(batches || []));
    yield put(actions.setDeliveryStatusSuccess());
  } catch (error) {
    yield put(actions.setDeliveryStatusFailure(error));
    Analytics.sendErrorReport(error, "getConfirmBatches");
  }
}

export function* watchGetConfirmBatches() {
  yield takeEvery(actions.GET_CONFIRM_BATCHES, getConfirmBatches);
}

export function* getItemLinks({ filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const itemLinks = yield call(API.listItemLinks, storeId, filters);
    yield put(actions.receiveItemLinks(itemLinks));
    yield put(actions.setDeliveryStatusSuccess());
  } catch (error) {
    yield put(actions.setDeliveryStatusFailure(error));
    Analytics.sendErrorReport(error, "getItemLinks");
  }
}

export function* watchGetItemLinks() {
  yield takeEvery(actions.GET_ITEM_LINKS, getItemLinks);
}

export function* importChannelOrders({ origins, start, end }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(
      API.importChannelOrders,
      storeId,
      origins,
      start,
      end,
    );
    yield put(actions.receiveImportChannelOrdersResult(result));
    yield put(actions.setDeliveryImportStatusSuccess());
    yield put(actions.getPreConfirmOrders());
    yield put(actions.getDeliveryStageOrders());
  } catch (error) {
    yield put(actions.setDeliveryImportStatusFailure(error));
    Analytics.sendErrorReport(error, "importChannelOrders", {
      origins,
      start,
      end,
    });
  }
}

export function* watchImportChannelOrders() {
  yield takeLeading(actions.IMPORT_CHANNEL_ORDERS, importChannelOrders);
}

export function* reloadDeliveryOrders({ channelOrderIds, retailStates, origins }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(
      API.reloadDeliveryOrders,
      storeId,
      channelOrderIds,
      retailStates,
      origins,
    );
    // 결과(skippedOrigins = 재동기화 미지원 채널)를 delivery.reloadResult 에 저장 — 토스트 표시용
    yield put(actions.receiveReloadResult(result));
    yield put(actions.setDeliveryStatusSuccess());
    yield put(actions.getPreConfirmOrders());
    yield put(actions.getDeliveryStageOrders());
    // 송장출력 [주문 동기화]도 이 saga 공용 — 출력 대기 목록 자동 반영(C 2번, 08-03)
    yield put(actions.getPrintTargets());
  } catch (error) {
    yield put(actions.setDeliveryStatusFailure(error));
    Analytics.sendErrorReport(error, "reloadDeliveryOrders");
  }
}

export function* watchReloadDeliveryOrders() {
  yield takeLeading(actions.RELOAD_DELIVERY_ORDERS, reloadDeliveryOrders);
}

export function* deleteUnconfirmedOrders({ channelOrderIds }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    yield call(API.deleteUnconfirmedOrders, storeId, channelOrderIds);
    yield put(actions.setDeliveryStatusSuccess());
    yield put(actions.getPreConfirmOrders());
    yield put(actions.getDeliveryStageOrders());
  } catch (error) {
    yield put(actions.setDeliveryStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteUnconfirmedOrders");
  }
}

export function* watchDeleteUnconfirmedOrders() {
  yield takeEvery(actions.DELETE_UNCONFIRMED_ORDERS, deleteUnconfirmedOrders);
}

export function* confirmDeliveryOrders({ orderIds, eventId }) {
  try {
    const { storeId, employeeId, locationId } = yield call(
      selectDeliveryContext,
    );
    const result = yield call(
      API.confirmDeliveryOrders,
      storeId,
      orderIds,
      employeeId,
      locationId,
      eventId,
    );
    yield put(actions.receiveConfirmDeliveryOrdersResult(result));
    // 발주완료된 건은 낙관 숨김 — 확인 직후 재조회가 DynamoDB GSI 지연으로 stale 행을 돌려줘
    // 발주완료 주문이 목록에 남아 보였다(2026-09-08 실측). 합포 무시/금지와 같은 장치를 쓴다.
    // 미매칭·합포후보·미결제 잔류분은 발주대기가 서버 최종 상태라 숨기지 않는다.
    // confirmedIds 는 서버 신규 필드 — 미배포면 numbers(확정 건 관리번호 맵)의 키로 대신한다.
    const confirmedIds =
      (result && result.confirmedIds) || Object.keys((result && result.numbers) || {});
    if (confirmedIds.length > 0) {
      yield put(actions.hidePreConfirmOrders(confirmedIds));
    }
    yield put(actions.setDeliveryConfirmStatusSuccess());
    yield put(actions.getPreConfirmOrders());
    yield put(actions.getDeliveryStageOrders());
    yield put(actions.getConfirmBatches()); // 발주확인 시 새 발주차수가 생기므로 이력도 갱신
  } catch (error) {
    yield put(actions.setDeliveryConfirmStatusFailure(error));
    Analytics.sendErrorReport(error, "confirmDeliveryOrders", { orderIds });
  }
}

export function* watchConfirmDeliveryOrders() {
  yield takeLeading(actions.CONFIRM_DELIVERY_ORDERS, confirmDeliveryOrders);
}

export function* autoMatchDeliveryOrders({ orderIds, eventId }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(
      API.autoMatchDeliveryOrders,
      storeId,
      orderIds,
      eventId,
    );
    yield put(actions.receiveAutoMatchResult(result));
    yield put(actions.setDeliveryMatchStatusSuccess());
    yield put(actions.getDeliveryStageOrders());
  } catch (error) {
    yield put(actions.setDeliveryMatchStatusFailure(error));
    Analytics.sendErrorReport(error, "autoMatchDeliveryOrders", { orderIds });
  }
}

export function* watchAutoMatchDeliveryOrders() {
  yield takeLeading(actions.AUTO_MATCH_DELIVERY_ORDERS, autoMatchDeliveryOrders);
}

export function* mergeCheckDeliveryOrders({ orderIds }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.mergeCheckDeliveryOrders, storeId, orderIds);
    yield put(actions.receiveMergeCheckResult(result));
    yield put(actions.setDeliveryMergeStatusSuccess());
    yield put(actions.getDeliveryStageOrders());
    yield put(actions.getPreConfirmOrders());
  } catch (error) {
    yield put(actions.setDeliveryMergeStatusFailure(error));
    Analytics.sendErrorReport(error, "mergeCheckDeliveryOrders", { orderIds });
  }
}

export function* watchMergeCheckDeliveryOrders() {
  yield takeLeading(actions.MERGE_CHECK_DELIVERY_ORDERS, mergeCheckDeliveryOrders);
}

export function* saveManualMatch({ orderId, orderItemId, skus, saveLink }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(
      API.saveManualMatch,
      storeId,
      orderId,
      orderItemId,
      skus,
      saveLink,
    );
    if (result && result.error) {
      throw new Error(typeof result.error === "string" ? result.error : JSON.stringify(result.error));
    }
    yield put(actions.setDeliveryMatchStatusSuccess());
    // 매칭 저장 후 합포검사 자동 실행(우진 결정) — 서버가 매칭완료·unconfirmed 만 검사하므로 안전
    yield put(actions.mergeCheckDeliveryOrders([orderId]));
    yield put(actions.getPreConfirmOrders());
  } catch (error) {
    yield put(actions.setDeliveryMatchStatusFailure(error));
    Analytics.sendErrorReport(error, "saveManualMatch", { orderId, orderItemId });
  }
}

export function* watchSaveManualMatch() {
  yield takeEvery(actions.SAVE_MANUAL_MATCH, saveManualMatch);
}

export function* deleteItemLink({ filters, refetchFilters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.deleteItemLink, storeId, filters);
    if (result && result.error) {
      throw new Error(typeof result.error === "string" ? result.error : JSON.stringify(result.error));
    }
    yield put(actions.setDeliveryStatusSuccess());
    // 삭제 후 재조회 — 호출부가 넘긴 현재 목록 조건 유지(미지정 시 전량)
    yield put(actions.getItemLinks(refetchFilters || {}));
  } catch (error) {
    yield put(actions.setDeliveryStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteItemLink", { filters });
  }
}

export function* watchDeleteItemLink() {
  yield takeEvery(actions.DELETE_ITEM_LINK, deleteItemLink);
}

export function* cancelConfirmOrders({ batchId, orderIds }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.cancelConfirmOrders, storeId, batchId, orderIds);
    yield put(actions.receiveCancelConfirmOrdersResult(result));
    yield put(actions.setDeliveryConfirmStatusSuccess());
    yield put(actions.getPreConfirmOrders());
    yield put(actions.getDeliveryStageOrders());
    yield put(actions.getConfirmBatches());
    // 송장출력 대기 벌크바 발주취소(우진 7/23) — 취소분이 출력 대기 목록에서도 빠지게 재조회
    yield put(actions.getPrintTargets());
  } catch (error) {
    yield put(actions.setDeliveryConfirmStatusFailure(error));
    Analytics.sendErrorReport(error, "cancelConfirmOrders", { batchId, orderIds });
  }
}

export function* watchCancelConfirmOrders() {
  yield takeLeading(actions.CANCEL_CONFIRM_ORDERS, cancelConfirmOrders);
}

export function* confirmMergeOrders({ mergeGroupId, orderIds }) {
  try {
    const { storeId, employeeId, locationId } = yield call(
      selectDeliveryContext,
    );
    const result = yield call(API.confirmMergeOrders, storeId, {
      mergeGroupId,
      orderIds,
      employeeId,
      locationId,
    });
    yield put(actions.receiveMergeResult(result));
    yield put(actions.setDeliveryMergeStatusSuccess());
    yield put(actions.getPreConfirmOrders());
    yield put(actions.getDeliveryStageOrders());
  } catch (error) {
    yield put(actions.setDeliveryMergeStatusFailure(error));
    Analytics.sendErrorReport(error, "confirmMergeOrders", { mergeGroupId });
  }
}

export function* watchConfirmMergeOrders() {
  // 그룹 단위 다중 확정(선택 그룹 일괄 확정)이 연속 dispatch 되므로 takeEvery
  yield takeEvery(actions.CONFIRM_MERGE_ORDERS, confirmMergeOrders);
}

export function* excludeMergeOrders({ orderIds, mergeGroupId }) {
  try {
    const { storeId, employeeId, locationId } = yield call(
      selectDeliveryContext,
    );
    const result = yield call(API.excludeMergeOrders, storeId, {
      orderIds,
      mergeGroupId,
      employeeId,
      locationId,
    });
    if (result && result.error) {
      throw new Error(typeof result.error === "string" ? result.error : JSON.stringify(result.error));
    }
    // 무시 시 mergeBlocked 자동 설정 제거(우진 7/23 — QA 7/20) — 재그룹 방지가 필요하면
    // 행별 합포 금지 버튼 사용.
    // 낙관 숨김(우진 7/24) — 단포 확정이 DDB 스트림→RDB 동기화 전이면 아래 재조회가
    // stale 주문을 되돌려줘 그룹이 화면에 잔존(QA 7/23 "무시 즉시 사라지면"). 성공
    // 경로에서만 등록해 실패 시 화면 오염 없음. CS 합포해제·송장출력 해제 경로에서는
    // 대상이 발주완료 주문이라 no-op(다음 RECEIVE 에서 id 자동 정리).
    // 확정 스킵분(skippedConfirm — 미매칭·미결제)은 숨기지 않는다: 금지 경로 7/29 B4와
    // 같은 이유로, 서버 최종 상태가 '발주대기 잔류'인데 숨기면 stale 정리 로직에 걸려
    // 세션 내내 발주 목록·합포 뱃지에서 사라진다(2026-08-05 무시 경로 누락분 반영).
    const skippedIds = new Set((result?.skippedConfirm || []).map((s) => s.id));
    yield put(
      actions.hidePreConfirmOrders(
        (orderIds || []).filter((id) => !skippedIds.has(id)),
      ),
    );
    yield put(actions.receiveMergeResult(result));
    yield put(actions.setDeliveryMergeStatusSuccess());
    yield put(actions.getPreConfirmOrders());
    yield put(actions.getDeliveryStageOrders());
  } catch (error) {
    yield put(actions.setDeliveryMergeStatusFailure(error));
    Analytics.sendErrorReport(error, "excludeMergeOrders", { orderIds });
  }
}

export function* watchExcludeMergeOrders() {
  // 그룹 단위 다중 제외(선택 그룹 합포 무시)가 연속 dispatch 되므로 takeEvery
  yield takeEvery(actions.EXCLUDE_MERGE_ORDERS, excludeMergeOrders);
}

export function* manualMergeOrders({ orderIds, primaryOrderId }) {
  try {
    // employeeId 동봉 — 작업자 감사 기록(스키마 push 2026-07-24, confirm/exclude와 동일 컨텍스트)
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(
      API.manualMergeOrders,
      storeId,
      orderIds,
      primaryOrderId,
      employeeId,
    );
    if (result && result.error) {
      throw new Error(typeof result.error === "string" ? result.error : JSON.stringify(result.error));
    }
    yield put(actions.receiveMergeResult(result));
    yield put(actions.setDeliveryMergeStatusSuccess());
    yield put(actions.getDeliveryStageOrders());
  } catch (error) {
    yield put(actions.setDeliveryMergeStatusFailure(error));
    Analytics.sendErrorReport(error, "manualMergeOrders", { orderIds });
  }
}

export function* watchManualMergeOrders() {
  yield takeLeading(actions.MANUAL_MERGE_ORDERS, manualMergeOrders);
}

// 합포금지 토글 — 신규 API 없음. updateOrderFlags 로 order.mergeBlocked set
// (DynamoDB 스트림이 RDB 동기화. 읽는 쪽 합포검사·수동합포 제외 로직은 서버 구현됨.
// updateOrder 는 OrderDelta 체인이 채널 주문에서 실패해 성공을 reject 로 둔갑시킴)
export function* setDeliveryMergeBlocked({ orderId, blocked }) {
  try {
    // 합포금지(true) 시 mergeGroupId 도 함께 비워 그룹에서 제외한다(미아 방지 — 합포금지인데
    // 기존 그룹에 남아 합포 탭엔 보이고 발주 화면엔 안 뜨던 문제). 해제(false)는 그룹 안 건드림.
    yield call(API.updateOrderFlags, {
      id: orderId,
      mergeBlocked: blocked,
      ...(blocked ? { mergeGroupId: null } : {}),
    });
    yield put(actions.setDeliveryMergeStatusSuccess());
    yield put(actions.getDeliveryStageOrders());
    yield put(actions.getPreConfirmOrders()); // 합포 화면(DeliveryMerge)은 preConfirmOrders 슬라이스 렌더 — 토글 반영 위해 재조회
  } catch (error) {
    yield put(actions.setDeliveryMergeStatusFailure(error));
    Analytics.sendErrorReport(error, "setDeliveryMergeBlocked", { orderId });
  }
}

export function* watchSetDeliveryMergeBlocked() {
  yield takeEvery(actions.SET_DELIVERY_MERGE_BLOCKED, setDeliveryMergeBlocked);
}

// 그룹 합포금지(우진 7/23) — 멤버 전원 mergeBlocked=true(재그룹 방지) 설정 후
// excludeMergeOrders 로 제외목록 등록 + 단포 자동 발주완료. 단건 토글 워커와 달리
// mergeGroupId 는 비우지 않음(exclude 가 그룹 인자로 처리).
export function* blockMergeGroup({ orderIds, mergeGroupId }) {
  try {
    const { storeId, employeeId, locationId } = yield call(
      selectDeliveryContext,
    );
    for (const orderId of orderIds || []) {
      // updateOrderFlags — updateOrder 는 OrderDelta 체인이 채널 주문에서 실패해
      // 성공한 본 업데이트까지 reject 시킴(excludeMerge 미도달 무음 실패 실경로)
      yield call(API.updateOrderFlags, { id: orderId, mergeBlocked: true });
    }
    const result = yield call(API.excludeMergeOrders, storeId, {
      orderIds,
      mergeGroupId,
      employeeId,
      locationId,
    });
    if (result && result.error) {
      throw new Error(typeof result.error === "string" ? result.error : JSON.stringify(result.error));
    }
    // 서버 excludeMerge 는 unconfirmed 중 매칭완료(_isFullyMatched)만 확정·gid 해제하고
    // 아니면 조용히 스킵 → gid 잔존으로 뱃지·화면이 어긋남(우진 7/24 실측: 뱃지 4 vs 화면 2).
    // 아무것도 처리 안 됐으면 성공으로 둔갑시키지 않고 실패 표면화(낙관 숨김도 안 함).
    // 단, **처리할 게 없어서 0건**인 경우와 **실패해서 0건**인 경우를 구분해야 한다. 이미 금지가
    //   끝난 그룹에 같은 버튼을 다시 누르면(멱등 재실행) 서버는 정상적으로 0건을 돌려주는데,
    //   이걸 실패로 보면 아래 catch 가 방금 세팅한 mergeBlocked 를 전원 false 로 되돌려
    //   **금지가 조용히 풀린다**(2026-08-05 검증 지적). 서버가 error 를 주지 않았고 대상이
    //   이미 그룹에서 빠져 있으면(gid 없음) 정상 완료로 본다.
    // 판정 기준 = **서버가 error 를 줬는가**. 처리 건수 0 을 실패로 보면 안 된다 — 이미 금지가
    //   끝난 그룹에 같은 버튼을 다시 누르면(멱등 재실행) 서버는 정상적으로 0건을 돌려주는데,
    //   그걸 실패로 읽으면 아래 catch 가 방금 세팅한 mergeBlocked 를 전원 false 로 되돌려
    //   **금지가 조용히 풀린다**(2026-08-05 검증 지적). 합포 무시 경로(excludeMergeOrders)도
    //   같은 기준(result.error)만 본다 — 두 경로 판정을 일치시킨다.
    // 응답 자체가 비었으면(null/undefined 또는 키가 하나도 없음) 서버가 아무 일도 안 한 것 —
    //   성공으로 두면 화면이 주문을 감추고 "단포 발주완료 처리됩니다" 토스트까지 띄운다.
    //   반대로 처리 0건은 실패가 아니다(이미 금지가 끝난 그룹의 멱등 재실행) — 그걸 실패로
    //   보면 아래 catch 가 mergeBlocked 를 되돌려 금지가 조용히 풀린다(2026-08-05 검증).
    if (!result || typeof result !== "object" || Object.keys(result).length === 0) {
      throw new Error("합포 금지 처리 결과를 받지 못했습니다 — 잠시 후 다시 시도해 주세요.");
    }
    // 낙관 숨김(우진 7/24 — "금지 확정해도 합포의심에 남아있음") — updateOrder(mergeBlocked)
    // 는 DDB 기록 후 스트림으로 RDB 동기화(위 주석)라 아래 재조회가 stale 주문을
    // 되돌려줘 그룹이 잔존. 성공 경로에서만 등록해 실패 시 화면 오염 없음.
    // 확정 스킵분(skippedConfirm — 미매칭·미결제)은 숨기지 않는다: 서버 최종 상태가
    // '발주대기 잔류'인데 숨기면 stale 정리 로직(재조회에 계속 존재 → id 유지)에 걸려
    // 세션 내내 발주 목록에서도 사라짐(7/29 B4). 실제 이탈분(확정된 형제 포함
    // result.numbers)만 숨긴다.
    const blockSkippedIds = new Set(
      (result?.skippedConfirm || []).map((s) => s.id),
    );
    const blockHideIds = Array.from(
      new Set([...(orderIds || []), ...Object.keys(result?.numbers || {})]),
    ).filter((id) => !blockSkippedIds.has(id));
    yield put(actions.hidePreConfirmOrders(blockHideIds));
    yield put(actions.receiveMergeResult(result));
    yield put(actions.setDeliveryMergeStatusSuccess());
    yield put(actions.getPreConfirmOrders());
    yield put(actions.getDeliveryStageOrders());
  } catch (error) {
    // 시퀀스 중간 중단 시 blocked 플래그만 남는 반쪽 상태 방지 — best-effort 원복.
    // (updateOrder 성공 후 excludeMerge 실패가 dev 잔재 115의 실경로 — 서버 20번 실측)
    for (const orderId of orderIds || []) {
      try {
        yield call(API.updateOrderFlags, { id: orderId, mergeBlocked: false });
      } catch (rollbackError) {
        // 원복 실패는 무시 — 서버측 정리(excludeMerge 보강·mergeBlocked 직렬화 방어)에 위임
      }
    }
    yield put(actions.setDeliveryMergeStatusFailure(error));
    Analytics.sendErrorReport(error, "blockMergeGroup", {
      orderIds,
      mergeGroupId,
    });
  }
}

export function* watchBlockMergeGroup() {
  // 확인 모달 후 단발 dispatch — 중복 클릭 방지 takeLeading
  yield takeLeading(actions.BLOCK_MERGE_GROUP, blockMergeGroup);
}

// ── 좌측 nav 뱃지(발주/매칭/합포 + CS조회) 자동 재조회(우진 7/24) ──
// Aside 는 마운트 시 1회만 조회 → 주문 상태를 바꾸는 쓰기 작업 성공 시마다 재조회.
// 조회 성공 액션(SET_DELIVERY_STATUS_SUCCESS·SET_CS_STATUS_SUCCESS)은 이 워커가 부르는
// getDeliveryStageOrders/getCsCounts 자신이 발행하므로 트리거에 넣지 않음(루프 방지).
// debounce 2초 = 연속 작업 합류 + updateOrder(DDB)→스트림→RDB 동기화 랙을 지나 재조회
// (작업 직후 각 사가의 즉시 재조회는 stale 일 수 있어 이 지연 패스가 최종 교정).
const NAV_BADGE_REFRESH_TRIGGERS = [
  // 발주/매칭/합포/수동발주/수집·동기화 쓰기
  actions.SET_DELIVERY_CONFIRM_STATUS_SUCCESS,
  actions.SET_DELIVERY_MATCH_STATUS_SUCCESS,
  actions.SET_DELIVERY_MERGE_STATUS_SUCCESS,
  actions.SET_MANUAL_ORDER_STATUS_SUCCESS,
  actions.SET_DELIVERY_IMPORT_STATUS_SUCCESS,
  actions.RECEIVE_RELOAD_RESULT,
  // 송장 커맨드(발번/삭제/재발번/분할/외부송장·발송·가송장) — 발주취소 복귀 등 뱃지 영향
  actions.SET_SHIPMENT_ISSUE_STATUS_SUCCESS,
  actions.SET_SHIPMENT_PUSH_STATUS_SUCCESS,
  actions.SET_SHIPMENT_PROVISION_STATUS_SUCCESS,
  // CS 쓰기(클레임/편집/수집·승인/결제) — CS조회 뱃지
  actions.SET_CS_CLAIM_STATUS_SUCCESS,
  actions.SET_CS_EDIT_STATUS_SUCCESS,
  actions.SET_CS_COLLECT_STATUS_SUCCESS,
  actions.SET_CS_PAYMENT_STATUS_SUCCESS,
];

export function* refreshNavBadges() {
  yield put(actions.getDeliveryStageOrders());
  yield put(actions.getCsCounts());
}

export function* watchNavBadgeRefresh() {
  yield debounce(2000, NAV_BADGE_REFRESH_TRIGGERS, refreshNavBadges);
}

// CS 목록 벌크 합포 금지 — 선택 주문 순차 mergeBlocked=true. 완료 관찰·재조회는
// CSList 가 mergeStatus 슬라이스로 수행(발송처리 관례).
// ※ 합포(그룹)된 주문은 호출 전에 CSList 가 차단(우진 2026-07-21: 합포해제 선행) —
//   단건 워커와 달리 mergeGroupId 를 건드리지 않는다(그룹을 몰래 찢지 않음).
export function* csBulkMergeBlock({ orderIds }) {
  try {
    for (const orderId of orderIds || []) {
      // updateOrderFlags — updateOrder 는 OrderDelta 체인이 채널 주문에서 실패해
      // 성공한 본 업데이트까지 reject 시킴(전건 실패로 보이는 함정)
      yield call(API.updateOrderFlags, {
        id: orderId,
        mergeBlocked: true,
      });
    }
    yield put(actions.setDeliveryMergeStatusSuccess());
  } catch (error) {
    yield put(actions.setDeliveryMergeStatusFailure(error));
    Analytics.sendErrorReport(error, "csBulkMergeBlock");
  }
}

export function* watchCsBulkMergeBlock() {
  yield takeLeading(actions.CS_BULK_MERGE_BLOCK, csBulkMergeBlock);
}

// 벌크 보류 설정/해제 — setDeliveryHold(주문 단위)를 loop 로 일괄(csBulkMergeBlock 관례).
// 상태는 csEditStatus(단건 보류와 공용) — 송장출력 화면이 idle 전이 관찰 후 getPrintTargets 재조회.
export function* csBulkSetHold({ orderIds, enabled, reasonCode }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    // 서버는 건별 {error} 페이로드로 실패를 알림(throw 아님) — 수집해 집계 실패로 전환
    const failed = [];
    for (const orderId of orderIds || []) {
      const result = yield call(
        API.setDeliveryHold,
        storeId,
        orderId,
        enabled,
        reasonCode,
        employeeId,
      );
      if (result && result.error) failed.push(result.error);
    }
    if (failed.length > 0) {
      const okCount = (orderIds || []).length - failed.length;
      const suffix = okCount > 0 ? ` (성공 ${okCount}건)` : "";
      yield put(
        actions.setCsEditStatusFailure(
          Error(`${failed.length}건 실패${suffix} — ${translateChannelError(failed[0])}`),
        ),
      );
      return;
    }
    yield put(actions.setCsEditStatusSuccess());
  } catch (error) {
    yield put(actions.setCsEditStatusFailure(error));
    Analytics.sendErrorReport(error, "csBulkSetHold");
  }
}

export function* watchCsBulkSetHold() {
  yield takeLeading(actions.CS_BULK_SET_HOLD, csBulkSetHold);
}

export function* createManualOrder({ order }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.createManualOrder, storeId, order);
    yield put(actions.receiveCreateManualOrderResult(result));
    yield put(actions.setManualOrderStatusSuccess());
    yield put(actions.getPreConfirmOrders());
  } catch (error) {
    yield put(actions.setManualOrderStatusFailure(error));
    Analytics.sendErrorReport(error, "createManualOrder");
  }
}

export function* watchCreateManualOrder() {
  yield takeLeading(actions.CREATE_MANUAL_ORDER, createManualOrder);
}

// 주문복사 blob 구성 — 원본 주문 → 미결제·발주대기 수동발주(createManualOrder) 인자.
// 서버 계약(delivery.py createManualOrder): origin=pos 고정·importedAt 서버 셋·
// productCode/optionCode=채널 원본코드(자동매칭 키). originId 는 유니크(중복 거부)라 접미 부여.
// ★ paid:false = 미결제(state=CREATED) 생성 — 서버 createManualOrder 미결제 옵션 전제(배포 대기).
function buildCopyOrderBlob(order) {
  const receiver = (order.receivers || [])[0] || {};
  const skuItems = (order.items || []).filter((it) =>
    ["sku", "product"].includes(it.type),
  );
  const shippingItem = (order.items || []).find((it) => it.type === "shipping");
  const address1 = receiver.address1 || order.address || "";
  const itemRows = skuItems.map((it) => ({
    productCode: it.providerProductId || it.model || it.productModel,
    optionCode: it.providerOptionId || it.model,
    productName: it.providerProductName || it.name,
    optionName: it.providerOptionName || it.description || undefined,
    quantity: it.quantity,
    price:
      it.price != null
        ? it.price
        : it.amount != null && it.quantity
          ? Math.round(it.amount / it.quantity)
          : 0,
  }));
  const shippingFee = shippingItem ? shippingItem.amount || 0 : order.fee || 0;
  return {
    // 결제완료 복사(A안 전환 — 우진 2026-07-21): 미결제(state=CREATED)는 발주확인 자체가
    // 차단됨(cafe24 가 입금전 주문의 배송준비중 전환을 거부 — confirmOrders unpaid 제외)
    // → 미결제 복사본은 발주로 못 가므로 paid=true. 서버가 amount·amountPaid 를 items 로
    // 파생(전달 amountPaid 무시)하고 charge(method=manual 정규화)를 기록한다.
    paid: true,
    // 원본 주문번호 + 복사 접미(유니크) — 서버 originId 중복 거부 회피
    originId: `${order.originId || order.id}-C${moment().format("YYMMDDHHmmss")}`,
    name: receiver.name || order.name || "",
    phone: receiver.phone || order.phone || "",
    zip: receiver.zip || order.zip || "",
    address1,
    address2: receiver.address2 || undefined,
    note: receiver.note || order.shippingMessage || undefined,
    shippingFee,
    shippingPayType: order.shippingPayType || "prepaid",
    // 구배포 서버 호환 — 구 createManualOrder 는 amountPaid 필수(없으면 KeyError).
    // 신 서버(paid 분기)는 전달값 무시하고 items 로 파생하므로 동일 총액을 계산해 병기.
    amountPaid:
      itemRows.reduce(
        (sum, row) => sum + (row.price || 0) * (row.quantity || 0),
        0,
      ) + shippingFee,
    payMethod: "manual",
    // 생성일·결제일 = 복사 당일(우진 확정). paidAt 은 paid=true 의 charge createdAt 소스
    orderedAt: moment().format("YYYY-MM-DD HH:mm"),
    paidAt: moment().format("YYYY-MM-DD HH:mm"),
    items: itemRows,
  };
}

// 주문복사 — 원본 주문(미로드 시 fetch) → 결제완료 blob(A안) → createManualOrder.
// 결과는 manualOrder 슬라이스 공유(CSList 가 관찰). takeEvery — 다건 선택 복사 지원.
export function* copyDeliveryOrder({ orderId }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const ordersById = yield select(reducers.getOrdersById);
    let order = ordersById && ordersById[orderId];
    // CSTask 행은 items 미포함 — 원본 주문 직렬화 확보 위해 fetch
    if (!order || !(order.items && order.items.length)) {
      order = yield call(API.getOrder, orderId);
    }
    if (!order) throw new Error("order not found");
    const blob = buildCopyOrderBlob(order);
    const result = yield call(API.createManualOrder, storeId, blob);
    yield put(actions.receiveCreateManualOrderResult(result));
    yield put(actions.setManualOrderStatusSuccess());
  } catch (error) {
    yield put(actions.setManualOrderStatusFailure(error));
    Analytics.sendErrorReport(error, "copyDeliveryOrder", { orderId });
  }
}

export function* watchCopyDeliveryOrder() {
  yield takeEvery(actions.COPY_DELIVERY_ORDER, copyDeliveryOrder);
}

export function* uploadManualOrderRows({ rows }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.uploadManualOrderRows, storeId, rows);
    yield put(actions.receiveUploadManualOrderRowsResult(result));
    yield put(actions.setManualOrderStatusSuccess());
    yield put(actions.getPreConfirmOrders());
  } catch (error) {
    yield put(actions.setManualOrderStatusFailure(error));
    Analytics.sendErrorReport(error, "uploadManualOrderRows");
  }
}

export function* watchUploadManualOrderRows() {
  yield takeLeading(actions.UPLOAD_MANUAL_ORDER_ROWS, uploadManualOrderRows);
}

// ===== 배송주문 관리 — 송장(발번/출력차수/발송처리/가송장/배송관리) =====
// 서버 미구현/미배포 — 문서 계약 선작성. todo: 배포 후 응답 shape 대조.

export function* getPrintTargets({ range } = {}) {
  try {
    const storeId = yield select(reducers.getStoreId);
    // 서버 listPrintTargets 는 {printTargets, errors, holds, count...} dict 반환 —
    // 3분류 전부 보관(보류 칩·별도 분리 노출용. 이전엔 printTargets 만 추출해 holds 유실)
    const result = yield call(API.listPrintTargets, storeId, range);
    const targets = Array.isArray(result)
      ? result
      : (result && result.printTargets) || [];
    const holds = (!Array.isArray(result) && result && result.holds) || [];
    const errs = (!Array.isArray(result) && result && result.errors) || [];
    yield put(actions.receivePrintTargets(targets, holds, errs));
    yield put(actions.setShipmentStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentStatusFailure(error));
    Analytics.sendErrorReport(error, "getPrintTargets");
  }
}

export function* watchGetPrintTargets() {
  yield takeEvery(actions.GET_PRINT_TARGETS, getPrintTargets);
}

export function* getShippingStageOrders({ tab }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    // 서버 listDeliveryStageOrdersByTab 는 {tab, orders, count} dict 반환 → orders 배열만 추출
    const result = yield call(API.listDeliveryStageOrdersByTab, storeId, tab);
    const orders = Array.isArray(result) ? result : (result && result.orders) || [];
    yield put(actions.receiveShippingStageOrders(tab, orders));
    yield put(actions.setShipmentStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentStatusFailure(error));
    Analytics.sendErrorReport(error, "getShippingStageOrders", { tab });
  }
}

export function* watchGetShippingStageOrders() {
  yield takeEvery(actions.GET_SHIPPING_STAGE_ORDERS, getShippingStageOrders);
}

export function* getPrintBatches({ limit }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    // 서버 listPrintBatches 는 {batches:[...]} dict 반환 → batches 배열만 추출
    const result = yield call(API.listPrintBatches, storeId, limit);
    const batches = Array.isArray(result)
      ? result
      : (result && result.batches) || [];
    yield put(actions.receivePrintBatches(batches));
    // 차수 발행 직원 이름 표시 — 미로딩 employeeId 만 지연 조회(Order 히스토리 관례).
    // (현재 로그인 직원 외 다른 직원이 발행한 차수도 이름 해석되게. 서버가 batch.employeeId
    //  를 반환해야 표시됨 — 미반환 시 missingIds 비어 무동작)
    const employeesById = yield select(reducers.getEmployeesById);
    const missingIds = [
      ...new Set((batches || []).map((b) => b && b.employeeId).filter(Boolean)),
    ].filter((id) => !(id in employeesById));
    if (missingIds.length > 0) {
      let employees = yield all(missingIds.map((id) => call(API.getEmployee, id)));
      employees = employees.filter((emp) => emp);
      if (employees.length > 0) yield put(actions.receiveEmployees(employees));
    }
    yield put(actions.setShipmentStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentStatusFailure(error));
    Analytics.sendErrorReport(error, "getPrintBatches");
  }
}

export function* watchGetPrintBatches() {
  yield takeEvery(actions.GET_PRINT_BATCHES, getPrintBatches);
}

export function* getProvisionOrders() {
  try {
    const storeId = yield select(reducers.getStoreId);
    // 서버 listProvisionOrders 는 {orders, count} dict 반환 → orders 배열만 추출
    const result = yield call(API.listProvisionOrders, storeId);
    const orders = Array.isArray(result) ? result : (result && result.orders) || [];
    yield put(actions.receiveProvisionOrders(orders));
    yield put(actions.setShipmentStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentStatusFailure(error));
    Analytics.sendErrorReport(error, "getProvisionOrders");
  }
}

export function* watchGetProvisionOrders() {
  yield takeEvery(actions.GET_PROVISION_ORDERS, getProvisionOrders);
}

export function* issueDeliveryTracking({ orderIds, carrierCode, locationId, receiverId }) {
  try {
    const { storeId, employeeId, locationId: orgLocationId } = yield call(
      selectDeliveryContext,
    );
    // receiverId(배송건 단위 발번) — 분할배송 주문에서 그 출고건만 발번한다.
    // 2026-08-05 실측(ca-202608050005): 송장관리 1차 출고건 행 [발번] 하나에 1·2차가 모두
    // 발번돼(660034576010·660034576021) CJ 번호가 낭비됐다. 서버 issueTracking 은 이미
    // receiverId 를 받지만(delivery.py:5479 — receiverIds={receiverId} 로 한정),
    // API.issueDeliveryTracking 시그니처가 receiverId 를 버려 전달되지 않았다.
    // 스키마(LS_issueDeliveryTracking)에는 $receiverId 가 이미 선언돼 있으므로,
    // 지정 호출만 mutation 문서를 직접 태워 통과시킨다(미지정 = 기존 경로 그대로).
    const result = receiverId
      ? yield call(
          API._callDeliveryField,
          LS_issueDeliveryTracking,
          "LS_issueDeliveryTracking",
          {
            storeId,
            receiverId,
            orderIds,
            carrierCode,
            locationId: locationId || orgLocationId,
            employeeId,
          },
        )
      : yield call(API.issueDeliveryTracking, storeId, {
          orderIds,
          carrierCode,
          locationId: locationId || orgLocationId,
          employeeId,
        });
    yield put(actions.receiveIssueTrackingResult(result));
    yield put(actions.setShipmentIssueStatusSuccess());
    yield put(actions.getPrintTargets());
  } catch (error) {
    yield put(actions.setShipmentIssueStatusFailure(error));
    Analytics.sendErrorReport(error, "issueDeliveryTracking", { orderIds });
  }
}

export function* watchIssueDeliveryTracking() {
  yield takeLeading(actions.ISSUE_DELIVERY_TRACKING, issueDeliveryTracking);
}

export function* deleteDeliveryTracking({ orderId, receiverId }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(
      API.deleteDeliveryTracking,
      storeId,
      orderId,
      employeeId,
      receiverId,
    );
    yield put(actions.receiveShipmentCommandResult("deleteTracking", result));
    yield put(actions.setShipmentIssueStatusSuccess());
    yield put(actions.getPrintTargets());
  } catch (error) {
    yield put(actions.setShipmentIssueStatusFailure(error));
    Analytics.sendErrorReport(error, "deleteDeliveryTracking", { orderId });
  }
}

export function* watchDeleteDeliveryTracking() {
  yield takeEvery(actions.DELETE_DELIVERY_TRACKING, deleteDeliveryTracking);
}

export function* pushDeliveryShipment({ orderIds, status, receiverId }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(API.pushDeliveryShipment, storeId, {
      orderIds,
      status,
      receiverId,
      employeeId,
    });
    yield put(actions.receivePushShipmentResult(result, status));
    yield put(actions.setShipmentPushStatusSuccess());
    // 채널 push 실패는 예외가 아니라 정상 페이로드로 온다
    // ({results:{orderId:{sent:[], failed:[{receiverId,error}]}}} — delivery.py pushShipment).
    // 2026-08-05 실측(ca-202608050005 2회차): cafe24 가 'You cannot change to that order
    // state.' 로 거부 → 서버는 receiver.providerSyncStatus=failed + CHANNEL_UNSYNCED task 를
    // 남기는데 프론트가 아무것도 갱신하지 않아 CS 목록에도 안 떴다. 실패가 있으면 CS
    // 목록/카운트를 갱신해 '판매처 미반영' task 가 바로 보이게 한다(refetchCsAfterWrite 관례).
    const pushResults = (result && result.results) || {};
    const hasPushFailure =
      Boolean(result && result.error) ||
      Object.keys(pushResults).some((oid) => {
        const r = pushResults[oid] || {};
        return Boolean(r.error) || (Array.isArray(r.failed) && r.failed.length > 0);
      });
    if (hasPushFailure) {
      yield put(actions.getCsTasks());
      yield put(actions.getCsCounts());
    }
  } catch (error) {
    yield put(actions.setShipmentPushStatusFailure(error));
    Analytics.sendErrorReport(error, "pushDeliveryShipment", { orderIds });
  }
}

export function* watchPushDeliveryShipment() {
  yield takeLeading(actions.PUSH_DELIVERY_SHIPMENT, pushDeliveryShipment);
}

export function* applyExternalTracking({ rows }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(API.applyExternalTracking, storeId, rows, employeeId);
    yield put(actions.receiveApplyExternalTrackingResult(result));
    yield put(actions.setShipmentIssueStatusSuccess());
    yield put(actions.getPrintTargets());
  } catch (error) {
    yield put(actions.setShipmentIssueStatusFailure(error));
    Analytics.sendErrorReport(error, "applyExternalTracking");
  }
}

export function* watchApplyExternalTracking() {
  yield takeLeading(actions.APPLY_EXTERNAL_TRACKING, applyExternalTracking);
}

export function* issueProvisionTracking({ orderIds, locationId }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(API.issueProvisionTracking, storeId, orderIds, locationId, employeeId);
    yield put(actions.receiveProvisionResult(result));
    yield put(actions.setShipmentProvisionStatusSuccess());
    yield put(actions.getProvisionOrders());
    yield put(actions.getPrintTargets());
    // 가송장 발번 = 출력차수 동시 기록 — 차수 이력 탭 자동 갱신(QA 08-03: 새로고침해야 보임)
    yield put(actions.getPrintBatches());
  } catch (error) {
    yield put(actions.setShipmentProvisionStatusFailure(error));
    Analytics.sendErrorReport(error, "issueProvisionTracking", { orderIds });
  }
}

export function* watchIssueProvisionTracking() {
  yield takeLeading(actions.ISSUE_PROVISION_TRACKING, issueProvisionTracking);
}

export function* convertProvisionToReal({ orderIds, carrierCode, locationId }) {
  try {
    const { storeId, employeeId, locationId: orgLocationId } = yield call(
      selectDeliveryContext,
    );
    const result = yield call(API.convertProvisionToReal, storeId, {
      orderIds,
      carrierCode,
      locationId: locationId || orgLocationId,
      employeeId,
    });
    yield put(actions.receiveProvisionResult(result));
    yield put(actions.setShipmentProvisionStatusSuccess());
    yield put(actions.getProvisionOrders());
  } catch (error) {
    yield put(actions.setShipmentProvisionStatusFailure(error));
    Analytics.sendErrorReport(error, "convertProvisionToReal", { orderIds });
  }
}

export function* watchConvertProvisionToReal() {
  yield takeLeading(actions.CONVERT_PROVISION_TO_REAL, convertProvisionToReal);
}

export function* completeProvisionDelivery({ orderIds, receiverId }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(
      API.completeProvisionDelivery,
      storeId,
      orderIds,
      employeeId,
      receiverId,
    );
    yield put(actions.receiveProvisionResult(result));
    yield put(actions.setShipmentProvisionStatusSuccess());
    yield put(actions.getProvisionOrders());
  } catch (error) {
    yield put(actions.setShipmentProvisionStatusFailure(error));
    Analytics.sendErrorReport(error, "completeProvisionDelivery", { orderIds });
  }
}

export function* watchCompleteProvisionDelivery() {
  yield takeLeading(actions.COMPLETE_PROVISION_DELIVERY, completeProvisionDelivery);
}

export function* splitDeliveryShipment({ orderId, itemIds }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(
      API.splitDeliveryShipment,
      storeId,
      orderId,
      itemIds,
      employeeId,
    );
    yield put(actions.receiveShipmentCommandResult("splitShipment", result));
    yield put(actions.setShipmentIssueStatusSuccess());
    yield put(actions.getPrintTargets());
  } catch (error) {
    yield put(actions.setShipmentIssueStatusFailure(error));
    Analytics.sendErrorReport(error, "splitDeliveryShipment", { orderId });
  }
}

export function* watchSplitDeliveryShipment() {
  yield takeEvery(actions.SPLIT_DELIVERY_SHIPMENT, splitDeliveryShipment);
}

export function* addShipmentBox({ orderId, itemIds, issue, count }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    // 서버 addShipmentBox 는 호출당 boxCount+1(기존 송장 취소 후 재발급) — 박스 수
    // 인자가 없어 N박스는 순차 N회 호출 // todo 서버 count 인자 확장 시 단건 호출로 정리
    const times = Math.max(1, Number(count) || 1);
    let result = null;
    for (let i = 0; i < times; i += 1) {
      result = yield call(API.addShipmentBox, storeId, {
        orderId,
        itemIds,
        issue,
        employeeId,
      });
      if (result?.error) break; // 서버 게이팅 에러 — 남은 반복 중단(부분 성공 결과 유지)
    }
    yield put(actions.receiveShipmentCommandResult("addShipmentBox", result));
    if (result?.error) {
      // 서버 게이팅 에러(집화 후 등)는 resolve 페이로드로 옴 — 성공으로 위장하지 않도록
      // failure 종료(관찰 표면이 모달 유지·재조회). 반복 중간 실패 시 기발행분은 유지됨.
      yield put(actions.setShipmentIssueStatusFailure(result.error));
      return;
    }
    yield put(actions.setShipmentIssueStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentIssueStatusFailure(error));
    Analytics.sendErrorReport(error, "addShipmentBox", { orderId });
  }
}

export function* watchAddShipmentBox() {
  yield takeEvery(actions.ADD_SHIPMENT_BOX, addShipmentBox);
}

// 추가송장(다박스) 신 API — 원송장 유지·박스별 새 receiver+발번. count 네이티브라
// 단건 호출(구 addShipmentBox 순차반복 대체). 완료는 issueStatus 슬라이스 관찰(모달).
export function* addShipmentReceiver({
  orderId,
  receiverId,
  count,
  boxType,
  freightType,
  contents,
}) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(API.addShipmentReceiver, storeId, {
      orderId,
      receiverId,
      count,
      boxType,
      freightType,
      contents,
      employeeId,
    });
    yield put(actions.receiveShipmentCommandResult("addShipmentReceiver", result));
    if (result?.error) {
      // 서버 게이팅 에러(집화 후·상한 초과 등)는 resolve 페이로드 — failure 종료(모달 유지)
      yield put(actions.setShipmentIssueStatusFailure(result.error));
      return;
    }
    yield put(actions.setShipmentIssueStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentIssueStatusFailure(error));
    Analytics.sendErrorReport(error, "addShipmentReceiver", { orderId });
  }
}

export function* watchAddShipmentReceiver() {
  yield takeEvery(actions.ADD_SHIPMENT_RECEIVER, addShipmentReceiver);
}

export function* reissueDeliveryTracking({ orderId, carrierCode, locationId, receiverId }) {
  try {
    const { storeId, employeeId, locationId: orgLocationId } = yield call(
      selectDeliveryContext,
    );
    const result = yield call(API.reissueDeliveryTracking, storeId, {
      orderId,
      carrierCode,
      locationId: locationId || orgLocationId,
      receiverId,
      employeeId,
    });
    yield put(actions.receiveShipmentCommandResult("reissueTracking", result));
    yield put(actions.setShipmentIssueStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentIssueStatusFailure(error));
    Analytics.sendErrorReport(error, "reissueDeliveryTracking", { orderId });
  }
}

export function* watchReissueDeliveryTracking() {
  yield takeEvery(actions.REISSUE_DELIVERY_TRACKING, reissueDeliveryTracking);
}

export function* changeShippingAfterIssue({ orderId, shipping }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(API.changeShippingAfterIssue, storeId, {
      orderId,
      ...shipping,
      employeeId,
    });
    yield put(actions.receiveShipmentCommandResult("changeShipping", result));
    yield put(actions.setShipmentIssueStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentIssueStatusFailure(error));
    Analytics.sendErrorReport(error, "changeShippingAfterIssue", { orderId });
  }
}

export function* watchChangeShippingAfterIssue() {
  yield takeEvery(actions.CHANGE_SHIPPING_AFTER_ISSUE, changeShippingAfterIssue);
}

export function* createPrintBatch({ orderIds, shippingCompanyCode, templateId }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(API.createPrintBatch, storeId, {
      orderIds,
      shippingCompanyCode,
      employeeId,
      templateId, // 발번 모달 선택 양식 — 미선택 호출부(CS 계열)는 서버 기본 폴백
    });
    yield put(actions.receivePrintBatchResult(result));
    yield put(actions.setShipmentBatchStatusSuccess());
    yield put(actions.getPrintBatches());
  } catch (error) {
    yield put(actions.setShipmentBatchStatusFailure(error));
    Analytics.sendErrorReport(error, "createPrintBatch", { orderIds });
  }
}

export function* watchCreatePrintBatch() {
  yield takeLeading(actions.CREATE_PRINT_BATCH, createPrintBatch);
}

export function* reprintBatch({ batchId }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(API.reprintBatch, storeId, batchId, employeeId);
    yield put(actions.receivePrintBatchResult(result));
    yield put(actions.setShipmentBatchStatusSuccess());
    yield put(actions.getPrintBatches());
  } catch (error) {
    yield put(actions.setShipmentBatchStatusFailure(error));
    Analytics.sendErrorReport(error, "reprintBatch", { batchId });
  }
}

export function* watchReprintBatch() {
  yield takeLeading(actions.REPRINT_BATCH, reprintBatch);
}

export function* exportPrintTargetsExcel() {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.exportPrintTargetsExcel, storeId);
    // 서버는 {key, url, count} dict 반환 — window.open에 객체가 들어가지 않게 url만 추출
    const url = result && result.url ? result.url : result;
    yield put(actions.receiveExportPrintTargetsUrl(url));
    yield put(actions.setShipmentBatchStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentBatchStatusFailure(error));
    Analytics.sendErrorReport(error, "exportPrintTargetsExcel");
  }
}

export function* watchExportPrintTargetsExcel() {
  yield takeLeading(actions.EXPORT_PRINT_TARGETS_EXCEL, exportPrintTargetsExcel);
}

export function* trackShipment({ trackingNumber, carrierCode }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.trackShipment, storeId, trackingNumber, carrierCode);
    yield put(actions.receiveTrackShipmentResult(result));
    yield put(actions.setShipmentStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentStatusFailure(error));
    Analytics.sendErrorReport(error, "trackShipment", { trackingNumber });
  }
}

export function* watchTrackShipment() {
  yield takeLeading(actions.TRACK_SHIPMENT, trackShipment);
}

export function* checkDeliveryDelays() {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.checkDeliveryDelays, storeId);
    yield put(actions.receiveDeliveryDelays(result));
    yield put(actions.setShipmentStatusSuccess());
  } catch (error) {
    yield put(actions.setShipmentStatusFailure(error));
    Analytics.sendErrorReport(error, "checkDeliveryDelays");
  }
}

export function* watchCheckDeliveryDelays() {
  yield takeLeading(actions.CHECK_DELIVERY_DELAYS, checkDeliveryDelays);
}

// ===== 배송 환경설정 — ShippingConfig(스토어 단위) + Carrier(택배사·내장 양식) =====
// 모델 CRUD(codegen 문서) — ProductCodeRule 선례: 뮤테이션 성공 후 재조회로 redux 갱신.
// 양식(ShipmentLabelTemplate)은 Carrier.templates 내장 타입 — 전용 뮤테이션
// (LS_save/copy/deleteShipmentTemplate, 서버 배포 7/23)으로 갱신. 통짜 교체는 legacy.

export function* getShippingSettings({ createIfMissing = true } = {}) {
  try {
    const storeId = yield select(reducers.getStoreId);
    let config = yield call(API.getShippingConfig, storeId);
    if (!config && createIfMissing) {
      // 최초 진입 — 확정 기본값으로 생성(기획 C2/C3/C4·§3: stockLimitedPrint ON ·
      // reprintWarning ON · SUMMARY/5행 · printSortKeys [zip, mgmtNo] · 가송장 OFF ·
      // 일괄배송처리 ON)
      config = yield call(API.createShippingConfig, {
        storeId,
        stockLimitedPrint: true,
        reprintWarning: true,
        overflowItemMode: "SUMMARY",
        printSortKeys: ["zip", "mgmtNo"],
        provisionEnabled: false,
        batchShipEnabled: true,
      });
    }
    const carriers = yield call(API.getShippingCarriers, storeId);
    yield put(actions.receiveShippingConfig(config));
    yield put(actions.receiveShippingCarriers(carriers.items || []));
    yield put(actions.setShippingSettingsStatusSuccess());
  } catch (error) {
    yield put(actions.setShippingSettingsStatusFailure(error));
    Analytics.sendErrorReport(error, "getShippingSettings");
  }
}

export function* watchGetShippingSettings() {
  yield takeLeading(actions.GET_SHIPPING_SETTINGS, getShippingSettings);
}

export function* saveShippingConfig({ patch, templateUpdates }) {
  // config patch와 template 저장이 2콜로 갈려 첫 콜만 성공하면 부분 저장 상태가 되는데,
  // 종전엔 '전부 실패'로만 보였음(A-3⑤ 검수). 부분 저장 사실을 실패 배너에 표면화(확정 2026-07-30).
  let configSaved = false;
  try {
    const storeId = yield select(reducers.getStoreId);
    if (patch && Object.keys(patch).length > 0) {
      const current = yield select(reducers.getShippingConfig);
      if (!current || !current.id) throw new Error("shipping config not loaded");
      const updated = yield call(API.updateShippingConfig, {
        id: current.id,
        ...patch,
      });
      yield put(actions.receiveShippingConfig(updated));
      configSaved = true;
    }
    if (templateUpdates && templateUpdates.length > 0) {
      // templateUpdates = [{ carrierId, template }] (병합 완료 단일 양식) —
      // 전용 뮤테이션으로 교체(서버 배포 7/23), 통짜 교체(createOrUpdateCarrier) 폐기
      const employee = yield select(reducers.getEmployee);
      for (const update of templateUpdates) {
        yield call(
          API.saveShipmentTemplate,
          storeId,
          update.carrierId,
          update.template,
          employee ? employee.id : null,
        );
      }
      const carriers = yield call(API.getShippingCarriers, storeId);
      yield put(actions.receiveShippingCarriers(carriers.items || []));
    }
    yield put(actions.setShippingSettingsSaveSuccess());
  } catch (error) {
    if (configSaved && error && typeof error === "object") {
      error.partialSaved = true; // 기본 설정은 저장됨 — 배너 문구 분기용
    }
    yield put(actions.setShippingSettingsSaveFailure(error));
    Analytics.sendErrorReport(error, "saveShippingConfig");
  }
}

export function* watchSaveShippingConfig() {
  yield takeLeading(actions.SAVE_SHIPPING_CONFIG, saveShippingConfig);
}

export function* saveShippingCarrier({ carrier }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    // isNew = 명시 플래그(클라이언트 발급 id 라 id 유무로 판별 불가) — input 에서 제거.
    // setAsDefault 는 이 워커가 함께 처리(별도 dispatch 2회 = 공용 saveStatus
    // falling edge 조기 발화 금기 — actions saveShippingConfig 주석 참조)
    const { isNew, setAsDefault, ...rest } = carrier;
    const input = isNew ? { ...rest, storeId } : rest;
    yield call(API.createOrUpdateCarrier, input, Boolean(isNew));
    if (setAsDefault) {
      const current = yield select(reducers.getShippingConfig);
      if (current && current.id) {
        const updated = yield call(API.updateShippingConfig, {
          id: current.id,
          carrierId: rest.id,
        });
        yield put(actions.receiveShippingConfig(updated));
      }
    }
    const carriers = yield call(API.getShippingCarriers, storeId);
    yield put(actions.receiveShippingCarriers(carriers.items || []));
    yield put(actions.setShippingSettingsSaveSuccess());
  } catch (error) {
    yield put(actions.setShippingSettingsSaveFailure(error));
    Analytics.sendErrorReport(error, "saveShippingCarrier");
  }
}

export function* watchSaveShippingCarrier() {
  yield takeLeading(actions.SAVE_SHIPPING_CARRIER, saveShippingCarrier);
}

export function* deleteShippingCarrier({ carrierId }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    yield call(API.deleteCarrier, carrierId);
    const carriers = yield call(API.getShippingCarriers, storeId);
    yield put(actions.receiveShippingCarriers(carriers.items || []));
    yield put(actions.setShippingSettingsSaveSuccess());
  } catch (error) {
    yield put(actions.setShippingSettingsSaveFailure(error));
    Analytics.sendErrorReport(error, "deleteShippingCarrier");
  }
}

export function* watchDeleteShippingCarrier() {
  yield takeLeading(actions.DELETE_SHIPPING_CARRIER, deleteShippingCarrier);
}

// [legacy-deprecated] Carrier.templates 통짜 교체 — 전용 뮤테이션(아래 3종,
// 서버 배포 7/23)으로 대체. 잔존 참조 대비 유지, 신규 사용 금지.
// updates = [{ carrierId, templates }] — 기본 양식 이관(두 캐리어 동시)까지 순차 처리
export function* updateCarrierTemplates({ updates }) {
  // 캐리어별 루프라 첫 건 성공 후 다음 건 실패 시 부분 저장이 됨 — 종전엔 '전부 실패'로만
  // 보였음(A-3⑤ 검수의 실경로, ultracode 검증으로 위치 재확인). 부분 저장 사실을 배너에 표면화.
  let savedCount = 0;
  try {
    const storeId = yield select(reducers.getStoreId);
    for (const update of updates || []) {
      yield call(API.createOrUpdateCarrier, {
        id: update.carrierId,
        templates: update.templates,
      });
      savedCount += 1;
    }
    const carriers = yield call(API.getShippingCarriers, storeId);
    yield put(actions.receiveShippingCarriers(carriers.items || []));
    yield put(actions.setShippingSettingsSaveSuccess());
  } catch (error) {
    if (savedCount > 0 && error && typeof error === "object") {
      error.partialSaved = true; // 일부 택배사 양식은 저장됨 — 배너 문구 분기용
    }
    yield put(actions.setShippingSettingsSaveFailure(error));
    Analytics.sendErrorReport(error, "updateCarrierTemplates");
  }
}

export function* watchUpdateCarrierTemplates() {
  yield takeLeading(actions.UPDATE_CARRIER_TEMPLATES, updateCarrierTemplates);
}

// ----- 양식(ShipmentLabelTemplate) 전용 뮤테이션 — 서버 배포 7/23 -----
// 응답에 해당 캐리어 templates 전체 동봉 계약이나, 응답 형태 의존을 줄이기 위해
// 기존 관례(전체 캐리어 재조회 → receiveShippingCarriers)로 redux 갱신.
// isDefault 전역 1개 불변식은 서버가 처리 — 프론트 교차 캐리어 해제 없음.
// employeeId = 감사 축(옵션) — 다른 배송 워커(selectDeliveryContext) 관례 준용.

export function* saveShipmentTemplate({ carrierId, template }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const employee = yield select(reducers.getEmployee);
    const result = yield call(
      API.saveShipmentTemplate,
      storeId,
      carrierId,
      template,
      employee ? employee.id : null,
    );
    // 서버는 실패를 {error} 값으로 반환(200 OK) — 예외 아님. 값으로 실패 처리(UI가 saveStatus.error 소비).
    // 예상된 비즈니스 에러라 crash 리포트는 보내지 않음(그게 서버가 {error}로 돌려준 이유).
    if (result && result.error) {
      yield put(actions.setShippingSettingsSaveFailure(result.error));
      // 실패해도 목록은 다시 읽는다 — 다른 탭이 양식을 지운 뒤 저장하면 서버가
      // "template not found"를 주는데, 재조회가 없으면 이미 없는 양식이 목록·편집기에
      // 계속 남아 같은 실패만 반복된다(2026-08-05 실측). 재조회하면 편집 대상이 사라져
      // DeliveryConfig의 자동 닫힘 이펙트가 편집기를 닫아 준다.
      const stale = yield call(API.getShippingCarriers, storeId);
      yield put(actions.receiveShippingCarriers(stale.items || []));
      return;
    }
    const carriers = yield call(API.getShippingCarriers, storeId);
    yield put(actions.receiveShippingCarriers(carriers.items || []));
    yield put(actions.setShippingSettingsSaveSuccess());
  } catch (error) {
    yield put(actions.setShippingSettingsSaveFailure(error));
    Analytics.sendErrorReport(error, "saveShipmentTemplate");
  }
}

export function* watchSaveShipmentTemplate() {
  yield takeLeading(actions.SAVE_SHIPMENT_TEMPLATE, saveShipmentTemplate);
}

export function* copyShipmentTemplate({ carrierId, templateId, name }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const employee = yield select(reducers.getEmployee);
    // 사본 id·이름·isDefault=false 부여는 서버 책임 — 응답 형태(사본 포함
    // templates 전체) 가정은 재조회로 우회(suspect: 계약 확정 시 응답 직사용 검토)
    const result = yield call(
      API.copyShipmentTemplate,
      storeId,
      carrierId,
      templateId,
      name,
      employee ? employee.id : null,
    );
    // 서버 실패는 {error} 값 반환 — 값으로 실패 처리(예외 아님)
    if (result && result.error) {
      yield put(actions.setShippingSettingsSaveFailure(result.error));
      return;
    }
    const carriers = yield call(API.getShippingCarriers, storeId);
    yield put(actions.receiveShippingCarriers(carriers.items || []));
    yield put(actions.setShippingSettingsSaveSuccess());
  } catch (error) {
    yield put(actions.setShippingSettingsSaveFailure(error));
    Analytics.sendErrorReport(error, "copyShipmentTemplate");
  }
}

export function* watchCopyShipmentTemplate() {
  yield takeLeading(actions.COPY_SHIPMENT_TEMPLATE, copyShipmentTemplate);
}

export function* deleteShipmentTemplate({ carrierId, templateId }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const employee = yield select(reducers.getEmployee);
    const result = yield call(
      API.deleteShipmentTemplate,
      storeId,
      carrierId,
      templateId,
      employee ? employee.id : null,
    );
    // 서버 실패는 {error} 값 반환 — 값으로 실패 처리(예외 아님)
    if (result && result.error) {
      yield put(actions.setShippingSettingsSaveFailure(result.error));
      return;
    }
    const carriers = yield call(API.getShippingCarriers, storeId);
    yield put(actions.receiveShippingCarriers(carriers.items || []));
    yield put(actions.setShippingSettingsSaveSuccess());
  } catch (error) {
    yield put(actions.setShippingSettingsSaveFailure(error));
    Analytics.sendErrorReport(error, "deleteShipmentTemplate");
  }
}

export function* watchDeleteShipmentTemplate() {
  yield takeLeading(actions.DELETE_SHIPMENT_TEMPLATE, deleteShipmentTemplate);
}

// 합포 설정 현황 조회 — 서버 검색·상태필터·페이징(LS_listSkuMergeRules)
export function* getSkuMergeRules({ filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.listSkuMergeRules, storeId, filters || {});
    yield put(actions.receiveSkuMergeRules(result));
    yield put(actions.setSkuMergeRulesStatusSuccess());
  } catch (error) {
    yield put(actions.setSkuMergeRulesStatusFailure(error));
    Analytics.sendErrorReport(error, "getSkuMergeRules");
  }
}

export function* watchGetSkuMergeRules() {
  yield takeLatest(actions.GET_SKU_MERGE_RULES, getSkuMergeRules);
}

// 합포 설정 업데이트(단건·일괄·전체해제) — 부분 실패({failed})는 정상 페이로드로
// 결과 슬라이스에 보고, 목록 재조회는 화면이 유지 필터로 수행(설계 §3-5 관례)
export function* updateSkuMergeRules({ updates, clearAll }) {
  try {
    const { storeId, employeeId } = yield call(selectDeliveryContext);
    const result = yield call(API.updateSkuMergeRules, storeId, {
      updates,
      clearAll,
      employeeId,
    });
    yield put(actions.receiveSkuMergeUpdateResult(result));
    yield put(actions.setShippingSettingsSaveSuccess());
  } catch (error) {
    yield put(actions.setShippingSettingsSaveFailure(error));
    Analytics.sendErrorReport(error, "updateSkuMergeRules");
  }
}

export function* watchUpdateSkuMergeRules() {
  yield takeLeading(actions.UPDATE_SKU_MERGE_RULES, updateSkuMergeRules);
}

// 미사용 송장 조회 — LS_listCanceledWaybills(폐기 이력). 미사용 송장 = 교체발번·
// 송장삭제로 폐기된 번호(우진 확정 2026-07-17 — 집하 지연 보류 건은 미사용 아님).
// ※ LS_listUnusedTrackings(미집화 활성 송장 점검 축)는 API 레이어만 유지, 화면 미사용
export function* getUnusedTrackings({ filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.listCanceledWaybills, storeId, filters || {});
    yield put(actions.receiveUnusedTrackings(result));
    yield put(actions.setUnusedTrackingsStatusSuccess());
  } catch (error) {
    yield put(actions.setUnusedTrackingsStatusFailure(error));
    Analytics.sendErrorReport(error, "getUnusedTrackings");
  }
}

export function* watchGetUnusedTrackings() {
  yield takeLatest(actions.GET_UNUSED_TRACKINGS, getUnusedTrackings);
}


// ===== 배송주문 관리 — CS(클레임/주문 편집·운영/결제·정산/수집·승인) =====
// worker+watcher 1:1. 조회=takeEvery / 쓰기 전부 takeLeading(더블클릭 방지 —
// issueDeliveryTracking 관례). 게이팅 에러({error, needs...})는 정상 페이로드 —
// receiveCsActionResult 로 리듀서에 넣고 패널이 분기(성공 refetch 는 생략).
// 조회 4종(listCsTasks/countCsTasks/listCsActivities/searchCsOrders) 스키마
// 배포 확인(2026-07-10) — 응답 shape 실데이터 검증 todo
// 기존 delivery/shipment 워커 재사용분(split/issue/delete/reissue/push/applyExternal/
// track/manualMerge/excludeMerge/setDeliveryMergeBlocked/changeShippingAfterIssue)은
// 무수정 — CS 표면에서는 status 슬라이스 관찰 후 페이지가 getCsTasks()/getOrder() 재디스패치.

// 쓰기 성공 후 공용 refetch — importChannelOrders 패턴 준용.
// 리스트가 유지한 필터는 CSList 가 재디스패치.
function* refetchCsAfterWrite(orderId) {
  yield put(actions.getCsCounts());
  yield put(actions.getCsTasks());
  if (orderId) {
    yield put(actions.getCsActivities({ orderId }));
    yield put(actions.getOrder(orderId)); // 기존 단건 조회 재사용 → getOrdersById 갱신
  }
}

// CS 쓰기 워커 공통 골격 — result 수신 → 게이팅 에러 분기 → status → refetch
function* runCsWrite({ actionKey, apiCall, orderId, setSuccess, setFailure, reportName, reportMeta }) {
  try {
    const result = yield call(apiCall);
    yield put(actions.receiveCsActionResult(actionKey, result));
    if (result && result.error) {
      // 게이팅 에러는 정상 페이로드 — 패널이 needs* 분기. 실패 status 만 세팅.
      yield put(setFailure(Error(translateChannelError(result.error))));
      return;
    }
    yield put(setSuccess());
    yield* refetchCsAfterWrite(orderId);
    return result;                       // 호출부 후처리용(채널 왕복 경로의 지연 재조회 등)
  } catch (error) {
    yield put(setFailure(error));
    Analytics.sendErrorReport(error, reportName, reportMeta);
  }
  return null;
}

// ----- 조회 -----

export function* getCsTasks({ filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const tasks = yield call(API.listCsTasks, storeId, filters);
    yield put(actions.receiveCsTasks(tasks));
    yield put(actions.setCsStatusSuccess());
  } catch (error) {
    yield put(actions.setCsStatusFailure(error));
    Analytics.sendErrorReport(error, "getCsTasks");
  }
}

export function* watchGetCsTasks() {
  yield takeEvery(actions.GET_CS_TASKS, getCsTasks);
}

// CS 목록 주문 단위 조회(B안) — listCsOrders. 응답 shape 동일이라 cs.tasks 슬라이스 재사용.
export function* getCsOrders({ filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const orders = yield call(API.listCsOrders, storeId, filters);
    yield put(actions.receiveCsTasks(orders));
    yield put(actions.setCsStatusSuccess());
  } catch (error) {
    yield put(actions.setCsStatusFailure(error));
    Analytics.sendErrorReport(error, "getCsOrders");
  }
}

export function* watchGetCsOrders() {
  yield takeEvery(actions.GET_CS_ORDERS, getCsOrders);
}

export function* getCsCounts() {
  try {
    const storeId = yield select(reducers.getStoreId);
    const counts = yield call(API.countCsTasks, storeId);
    yield put(actions.receiveCsCounts(counts));
    yield put(actions.setCsStatusSuccess());
  } catch (error) {
    yield put(actions.setCsStatusFailure(error));
    Analytics.sendErrorReport(error, "getCsCounts");
  }
}

export function* watchGetCsCounts() {
  yield takeEvery(actions.GET_CS_COUNTS, getCsCounts);
}

export function* getCsActivities({ filters }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const activities = yield call(API.listCsActivities, storeId, filters);
    yield put(actions.receiveCsActivities(filters?.orderId, activities || []));
    yield put(actions.setCsStatusSuccess());
  } catch (error) {
    yield put(actions.setCsStatusFailure(error));
    Analytics.sendErrorReport(error, "getCsActivities");
  }
}

export function* watchGetCsActivities() {
  yield takeEvery(actions.GET_CS_ACTIVITIES, getCsActivities);
}

export function* searchCsOrders({ keyword, limit }) {
  try {
    const storeId = yield select(reducers.getStoreId);
    const result = yield call(API.searchCsOrders, storeId, keyword, limit);
    yield put(actions.receiveCsSearchResult(result));
    yield put(actions.setCsStatusSuccess());
  } catch (error) {
    yield put(actions.setCsStatusFailure(error));
    Analytics.sendErrorReport(error, "searchCsOrders");
  }
}

export function* watchSearchCsOrders() {
  // takeLatest — 상세 왕복 복원 재검색과 새 검색이 겹칠 때 이전 응답이 늦게 도착해
  // 새 검색 결과를 덮는 레이스 방지(08-04 실측: 0033 검색에 직전 0037 결과 표시)
  yield takeLatest(actions.SEARCH_CS_ORDERS, searchCsOrders);
}

// ----- 클레임 (취소/철회/반품/교환) -----

export function* csCancelOrder({ orderId, payload }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "cancel.accept",
    apiCall: () => API.cancelDeliveryOrder(storeId, orderId, { ...payload, employeeId }),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csCancelOrder",
    reportMeta: { orderId },
  });
}

export function* watchCsCancelOrder() {
  yield takeLeading(actions.CS_CANCEL_ORDER, csCancelOrder);
}

export function* csRestoreOrder({ orderId, undoneReasonType }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "restore",
    apiCall: () => API.restoreDeliveryOrder(storeId, orderId, undoneReasonType, employeeId),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csRestoreOrder",
    reportMeta: { orderId },
  });
}

export function* watchCsRestoreOrder() {
  yield takeLeading(actions.CS_RESTORE_ORDER, csRestoreOrder);
}

export function* csRestoreReturn({ orderId, undoneReasonType }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "restore",
    apiCall: () => API.restoreDeliveryReturn(storeId, orderId, undoneReasonType, employeeId),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csRestoreReturn",
    reportMeta: { orderId },
  });
}

export function* watchCsRestoreReturn() {
  yield takeLeading(actions.CS_RESTORE_RETURN, csRestoreReturn);
}

export function* csRestoreExchange({ orderId, undoneReasonType }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "restore",
    apiCall: () => API.restoreDeliveryExchange(storeId, orderId, undoneReasonType, employeeId),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csRestoreExchange",
    reportMeta: { orderId },
  });
}

export function* watchCsRestoreExchange() {
  yield takeLeading(actions.CS_RESTORE_EXCHANGE, csRestoreExchange);
}

export function* csAcceptReturn({ orderId, payload }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "return.accept",
    apiCall: () => API.acceptDeliveryReturn(storeId, orderId, { ...payload, employeeId }),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csAcceptReturn",
    reportMeta: { orderId },
  });
}

export function* watchCsAcceptReturn() {
  yield takeLeading(actions.CS_ACCEPT_RETURN, csAcceptReturn);
}

export function* csReceiveReturn({ orderId, payload }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "return.receive",
    apiCall: () => API.receiveDeliveryReturn(storeId, orderId, { ...payload, employeeId }),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csReceiveReturn",
    reportMeta: { orderId },
  });
}

export function* watchCsReceiveReturn() {
  yield takeLeading(actions.CS_RECEIVE_RETURN, csReceiveReturn);
}

export function* csCompleteReturn({ orderId, refundMethodCode }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "return.complete",
    apiCall: () => API.completeDeliveryReturn(storeId, orderId, refundMethodCode, employeeId),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csCompleteReturn",
    reportMeta: { orderId },
  });
}

export function* watchCsCompleteReturn() {
  yield takeLeading(actions.CS_COMPLETE_RETURN, csCompleteReturn);
}

export function* csAcceptExchange({ orderId, payload }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "exchange.accept",
    apiCall: () => API.acceptDeliveryExchange(storeId, orderId, { ...payload, employeeId }),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csAcceptExchange",
    reportMeta: { orderId },
  });
}

export function* watchCsAcceptExchange() {
  yield takeLeading(actions.CS_ACCEPT_EXCHANGE, csAcceptExchange);
}

export function* csCreateRedelivery({ orderId, payload }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "redelivery.create",
    apiCall: () => API.createRedelivery(storeId, orderId, { ...payload, employeeId }),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csCreateRedelivery",
    reportMeta: { orderId },
  });
}

export function* watchCsCreateRedelivery() {
  yield takeLeading(actions.CS_CREATE_REDELIVERY, csCreateRedelivery);
}

export function* csReceiveExchange({ orderId, payload }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "exchange.receive",
    apiCall: () => API.receiveDeliveryExchange(storeId, orderId, { ...payload, employeeId }),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csReceiveExchange",
    reportMeta: { orderId },
  });
}

export function* watchCsReceiveExchange() {
  yield takeLeading(actions.CS_RECEIVE_EXCHANGE, csReceiveExchange);
}

export function* csCompleteExchange({ orderId, groupId }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "exchange.complete",
    apiCall: () => API.completeDeliveryExchange(storeId, orderId, employeeId, groupId),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csCompleteExchange",
    reportMeta: { orderId },
  });
}

export function* watchCsCompleteExchange() {
  yield takeLeading(actions.CS_COMPLETE_EXCHANGE, csCompleteExchange);
}

export function* csCompleteCancel({ params }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  const { orderId, claimCode } = params || {};
  yield* runCsWrite({
    actionKey: "cancel.complete",
    apiCall: () => API.completeDeliveryCancel(storeId, orderId, claimCode, employeeId),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csCompleteCancel",
    reportMeta: { orderId },
  });
}

export function* watchCsCompleteCancel() {
  yield takeLeading(actions.CS_COMPLETE_CANCEL, csCompleteCancel);
}

export function* csIssuePickupTracking({ params }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  const { orderId, claimCode, locationId, boxQty, trackingNumber, carrierCode } =
    params || {};
  yield* runCsWrite({
    actionKey: "pickup.issueTracking",
    apiCall: () =>
      API.issuePickupTracking(storeId, orderId, {
        claimCode,
        locationId,
        boxQty,
        trackingNumber,
        carrierCode,
        employeeId,
      }),
    orderId,
    setSuccess: actions.setCsClaimStatusSuccess,
    setFailure: actions.setCsClaimStatusFailure,
    reportName: "csIssuePickupTracking",
    reportMeta: { orderId },
  });
}

export function* watchCsIssuePickupTracking() {
  yield takeLeading(actions.CS_ISSUE_PICKUP_TRACKING, csIssuePickupTracking);
}

// ----- 주문 편집·운영 -----

export function* csSetHold({ orderId, enabled, reasonCode }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "hold.set",
    apiCall: () => API.setDeliveryHold(storeId, orderId, enabled, reasonCode, employeeId),
    orderId,
    setSuccess: actions.setCsEditStatusSuccess,
    setFailure: actions.setCsEditStatusFailure,
    reportName: "csSetHold",
    reportMeta: { orderId },
  });
}

export function* watchCsSetHold() {
  yield takeLeading(actions.CS_SET_HOLD, csSetHold);
}

export function* csUpdateReceiver({ orderId, receiver }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "receiver.update",
    apiCall: () => API.updateDeliveryReceiver(storeId, orderId, receiver, employeeId),
    orderId,
    setSuccess: actions.setCsEditStatusSuccess,
    setFailure: actions.setCsEditStatusFailure,
    reportName: "csUpdateReceiver",
    reportMeta: { orderId },
  });
}

export function* watchCsUpdateReceiver() {
  yield takeLeading(actions.CS_UPDATE_RECEIVER, csUpdateReceiver);
}

export function* csAddOrderItems({ orderId, items, paymentOption }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "items.add",
    apiCall: () => API.addDeliveryOrderItems(storeId, orderId, items, paymentOption, employeeId),
    orderId,
    setSuccess: actions.setCsEditStatusSuccess,
    setFailure: actions.setCsEditStatusFailure,
    reportName: "csAddOrderItems",
    reportMeta: { orderId },
  });
}

export function* watchCsAddOrderItems() {
  yield takeLeading(actions.CS_ADD_ORDER_ITEMS, csAddOrderItems);
}

export function* csSwapOrderItem({ orderId, orderItemId, newSkuId, paymentOption }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "items.swap",
    apiCall: () =>
      API.swapDeliveryOrderItem(storeId, orderId, orderItemId, newSkuId, paymentOption, employeeId),
    orderId,
    setSuccess: actions.setCsEditStatusSuccess,
    setFailure: actions.setCsEditStatusFailure,
    reportName: "csSwapOrderItem",
    reportMeta: { orderId },
  });
}

export function* watchCsSwapOrderItem() {
  yield takeLeading(actions.CS_SWAP_ORDER_ITEM, csSwapOrderItem);
}

export function* csHandleShortage({ skuId, shortageAction, orderIds }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "shortage",
    // 응답이 주문별 results 구조라 개별 실패를 top-level error로 집계해야 runCsWrite가 실패로 처리함
    apiCall: async () => {
      const result = await API.handleDeliveryShortage(storeId, skuId, shortageAction, orderIds, employeeId);
      const entries = Object.entries((result && result.results) || {});
      const failed = entries.filter(([, r]) => r && r.error);
      if (failed.length) {
        const okCount = entries.length - failed.length;
        const suffix = okCount > 0 ? ` (성공 ${okCount}건)` : "";
        const first = failed[0][1].error;
        // 단건은 실행 라인이 이미 건수를 표기 — 접두어 없이 에러만
        return {
          ...result,
          error: entries.length > 1 ? `${failed.length}건 실패${suffix} — ${first}` : first,
        };
      }
      return result;
    },
    orderId: null, // sku 단위 일괄 — 개별 주문 refetch 없음(목록만)
    setSuccess: actions.setCsEditStatusSuccess,
    setFailure: actions.setCsEditStatusFailure,
    reportName: "csHandleShortage",
    reportMeta: { skuId },
  });
}

export function* watchCsHandleShortage() {
  yield takeLeading(actions.CS_HANDLE_SHORTAGE, csHandleShortage);
}

export function* csOpenInquiry({ orderId, reasonCode, memo }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "inquiry.open",
    apiCall: () => API.openInquiry(storeId, orderId, reasonCode, memo, employeeId),
    orderId,
    setSuccess: actions.setCsEditStatusSuccess,
    setFailure: actions.setCsEditStatusFailure,
    reportName: "csOpenInquiry",
    reportMeta: { orderId },
  });
}

export function* watchCsOpenInquiry() {
  yield takeLeading(actions.CS_OPEN_INQUIRY, csOpenInquiry);
}

export function* csCompleteInquiry({ orderId, taskId }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "inquiry.complete",
    apiCall: () => API.completeInquiry(storeId, orderId, taskId, employeeId),
    orderId,
    setSuccess: actions.setCsEditStatusSuccess,
    setFailure: actions.setCsEditStatusFailure,
    reportName: "csCompleteInquiry",
    reportMeta: { orderId },
  });
}

export function* watchCsCompleteInquiry() {
  yield takeLeading(actions.CS_COMPLETE_INQUIRY, csCompleteInquiry);
}

export function* csResyncChannel({ params }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  const { orderId, taskId } = params || {};
  yield* runCsWrite({
    actionKey: "channel.resync",
    apiCall: () => API.resyncChannel(storeId, orderId, taskId, employeeId),
    orderId,
    setSuccess: actions.setCsEditStatusSuccess,
    setFailure: actions.setCsEditStatusFailure,
    reportName: "csResyncChannel",
    reportMeta: { orderId },
  });
}

export function* watchCsResyncChannel() {
  yield takeLeading(actions.CS_RESYNC_CHANNEL, csResyncChannel);
}

// ----- 결제·정산 -----

export function* csConfirmPayment({ orderId, payload }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  const { amount, method, extraMethod } = payload || {};
  const result = yield* runCsWrite({
    actionKey: "payment.confirm",
    apiCall: () => API.confirmDeliveryPayment(storeId, orderId, amount, method, extraMethod, employeeId),
    orderId,
    setSuccess: actions.setCsPaymentStatusSuccess,
    setFailure: actions.setCsPaymentStatusFailure,
    reportName: "csConfirmPayment",
    reportMeta: { orderId },
  });
  // 채널 미입금 경로(서버 {channel:"cafe24", pushed:"paid"})는 우리 DB를 즉시 바꾸지 않는다 —
  // cafe24 입금확인 push → 90025 웹훅이 state/amountPaid/charges 를 반영하는 구조라, 응답 직후
  // 재조회는 아직 옛 값을 읽어 화면이 그대로다(08-04 실측 "완료했는데 화면이 안 바뀌네").
  // 웹훅 왕복 여유를 두고 한 번 더 조회한다(실패해도 [새로고침]으로 복구되는 보조 경로).
  if (result && result.channel === "cafe24" && orderId) {
    yield delay(2500);
    yield put(actions.getOrder(orderId));
    yield put(actions.getCsTasks());
  }
}

export function* watchCsConfirmPayment() {
  yield takeLeading(actions.CS_CONFIRM_PAYMENT, csConfirmPayment);
}

export function* csSettleReturn({ orderId, claimCode }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "settle",
    apiCall: () => API.settleDeliveryReturn(storeId, orderId, claimCode, employeeId),
    orderId,
    setSuccess: actions.setCsPaymentStatusSuccess,
    setFailure: actions.setCsPaymentStatusFailure,
    reportName: "csSettleReturn",
    reportMeta: { orderId },
  });
}

export function* watchCsSettleReturn() {
  yield takeLeading(actions.CS_SETTLE_RETURN, csSettleReturn);
}

export function* csRefundTransfer({ params }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  const { orderId, taskId, refundCode, sendSms, sendMail } = params || {};
  yield* runCsWrite({
    actionKey: "refund.transfer",
    apiCall: () =>
      API.completeRefundTransfer(storeId, orderId, {
        taskId,
        refundCode,
        sendSms,
        sendMail,
        employeeId,
      }),
    orderId,
    setSuccess: actions.setCsPaymentStatusSuccess,
    setFailure: actions.setCsPaymentStatusFailure,
    reportName: "csRefundTransfer",
    reportMeta: { orderId },
  });
}

export function* watchCsRefundTransfer() {
  yield takeLeading(actions.CS_REFUND_TRANSFER, csRefundTransfer);
}

// ----- 수집·승인 -----
// 잠재 장시간 호출(Lambda 타임아웃 가능) — takeLeading + collectStatus 스피너.
// 서버 _emitProgress 미구현(confirm/autoMatch 2종뿐) — 추후 추가 시 ASYNC_PROGRESS_FUNC 등록.

export function* csCollectClaims({ filters }) {
  const { storeId } = yield call(selectDeliveryContext);
  const { days, start, end } = filters || {};
  yield* runCsWrite({
    actionKey: "collect.claims",
    apiCall: () => API.collectClaimRequests(storeId, { days, start, end }),
    orderId: null,
    setSuccess: actions.setCsCollectStatusSuccess,
    setFailure: actions.setCsCollectStatusFailure,
    reportName: "csCollectClaims",
    reportMeta: { days, start, end },
  });
}

export function* watchCsCollectClaims() {
  yield takeLeading(actions.CS_COLLECT_CLAIMS, csCollectClaims);
}

export function* csCollectInquiries({ filters }) {
  const { storeId } = yield call(selectDeliveryContext);
  const { boardNos, days } = filters || {};
  yield* runCsWrite({
    actionKey: "collect.inquiries",
    apiCall: () => API.collectDeliveryInquiries(storeId, { boardNos, days }),
    orderId: null,
    setSuccess: actions.setCsCollectStatusSuccess,
    setFailure: actions.setCsCollectStatusFailure,
    reportName: "csCollectInquiries",
    reportMeta: { boardNos, days },
  });
}

export function* watchCsCollectInquiries() {
  yield takeLeading(actions.CS_COLLECT_INQUIRIES, csCollectInquiries);
}

// 2026-07-20 수집함 조회 전용 전환 — UI 미사용. 채널 수락 통보 재구조(승인=접수만) 확정 후 재사용/제거 결정
export function* csApproveClaim({ claimType, kwargs }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  const orderId = kwargs?.orderId;
  yield* runCsWrite({
    actionKey: "claim.approve",
    apiCall: () => API.approveClaimRequest(storeId, claimType, { employeeId, ...kwargs }),
    orderId,
    setSuccess: actions.setCsCollectStatusSuccess,
    setFailure: actions.setCsCollectStatusFailure,
    reportName: "csApproveClaim",
    reportMeta: { claimType, orderId },
  });
}

export function* watchCsApproveClaim() {
  yield takeLeading(actions.CS_APPROVE_CLAIM, csApproveClaim);
}

export function* csRejectClaim({ orderId, claimType, orderItemIds, rejectReason, taskId }) {
  const { storeId, employeeId } = yield call(selectDeliveryContext);
  yield* runCsWrite({
    actionKey: "claim.reject",
    apiCall: () =>
      API.rejectClaimRequest(
        storeId,
        orderId,
        claimType,
        orderItemIds,
        rejectReason,
        employeeId,
        taskId,
      ),
    orderId,
    setSuccess: actions.setCsCollectStatusSuccess,
    setFailure: actions.setCsCollectStatusFailure,
    reportName: "csRejectClaim",
    reportMeta: { claimType, orderId },
  });
}

export function* watchCsRejectClaim() {
  yield takeLeading(actions.CS_REJECT_CLAIM, csRejectClaim);
}

export default function* root() {
  yield all([
    fork(watchGenerateStore),
    fork(watchGetStore),
    fork(watchProviderConnection),
    fork(watchConnectProvider),
    fork(watchUpdateStore),
    fork(watchUpdateMirrorConfig),
    fork(watchUpdateDualConfig),
    fork(watchSpreadMirrorCnfig),
    fork(watchUpdateStoreLoyalty),
    fork(watchGetBillingHistories),
    fork(watchSendBarcodeCouponCampaign),
    fork(watchGetCampaigns),
    fork(watchGetCampaign),
    fork(watchCreateCampaign),
    fork(watchUpdateCampaign),
    fork(watchLanuchCampaign),
    fork(watchCancelCampaign),
    fork(watchGetCampaignWithReport),
    fork(watchGetKakaoProfile),
    fork(watchApplyBrandMessage),
    fork(watchGetItems),
    fork(watchGetItemsWithoutProduct),
    fork(watchGetCategories),
    fork(watchGetLoyaltyReport1),
    fork(watchGetLoyaltySummary),
    fork(watchGetSalesReport),
    fork(watchGetItemsReport),
    fork(watchGetSalesSummary),
    fork(watchGetSalesHistories),
    fork(watchGetSettlements),
    fork(watchCreateOrUpdatePurchaseOrder),
    fork(watchDeletePurchaseOrder),
    fork(watchGetPurchaseOrders),
    fork(watchGetEmployeeByPurchaseOrderId),
    fork(watchGetPurchaseOrderById),
    fork(watchUpdateProducts),
    fork(watchUpdateStoreMenuPlan),
    fork(watchCreateOrUpdateProduct),
    fork(watchCopyProducts),
    fork(watchDeleteProduct),
    fork(watchCreateOrUpdateCategory),
    fork(watchDeleteCategory),
    fork(watchCreateOrUpdateModifier),
    fork(watchDeleteModifier),
    fork(watchCreateOrUpdateEquipment),
    fork(watchDeleteEquipment),
    fork(watchAdjustEquipments),
    fork(watchApplyEquipmentTransfer),
    fork(watchGetEquipmentHistories),
    fork(watchGetProductCodeRules),
    fork(watchCreateOrUpdateProductCodeRule),
    fork(watchDeleteProductCodeRule),
    fork(watchDeleteItems),
    fork(watchGetOrder),
    fork(watchGetOrderWithTransactions),
    fork(watchGetInventoryHistoriesByStore),
    fork(watchGetInventoryHistoriesByLocation),
    fork(watchGetAllOrderHistories),
    fork(watchGetShippingOrders),
    fork(watchGetCustomers),
    fork(watchSearchProviderCustomer),
    fork(watchRetrieveCustomer),
    fork(watchGetProviderCustomer),
    fork(watchGetCustomer),
    fork(watchUpdateOrder),
    fork(watchUpdateOrders),
    fork(watchCreateOrUpdateCustomerGroup),
    fork(watchDeleteCustomerGroup),
    fork(watchCreateCustomer),
    fork(watchGetCustomerGorups),
    fork(watchDeleteCustomer),
    fork(watchUpdateCustomer),
    fork(watchGetPointTransactions),
    fork(watchUpdateEmployee),
    fork(watchUpdateOrganizationEmployee),
    fork(watchDeleteOrganizationEmployee),
    fork(watchGetOrdersByStore),
    fork(watchGetBillingCost),
    fork(watchGetUser),
    fork(watchUpdateUser),
    fork(watchSignUpMarket),
    fork(watchSignInMarket),
    fork(watchSetStoreFavorite),
    fork(watchLoadStore),
    fork(watchAddItemToCart),
    fork(watchAddItemsToCart),
    fork(watchDuplicateOrderToCart),
    fork(watchRemoveItemFromCart),
    fork(watchDeleteCart),
    fork(watchGetCart),
    fork(watchRecievePaidOrder),
    fork(watchGetOrdersByCustomer),
    fork(watchPayOrder),
    fork(watchRegisterOrUpdateAccount),
    fork(watchSendReceiptLink),
    fork(watchCancelOrder),
    fork(watchResetMarketPassword),
    fork(watchGetCoupon),
    fork(watchGetCoupons),
    fork(watchGetCouponsForPublish),
    fork(watchCreateCoupon),
    fork(watchUpdateCoupon),
    fork(watchDeleteCoupon),
    fork(watchDeleteCoupons),
    fork(watchSetOrderType),
    fork(watchLanunchMultiLocation),
    fork(watchGetOrderReport),
    fork(watchGetStoreMemos),
    fork(watchGetCustomerVisitReport),
    fork(watchGetCustomerSalesReport),
    fork(watchGetCustomerSummaryReport),
    fork(watchGetCustomerDemographicReport),
    fork(watchGetItemsDemographicReport),
    fork(watchGetDiscountReport),
    fork(watchGetDiscountTopProductsReport),
    fork(watchChannelOrderReport),
    fork(watchGetPointReport),
    fork(watchGetInventoryItemReport),
    fork(watchGetInventoryDateReport),
    fork(watchGetInventoryTurnoverReport),
    fork(watchCreateOrUpdateMenuPlan),
    fork(watchDeleteMenuPlan),
    fork(watchCreateOrUpdateOrderPlan),
    fork(watchDeleteOrderPlan),
    fork(watchCreateOrUpdateLocation),
    fork(watchInviteEmployee),
    fork(watchGetOrganizations),
    fork(watchSetCurrentOrganization),
    fork(watchConfirmInviteEmployee),
    fork(watchAdjustInventories),
    fork(watchUpdateSkuLocations),
    fork(watchCreateSkuInventory),
    fork(watchUpdateSkuInventory),
    fork(watchDeleteSkuInventory),
    fork(watchUpdateSyncMenuBoard),
    fork(watchCreateOrUpdateCustomerTag),
    fork(watchUpdateCustomerTags),
    fork(watchDeleteCustomerTag),
    fork(watchCreateOrUpdateStoreAtrributes),
    fork(watchGetCustomerTags),
    fork(watchUploadItemTemplate),
    fork(watchSyncItemTemplate),
    fork(watchReceiveStoreStatus),
    fork(watchUpdateActiveChangedProducts),
    fork(watchGetOrderByApiKey),
    fork(watchUpdateOrderFromReceipt),
    fork(watchGetCorporateCustomers),
    fork(watchGetSubscriptionPlans),
    fork(watchGetSubscriptionPlan),
    fork(watchCreateSubscriptionPlan),
    fork(watchUpdateSubscriptionPlan),
    fork(watchDeleteSubscriptionPlan),
    fork(watchCreateCustomerPayment),
    fork(watchDeleteCustomerPayment),
    fork(watchSubscribeSubscriptionPlan),
    fork(watchStopSubscription),
    fork(watchRestoreSubscription),
    fork(watchCancelSubscription),
    fork(watchGetSubscriptionEvents),
    fork(watchGetSubscriptionPlansByIds),
    fork(watchGetSubscriptionReport),
    fork(watchCreateOrUpdateTerminal),
    fork(watchDeleteTerminal),
    fork(watchGetTerminals),
    fork(watchGetCustomerGroupsWithCount),
    fork(watchGetCustomersByTag),
    fork(watchGetCustomersStats),
    fork(watchGetRecipientCount),
    fork(watchGetRegisteredCustomerCount),
    fork(watchGetCustomerTagsCount),
    fork(watchPayFailedBillings),
    fork(watchUpdateLocation),
    fork(watchUpdateReceiptConfig),
    fork(watchUpdateViewConfig),
    fork(watchGetDeliveryReport),
    fork(watchFindEmployeeByEmail),
    fork(watchGetMgmtContract),
    fork(watchClearAllContract),
    fork(watchCreateOrUpdateContract),
    fork(watchUpdateMgmtContract),
    fork(watchCreateOrUpdateRentalContract),
    fork(watchFindStoresByOperatorId),
    fork(watchCreateOrUpdateLabelTemplate),
    fork(watchDeleteLabelTemplate),
    fork(watchGetLabelTemplates),
    fork(watchUpdateSettings),
    fork(watchConvertPosMode),
    fork(watchGetSuppliers),
    fork(watchCreateOrUpdateSupplier),
    fork(watchDeleteSupplier),
    fork(watchGetBrands),
    fork(watchCreateOrUpdateBrand),
    fork(watchDeleteBrand),
    fork(watchCreateInventoryTransferHistory),
    fork(watchGetInventoryHistoriesByGroupId),
    fork(watchGetInventoryTransferHistories),
    fork(watchGetSkuInventoryHistories),
    fork(watchGetInventoryHistorySummary),
    fork(watchCompleteInventoryTransferHistory),
    fork(watchDeleteInventoryTransferHistory),
    fork(watchGetCountHistories),
    fork(watchShareHistoryPdf),
    fork(watchSharePurchaseOrderPdf),
    fork(watchUpdateInventoryHistory),
    fork(watchUpdateInventoryHistoryNote),
    fork(watchRefreshInventoryHistory),
    fork(watchDownloadInventoryHistories),
    fork(watchShareGroupHistoryPdf),
    fork(watchSyncInventoryTemplate),
    fork(watchUploadInventoryTemplate),
    fork(watchGenerateInventoryTemplate),
    fork(watchGetInventoryAssetReport),
    fork(watchGetProduct),
    fork(watchCreateCouponTransactions),
    fork(watchIssueCouponToCustomers),
    fork(watchIssueCouponByFilter),
    fork(watchSearchCouponTransactions),
    fork(watchDownloadCsvForTransactions),
    fork(watchGetBarcodeCoupon),
    fork(watchGetBarcodeCouponTransaction),
    fork(watchGetBarcodeCoupons),
    fork(watchGetBarcodeCouponTransactionByPhone),
    fork(watchIssueBarcodeCouponForProviderCustomer),
    fork(watchIssueBarcodeCouponWithStoreCustomerId),
    fork(watchDropoutAccount),
    fork(watchSyncExternalLoyalty),
    fork(watchGetProviderCoupons),
    fork(watchUpdateProviderConfig),
    fork(watchGetProviderConfig),
    fork(watchGetWaiting),
    fork(watchGetWaitingInfo),
    fork(watchCancelWaiting),
    fork(watchUpdateCreditConfig),
    fork(watchGetCreditStatus),
    fork(watchGetCreditHistories),
    fork(watchChargeCredit),
    fork(watchGetRequestProgressEvents),
    fork(watchReceiveRequestProgressEvent),
    fork(watchGetProviderOrders),
    fork(watchGetProviderCustomerGroups),
    fork(watchGetAvailableShops),
    fork(watchGetTierUpdateHistories),
    fork(watchLinkProviderProducts),
    fork(watchGetProviderProducts),
    fork(watchUpdateStoreProvider),
    fork(watchSyncProviderInventories),
    fork(watchGetInventorySyncHistories),
    fork(watchGetProviderProductsLinkHistories),
    fork(watchUnlinkProviderProducts),
    fork(watchSyncProviderProducts),
    fork(watchGetDraftOnoffConfig),
    fork(watchGetLiveOnoffConfig),
    fork(watchCreateOrUpdateOnoffConfig),
    fork(watchDeleteDraftOnoffConfig),
    fork(watchRetrieveCustomerWithPoint),
    fork(watchRetrieveCustomerWithOrder),
    fork(watchRetrieveCustomerWithCredit),
    fork(watchRetrieveCustomerWithCoupon),
    fork(watchGetCherryPickers),
    fork(watchGetBlackListCustomers),
    fork(watchGetCustomerTierHistories),
    fork(watchUpdateCustomerTier),
    fork(watchUpdateCustomerBlackList),
    fork(watchGetStoreDevices),
    fork(watchSendShippingNotice),
    fork(watchGetProductInventories),
    fork(watchGetSkuInventoryReport),
    fork(watchGetProductList),
    fork(watchGetProductsByIds),
    fork(watchGetAllProductInventoriesForExport),
    fork(watchGetContractSubscriptionPlans),
    fork(watchPaySubscriptionPlan),
    fork(watchCancelSubscriptionPlan),
    fork(watchUpdateContractSubscriptionPlan),
    fork(watchGetLocationStats),
    fork(watchGetCustomerEvents),
    fork(watchCreateOrUpdateCustomerEvent),
    fork(watchDeleteCustomerEvent),
    fork(watchGetCustomerEventReport),
    fork(watchGetCustomerEventUserList),
    fork(watchGetProviderCustomerCount),
    fork(watchGetInventoryNewRequests),
    fork(watchCreateInventoryNewRequest),
    fork(watchUpdateInventoryNewRequest),
    fork(watchCreateInventoryNewRequests),
    fork(watchUpdateInventoryNewRequests),
    fork(watchDeleteInventoryNewRequest),
    fork(watchGetmembershipReport),
    fork(watchCreateOrUpdateCampaignTemplate),
    fork(watchDeleteCampaignTemplate),
    fork(watchGetCampaignTemplates),
    fork(watchGetMessageCmapignReport),
    // 배송주문 관리 — 발주(주문 가져오기/발주확인/매칭/합포)
    fork(watchGetPreConfirmOrders),
    fork(watchGetDeliveryStageOrders),
    fork(watchGetConfirmBatches),
    fork(watchGetItemLinks),
    fork(watchImportChannelOrders),
    fork(watchReloadDeliveryOrders),
    fork(watchDeleteUnconfirmedOrders),
    fork(watchConfirmDeliveryOrders),
    fork(watchAutoMatchDeliveryOrders),
    fork(watchMergeCheckDeliveryOrders),
    fork(watchSaveManualMatch),
    fork(watchDeleteItemLink),
    fork(watchCancelConfirmOrders),
    fork(watchConfirmMergeOrders),
    fork(watchExcludeMergeOrders),
    fork(watchManualMergeOrders),
    fork(watchSetDeliveryMergeBlocked),
    fork(watchBlockMergeGroup),
    fork(watchNavBadgeRefresh),
    fork(watchCsBulkMergeBlock),
    fork(watchCsBulkSetHold),
    fork(watchCreateManualOrder),
    fork(watchCopyDeliveryOrder),
    fork(watchUploadManualOrderRows),
    // 배송주문 관리 — 송장(발번/출력차수/발송처리/가송장/배송관리)
    fork(watchGetPrintTargets),
    fork(watchGetShippingStageOrders),
    fork(watchGetPrintBatches),
    fork(watchGetProvisionOrders),
    fork(watchIssueDeliveryTracking),
    fork(watchDeleteDeliveryTracking),
    fork(watchPushDeliveryShipment),
    fork(watchApplyExternalTracking),
    fork(watchIssueProvisionTracking),
    fork(watchConvertProvisionToReal),
    fork(watchCompleteProvisionDelivery),
    fork(watchSplitDeliveryShipment),
    fork(watchAddShipmentBox),
    fork(watchAddShipmentReceiver),
    fork(watchReissueDeliveryTracking),
    fork(watchChangeShippingAfterIssue),
    fork(watchCreatePrintBatch),
    fork(watchReprintBatch),
    fork(watchExportPrintTargetsExcel),
    fork(watchTrackShipment),
    fork(watchCheckDeliveryDelays),
    // 배송 환경설정 (ShippingConfig + Carrier)
    fork(watchGetShippingSettings),
    fork(watchSaveShippingConfig),
    fork(watchSaveShippingCarrier),
    fork(watchDeleteShippingCarrier),
    fork(watchUpdateCarrierTemplates),
    // 양식 전용 뮤테이션 — 서버 배포 7/23
    fork(watchSaveShipmentTemplate),
    fork(watchCopyShipmentTemplate),
    fork(watchDeleteShipmentTemplate),
    fork(watchGetSkuMergeRules),
    fork(watchUpdateSkuMergeRules),
    fork(watchGetUnusedTrackings),
    // 배송주문 관리 — CS(클레임/주문 편집·운영/결제·정산/수집·승인)
    fork(watchGetCsTasks),
    fork(watchGetCsOrders),
    fork(watchGetCsCounts),
    fork(watchGetCsActivities),
    fork(watchSearchCsOrders),
    fork(watchCsCancelOrder),
    fork(watchCsRestoreOrder),
    fork(watchCsRestoreReturn),
    fork(watchCsRestoreExchange),
    fork(watchCsAcceptReturn),
    fork(watchCsReceiveReturn),
    fork(watchCsCompleteReturn),
    fork(watchCsAcceptExchange),
    fork(watchCsCreateRedelivery),
    fork(watchCsReceiveExchange),
    fork(watchCsCompleteExchange),
    fork(watchCsCompleteCancel),
    fork(watchCsIssuePickupTracking),
    fork(watchCsSetHold),
    fork(watchCsUpdateReceiver),
    fork(watchCsAddOrderItems),
    fork(watchCsSwapOrderItem),
    fork(watchCsHandleShortage),
    fork(watchCsOpenInquiry),
    fork(watchCsCompleteInquiry),
    fork(watchCsResyncChannel),
    fork(watchCsConfirmPayment),
    fork(watchCsSettleReturn),
    fork(watchCsRefundTransfer),
    fork(watchCsCollectClaims),
    fork(watchCsCollectInquiries),
    fork(watchCsApproveClaim),
    fork(watchCsRejectClaim),
  ]);
}
